// 스타일 — Vite가 번들링하도록 import (경로 참조 대신)
import './style.css';

// 모듈 임포트
import { exportChartSVG } from './export-svg.js';

// 서버 API 클라이언트 (Commit / Checkout / History)
import { api } from '../src/api.js';

// 게임 카메라 시뮬레이터 — 데모 캔버스의 "카메라 영역" 표시용.
// 🔴 전역 `zoom`/`viewOffset`(에디터 뷰)과 게임 카메라는 무관하다. camera-model.js 참조.
import {
    cameraAt,
    cameraCorners,
    clampedPositionAt
} from './camera-model.js';

import {
    lerp,
    getNoteTimingParams,
    convertNoteTypeToExternal,
    calculateLongNoteTime,
    calculatePathBeat,
    timeToBeat,
    beatToTime,
    directionToVector,
    directionToVectorWithAngle,
    resolvePathHeadings,
    clampAngleOffset,
    MAX_ANGLE_OFFSET,
    CURVATURE_SOFT_LIMIT,
    CURVE_PRESETS,
    solveCurveSegment,
    buildArcLengthLUT,
    pointAtArcProgress,
    getPreDelaySeconds,
    calculateUnityMovementPerBeat,
    calculateUnityNodeDistance,
    convertEditorToUnityCoordinate,
    convertUnityToEditorCoordinate,
    calculateUnityNotePosition,
    calculateFadeProgress,
    calculateSectionOffsets,
    recomputeSectionIndices
} from '@wl/shared/utils.js';

import {
    drawCircle,
    drawText,
    drawDirectionArrow,
    drawTriangle,
    processLongNote,
    drawRuler
} from './canvas.js';

import {
    SoundPool,
    formatTime
} from './audio.js';

import {
    validateNote,
    validateChart,
    noteToJsonFormat,
    jsonToNoteFormat,
    sortNotesByTime,
    ensureInitialDirectionNote,
    ensureFinalDirectionNote,
    getNoteColor,
    cloneNote
} from '@wl/shared/notes.js';

import {
    addEvent,
    removeEvent,
    addEventParam,
    removeEventParam,
    getAllEvents,
    clearAllEvents,
    eventsToJson,
    loadEventsFromJson,
    getEventTypes as builtinGetEventTypes,
    getEventTypeDescription as builtinGetEventTypeDescription,
    getEventIdsByType as builtinGetEventIdsByType,
    isCustomEventType,
    createEvent,
    createEventParam,
    sortEventsByTime,
    getPredefinedParamsForEventId as builtinGetPredefinedParams,
    applyPredefinedParams as builtinApplyPredefinedParams,
    cloneEvent,
    insertMultipleEvents,
    getEventAtIndex,
    updateEvent,
    updateMultipleEvents,
    isDialogEvent,
    addDialogItem,
    removeDialogItem,
    moveDialogItem,
    updateDialogItem,
    getDialogItemTypes as builtinGetDialogItemTypes,
    getDialogItemFields as builtinGetDialogItemFields,
    createDialogItem
} from '@wl/shared/events.js';

// 타임라인 뷰 (Phase 1). 좌표 계산은 timeline-model.js, 그리기는 timeline.js가 한다.
// 둘 다 **읽기 전용**이다 — notes를 수정하지 않는다(감사 B-4를 되풀이하지 않기 위함).
import {
    createTimelineState,
    beatsPerRowOf,
    renderTimeline,
    playheadOffsetPx,
    followScrollTop
} from './timeline.js';
import {
    rowToBeat,
    snapBeat,
    calculateAbsoluteBeats,
    calculateSectionOffsetBeats,
    absoluteBeatToNoteBeat,
    retimeBeatForSubdivisions,
    findInsertionIndex,
    sectionOffsetForInsertion,
    findChartEndNoteIndex,
    minimumChartEndBeat,
    governingBpmDeclaration,
    sectionStartBeats,
    clampBeatToSection,
    absoluteBeatToEventTime,
    eventTimeToAbsoluteBeat
} from './timeline-model.js';

// ── 이벤트 정의: 서버 오버라이드 ────────────────────────────────────────
//
// 드롭다운 목록과 사전정의 파라미터는 shared/event-config.js가 기본값이다.
// 그 파일은 Unity 계약 사본이라 런타임에 고치지 않는다. 대신 관리자가 서버에
// 저장한 설정이 있으면 그것을 얹어 쓴다(없으면 기본값 그대로).
//
// 검증(validateEvent)은 목록 소속을 보지 않고 형태만 보므로, 이 설정을 바꿔도
// 기존 차트가 무효가 되지 않는다 — 에디터가 "무엇을 제안하는가"만 달라진다.
let eventConfigOverride = null;

function getEventTypes() {
    return eventConfigOverride ? [...eventConfigOverride.eventTypes] : builtinGetEventTypes();
}

function getEventTypeDescription(eventType) {
    if (eventConfigOverride) return eventConfigOverride.typeDescriptions?.[eventType] || '';
    return builtinGetEventTypeDescription(eventType);
}

function getEventIdsByType(eventType) {
    if (eventConfigOverride) return [...(eventConfigOverride.eventIdsByType?.[eventType] || [])];
    return builtinGetEventIdsByType(eventType);
}

function getPredefinedParamsForEventId(eventType, eventId) {
    if (!eventType || !eventId) return [];
    if (eventConfigOverride) {
        return [...(eventConfigOverride.predefinedParams?.[`${eventType}-${eventId}`] || [])];
    }
    return builtinGetPredefinedParams(eventType, eventId);
}

function getDialogItemTypes() {
    return eventConfigOverride
        ? [...(eventConfigOverride.dialogItemTypes || [])]
        : builtinGetDialogItemTypes();
}

function getDialogItemFields(itemType) {
    return eventConfigOverride
        ? [...(eventConfigOverride.dialogItemFields?.[itemType] || [])]
        : builtinGetDialogItemFields(itemType);
}

/**
 * paramType별 기본값.
 *
 * shared/events.js의 getDefaultValueForParamType과 **정확히 같아야 한다**
 * (그 함수는 export되지 않아 재사용할 수 없다). 값이 갈라지면 오버라이드를
 * 쓸 때와 안 쓸 때 기본값이 달라진다.
 */
function defaultValueForParamType(paramType) {
    switch (paramType) {
        case 'float': return '0.0';
        case 'int': return '0';
        case 'string': return '';
        case 'bool': return 'false';
        case 'vector3': return '0.0,0.0,0.0';
        case 'vector2': return '0.0,0.0';
        case 'color': return '#FFFFFF';
        case 'enum': return '';
        default: return '';
    }
}

/**
 * 사전정의 파라미터 자동 추가.
 *
 * shared 버전은 내부에서 built-in 목록을 보므로, 관리자가 바꾼 설정이 반영되지
 * 않는다. 오버라이드가 있을 때만 같은 로직을 여기서 수행한다.
 */
function applyPredefinedParams(eventIndex) {
    if (!eventConfigOverride) return builtinApplyPredefinedParams(eventIndex);

    const event = getEventAtIndex(eventIndex);
    if (!event) return;

    const predefined = getPredefinedParamsForEventId(event.eventType, event.eventId);
    if (predefined.length === 0) return;

    const existing = event.eventParams.map((p) => p.paramName);
    predefined.forEach((p) => {
        if (!existing.includes(p.paramName)) {
            event.eventParams.push({
                paramName: p.paramName,
                paramValue: defaultValueForParamType(p.paramType),
            });
        }
    });
}

// 전역 변수들
let zoom = 30;
let viewOffset = {
    x: 0,
    y: 0
};
let isPanning = false;
let lastMousePos = {
    x: 0,
    y: 0
};

let isPlaying = false;
let isPaused = false;
let isCameraTracking = true;

// 게임 카메라 영역(흰 사각형) 표시 여부. 🔴 위의 isCameraTracking(에디터 뷰 추적)과 다르다.
let isCameraBoundsVisible = false;
// 댐핑 연속성을 위한 직전 프레임 카메라. 재생이 끊기면 리셋한다.
const cameraBoundsPrev = { cam: null, time: null };
let startTime = 0;
let pauseStartTime = 0; // 일시정지 시작 시간
let elapsedTime = 0;
let animationFrameId = null;

let countdownTimer = null;
let demoAudio = new Audio();
let audioFileURL = null;
let savedAudioFile = null; // 저장된 오디오 파일 정보

let highlightedNoteIndex = null;
let highlightedNoteTimer = 0;
let highlightedEventIndex = null;
let highlightedEventTimer = 0;
let selectedNoteIndex = null; // 현재 선택된 노트의 인덱스 (포커스용)
let selectedNoteIndices = new Set(); // 선택된 노트들의 인덱스 세트 (다중 선택용)
let lastClickedNoteIndex = null; // 마지막으로 클릭된 노트 인덱스 (Shift 선택용)
let selectedEventIndices = new Set(); // 선택된 이벤트들의 인덱스 세트
let lastClickedEventIndex = null; // 마지막으로 클릭된 이벤트 인덱스 (Shift 선택용)

let globalAnimationFrameId = null;
let isDrawLoopRunning = false;
let isBatchEditEnabled = false;

let musicVolume = 0.5; // 0.0 ~ 1.0
let sfxVolume = 1.0; // 0.0 ~ 1.0
let speedMultiplier = 1.0; // 1.0 ~ 3.0 배속

// 렌더링 디바운싱을 위한 변수
let renderScheduled = false;
let pendingRenderFlags = {
    noteList: false,
    eventList: false,
    canvas: false,
    timeline: false
};

// Virtual Scrolling 상태
let virtualScrollState = {
    // NoteList용
    note: {
        scrollTop: 0,
        itemHeight: 35, // 각 행의 높이 (픽셀)
        visibleCount: 50, // 화면에 보이는 항목 수
        bufferCount: 10 // 위아래 버퍼 항목 수
    },
    // EventList용
    event: {
        scrollTop: 0,
        containerHeight: 0,
        itemHeight: 120, // 기본 아이템 높이 (동적으로 조정됨)
        overscan: 5, // 버퍼로 추가 렌더링할 아이템 수
        renderedRange: { start: 0, end: 0 },
        itemHeights: new Map(), // 각 아이템의 실제 높이 캐싱
        enabled: false // 가상 스크롤링 활성화 여부
    }
};

const demoPlayer = {
    x: 0,
    y: 0
};

const notes = [];

/**
 * Signature 구간 — { startTime, endTime } 배열.
 *
 * **에디터에서 다루는 값은 preDelay 적용 전 시간(originalTime)이다.** 작업자가
 * 혼란 없이 입력할 수 있게 하기 위함이다. 반면 JSON에 저장되는 값은
 * finalTime = originalTime + preDelaySeconds 다.
 *
 * 변환은 딱 두 경계에서만 한다:
 *   - JSON 저장/불러오기: signatureToFinal / signatureFromFinal
 *   - 캔버스 렌더: 세그먼트가 finalTime이므로 비교 직전에 +preDelay
 * 그 외 내부(입력 UI, autosave)는 전부 에디터 시간 그대로 둔다.
 *
 * 철자가 "signature"인 것은 의도된 계약 철자다 — 고치지 말 것.
 */
const signatures = [];

/**
 * 적정 배속 (JSON 키 `speed`) — **패턴 전역 데이터**로 `level`과 같은 depth에 있다.
 * 노트별 값이 아니다.
 *
 * ⚠️ 캔버스 미리보기용 `speedMultiplier`(#speed-multiplier)와 **다른 값**이다.
 * 그쪽은 화면에 경로를 얼마나 벌려 그릴지를 정하는 로컬 뷰 설정이고 JSON에
 * 들어가지 않는다. 이쪽은 차트 제작자가 권장하는 배속으로 JSON에 저장된다.
 *
 * 기본값이면 Save Json에서 키를 생략한다 — 이 필드가 없는 기존 차트의 바이트를
 * 그대로 유지하기 위해서다(signature·angleOffset·bezier와 같은 규칙, D-003).
 */
const DEFAULT_SPEED = 1.0;
const MIN_SPEED = 0.5;
const MAX_SPEED = 3.0;

/** #speed 입력값을 범위 안으로 정리해 읽는다. 비었거나 숫자가 아니면 기본값. */
function getSpeedValue() {
    const field = document.getElementById("speed");
    const parsed = parseFloat(field ? field.value : NaN);
    if (!Number.isFinite(parsed)) return DEFAULT_SPEED;
    // 0.1 단위 입력에서 부동소수 오차가 JSON에 새지 않게 소수 1자리로 맞춘다.
    const clamped = Math.max(MIN_SPEED, Math.min(MAX_SPEED, parsed));
    return Math.round(clamped * 10) / 10;
}

/** finalTime이 signature 구간 안에 있는가 */
function isTimeInSignature(time) {
    return signatures.some((s) =>
        typeof s.startTime === "number" && typeof s.endTime === "number" &&
        time >= s.startTime && time < s.endTime);
}

/** 경로 세그먼트(start~end)가 signature 구간과 겹치는가 */
function segmentTouchesSignature(start, end) {
    return signatures.some((s) => {
        if (typeof s.startTime !== "number" || typeof s.endTime !== "number") return false;
        return start < s.endTime && end > s.startTime;
    });
}

/**
 * Signature 입력 행들을 다시 그린다.
 *
 * 모듈 스코프에 둔 이유: 초기화 코드(loadFromStorage 직후)와 IIFE 안쪽 양쪽에서
 * 불러야 하는데, 두 곳의 스코프가 다르다.
 */
function renderSignatureList() {
    const list = document.getElementById("signature-list");
    if (!list) return;
    list.innerHTML = "";

    signatures.forEach((sig, index) => {
        const row = document.createElement("div");
        row.className = "signature-row";
        // 끝이 시작보다 빠르면 시각적으로 알린다(구간으로 성립하지 않음).
        if (typeof sig.startTime === "number" && typeof sig.endTime === "number"
            && sig.endTime <= sig.startTime) {
            row.classList.add("invalid");
            row.title = "끝 시간이 시작 시간보다 커야 합니다.";
        }

        const mkInput = (key, title) => {
            const el = document.createElement("input");
            el.type = "number";
            el.step = "0.001";
            el.value = sig[key] ?? 0;
            el.title = title;
            el.addEventListener("input", () => {
                sig[key] = parseFloat(el.value) || 0;
                saveToStorage();
                renderSignatureList();
                drawPath();
            });
            return el;
        };

        const sep = document.createElement("span");
        sep.className = "sig-sep";
        sep.textContent = "~";

        const del = document.createElement("button");
        del.type = "button";
        del.className = "sig-del";
        del.textContent = "✖";
        del.title = "이 구간 삭제";
        del.addEventListener("click", () => {
            signatures.splice(index, 1);
            saveToStorage();
            renderSignatureList();
            drawPath();
        });

        row.appendChild(mkInput("startTime", "시작 (preDelay 전 초)"));
        row.appendChild(sep);
        row.appendChild(mkInput("endTime", "끝 (preDelay 전 초)"));
        row.appendChild(del);
        list.appendChild(row);
    });
}

// 성능 최적화를 위한 캐싱 시스템
let pathCache = {
    pathDirectionNotes: null,
    nodePositions: null,
    segmentTimes: null,
    lastNotesHash: null,
    lastBpm: null,
    lastSubdivisions: null,
    lastPreDelaySeconds: null,
    lastSpeedMultiplier: null
};

// 배경 레이어 캐시 (그리드, 경로, 마커)
let backgroundCache = {
    lastZoom: null,
    lastViewOffsetX: null,
    lastViewOffsetY: null,
    lastNotesHash: null,
    lastBpm: null,
    lastSubdivisions: null,
    lastPreDelaySeconds: null,
    lastRealtimeDrawingEnabled: null,
    lastIsPlaying: null,
    lastDrawTime: null
};

// 구간(Section) 오프셋 캐시 - notes 배열과 1:1 대응
let noteSectionOffsets = [];

// 구간 오프셋 lazy 계산 (notes 변경 시 자동 무효화)
function getOrComputeSectionOffsets() {
    if (noteSectionOffsets.length !== notes.length) {
        const bpm = parseFloat(document.getElementById("bpm").value || 120);
        const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
        noteSectionOffsets = calculateSectionOffsets(notes, bpm, subdivisions);
    }
    return noteSectionOffsets;
}

// direction/node 계열 노트를 _sectionOffset 첨부 후 절대시간 순으로 반환
// (경로 계산, 위치 조회 등 공통으로 사용)
function getDirectionNotesWithOffsets(bpmVal, subsVal) {
    const offsets = getOrComputeSectionOffsets();
    return notes
        // _originalIndex: 곡선 드래그 핸들이 세그먼트 → 원본 notes[] 인덱스를 역참조하는 데 쓴다.
        // 사설 필드라 직렬화(noteToJsonFormat 화이트리스트)에 새어 나가지 않는다.
        .map((n, i) => ({ ...n, _sectionOffset: offsets[i], _originalIndex: i }))
        .filter(n =>
            n.type === "direction" ||
            n.type === "both" ||
            n.type === "longdirection" ||
            n.type === "longboth" ||
            n.type === "node"
        )
        .sort((a, b) => {
            const aTime = (a._sectionOffset || 0) + beatToTime(a.beat, a.bpm || bpmVal, a.subdivisions || subsVal);
            const bTime = (b._sectionOffset || 0) + beatToTime(b.beat, b.bpm || bpmVal, b.subdivisions || subsVal);
            return aTime - bTime;
        });
}

// 캐시 무효화 함수
function invalidatePathCache() {
    pathCache.pathDirectionNotes = null;
    pathCache.nodePositions = null;
    pathCache.segmentTimes = null;
    pathCache.lastNotesHash = null;
    noteSectionOffsets = []; // 구간 오프셋도 함께 무효화
    // 배열 순서 기준으로 sectionIndex 재계산
    recomputeSectionIndices(notes);
}

// 배경 캐시 무효화
function invalidateBackgroundCache() {
    backgroundCache.lastZoom = null;
    backgroundCache.lastViewOffsetX = null;
    backgroundCache.lastViewOffsetY = null;
    backgroundCache.lastNotesHash = null;
}

// 노트 배열 해시 생성 (변경 감지용)
function generateNotesHash(notes, bpm, subdivisions, preDelaySeconds) {
    const noteString = notes.map(n =>
        `${n.beat}-${n.type}-${n.direction}-${n.bpm || bpm}-${n.subdivisions || subdivisions}` +
        `-${n.angleOffset || 0}-${n.bezier ? `${n.bezier.curvature || 0},${n.bezier.bias || 0}` : ''}`
    ).join('|');
    return `${noteString}-${bpm}-${subdivisions}-${preDelaySeconds}`;
}

// 곡선을 편집할 수 있는 노트 타입인지(경로 생성 노트 + wait 아님).
// Curve 셀/프리셋/드래그 핸들이 모두 같은 게이트를 쓰도록 일원화한다.
function noteAcceptsCurve(note) {
    if (!note) return false;
    if (note.type === "node" && note.wait) return false; // wait Node는 곡선 미정의
    return ["direction", "longdirection", "both", "longboth", "node"].includes(note.type);
}

// 곡선 값을 노트(들)에 적용하는 단일 경로. 기본값(0,0)이면 필드를 제거해
// 기존 차트가 무수정 재현되도록 한다(export의 생략 규칙과 대칭).
// index 하나만 주면 그 노트에, 다중선택 상태면 선택된 곡선 가능 노트 전체에 적용.
function applyCurveToNotes(index, curvature, bias) {
    const targetIndices = (selectedNoteIndices.has(index) && selectedNoteIndices.size > 1)
        ? [...selectedNoteIndices].filter(i => noteAcceptsCurve(notes[i]))
        : (noteAcceptsCurve(notes[index]) ? [index] : []);

    const c = Number(curvature) || 0;
    const b = Number(bias) || 0;
    targetIndices.forEach(i => {
        const n = notes[i];
        if (!c && !b) delete n.bezier;
        else n.bezier = { curvature: c, bias: b };
    });
    // 호출부가 "바뀐 행만" 화면에 반영할 수 있도록 대상 인덱스를 돌려준다.
    return targetIndices;
}

// 화면 어디서든 열 수 있는 단일 컨텍스트 메뉴. 열려 있으면 먼저 닫는다.
let activeContextMenu = null;
function closeContextMenu() {
    if (activeContextMenu) {
        activeContextMenu.remove();
        activeContextMenu = null;
        document.removeEventListener("mousedown", onContextMenuOutside, true);
        document.removeEventListener("keydown", onContextMenuEsc, true);
    }
}
function onContextMenuOutside(e) {
    if (activeContextMenu && !activeContextMenu.contains(e.target)) closeContextMenu();
}
function onContextMenuEsc(e) {
    if (e.key === "Escape") closeContextMenu();
}

// items: [{ label, onClick, active? }]. 구분선은 { separator: true }.
function showContextMenu(clientX, clientY, items) {
    closeContextMenu();
    const menu = document.createElement("div");
    menu.className = "curve-context-menu";
    items.forEach(item => {
        if (item.separator) {
            const hr = document.createElement("div");
            hr.className = "curve-context-menu-sep";
            menu.appendChild(hr);
            return;
        }
        const el = document.createElement("div");
        el.className = "curve-context-menu-item" + (item.active ? " active" : "");
        el.textContent = (item.active ? "✓ " : "") + item.label;
        el.addEventListener("click", () => {
            closeContextMenu();
            item.onClick();
        });
        menu.appendChild(el);
    });

    // 화면 밖으로 넘치지 않게 위치 보정 (먼저 화면 밖에 붙여 크기 측정 후 조정)
    menu.style.left = "-9999px";
    menu.style.top = "-9999px";
    document.body.appendChild(menu);
    const rect = menu.getBoundingClientRect();
    const x = Math.min(clientX, window.innerWidth - rect.width - 4);
    const y = Math.min(clientY, window.innerHeight - rect.height - 4);
    menu.style.left = `${Math.max(0, x)}px`;
    menu.style.top = `${Math.max(0, y)}px`;

    activeContextMenu = menu;
    document.addEventListener("mousedown", onContextMenuOutside, true);
    document.addEventListener("keydown", onContextMenuEsc, true);
}

// 곡선 프리셋 메뉴를 특정 노트(index)에 대해 연다. 적용 후 프리뷰/저장/리렌더.
function showCurvePresetMenu(clientX, clientY, index) {
    const note = notes[index];
    if (!noteAcceptsCurve(note)) return;

    const cur = note.bezier || { curvature: 0, bias: 0 };
    const items = CURVE_PRESETS.map(p => ({
        label: `${p.label}  (${p.curvature}, ${p.bias})`,
        // 현재 값과 일치하는 프리셋에 체크 표시
        active: (cur.curvature || 0) === p.curvature && (cur.bias || 0) === p.bias,
        onClick: () => {
            saveState();
            const changed = applyCurveToNotes(index, p.curvature, p.bias);
            invalidatePathCache();
            saveToStorage();
            drawPath();
            // 바뀐 행의 Curve 셀만 갱신한다(목록 재생성은 편집 중인 입력을 파괴한다).
            changed.forEach(syncCurveCellInputs);
        }
    }));
    showContextMenu(clientX, clientY, items);
}

// --- 곡선 드래그 핸들 (E7-b) ---
// 핸들은 "점을 옮기는" UI가 아니다. 끝점은 파생값이므로, 핸들이 실제로 조작하는 값은
// curvature/bias 두 스칼라뿐이다(Q3 합의). 드래그한 화면 좌표를 이 둘로 역산하고,
// 끝점과 그 이후 경로는 solveCurveSegment가 다시 풀어 통째로 따라 움직인다(실시간 프리뷰).
let curveHandlesEnabled = true;     // 토글(체크박스)로 켜고 끔
const HANDLE_HIT_RADIUS = 9;        // 화면 픽셀 기준 잡기 반경
let draggingHandle = null;          // { noteIndex } — 드래그 중인 핸들
let handleInteractionActive = false; // mousedown이 핸들에서 시작됐는지(click 새는 것 방지)

// bias가 과하면 제어점이 구간 밖으로 튀어 곡선이 뒤집힌다. 소프트리밋 밖은 허용하되(§Q2)
// 물리적으로 의미 있는 범위로만 제한한다. curvature는 clamp하지 않는다(부호/크기 자유).
const BIAS_DRAG_LIMIT = 0.45;

function screenToWorld(sx, sy) {
    return { x: (sx - viewOffset.x) / zoom, y: (sy - viewOffset.y) / zoom };
}

// 현재 캐시된 세그먼트에서 (mx,my) 화면좌표에 가장 가까운 핸들을 찾는다.
function findHandleAt(mx, my) {
    const segs = pathCache.segmentTimes;
    if (!segs) return null;
    for (const seg of segs) {
        if (!seg.handle) continue;
        const hx = seg.handle.pos.x * zoom + viewOffset.x;
        const hy = seg.handle.pos.y * zoom + viewOffset.y;
        if (Math.hypot(hx - mx, hy - my) <= HANDLE_HIT_RADIUS) {
            return seg.handle;
        }
    }
    return null;
}

// 핸들 드래그 → curvature/bias 역산. 분모는 세그먼트의 solved r(호장보존 스케일).
// (검증: e7-math.mjs — r을 분모로 쓰면 forward/inverse가 정확히 대칭이 된다.)
function updateCurveFromHandleDrag(handle, worldX, worldY) {
    const { start, u, n, r } = handle;
    if (!r) return;
    const dx = worldX - start.x, dy = worldY - start.y;
    const tp = dx * u.x + dy * u.y;    // 접선 성분
    const np = dx * n.x + dy * n.y;    // 법선 성분
    const curvature = np / r;
    let bias = tp / r - 0.5;
    bias = Math.max(-BIAS_DRAG_LIMIT, Math.min(BIAS_DRAG_LIMIT, bias));
    applyCurveToNotes(handle.noteIndex, curvature, bias);
}

// 곡선 핸들의 현재 화면 좌표 목록. 디버깅/자동화 검증용 읽기 전용 접근자.
// (곡선이 이상하게 보일 때 핸들이 어디 붙었는지 콘솔에서 바로 확인하는 용도)
window.__curveHandles = function () {
    const segs = pathCache.segmentTimes || [];
    const rect = canvas.getBoundingClientRect();
    return segs.filter(s => s.handle).map(s => ({
        noteIndex: s.handle.noteIndex,
        clientX: s.handle.pos.x * zoom + viewOffset.x + rect.left,
        clientY: s.handle.pos.y * zoom + viewOffset.y + rect.top
    }));
};

// 특정 핸들이 캔버스 중앙에 오도록 뷰를 이동한다(디버깅/검증용).
window.__centerOnHandle = function (noteIndex) {
    const segs = pathCache.segmentTimes || [];
    const seg = segs.find(s => s.handle && s.handle.noteIndex === noteIndex) || segs.find(s => s.handle);
    if (!seg) return false;
    viewOffset.x = canvas.width / 2 - seg.handle.pos.x * zoom;
    viewOffset.y = canvas.height / 2 - seg.handle.pos.y * zoom;
    drawPath();
    return true;
};

// 곡선 핸들 렌더: 시작점→핸들 가이드선 + 핸들 점. 곡률이 과하면 경고색.
function drawCurveHandles(segmentTimes) {
    if (!segmentTimes) return;
    for (const seg of segmentTimes) {
        const h = seg.handle;
        if (!h) continue;
        const sx = h.start.x * zoom + viewOffset.x;
        const sy = h.start.y * zoom + viewOffset.y;
        const hx = h.pos.x * zoom + viewOffset.x;
        const hy = h.pos.y * zoom + viewOffset.y;

        // 화면 밖 핸들은 스킵
        if (!isNoteInViewport(hx, hy) && !isNoteInViewport(sx, sy)) continue;

        const note = notes[h.noteIndex];
        const curv = note?.bezier?.curvature || 0;
        const warn = Math.abs(curv) > CURVATURE_SOFT_LIMIT;
        const isDragging = draggingHandle && draggingHandle.noteIndex === h.noteIndex;
        const color = warn ? "#FF9800" : (isDragging ? "#E91E63" : "#2196F3");

        // 가이드선
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(hx, hy);
        ctx.strokeStyle = warn ? "rgba(255,152,0,0.5)" : "rgba(33,150,243,0.4)";
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.stroke();
        ctx.setLineDash([]);

        // 핸들 점
        ctx.beginPath();
        ctx.arc(hx, hy, isDragging ? 6 : 5, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 1.5;
        ctx.stroke();
    }
}

// pathDirectionNotes 계산 함수 (_sectionOffset 지원)
function calculatePathDirectionNotes(directionNotes, bpm, subdivisions, preDelaySeconds) {
    return directionNotes.map((note, index) => {
        const pathBeat = calculatePathBeat(note, preDelaySeconds, bpm, subdivisions);
        const sectionOffset = note._sectionOffset || 0;
        let finalTime;
        if (note.beat === 0 && note.type === "direction" && sectionOffset === 0) {
            finalTime = 0;
        } else {
            const noteBpm = note.bpm || bpm;
            const noteSubdivisions = note.subdivisions || subdivisions;
            const originalTime = sectionOffset + beatToTime(note.beat, noteBpm, noteSubdivisions);
            finalTime = originalTime + preDelaySeconds;
        }
        return {
            ...note,
            finalTime: finalTime,
            pathBeat: pathBeat
        };
    }).sort((a, b) => a.finalTime - b.finalTime);
}

// 곡선 한 구간을 캔버스에 그릴 때 쓰는 선분 수. 렌더 품질용이라 §2.3 상수와 무관하다.
const CURVE_RENDER_STEPS = 24;

// nodePositions 및 segmentTimes 계산 함수
function calculateNodePositions(pathDirectionNotes, bpm, subdivisions) {
    const nodePositions = [];
    const segmentTimes = [];
    const headings = resolvePathHeadings(pathDirectionNotes);
    let pos = { x: 0, y: 0 };
    nodePositions.push(pos);

    for (let i = 0; i < pathDirectionNotes.length - 1; i++) {
        const a = pathDirectionNotes[i];
        const b = pathDirectionNotes[i + 1];
        const dTime = b.finalTime - a.finalTime;
        let adjustedDTime = dTime;

        let next;
        let curve = null;
        let handle = null;   // 곡선 드래그 핸들용 기하(가능한 구간에서만)
        if (b.type === "node" && b.wait) {
            next = { x: pos.x, y: pos.y };
        } else {
            const movementSpeed = calculateMovementSpeed(a, b, bpm, subdivisions);
            const dist = movementSpeed * adjustedDTime;

            const { direction, angleOffset } = headings[i];
            const solved = solveCurveSegment(pos, direction, angleOffset, dist, a.bezier);
            next = solved.end;

            // 곡선 구간만 렌더/주행용 데이터를 싣는다. 직선은 from/to로 충분하다.
            if (solved.p1) {
                curve = { ...solved, lut: buildArcLengthLUT(solved.p0, solved.p1, solved.p2) };
            }

            // 핸들 기하: 이 세그먼트를 만든 노트가 곡선 편집 가능하고 길이가 있을 때만.
            // u/n/r을 실어 핸들 위치와 역산(화면좌표 → curvature/bias)에 그대로 쓴다.
            const startNote = a._originalIndex !== undefined ? notes[a._originalIndex] : null;
            if (dist > 1e-6 && noteAcceptsCurve(startNote)) {
                const [rawX, rawY] = directionToVectorWithAngle(direction, angleOffset);
                const mag = Math.hypot(rawX, rawY) || 1;
                const u = { x: rawX / mag, y: rawY / mag };
                const n = { x: u.y, y: -u.x };          // utils.js와 동일한 화면-CCW 법선
                const r = solved.r;                     // 호장보존으로 당겨진 실제 스케일(역산 분모)
                const curv = a.bezier?.curvature || 0;
                const bias = a.bezier?.bias || 0;
                // 핸들 위치 = 제어점 p1. 직선(곡률0)이면 p1이 없으니 forward 공식으로 재구성.
                const hp = curve ? curve.p1 : {
                    x: pos.x + u.x * (r * (0.5 + bias)) + n.x * (curv * r),
                    y: pos.y + u.y * (r * (0.5 + bias)) + n.y * (curv * r)
                };
                handle = { noteIndex: a._originalIndex, start: { ...pos }, u, n, r, pos: hp };
            }
        }

        segmentTimes.push({
            start: a.finalTime,
            end: b.finalTime,
            from: { ...pos },
            to: { ...next },
            curve,
            handle
        });
        pos = next;
        nodePositions.push(pos);
    }

    return { nodePositions, segmentTimes };
}

// 경로 세그먼트 그리기 함수
function drawPathSegments(targetCtx, pathDirectionNotes, nodePositions, segmentTimes, realtimeDrawingEnabled, isPlaying, drawTime) {
    if (nodePositions.length === 0) return;

    targetCtx.beginPath();
    targetCtx.moveTo(nodePositions[0].x * zoom + viewOffset.x, nodePositions[0].y * zoom + viewOffset.y);

    for (let i = 0; i < nodePositions.length - 1; i++) {
        const currentPos = nodePositions[i];
        const nextPos = nodePositions[i + 1];
        const segment = segmentTimes[i];

        if (!segment) continue;

        // Node Note의 wait 플래그가 있는 경우 해당 구간의 경로를 그리지 않음
        if (i + 1 < pathDirectionNotes.length && pathDirectionNotes[i + 1].type === "node" && pathDirectionNotes[i + 1].wait) {
            // wait가 설정된 Node Note로 향하는 구간은 경로를 그리지 않음 (정지 상태이므로)
            continue;
        }

        // 곡선 구간은 진행도를 호장 기준으로 따라간다. 직선 보간으로 그리면 곡선을 가로질러 버린다.
        const traceTo = (progress) => {
            if (!segment.curve) {
                const p = progress >= 1 ? nextPos : {
                    x: currentPos.x + (nextPos.x - currentPos.x) * progress,
                    y: currentPos.y + (nextPos.y - currentPos.y) * progress
                };
                targetCtx.lineTo(p.x * zoom + viewOffset.x, p.y * zoom + viewOffset.y);
                return;
            }
            const { p0, p1, p2, lut } = segment.curve;
            const steps = Math.max(2, Math.ceil(CURVE_RENDER_STEPS * progress));
            for (let s = 1; s <= steps; s++) {
                const p = pointAtArcProgress(p0, p1, p2, lut, (s / steps) * progress);
                targetCtx.lineTo(p.x * zoom + viewOffset.x, p.y * zoom + viewOffset.y);
            }
        };

        // 실시간 그리기 로직
        if (realtimeDrawingEnabled && isPlaying && segment.end <= drawTime) {
            traceTo(1);
        } else if (realtimeDrawingEnabled && isPlaying && segment.start <= drawTime && segment.end > drawTime) {
            traceTo((drawTime - segment.start) / (segment.end - segment.start));
        } else if (!realtimeDrawingEnabled || !isPlaying) {
            traceTo(1);
        }
    }
}

/**
 * Signature 구간의 경로를 분홍색으로 덧그린다.
 *
 * 본 경로를 검은색으로 stroke한 **뒤에** 호출한다. 구간에 걸치는 세그먼트만
 * 다시 그리되, 세그먼트가 구간 경계에 반쯤 걸치면 걸친 부분만 그린다 —
 * 그래야 표시가 finalTime 경계와 실제로 일치한다.
 *
 * 좌표 변환·곡선 추적은 drawPathSegments와 같은 규칙을 따른다.
 */
function drawSignatureOverlay(targetCtx, pathDirectionNotes, nodePositions, segmentTimes) {
    if (signatures.length === 0 || nodePositions.length === 0) return;

    // 세그먼트 start/end는 finalTime이다. signature는 에디터 시간(preDelay 전)이므로
    // 비교 전에 preDelay를 더해 finalTime 축으로 맞춘다.
    const preDelaySeconds = getPreDelaySeconds();
    const sigRanges = signatures
        .filter((s) => typeof s.startTime === "number" && typeof s.endTime === "number"
            && s.endTime > s.startTime)
        .map((s) => ({ startTime: s.startTime + preDelaySeconds, endTime: s.endTime + preDelaySeconds }));
    if (sigRanges.length === 0) return;

    const toScreen = (p) => ({ x: p.x * zoom + viewOffset.x, y: p.y * zoom + viewOffset.y });

    targetCtx.save();
    targetCtx.strokeStyle = "#FF4FA3";   // 분홍
    targetCtx.lineWidth = 4;
    targetCtx.lineCap = "round";

    for (let i = 0; i < nodePositions.length - 1; i++) {
        const segment = segmentTimes[i];
        if (!segment) continue;

        // wait 구간은 본 경로에서도 그리지 않으므로 여기서도 건너뛴다.
        if (i + 1 < pathDirectionNotes.length
            && pathDirectionNotes[i + 1].type === "node" && pathDirectionNotes[i + 1].wait) {
            continue;
        }

        const segStart = segment.start;
        const segEnd = segment.end;
        const span = segEnd - segStart;
        if (!(span > 0)) continue;

        // 이 세그먼트와 겹치는 signature 구간마다, 겹치는 비율만 그린다.
        // sigRanges는 이미 finalTime 축으로 변환돼 있다.
        sigRanges.forEach((sig) => {
            const overlapStart = Math.max(segStart, sig.startTime);
            const overlapEnd = Math.min(segEnd, sig.endTime);
            if (!(overlapEnd > overlapStart)) return;

            const t0 = (overlapStart - segStart) / span;
            const t1 = (overlapEnd - segStart) / span;

            targetCtx.beginPath();
            if (!segment.curve) {
                const from = segment.from, to = segment.to;
                const at = (t) => ({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t });
                const a = toScreen(at(t0));
                const b = toScreen(at(t1));
                targetCtx.moveTo(a.x, a.y);
                targetCtx.lineTo(b.x, b.y);
            } else {
                const { p0, p1, p2, lut } = segment.curve;
                const steps = Math.max(2, Math.ceil(CURVE_RENDER_STEPS * (t1 - t0)));
                const first = toScreen(pointAtArcProgress(p0, p1, p2, lut, t0));
                targetCtx.moveTo(first.x, first.y);
                for (let s = 1; s <= steps; s++) {
                    const t = t0 + ((t1 - t0) * s) / steps;
                    const p = toScreen(pointAtArcProgress(p0, p1, p2, lut, t));
                    targetCtx.lineTo(p.x, p.y);
                }
            }
            targetCtx.stroke();
        });
    }

    targetCtx.restore();
}

// 뷰포트 기반 컬링 함수 (줌 레벨에 따라 동적 마진)
function isNoteInViewport(screenX, screenY, zoomLevel = zoom) {
    // 줌 레벨이 낮을수록 마진을 줄여서 더 적극적으로 컬링
    // 줌이 클 때는 여유있게, 작을 때는 엄격하게
    const margin = Math.max(20, Math.min(100, zoomLevel * 3));

    return screenX >= -margin &&
        screenX <= canvas.width + margin &&
        screenY >= -margin &&
        screenY <= canvas.height + margin;
}

// 최적화된 노트 렌더링 함수
function renderNotesOptimized(notes, pathDirectionNotes, nodePositions, bpm, subdivisions, preDelaySeconds, realtimeDrawingEnabled, isPlaying, drawTime, segmentTimes) {
    // 줌 레벨이 너무 작으면 노트 렌더링 스킵 (성능 최적화)
    // 줌 레벨이 1.25 이하일 때는 노트가 너무 작아서 겹쳐 보이므로 렌더링 생략 (기존 5에서 1.25로 변경하여 4배 더 줌아웃 가능)
    const MIN_ZOOM_FOR_NOTES = 1.25;
    if (zoom < MIN_ZOOM_FOR_NOTES) {
        // 줌아웃 시 노트 렌더링 스킵 (경로만 표시)
        return;
    }

    // 렌더링할 노트들을 미리 필터링하고 계산된 데이터 캐싱
    const notesToRender = [];
    const renderOffsets = getOrComputeSectionOffsets();

    for (let index = 0; index < notes.length; index++) {
        const note = notes[index];
        if (!note) continue;
        const sectionOffset = renderOffsets[index] || 0;
        // beat=0이면서 구간 시작(sectionOffset=0)인 경우 초기 direction 노트만 허용
        if (note.beat === 0 && sectionOffset === 0 && !(index === 0 && note.type === "direction")) continue;

        // 노트의 실제 시간 계산
        let noteTime, finalTime;
        if (note.beat === 0 && note.type === "direction" && sectionOffset === 0) {
            noteTime = preDelaySeconds;
            finalTime = 0;
        } else if ((note.type === "tab" || note.type === "longtab") &&
                   note.hasOwnProperty('fadeDirectTime')) {
            // fade 구간의 tab/longtab 노트는 저장된 직접 시간값 사용
            // fadeDirectTime은 이미 finalTime이므로 그대로 사용
            noteTime = note.fadeDirectTime;
            finalTime = note.fadeDirectTime;
        } else {
            const noteBpm = note.bpm || bpm;
            const noteSubdivisions = note.subdivisions || subdivisions;
            const originalTime = sectionOffset + beatToTime(note.beat, noteBpm, noteSubdivisions);
            noteTime = originalTime + preDelaySeconds;
            finalTime = originalTime + preDelaySeconds;
        }

        // 실시간 그리기: 그리기 오브젝트가 지나간 노트만 표시
        if (realtimeDrawingEnabled && isPlaying && noteTime > drawTime) {
            continue;
        }

        const pos = getNotePositionFromPathData(finalTime, pathDirectionNotes, nodePositions, segmentTimes);
        if (!pos) continue;

        const screenX = pos.x * zoom + viewOffset.x;
        const screenY = pos.y * zoom + viewOffset.y;

        // 뷰포트 컬링
        if (!isNoteInViewport(screenX, screenY)) continue;

        const pathBeat = timeToBeat(finalTime, bpm, subdivisions);

        notesToRender.push({
            note,
            index,
            screenX,
            screenY,
            pathBeat,
            finalTime
        });
    }

    // 배치된 렌더링으로 성능 최적화
    renderNotesBatched(notesToRender, pathDirectionNotes, nodePositions, bpm, subdivisions, segmentTimes);
}

// 배치된 노트 렌더링 함수 (노트 타입별로 그룹화하여 렌더링)
// segmentTimes는 롱노트 바가 곡선을 따르도록 renderLongNotes까지 전달한다.
function renderNotesBatched(notesToRender, pathDirectionNotes, nodePositions, bpm, subdivisions, segmentTimes) {
    // OffscreenCanvas 사용 시도
    let targetCtx = ctx;
    let useOffscreen = false;

    if (offscreenSupported && notesToRender.length > 100) {
        // 노트가 많을 때만 OffscreenCanvas 사용
        try {
            if (!notesOffscreenCanvas || notesOffscreenCanvas.width !== canvas.width || notesOffscreenCanvas.height !== canvas.height) {
                notesOffscreenCanvas = new OffscreenCanvas(canvas.width, canvas.height);
                notesOffscreenCtx = notesOffscreenCanvas.getContext('2d');
            }
            notesOffscreenCtx.clearRect(0, 0, notesOffscreenCanvas.width, notesOffscreenCanvas.height);
            targetCtx = notesOffscreenCtx;
            useOffscreen = true;
        } catch (e) {
            // Offscreen 실패 시 폴백
            console.warn('OffscreenCanvas fallback:', e);
            targetCtx = ctx;
            useOffscreen = false;
        }
    }

    // 노트 타입별로 그룹화
    const notesByType = {
        tab: [],
        direction: [],
        both: [],
        node: [],
        longtab: [],
        longdirection: [],
        longboth: []
    };

    // 노트들을 타입별로 분류
    notesToRender.forEach(noteData => {
        const type = noteData.note.type;
        if (notesByType[type]) {
            notesByType[type].push(noteData);
        }
    });

    // 타입별로 배치 렌더링 (롱노트를 먼저 렌더링하여 1회성 노트가 위에 표시되도록 함)
    // 롱노트는 복잡한 구조로 인해 ctx를 직접 사용 (추후 개선 가능)
    const originalCtx = ctx;
    if (useOffscreen && targetCtx !== ctx) {
        // OffscreenCanvas를 사용 중이면 임시로 전역 ctx를 교체
        window.tempCtx = ctx;
        window.ctx = targetCtx;
    }

    renderLongNotes(notesByType.longtab, notesByType.longdirection, notesByType.longboth, pathDirectionNotes, nodePositions, bpm, subdivisions, ctx, segmentTimes);
    renderTabNotes(notesByType.tab, targetCtx);
    renderDirectionNotes(notesByType.direction, targetCtx);
    renderBothNotes(notesByType.both, targetCtx);
    renderNodeNotes(notesByType.node, bpm, targetCtx);

    if (useOffscreen && window.tempCtx) {
        // 전역 ctx 복원
        window.ctx = window.tempCtx;
        delete window.tempCtx;
    }

    // OffscreenCanvas를 사용한 경우 메인 캔버스로 전송
    if (useOffscreen && notesOffscreenCanvas) {
        ctx.drawImage(notesOffscreenCanvas, 0, 0);
    }
}

// 탭 노트 배치 렌더링 (배치 최적화)
function renderTabNotes(tabNotes, targetCtx = ctx) {
    if (tabNotes.length === 0) return;

    // 성능 최적화: 노트를 색상별로 그룹화하여 배치 렌더링
    const redNotes = [];
    const normalNotes = [];

    tabNotes.forEach(({ note, screenX, screenY }) => {
        if (note.beat === 0 && note.type === "direction") {
            redNotes.push({ screenX, screenY });
        } else {
            normalNotes.push({ screenX, screenY });
        }
    });

    // 빨간 노트들을 한 번에 렌더링
    if (redNotes.length > 0) {
        targetCtx.fillStyle = "red";
        targetCtx.beginPath();
        redNotes.forEach(({ screenX, screenY }) => {
            targetCtx.moveTo(screenX + 5, screenY);
            targetCtx.arc(screenX, screenY, 5, 0, 2 * Math.PI);
        });
        targetCtx.fill();
    }

    // 일반 노트들을 한 번에 렌더링
    if (normalNotes.length > 0) {
        targetCtx.fillStyle = "#FF6B6B";
        targetCtx.strokeStyle = "#4CAF50";
        targetCtx.lineWidth = 2;

        // Fill 패스
        targetCtx.beginPath();
        normalNotes.forEach(({ screenX, screenY }) => {
            targetCtx.moveTo(screenX + 5, screenY);
            targetCtx.arc(screenX, screenY, 5, 0, 2 * Math.PI);
        });
        targetCtx.fill();

        // Stroke 패스
        targetCtx.beginPath();
        normalNotes.forEach(({ screenX, screenY }) => {
            targetCtx.moveTo(screenX + 5, screenY);
            targetCtx.arc(screenX, screenY, 5, 0, 2 * Math.PI);
        });
        targetCtx.stroke();
    }
}

// 방향 노트 배치 렌더링
function renderDirectionNotes(directionNotes, targetCtx = ctx) {
    if (directionNotes.length === 0) return;

    targetCtx.lineWidth = 2;

    directionNotes.forEach(({ note, screenX, screenY }) => {
        const [dx, dy] = directionToVector(note.direction);
        const mag = Math.hypot(dx, dy) || 1;
        const ux = (dx / mag) * 16;
        const uy = (dy / mag) * 16;
        const endX = screenX + ux;
        const endY = screenY + uy;

        // 화살표 선 그리기
        targetCtx.beginPath();
        targetCtx.moveTo(screenX, screenY);
        targetCtx.lineTo(endX, endY);

        if (note.beat === 0) {
            targetCtx.strokeStyle = "#f00";
            targetCtx.fillStyle = "#f00";
        } else {
            targetCtx.strokeStyle = "#4CAF50";
            targetCtx.fillStyle = "#4CAF50";
        }
        targetCtx.stroke();

        // 화살표 머리 그리기
        const perpX = -uy * 0.5;
        const perpY = ux * 0.5;
        targetCtx.beginPath();
        targetCtx.moveTo(endX, endY);
        targetCtx.lineTo(endX - ux * 0.4 + perpX, endY - uy * 0.4 + perpY);
        targetCtx.lineTo(endX - ux * 0.4 - perpX, endY - uy * 0.4 - perpY);
        targetCtx.closePath();
        targetCtx.fill();
    });
}

// both 노트 배치 렌더링
function renderBothNotes(bothNotes, targetCtx = ctx) {
    if (bothNotes.length === 0) return;

    targetCtx.fillStyle = "#9C27B0";
    targetCtx.strokeStyle = "#4A148C";
    targetCtx.lineWidth = 2;

    bothNotes.forEach(({ note, screenX, screenY }) => {
        // 원 그리기
        targetCtx.beginPath();
        targetCtx.arc(screenX, screenY, 5, 0, 2 * Math.PI);
        targetCtx.fill();
        targetCtx.stroke();

        // 방향 화살표 그리기
        const [dx, dy] = directionToVector(note.direction);
        const mag = Math.hypot(dx, dy) || 1;
        const ux = (dx / mag) * 16;
        const uy = (dy / mag) * 16;
        const endX = screenX + ux;
        const endY = screenY + uy;

        targetCtx.beginPath();
        targetCtx.moveTo(screenX, screenY);
        targetCtx.lineTo(endX, endY);
        targetCtx.strokeStyle = "#9C27B0";
        targetCtx.stroke();

        const perpX = -uy * 0.5;
        const perpY = ux * 0.5;
        targetCtx.beginPath();
        targetCtx.moveTo(endX, endY);
        targetCtx.lineTo(endX - ux * 0.4 + perpX, endY - uy * 0.4 + perpY);
        targetCtx.lineTo(endX - ux * 0.4 - perpX, endY - uy * 0.4 - perpY);
        targetCtx.closePath();
        targetCtx.fillStyle = "#9C27B0";
        targetCtx.fill();
    });
}

// 노드 노트 배치 렌더링
function renderNodeNotes(nodeNotes, bpm, targetCtx = ctx) {
    if (nodeNotes.length === 0) return;

    targetCtx.fillStyle = "#607D8B";
    targetCtx.strokeStyle = "#263238";
    targetCtx.lineWidth = 2;

    nodeNotes.forEach(({ note, screenX, screenY }) => {
        const nodeDisplayY = screenY - 30;

        targetCtx.fillStyle = "#607D8B";
        targetCtx.strokeStyle = "#263238";
        targetCtx.lineWidth = 2;
        targetCtx.beginPath();
        targetCtx.arc(screenX, nodeDisplayY, 6, 0, 2 * Math.PI);
        targetCtx.fill();
        targetCtx.stroke();

        // BPM 텍스트 표시
        targetCtx.fillStyle = "white";
        targetCtx.font = "bold 8px Arial";
        targetCtx.textAlign = "center";
        targetCtx.textBaseline = "middle";
        const noteBpm = note.bpm || bpm;
        targetCtx.fillText(noteBpm.toString(), screenX, nodeDisplayY);

        // 연결선 그리기
        targetCtx.beginPath();
        targetCtx.moveTo(screenX, nodeDisplayY + 6);
        targetCtx.lineTo(screenX, screenY - 3);
        targetCtx.strokeStyle = "rgba(96, 125, 139, 0.5)";
        targetCtx.lineWidth = 1;
        targetCtx.stroke();

        // 명시적 direction이 설정된 경우 화살표 표시
        if (note.direction && note.direction !== "none") {
            const [dx, dy] = directionToVector(note.direction);
            const mag = Math.hypot(dx, dy) || 1;
            const ux = (dx / mag) * 14;
            const uy = (dy / mag) * 14;
            const endX = screenX + ux;
            const endY = nodeDisplayY + uy;

            targetCtx.strokeStyle = "#607D8B";
            targetCtx.fillStyle = "#607D8B";
            targetCtx.lineWidth = 2;
            targetCtx.beginPath();
            targetCtx.moveTo(screenX, nodeDisplayY);
            targetCtx.lineTo(endX, endY);
            targetCtx.stroke();

            const perpX = -uy * 0.5;
            const perpY = ux * 0.5;
            targetCtx.beginPath();
            targetCtx.moveTo(endX, endY);
            targetCtx.lineTo(endX - ux * 0.4 + perpX, endY - uy * 0.4 + perpY);
            targetCtx.lineTo(endX - ux * 0.4 - perpX, endY - uy * 0.4 - perpY);
            targetCtx.closePath();
            targetCtx.fill();
        }
    });
}

// 롱노트 배치 렌더링
function renderLongNotes(longTabNotes, longDirectionNotes, longBothNotes, pathDirectionNotes, nodePositions, bpm, subdivisions, targetCtx = ctx, segmentTimes) {
    // 롱노트는 개별적으로 처리해야 함 (각각 다른 길이를 가지므로)
    [...longTabNotes, ...longDirectionNotes, ...longBothNotes].forEach(({ note, screenX, screenY, pathBeat, finalTime }) => {
        if (note.longTime <= 0) return;

        // 노트 타입별 고정 색상 사용
        const color = getNoteColor(note.type);
        // 끝점도 곡선을 반영하도록 곡선 인지 샘플러를 주입한다(바와 동일 경로).
        const endPositionFn = (endPathBeat) =>
            pathPointAtBeat(endPathBeat, pathDirectionNotes, nodePositions, segmentTimes, subdivisions);
        const endPos = processLongNote(note, pathBeat, pathDirectionNotes, nodePositions, color, bpm, subdivisions, drawLongNoteBar, segmentTimes, endPositionFn);

        // 롱노트 시작점 렌더링
        if (note.type === "longtab") {
            renderTabNotes([{ note, screenX, screenY }]);
        } else if (note.type === "longdirection") {
            renderDirectionNotes([{ note, screenX, screenY }]);
        } else if (note.type === "longboth") {
            renderBothNotes([{ note, screenX, screenY }]);
        }

        // 롱노트 끝점 표시
        if (endPos) {
            const endScreenX = endPos.x * zoom + viewOffset.x;
            const endScreenY = endPos.y * zoom + viewOffset.y;
            if (isNoteInViewport(endScreenX, endScreenY)) {
                ctx.beginPath();
                ctx.arc(endScreenX, endScreenY, 4, 0, 2 * Math.PI);
                ctx.fillStyle = color;
                ctx.fill();
                ctx.strokeStyle = "white";
                ctx.lineWidth = 1;
                ctx.stroke();
            }
        }
    });
}

// 히스토리 스냅샷용 노트 복사.
// 계약의 cloneNote를 쓴다 — 손으로 짠 필드 목록이 angleOffset·bezier를 빠뜨려
// undo 한 번에 곡선이 영구 소실됐다(감사 B-1).
// fadeDirectTime은 계약 필드가 아니지만 존재 여부가 fade 구간 판정을 좌우하므로
// (hasOwnProperty 검사) 스냅샷에서도 존재 여부를 그대로 보존해야 왕복이 무손실이다.
function snapshotNote(note) {
    const copy = cloneNote(note);
    if (note.hasOwnProperty('fadeDirectTime')) {
        copy.fadeDirectTime = note.fadeDirectTime;
    }
    return copy;
}

// 히스토리 시스템 (Undo/Redo)
let undoStack = [];
let redoStack = [];
const MAX_HISTORY_SIZE = 50;
let isPerformingUndoRedo = false;

// 기존 구조와의 호환성을 위한 스택 초기화
function initializeUndoRedo() {
    undoStack = [];
    redoStack = [];
    updateUndoRedoButtons();
}

// 현재 상태를 히스토리에 저장
function saveState() {
    if (isPerformingUndoRedo) return; // Undo/Redo 중에는 히스토리 저장 안함

    // 현재 notes 배열의 깊은 복사본을 생성
    const currentNotesState = notes.map(snapshotNote);

    // 현재 events 배열의 깊은 복사본을 생성
    const currentEventsState = getAllEvents().map(event => {
        const eventCopy = {
            eventType: event.eventType,
            eventId: event.eventId,
            eventTime: event.eventTime,
            eventParams: event.eventParams.map(param => ({
                paramName: param.paramName,
                paramValue: param.paramValue
            }))
        };

        // dialogItems도 복사 (System-dialog 이벤트용)
        if (event.dialogItems && Array.isArray(event.dialogItems)) {
            eventCopy.dialogItems = event.dialogItems.map(item => ({
                ...item  // 모든 필드 복사 (type, text, speaker, character, emotion, animation 등)
            }));
        }

        return eventCopy;
    });

    // 노트와 이벤트를 함께 저장
    const currentState = {
        notes: currentNotesState,
        events: currentEventsState
    };

    undoStack.push(currentState);

    // 스택 크기 제한
    if (undoStack.length > MAX_HISTORY_SIZE) {
        undoStack.shift();
    }

    // 새로운 상태가 저장되면 redo 스택 초기화
    redoStack = [];

    // 버튼 상태 업데이트
    updateUndoRedoButtons();
}

// 실행 취소 (Ctrl+Z)
function undo() {
    if (undoStack.length === 0) return false;

    isPerformingUndoRedo = true;

    // 현재 상태를 redo 스택에 저장
    const currentNotesState = notes.map(snapshotNote);

    const currentEventsState = getAllEvents().map(event => {
        const eventCopy = {
            eventType: event.eventType,
            eventId: event.eventId,
            eventTime: event.eventTime,
            eventParams: event.eventParams.map(param => ({
                paramName: param.paramName,
                paramValue: param.paramValue
            }))
        };

        // dialogItems도 복사 (System-dialog 이벤트용)
        if (event.dialogItems && Array.isArray(event.dialogItems)) {
            eventCopy.dialogItems = event.dialogItems.map(item => ({
                ...item
            }));
        }

        return eventCopy;
    });

    redoStack.push({
        notes: currentNotesState,
        events: currentEventsState
    });

    // 이전 상태 복원
    const previousState = undoStack.pop();

    // 배열이 통째로 바뀌므로 인덱스 기반 선택은 무효다(감사 B-2).
    resetSelectionState();

    // 노트 복원
    notes.length = 0;
    notes.push(...previousState.notes);

    // 캐시 무효화
    invalidatePathCache();

    // 이벤트 복원
    clearAllEvents();
    loadEventsFromJson(previousState.events);

    // UI 업데이트
    renderNoteList();
    renderEventList();
    drawPath();
    updateUndoRedoButtons();

    isPerformingUndoRedo = false;
    return true;
}

// 다시 실행 (Ctrl+Y)
function redo() {
    if (redoStack.length === 0) return false;

    isPerformingUndoRedo = true;

    // 현재 상태를 undo 스택에 저장
    const currentNotesState = notes.map(snapshotNote);

    const currentEventsState = getAllEvents().map(event => {
        const eventCopy = {
            eventType: event.eventType,
            eventId: event.eventId,
            eventTime: event.eventTime,
            eventParams: event.eventParams.map(param => ({
                paramName: param.paramName,
                paramValue: param.paramValue
            }))
        };

        // dialogItems도 복사 (System-dialog 이벤트용)
        if (event.dialogItems && Array.isArray(event.dialogItems)) {
            eventCopy.dialogItems = event.dialogItems.map(item => ({
                ...item
            }));
        }

        return eventCopy;
    });

    undoStack.push({
        notes: currentNotesState,
        events: currentEventsState
    });

    // 다음 상태 복원
    const nextState = redoStack.pop();

    // 배열이 통째로 바뀌므로 인덱스 기반 선택은 무효다(감사 B-2).
    resetSelectionState();

    // 노트 복원
    notes.length = 0;
    notes.push(...nextState.notes);

    // 캐시 무효화
    invalidatePathCache();

    // 이벤트 복원
    clearAllEvents();
    loadEventsFromJson(nextState.events);

    // UI 업데이트
    renderNoteList();
    renderEventList();
    drawPath();
    updateUndoRedoButtons();

    isPerformingUndoRedo = false;
    return true;
}

// Undo/Redo 버튼 상태 업데이트
function updateUndoRedoButtons() {
    const undoBtn = document.getElementById("undo-btn");
    const redoBtn = document.getElementById("redo-btn");

    if (undoBtn) {
        undoBtn.disabled = undoStack.length === 0;
    }

    if (redoBtn) {
        redoBtn.disabled = redoStack.length === 0;
    }
}

let audioBuffer = null;
let pathHighlightTimer = 0;
let hasAudioFile = false; // 오디오 파일 로드 상태 추가

let tabSoundPool = []; // Tab 사운드 풀
let directionSoundPool = []; // Direction 사운드 풀
let playedNotes = new Set(); // 이미 재생된 노트들을 추적
let executedEvents = new Set(); // 이미 실행된 이벤트들을 추적
const SOUND_POOL_SIZE = 10; // 동시 재생 가능한 사운드 수
const MUSIC_START_TIME = 3.0;

// DOM 요소
const inputAudio = document.getElementById("audio-file");
const btnDemoPlay = document.getElementById("demo-play");
const btnDemoPause = document.getElementById("demo-pause");
const btnDemoStop = document.getElementById("demo-stop");
const spanDemoTime = document.getElementById("demo-time");
const seekbar = document.getElementById("demo-seekbar");
// Canvas layers
const canvas = document.getElementById("canvas");
const ctx = canvas.getContext("2d");
const backgroundCanvas = document.getElementById("background-canvas");
const backgroundCtx = backgroundCanvas.getContext("2d");

const rulerCanvas = document.getElementById("ruler-canvas");
const rulerCtx = rulerCanvas ? rulerCanvas.getContext("2d") : null;

// OffscreenCanvas support detection and fallback
let offscreenSupported = false;
let notesOffscreenCanvas = null;
let notesOffscreenCtx = null;

try {
    if (typeof OffscreenCanvas !== 'undefined') {
        offscreenSupported = true;
        console.log('OffscreenCanvas is supported');
    } else {
        console.log('OffscreenCanvas not supported, using fallback');
    }
} catch (e) {
    console.log('OffscreenCanvas detection failed, using fallback');
}



// 캔버스 관련 함수들
let lastCanvasWidth = 0;
let lastCanvasHeight = 0;

function resizeCanvas() {
    const currentWidth = canvas.clientWidth;
    const currentHeight = canvas.clientHeight;

    // 크기가 변경되었을 때만 실제로 리사이즈 (성능 최적화)
    if (canvas.width !== currentWidth || canvas.height !== currentHeight) {
        canvas.width = currentWidth;
        canvas.height = currentHeight;
        backgroundCanvas.width = currentWidth;
        backgroundCanvas.height = currentHeight;
        lastCanvasWidth = currentWidth;
        lastCanvasHeight = currentHeight;

        // OffscreenCanvas 크기도 조정
        if (offscreenSupported && notesOffscreenCanvas) {
            notesOffscreenCanvas.width = currentWidth;
            notesOffscreenCanvas.height = currentHeight;
        }

        return true; // 리사이즈 발생
    }
    return false; // 리사이즈 없음
}

function drawGrid(targetCtx, targetCanvas) {
    // 줌이 너무 작으면 그리드 생략 (성능 최적화)
    const MIN_ZOOM_FOR_GRID = 0.5;
    if (zoom < MIN_ZOOM_FOR_GRID) return;

    const gridSize = 8;
    const startX = Math.floor(-viewOffset.x / zoom / gridSize) - 1;
    const endX = Math.ceil((targetCanvas.width - viewOffset.x) / zoom / gridSize) + 1;
    const startY = Math.floor(-viewOffset.y / zoom / gridSize) - 1;
    const endY = Math.ceil((targetCanvas.height - viewOffset.y) / zoom / gridSize) + 1;

    // 성능 최적화: 모든 그리드 선을 한 번에 그리기
    targetCtx.strokeStyle = "rgba(150, 150, 150, 0.2)";
    targetCtx.lineWidth = 1;
    targetCtx.beginPath();

    // 수직선들 한 번에 그리기
    for (let i = startX; i <= endX; i++) {
        const x = i * gridSize * zoom + viewOffset.x;
        targetCtx.moveTo(x, 0);
        targetCtx.lineTo(x, targetCanvas.height);
    }

    // 수평선들 한 번에 그리기
    for (let j = startY; j <= endY; j++) {
        const y = j * gridSize * zoom + viewOffset.y;
        targetCtx.moveTo(0, y);
        targetCtx.lineTo(targetCanvas.width, y);
    }

    targetCtx.stroke();
}

// 배경 레이어 렌더링 (그리드, 경로, 마커)
function drawBackground(pathDirectionNotes, nodePositions, segmentTimes, bpm, subdivisions, realtimeDrawingEnabled, isPlaying, drawTime) {
    // 배경 캔버스 클리어
    backgroundCtx.clearRect(0, 0, backgroundCanvas.width, backgroundCanvas.height);

    // 그리드 그리기
    drawGrid(backgroundCtx, backgroundCanvas);

    // 경로 그리기
    drawPathSegments(backgroundCtx, pathDirectionNotes, nodePositions, segmentTimes, realtimeDrawingEnabled, isPlaying, drawTime);

    // 그려진 경로 표시
    backgroundCtx.strokeStyle = "#000";
    backgroundCtx.lineWidth = 2;
    backgroundCtx.stroke();

    // signature 구간은 그 위에 분홍색으로 덧그린다.
    drawSignatureOverlay(backgroundCtx, pathDirectionNotes, nodePositions, segmentTimes);

    // 시간 기준 마커 (1초 간격)
    const MIN_ZOOM_FOR_TIME_MARKERS = 2.5;
    if (zoom >= MIN_ZOOM_FOR_TIME_MARKERS) {
        const totalPathTime = pathDirectionNotes[pathDirectionNotes.length - 1]?.finalTime || 0;
        const maxMarkerTime = (realtimeDrawingEnabled && isPlaying) ? Math.min(totalPathTime, drawTime) : totalPathTime;

        const timeMarkers = [];
        for (let time = 1; time < maxMarkerTime; time += 1) {
            const position = getPositionAtTime(time, segmentTimes);
            if (position) {
                const screenX = position.x * zoom + viewOffset.x;
                const screenY = position.y * zoom + viewOffset.y;
                if (isNoteInViewport(screenX, screenY)) {
                    timeMarkers.push({ screenX, screenY });
                }
            }
        }

        if (timeMarkers.length > 0) {
            backgroundCtx.fillStyle = "rgba(128,128,128,0.4)";
            backgroundCtx.beginPath();
            timeMarkers.forEach(({ screenX, screenY }) => {
                backgroundCtx.moveTo(screenX + 4, screenY);
                backgroundCtx.arc(screenX, screenY, 4, 0, 2 * Math.PI);
            });
            backgroundCtx.fill();
        }
    }

    // BPM 기반 비트 마커
    const MIN_ZOOM_FOR_BEAT_MARKERS = 3.75;
    if (zoom >= MIN_ZOOM_FOR_BEAT_MARKERS) {
        for (let i = 0; i < pathDirectionNotes.length - 1; i++) {
            const a = pathDirectionNotes[i];
            const b = pathDirectionNotes[i + 1];
            const segmentBpm = a.bpm || bpm;
            const segmentSubdivisions = a.subdivisions || subdivisions;
            const beatInterval = beatToTime(1, segmentBpm, segmentSubdivisions);
            const maxMarkerTime = (realtimeDrawingEnabled && isPlaying) ? drawTime : (pathDirectionNotes[pathDirectionNotes.length - 1]?.finalTime || 0);

            const beatMarkers = [];
            for (let time = a.finalTime + beatInterval; time < b.finalTime && time <= maxMarkerTime; time += beatInterval) {
                const position = getPositionAtTime(time, segmentTimes);
                if (position) {
                    const screenX = position.x * zoom + viewOffset.x;
                    const screenY = position.y * zoom + viewOffset.y;
                    if (isNoteInViewport(screenX, screenY)) {
                        beatMarkers.push({ screenX, screenY });
                    }
                }
            }

            if (beatMarkers.length > 0) {
                backgroundCtx.fillStyle = "rgba(100,150,255,0.6)";
                backgroundCtx.beginPath();
                beatMarkers.forEach(({ screenX, screenY }) => {
                    backgroundCtx.moveTo(screenX + 2, screenY);
                    backgroundCtx.arc(screenX, screenY, 2, 0, 2 * Math.PI);
                });
                backgroundCtx.fill();
            }
        }
    }
}

/**
 * 게임 카메라 영역을 흰 사각형으로 그린다.
 *
 * 목적은 **기획자가 연출을 짐작할 수 있는 수준**이지 정밀 재현이 아니다.
 * 계산은 전부 `camera-model.js`(순수 모듈)가 하고, 여기서는 그리기만 한다.
 *
 * 🔴 이름 충돌: 전역 `zoom`/`viewOffset`은 **에디터 뷰**이고 게임 카메라와 무관하다.
 *    카메라 사각형은 월드 좌표로 계산해 기존 변환에 그대로 태운다
 *    (에디터 월드 단위 = Unity 월드 단위이므로 별도 변환이 필요 없다).
 */
function drawCameraBounds(pathDirectionNotes, nodePositions, segmentTimes, bpm, subdivisions) {
    if (!isCameraBoundsVisible) return;
    if (!pathDirectionNotes || pathDirectionNotes.length === 0) return;

    // 경로상 시각 → 위치. 클라 `StageUtils.GetPosition`과 같이 양끝을 clamp한다
    // (끝을 넘어가면 마지막 노드 고정, 외삽 없음 — 곡 끝의 감속이 여기서 나온다).
    const pathEndTime = pathDirectionNotes[pathDirectionNotes.length - 1]?.finalTime || 0;
    const rawPositionAt = (t) =>
        calculateExactPositionOnPath(t, pathDirectionNotes, bpm, subdivisions) || { x: 0, y: 0 };
    const positionAt = clampedPositionAt(rawPositionAt, 0, pathEndTime);

    // 재생 중이 아니면 t=0 기준으로 보여준다(정지 상태에서도 화각을 가늠할 수 있게).
    const time = isPlaying ? elapsedTime : 0;

    // 댐핑 연속성 — 재생 중에만 이전 프레임을 이어받는다.
    const now = performance.now();
    const dt = isPlaying && cameraBoundsPrev.time !== null
        ? Math.min(0.1, Math.max(0, (now - cameraBoundsPrev.time) / 1000))
        : 0;

    let cam;
    try {
        cam = cameraAt(getAllEvents(), time, {
            speedMultiplier,
            positionAt,
            prev: isPlaying ? cameraBoundsPrev.cam : null,
            dt
        });
    } catch (err) {
        // 카메라 표시는 보조 기능이다 — 실패해도 에디터 본체를 막지 않는다.
        console.warn('카메라 영역 계산 실패:', err);
        return;
    }

    cameraBoundsPrev.cam = { drawn: cam.center };
    cameraBoundsPrev.time = now;

    const corners = cameraCorners(cam).map((p) => ({
        x: p.x * zoom + viewOffset.x,
        y: p.y * zoom + viewOffset.y
    }));

    ctx.save();

    // 위치 고정(isEvent) 상태는 테두리를 구분해 모드가 한눈에 보이게 한다.
    ctx.strokeStyle = cam.isEvent ? "rgba(255, 235, 130, 0.95)" : "rgba(255, 255, 255, 0.95)";
    ctx.lineWidth = 2;
    ctx.setLineDash(cam.isEvent ? [10, 5] : []);

    ctx.beginPath();
    corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.stroke();

    // 화면 중심 십자 — 회전·고정 상태를 읽기 쉽게 하는 최소한의 보조선.
    const cx = cam.center.x * zoom + viewOffset.x;
    const cy = cam.center.y * zoom + viewOffset.y;
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
    ctx.beginPath();
    ctx.moveTo(cx - 6, cy);
    ctx.lineTo(cx + 6, cy);
    ctx.moveTo(cx, cy - 6);
    ctx.lineTo(cx, cy + 6);
    ctx.stroke();

    // 상단 모서리에 현재 카메라 값 표기(줌/회전이 눈금 없이도 읽히도록).
    const label = `ortho ${cam.ortho.toFixed(1)}` +
        (cam.rotation !== 0 ? ` · ${cam.rotation.toFixed(1)}°` : '') +
        (cam.isEvent ? ' · 고정' : '');
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.font = "12px Arial";
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText(label, corners[0].x + 4, corners[0].y - 4);

    ctx.restore();
}

function drawPath() {
    const startTime = performance.now();

    resizeCanvas();

    ensureInitialDirectionNote(notes);

    const preDelaySeconds = getPreDelaySeconds();
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    // 실시간 그리기를 위한 변수들
    const realtimeDrawingEnabled = document.getElementById("realtime-drawing").checked;
    const currentTime = isPlaying ? elapsedTime : 0;
    const drawAheadTime = parseFloat(document.getElementById("draw-ahead-time").value || 2.0);
    const drawTime = currentTime + drawAheadTime;

    // 캐시 검증 및 갱신
    const currentNotesHash = generateNotesHash(notes, bpm, subdivisions, preDelaySeconds);
    const needsRecalculation =
        pathCache.lastNotesHash !== currentNotesHash ||
        pathCache.lastBpm !== bpm ||
        pathCache.lastSubdivisions !== subdivisions ||
        pathCache.lastPreDelaySeconds !== preDelaySeconds ||
        pathCache.lastSpeedMultiplier !== speedMultiplier;

    let pathDirectionNotes, nodePositions, segmentTimes;

    if (needsRecalculation) {
        // 캐시 미스 - 새로 계산 (_sectionOffset 첨부된 direction 노트 사용)
        const directionNotes = getDirectionNotesWithOffsets(bpm, subdivisions);

        pathDirectionNotes = calculatePathDirectionNotes(directionNotes, bpm, subdivisions, preDelaySeconds);
        const pathData = calculateNodePositions(pathDirectionNotes, bpm, subdivisions);
        nodePositions = pathData.nodePositions;
        segmentTimes = pathData.segmentTimes;

        // 캐시 업데이트
        pathCache.pathDirectionNotes = pathDirectionNotes;
        pathCache.nodePositions = nodePositions;
        pathCache.segmentTimes = segmentTimes;
        pathCache.lastNotesHash = currentNotesHash;
        pathCache.lastBpm = bpm;
        pathCache.lastSubdivisions = subdivisions;
        pathCache.lastPreDelaySeconds = preDelaySeconds;
        pathCache.lastSpeedMultiplier = speedMultiplier;
    } else {
        // 캐시 히트 - 기존 데이터 사용
        pathDirectionNotes = pathCache.pathDirectionNotes;
        nodePositions = pathCache.nodePositions;
        segmentTimes = pathCache.segmentTimes;
    }

    // 배경 레이어 업데이트 확인 (변경사항이 있을 때만 다시 그리기)
    const needsBackgroundRedraw =
        backgroundCache.lastZoom !== zoom ||
        backgroundCache.lastViewOffsetX !== viewOffset.x ||
        backgroundCache.lastViewOffsetY !== viewOffset.y ||
        backgroundCache.lastNotesHash !== currentNotesHash ||
        backgroundCache.lastBpm !== bpm ||
        backgroundCache.lastSubdivisions !== subdivisions ||
        backgroundCache.lastPreDelaySeconds !== preDelaySeconds ||
        backgroundCache.lastRealtimeDrawingEnabled !== realtimeDrawingEnabled ||
        backgroundCache.lastIsPlaying !== isPlaying ||
        (realtimeDrawingEnabled && isPlaying && Math.abs(backgroundCache.lastDrawTime - drawTime) > 0.1);

    if (needsBackgroundRedraw) {
        drawBackground(pathDirectionNotes, nodePositions, segmentTimes, bpm, subdivisions, realtimeDrawingEnabled, isPlaying, drawTime);

        // 배경 캐시 업데이트
        backgroundCache.lastZoom = zoom;
        backgroundCache.lastViewOffsetX = viewOffset.x;
        backgroundCache.lastViewOffsetY = viewOffset.y;
        backgroundCache.lastNotesHash = currentNotesHash;
        backgroundCache.lastBpm = bpm;
        backgroundCache.lastSubdivisions = subdivisions;
        backgroundCache.lastPreDelaySeconds = preDelaySeconds;
        backgroundCache.lastRealtimeDrawingEnabled = realtimeDrawingEnabled;
        backgroundCache.lastIsPlaying = isPlaying;
        backgroundCache.lastDrawTime = drawTime;
    }

    // 전경 레이어 클리어 (매 프레임)
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 그리기 오브젝트 표시 (투명한 원) - 실시간 그리기 활성화 시에만
    if (realtimeDrawingEnabled && isPlaying && drawTime <= (pathDirectionNotes[pathDirectionNotes.length - 1]?.finalTime || 0)) {
        const drawPosition = getPositionAtTime(drawTime, segmentTimes);
        if (drawPosition) {
            ctx.beginPath();
            ctx.arc(drawPosition.x * zoom + viewOffset.x, drawPosition.y * zoom + viewOffset.y, 8, 0, 2 * Math.PI);
            ctx.fillStyle = "rgba(100, 200, 255, 0.6)";
            ctx.fill();
            ctx.strokeStyle = "rgba(100, 200, 255, 0.8)";
            ctx.lineWidth = 2;
            ctx.stroke();
        }
    }

    // 최적화된 노트 렌더링
    renderNotesOptimized(notes, pathDirectionNotes, nodePositions, bpm, subdivisions, preDelaySeconds, realtimeDrawingEnabled, isPlaying, drawTime, segmentTimes);

    // 게임 카메라 영역(흰 사각형). 노트 위, 플레이어 마커 아래에 그린다.
    drawCameraBounds(pathDirectionNotes, nodePositions, segmentTimes, bpm, subdivisions);

    if (isPlaying && !isNaN(demoPlayer.x) && !isNaN(demoPlayer.y)) {
        const screenX = demoPlayer.x * zoom + viewOffset.x;
        const screenY = demoPlayer.y * zoom + viewOffset.y;

        ctx.save();
        ctx.translate(screenX, screenY);
        ctx.strokeStyle = "blue";
        ctx.fillStyle = "blue";
        ctx.beginPath();
        const spikes = 5;
        const outerRadius = 10;
        const innerRadius = 4;
        for (let i = 0; i < spikes * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (i * Math.PI) / spikes;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.restore();
    }

    if (highlightedNoteIndex !== null && highlightedNoteTimer > 0) {
        const note = notes[highlightedNoteIndex];
        let noteFinalTime;
        const highlightSectionOffset = getOrComputeSectionOffsets()[highlightedNoteIndex] || 0;
        if (note.beat === 0 && note.type === "direction" && highlightSectionOffset === 0) {
            noteFinalTime = 0;
        } else if ((note.type === "tab" || note.type === "longtab") &&
                   note.hasOwnProperty('fadeDirectTime')) {
            noteFinalTime = note.fadeDirectTime;
        } else {
            const noteBpm = note.bpm || parseFloat(document.getElementById("bpm").value || 120);
            const noteSubdivisions = note.subdivisions || parseInt(document.getElementById("subdivisions").value || 16);
            noteFinalTime = highlightSectionOffset + beatToTime(note.beat, noteBpm, noteSubdivisions) + preDelaySeconds;
        }

        const pos = getNotePositionFromPathData(noteFinalTime, pathDirectionNotes, nodePositions, segmentTimes);
        if (pos) {
            const x = pos.x * zoom + viewOffset.x;
            const y = pos.y * zoom + viewOffset.y;

            const alpha = Math.min(1, highlightedNoteTimer * 2);
            const radius = 15 + (0.5 - highlightedNoteTimer) * 30;

            ctx.beginPath();
            ctx.arc(x, y, radius, 0, 2 * Math.PI);
            ctx.strokeStyle = `rgba(255, 200, 0, ${alpha})`;
            ctx.lineWidth = 3;
            ctx.stroke();
        }
    }

    // 선택된 노트 하이라이트 (노란색 원)
    if (selectedNoteIndex !== null && notes[selectedNoteIndex]) {
        const note = notes[selectedNoteIndex];
        let noteFinalTime;
        const selectedSectionOffset = getOrComputeSectionOffsets()[selectedNoteIndex] || 0;
        if (note.beat === 0 && note.type === "direction" && selectedSectionOffset === 0) {
            noteFinalTime = 0;
        } else if ((note.type === "tab" || note.type === "longtab") &&
                   note.hasOwnProperty('fadeDirectTime')) {
            noteFinalTime = note.fadeDirectTime;
        } else {
            const noteBpm = note.bpm || parseFloat(document.getElementById("bpm").value || 120);
            const noteSubdivisions = note.subdivisions || parseInt(document.getElementById("subdivisions").value || 16);
            noteFinalTime = selectedSectionOffset + beatToTime(note.beat, noteBpm, noteSubdivisions) + preDelaySeconds;
        }

        const pos = getNotePositionFromPathData(noteFinalTime, pathDirectionNotes, nodePositions, segmentTimes);
        if (pos) {
            const x = pos.x * zoom + viewOffset.x;
            const y = pos.y * zoom + viewOffset.y;

            ctx.beginPath();
            ctx.arc(x, y, 12, 0, 2 * Math.PI); // 좀 더 큰 원
            ctx.strokeStyle = `rgba(255, 255, 0, 0.8)`; // 노란색
            ctx.lineWidth = 4;
            ctx.stroke();

            // 애니메이션 효과를 위한 추가 원 (선택 사항)
            ctx.beginPath();
            ctx.arc(x, y, 15, 0, 2 * Math.PI);
            ctx.strokeStyle = `rgba(255, 255, 0, 0.4)`; // 더 투명한 노란색
            ctx.lineWidth = 2;
            ctx.stroke();
        }
    }

    // EventList 마커 표시
    // 줌 레벨이 너무 작으면 이벤트 렌더링 스킵 (노트와 동일한 기준 적용)
    const MIN_ZOOM_FOR_EVENTS = 1.0; // 노트보다 조금 더 관대하게 설정 (노트: 1.25, 이벤트: 1.0)
    const events = getAllEvents();

    if (zoom >= MIN_ZOOM_FOR_EVENTS) {
        events.forEach((event, eventIndex) => {
            if (!event || typeof event.eventTime !== 'number') return;

            // 이벤트 시간에 해당하는 위치 계산
            const pos = getNotePositionFromPathData(event.eventTime, pathDirectionNotes, nodePositions, segmentTimes);
            if (!pos) return;

            const screenX = pos.x * zoom + viewOffset.x;
            const screenY = pos.y * zoom + viewOffset.y;

            // 뷰포트 컬링 - 화면 밖의 이벤트는 그리지 않음
            if (!isNoteInViewport(screenX, screenY)) return;

            // 강조된 이벤트인지 확인
            const isHighlighted = highlightedEventIndex === eventIndex && highlightedEventTimer > 0;

            if (isHighlighted) {
                // 강조 효과를 위한 큰 원 그리기
                const alpha = Math.min(1, highlightedEventTimer * 2);
                const radius = 15 + (2.0 - highlightedEventTimer) * 20;

                ctx.beginPath();
                ctx.arc(screenX, screenY, radius, 0, 2 * Math.PI);
                ctx.strokeStyle = `rgba(255, 152, 0, ${alpha * 0.8})`;
                ctx.lineWidth = 3;
                ctx.stroke();

                // 내부 원
                ctx.beginPath();
                ctx.arc(screenX, screenY, radius * 0.6, 0, 2 * Math.PI);
                ctx.fillStyle = `rgba(255, 152, 0, ${alpha * 0.3})`;
                ctx.fill();
            }

            // 삼각형 마커 그리기 (강조된 경우 더 크게)
            const markerSize = isHighlighted ? 15 : 10;
            const fillColor = isHighlighted ? "#FFB300" : "#FF9800";
            const strokeColor = isHighlighted ? "#FF8F00" : "#FF6F00";
            drawTriangle(ctx, screenX, screenY, markerSize, fillColor, strokeColor, 2);

            // 이벤트 ID를 삼각형 아래에 표시
            if (event.eventId) {
                const textColor = isHighlighted ? "#FFF3E0" : "#FFB74D";
                drawText(ctx, event.eventId, screenX, screenY + (isHighlighted ? 20 : 15), "bold 8px Arial", textColor);
            }
        });
    }

    if (pathHighlightTimer > 0) {
        ctx.save();
        ctx.strokeStyle = `rgba(255, 100, 100, ${pathHighlightTimer})`;
        ctx.lineWidth = 4;
        ctx.shadowColor = 'rgba(255, 100, 100, 0.5)';
        ctx.shadowBlur = 10;

        // 하이라이트도 본 경로와 같은 데이터를 쓴다. 자체 계산하면 곡선/각도가 빠져
        // 실제 경로 위에 엉뚱한 선이 겹쳐 그려진다.
        drawPathSegments(ctx, pathDirectionNotes, nodePositions, segmentTimes, false, false, 0);
        ctx.stroke();
        ctx.restore();
    }

    // 곡선 드래그 핸들 (편집 모드에서, 재생 중이 아닐 때만). 전경 최상단에 그린다.
    if (curveHandlesEnabled && !isPlaying) {
        drawCurveHandles(segmentTimes);
    }

    // 성능 측정 로그
    const endTime = performance.now();
    const renderTime = endTime - startTime;
    const cacheStatus = needsRecalculation ? 'MISS' : 'HIT';

    // 성능이 좋지 않을 때만 로그 출력 (10ms 이상)
    if (renderTime > 10) {
        console.log(`[Performance] drawPath: ${renderTime.toFixed(2)}ms, Cache: ${cacheStatus}, Notes: ${notes.length}`);
    }
}


function initializeNoteBpmSubdivisions() {
    const currentBpm = parseFloat(document.getElementById("bpm").value || 120);
    const currentSubdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    let hasChanges = false;
    notes.forEach(note => {
        if (note.bpm === undefined) {
            note.bpm = currentBpm;
            hasChanges = true;
        }
        if (note.subdivisions === undefined) {
            note.subdivisions = currentSubdivisions;
            hasChanges = true;
        }
    });

    if (hasChanges) {
        console.log('Initialized missing BPM/subdivision values for existing notes');
        saveToStorage();
    }
}

function getPositionAtTime(time, segmentTimes) {
    for (let s of segmentTimes) {
        if (s.start <= time && time <= s.end) {
            const interp = (time - s.start) / (s.end - s.start);
            return {
                x: s.from.x + (s.to.x - s.from.x) * interp,
                y: s.from.y + (s.to.y - s.from.y) * interp
            };
        }
    }
    return null;
}

function calculateMovementSpeed(fromNote, toNote, globalBpm, globalSubdivisions) {
    const fromBpm = fromNote.bpm || globalBpm;
    const toBpm = toNote.bpm || globalBpm;

    // Unity TrackGenerator 로직과 동일하게 처리
    // fade가 활성화되고 BPM이 변하면 평균 BPM 사용
    // 그 외에는 toBpm 사용 (기존 방식)
    let effectiveBpm;
    const toFade = toNote.fade || false;

    if (toFade && Math.abs(fromBpm - toBpm) > 0.01) {
        // fade가 활성화되면 평균 BPM 사용 (적분의 근사치)
        effectiveBpm = (fromBpm + toBpm) / 2;
    } else {
        // fade가 없으면 기존 방식 (목표 BPM 사용)
        effectiveBpm = toBpm;
    }

    // Unity 공식 적용: multiplierConstant = 0.4 × 배속
    // 속도 = multiplierConstant × BPM
    const multiplierConstant = 0.4 * speedMultiplier;
    return multiplierConstant * effectiveBpm;
}

function drawLongNoteBar(startPathBeat, endPathBeat, pathDirectionNotes, nodePositions, color, lineWidth, noteSubdivisions, segmentTimes) {
    const subdivisions = noteSubdivisions || parseInt(document.getElementById("subdivisions").value || 16);
    // 곡선 구간을 촘촘히 따라가도록 샘플 수를 넉넉히 잡는다(직선이면 과할 뿐 무해).
    const segments = Math.max(16, Math.floor((endPathBeat - startPathBeat) * 4));

    // 바 위 각 샘플의 경로 좌표를 곡선까지 반영해 미리 구한다(메인/오버레이 두 번 재사용).
    const points = [];
    for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const currentBeat = startPathBeat + (endPathBeat - startPathBeat) * t;
        points.push(pathPointAtBeat(currentBeat, pathDirectionNotes, nodePositions, segmentTimes, subdivisions));
    }

    const tracePoints = () => {
        ctx.beginPath();
        let first = true;
        for (const pos of points) {
            if (!pos) continue;
            const screenX = pos.x * zoom + viewOffset.x;
            const screenY = pos.y * zoom + viewOffset.y;
            if (first) { ctx.moveTo(screenX, screenY); first = false; }
            else ctx.lineTo(screenX, screenY);
        }
        ctx.stroke();
    };

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    tracePoints();

    const overlayColor = color.replace(/rgb\([^)]+\)/, (match) => {
        const values = match.match(/\d+/g);
        return `rgba(${values[0]}, ${values[1]}, ${values[2]}, 0.3)`;
    }).replace(/#([0-9A-Fa-f]{6})/, (match, hex) => {
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return `rgba(${r}, ${g}, ${b}, 0.3)`;
    });

    ctx.strokeStyle = overlayColor;
    ctx.lineWidth = lineWidth + 4;
    tracePoints();
}

// 오디오 관련 함수들
function loadNoteSounds() {
    try {
        tabSoundPool = [];
        for (let i = 0; i < SOUND_POOL_SIZE; i++) {
            const audio = new Audio('/legacy/sfx/tab.wav');
            audio.volume = sfxVolume;
            audio.preload = 'auto';
            tabSoundPool.push(audio);
        }

        directionSoundPool = [];
        for (let i = 0; i < SOUND_POOL_SIZE; i++) {
            const audio = new Audio('/legacy/sfx/tab.wav');
            audio.volume = sfxVolume;
            audio.preload = 'auto';
            directionSoundPool.push(audio);
        }

        console.log(`Note sound pools loaded: ${SOUND_POOL_SIZE} instances each (using sfx/tab.mp3)`);
    } catch (error) {
        console.warn('Failed to load note sounds:', error);
        tabSoundPool = [];
        directionSoundPool = [];
    }
}

function getAvailableSound(soundPool) {
    for (let audio of soundPool) {
        if (audio.paused || audio.ended || audio.currentTime === 0) {
            return audio;
        }
    }

    let oldestAudio = soundPool[0];
    let maxCurrentTime = soundPool[0].currentTime;

    for (let audio of soundPool) {
        if (audio.currentTime > maxCurrentTime) {
            maxCurrentTime = audio.currentTime;
            oldestAudio = audio;
        }
    }

    return oldestAudio;
}

function playNoteSound(noteType) {
    try {
        let audio = null;

        if ((noteType === 'tab' || noteType === 'longtab' || noteType === 'both' || noteType === 'longboth') && tabSoundPool.length > 0) {
            audio = getAvailableSound(tabSoundPool);
        } else if ((noteType === 'direction' || noteType === 'longdirection' || noteType === 'both' || noteType === 'longboth') && directionSoundPool.length > 0) {
            audio = getAvailableSound(directionSoundPool);
        }

        if (audio) {
            audio.currentTime = 0;
            audio.play().catch(e => {
                console.warn(`${noteType} sound play failed:`, e);
            });
        }
    } catch (error) {
        console.warn('Error playing note sound:', error);
    }
}



function processAudioFile(audioFile) {
    console.log('Processing audio file:', audioFile.name);

    hasAudioFile = true;
    savedAudioFile = audioFile;

    saveAudioFile(audioFile).catch(err => {
        console.warn('Failed to save audio to IndexedDB:', err);
    });

    // Audio duration detection
    const audio = new Audio();
    const url = URL.createObjectURL(audioFile);
    audio.src = url;

    audio.addEventListener('loadedmetadata', () => {
        console.log('Audio metadata loaded, duration:', audio.duration);
        audioBuffer = { duration: audio.duration };
        saveToStorage();
        URL.revokeObjectURL(url);
    });

    audio.addEventListener('error', () => {
        console.error('Audio element failed');
        hasAudioFile = false;
        savedAudioFile = null;
        audioBuffer = null;
        URL.revokeObjectURL(url);
    });
}

// 재생 관련 함수들
function startDemo() {
    isPlaying = true;
    isPaused = false;
    elapsedTime = 0;
    startTime = performance.now();

    resetPlayedNotes();
    resetExecutedEvents();

    updateDemo();

    setTimeout(() => {
        if (!isPaused) {
            demoAudio.currentTime = 0;
            demoAudio.play();
        }
    }, MUSIC_START_TIME * 1000);
}

function updateDemo() {
    if (!isPlaying || isPaused)
        return;

    elapsedTime = (performance.now() - startTime) / 1000;
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    // TODO: 변속 기능 - 각 노트의 BPM을 고려한 복합 시간 계산 필요
    // 현재는 전역 BPM으로만 계산하지만, 향후 각 구간별 BPM 변화를 반영해야 함
    const currentBeat = timeToBeat(elapsedTime, bpm, subdivisions);

    checkNoteHits(elapsedTime);
    checkEventExecution(elapsedTime);

    if (!isNaN(demoAudio.duration) && elapsedTime >= demoAudio.duration + MUSIC_START_TIME) {
        stopDemo();
        return;
    }

    if (!isNaN(demoAudio.duration)) {
        const totalTime = demoAudio.duration + getPreDelaySeconds();
        spanDemoTime.textContent = `${formatTime(elapsedTime)} / ${formatTime(totalTime)}`;
        seekbar.max = Math.round(totalTime * 1000);
        seekbar.value = Math.round(elapsedTime * 1000);
    }

    if (elapsedTime >= MUSIC_START_TIME) {
        demoAudio.play();
    }

    updateDemoPlayerPosition(elapsedTime);

    // 재생 헤드 갱신(병목 5). 타임라인 탭이 닫혀 있으면 renderTimelineImmediate가
    // 즉시 빠져나가므로 재생 중 비용이 거의 없다.
    renderTimelineView();

    if (isCameraTracking) {
        const targetX = canvas.width / 2 - demoPlayer.x * zoom;
        const targetY = canvas.height / 2 - demoPlayer.y * zoom;
        viewOffset.x = lerp(viewOffset.x, targetX, 0.1);
        viewOffset.y = lerp(viewOffset.y, targetY, 0.1);
    }

    drawPath();
    
    if (highlightedNoteTimer > 0) {
        highlightedNoteTimer -= 1 / 60;
        if (highlightedNoteTimer <= 0) {
            highlightedNoteTimer = 0;
            highlightedNoteIndex = null;
        }
    }

    animationFrameId = requestAnimationFrame(updateDemo);
}

function checkNoteHits(currentTime) {
    const preDelaySeconds = getPreDelaySeconds();
    const tolerance = 0.05;
    const hitOffsets = getOrComputeSectionOffsets();

    notes.forEach((note, index) => {
        const noteId = `${note.type}-${note.beat}-${index}`;

        if (playedNotes.has(noteId))
            return;

        const sectionOffset = hitOffsets[index] || 0;
        let finalTime;
        if (note.beat === 0 && note.type === "direction" && sectionOffset === 0) {
            finalTime = preDelaySeconds;
            if (currentTime >= finalTime - tolerance && currentTime <= finalTime + tolerance) {
                playedNotes.add(noteId);
                highlightNoteHit(index);
                console.log(`0번 노트 통과 (효과음 없음): beat ${note.beat}, finalTime ${finalTime.toFixed(3)}s, currentTime ${currentTime.toFixed(3)}s`);
            }
            return;
        } else if ((note.type === "tab" || note.type === "longtab") &&
                   note.hasOwnProperty('fadeDirectTime')) {
            // fade 구간의 tab/longtab 노트는 저장된 직접 시간값 사용
            finalTime = note.fadeDirectTime;
        } else {
            // 각 노트의 개별 BPM/subdivision 사용
            const noteBpm = note.bpm || bpm;
            const noteSubdivisions = note.subdivisions || subdivisions;
            const originalTime = sectionOffset + beatToTime(note.beat, noteBpm, noteSubdivisions);
            finalTime = originalTime + preDelaySeconds;
        }

        if (currentTime >= finalTime - tolerance &&
            currentTime <= finalTime + tolerance) {

            playNoteSound(note.type);
            playedNotes.add(noteId);
            highlightNoteHit(index);

            console.log(`Note hit: ${note.type} at beat ${note.beat}, finalTime ${finalTime.toFixed(3)}s, currentTime ${currentTime.toFixed(3)}s`);
        }
    });
}

function highlightNoteHit(noteIndex) {
    highlightedNoteIndex = noteIndex;
    highlightedNoteTimer = 0.3;

    if (!isDrawLoopRunning) {
        isDrawLoopRunning = true;
        drawLoop();
    }
}

function resetPlayedNotes() {
    playedNotes.clear();
    console.log('Played notes reset');
}

function resetExecutedEvents() {
    executedEvents.clear();
    // 카메라 댐핑의 직전 프레임도 함께 버린다. 남겨두면 되감은 뒤 첫 프레임이
    // 엉뚱한 위치에서 보간을 시작한다.
    cameraBoundsPrev.cam = null;
    cameraBoundsPrev.time = null;
    console.log('Executed events reset');
}

function checkEventExecution(currentTime) {
    const events = getAllEvents();
    const tolerance = 0.05; // 50ms 허용 오차

    events.forEach((event, index) => {
        if (!event || typeof event.eventTime !== 'number') return;

        const eventId = `${event.eventType}-${event.eventId}-${event.eventTime}-${index}`;

        // 이미 실행된 이벤트는 건너뛰기
        if (executedEvents.has(eventId)) return;

        // 현재 시간이 이벤트 시간에 도달했는지 확인
        if (currentTime >= event.eventTime - tolerance && currentTime <= event.eventTime + tolerance) {
            executeEvent(event, index);
            executedEvents.add(eventId);
        }
    });
}

function executeEvent(event, index) {
    console.log(`Executing event: ${event.eventType}-${event.eventId} at time ${event.eventTime}s`);

    // 이벤트 실행 시각적 피드백
    highlightEventExecution(index);

    // 실제 이벤트 실행 로직은 여기에 추가
    // 예: 오버레이 효과, 카메라 움직임 등
    // TODO: 실제 이벤트 실행 기능 구현
}

function highlightEventExecution(eventIndex) {
    highlightedEventIndex = eventIndex;
    highlightedEventTimer = 0.5; // 이벤트는 노트보다 조금 더 오래 강조

    if (!isDrawLoopRunning) {
        isDrawLoopRunning = true;
        drawLoop();
    }
}

function resetEventExecutionToTime(targetTime) {
    // 지정된 시간 이후의 모든 이벤트 실행 상태를 리셋
    const events = getAllEvents();
    const eventsToReset = [];

    events.forEach((event, index) => {
        if (event && typeof event.eventTime === 'number' && event.eventTime > targetTime) {
            const eventId = `${event.eventType}-${event.eventId}-${event.eventTime}-${index}`;
            eventsToReset.push(eventId);
        }
    });

    eventsToReset.forEach(eventId => {
        executedEvents.delete(eventId);
    });

    // 🔴 카메라는 노트와 달리 *상태*라서 실행 이력만 되돌리면 어긋난다
    // (뒤로 감았는데 줌은 그대로). 카메라 상태 자체는 매 프레임 0부터
    // 재구성하므로(cameraAt), 여기서는 댐핑의 직전 프레임만 버리면 된다.
    cameraBoundsPrev.cam = null;
    cameraBoundsPrev.time = null;

    console.log(`Reset ${eventsToReset.length} events after time ${targetTime}s`);
}

// fade를 고려한 정확한 경로상 위치 계산 함수
function calculateExactPositionOnPath(targetTime, pathDirectionNotes, bpm, subdivisions) {
    if (pathDirectionNotes.length === 0) {
        return { x: 0, y: 0 };
    }

    // 시작 위치
    let currentPos = { x: 0, y: 0 };
    const headings = resolvePathHeadings(pathDirectionNotes);

    // targetTime이 첫 번째 노트 시간보다 이전인 경우 시작점에서 첫 번째 노트로 이동
    if (pathDirectionNotes.length > 1 && targetTime < pathDirectionNotes[1].finalTime) {
        const firstGameNote = pathDirectionNotes[1];
        const movementSpeed = calculateMovementSpeed(pathDirectionNotes[0], firstGameNote, bpm, subdivisions);
        const timeRatio = targetTime / firstGameNote.finalTime;

        // 첫 번째 노트까지의 거리와 방향
        const [dx, dy] = directionToVectorWithAngle(headings[0].direction, headings[0].angleOffset);
        const mag = Math.hypot(dx, dy) || 1;
        const totalDist = movementSpeed * firstGameNote.finalTime;

        // fade가 있는지 확인
        const firstFade = firstGameNote.fade || false;
        const firstBpm = pathDirectionNotes[0].bpm || bpm;
        const firstNoteBpm = firstGameNote.bpm || bpm;

        let t = timeRatio;
        if (firstFade && Math.abs(firstBpm - firstNoteBpm) > 0.01) {
            t = calculateFadeProgress(timeRatio, firstBpm, firstNoteBpm);
        }

        return {
            x: currentPos.x + (dx / mag) * totalDist * t,
            y: currentPos.y + (dy / mag) * totalDist * t
        };
    }

    // 마지막 노트 시간 후면 마지막 노트까지의 경로를 모두 계산
    if (targetTime >= pathDirectionNotes[pathDirectionNotes.length - 1].finalTime) {
        for (let i = 0; i < pathDirectionNotes.length - 1; i++) {
            const a = pathDirectionNotes[i];
            const b = pathDirectionNotes[i + 1];

            if (b.type === "node" && b.wait) {
                // wait 노트는 이동하지 않음
                continue;
            }

            const movementSpeed = calculateMovementSpeed(a, b, bpm, subdivisions);
            const dTime = b.finalTime - a.finalTime;

            const { direction, angleOffset } = headings[i];
            const [dx, dy] = directionToVectorWithAngle(direction, angleOffset);
            const mag = Math.hypot(dx, dy) || 1;
            const totalDist = movementSpeed * dTime;

            currentPos.x += (dx / mag) * totalDist;
            currentPos.y += (dy / mag) * totalDist;
        }
        return currentPos;
    }

    // targetTime이 특정 구간 내에 있는 경우
    for (let i = 0; i < pathDirectionNotes.length - 1; i++) {
        const a = pathDirectionNotes[i];
        const b = pathDirectionNotes[i + 1];

        // 이전 구간들을 모두 통과하여 현재 위치 계산
        if (targetTime > b.finalTime) {
            if (b.type === "node" && b.wait) {
                continue;
            }

            const movementSpeed = calculateMovementSpeed(a, b, bpm, subdivisions);
            const dTime = b.finalTime - a.finalTime;

            const { direction, angleOffset } = headings[i];
            const [dx, dy] = directionToVectorWithAngle(direction, angleOffset);
            const mag = Math.hypot(dx, dy) || 1;
            const totalDist = movementSpeed * dTime;

            currentPos.x += (dx / mag) * totalDist;
            currentPos.y += (dy / mag) * totalDist;
            continue;
        }

        // 현재 구간에 있는 경우
        if (a.finalTime <= targetTime && targetTime <= b.finalTime) {
            // wait 노트인 경우 이전 위치에서 대기
            if (b.type === "node" && b.wait) {
                if (targetTime >= a.finalTime && targetTime < b.finalTime) {
                    return currentPos; // 현재 위치에서 대기
                }
                continue;
            }

            const dTime = b.finalTime - a.finalTime;
            const timeRatio = (targetTime - a.finalTime) / dTime;

            const { direction, angleOffset } = headings[i];
            const movementSpeed = calculateMovementSpeed(a, b, bpm, subdivisions);
            const [dx, dy] = directionToVectorWithAngle(direction, angleOffset);
            const mag = Math.hypot(dx, dy) || 1;
            const totalDist = movementSpeed * dTime;

            // fade를 고려한 진행도 계산
            let t = timeRatio;
            const bFade = b.fade || false;
            const aBpm = a.bpm || bpm;
            const bBpm = b.bpm || bpm;

            if (bFade && Math.abs(aBpm - bBpm) > 0.01) {
                t = calculateFadeProgress(timeRatio, aBpm, bBpm);
            }

            return {
                x: currentPos.x + (dx / mag) * totalDist * t,
                y: currentPos.y + (dy / mag) * totalDist * t
            };
        }
    }

    return currentPos;
}

function updateDemoPlayerPosition(currentTime) {
    const preDelaySeconds = getPreDelaySeconds();

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    const directionNotes = getDirectionNotesWithOffsets(bpm, subdivisions);

    const pathDirectionNotes = directionNotes.map((note, index) => {
        const pathBeat = calculatePathBeat(note, preDelaySeconds, bpm, subdivisions);
        const sectionOffset = note._sectionOffset || 0;
        let finalTime;
        if (note.beat === 0 && note.type === "direction" && sectionOffset === 0) {
            // beat 0 direction 노트는 실제 게임 시작점 (0초)
            finalTime = 0;
        } else {
            // 각 노트의 개별 BPM/subdivision 사용하여 finalTime 계산
            const noteBpm = note.bpm || bpm;
            const noteSubdivisions = note.subdivisions || subdivisions;
            const originalTime = sectionOffset + beatToTime(note.beat, noteBpm, noteSubdivisions);
            finalTime = originalTime + preDelaySeconds;
        }
        return {
            ...note,
            finalTime: finalTime,
            pathBeat: pathBeat
        };
    }).sort((a, b) => a.finalTime - b.finalTime);

    // 곡선 구간에서는 경로 데이터(LUT 포함)를 거쳐야 캐릭터가 곡선 위를 등속으로 달린다.
    // 직선 전용 누적 계산으로는 곡선을 가로질러 버린다.
    const { nodePositions, segmentTimes } = calculateNodePositions(pathDirectionNotes, bpm, subdivisions);
    const position = getNotePositionFromPathData(currentTime, pathDirectionNotes, nodePositions, segmentTimes)
        || calculateExactPositionOnPath(currentTime, pathDirectionNotes, bpm, subdivisions);

    demoPlayer.x = position.x;
    demoPlayer.y = position.y;
}

function getNotePositionFromPathData(finalTime, pathDirectionNotes, nodePositions, segmentTimes) {
    const bpm = parseFloat(document.getElementById("bpm").value || 120);

    for (let i = 0; i < pathDirectionNotes.length - 1; i++) {
        const a = pathDirectionNotes[i];
        const b = pathDirectionNotes[i + 1];
        const pa = nodePositions[i];
        const pb = nodePositions[i + 1];

        if (a.finalTime <= finalTime && finalTime <= b.finalTime) {
            // 시간 기반 진행도 계산
            let normalizedTime = (finalTime - a.finalTime) / (b.finalTime - a.finalTime);

            // fade를 고려한 실제 위치 진행도 계산
            let interp = normalizedTime;
            const bFade = b.fade || false;
            const aBpm = a.bpm || bpm;
            const bBpm = b.bpm || bpm;

            if (bFade && Math.abs(aBpm - bBpm) > 0.01) {
                // fade가 활성화되면 비선형 보간 (Unity StageUtils.GetPosition과 동일)
                interp = calculateFadeProgress(normalizedTime, aBpm, bBpm);
            }

            // 곡선 구간에서 interp는 거리 진행도다. LUT로 호장 위치를 찾아야 등속이 유지된다 —
            // 베지어 t로 직접 쓰면 구간 안에서 캐릭터가 출렁인다.
            const curve = segmentTimes?.[i]?.curve;
            if (curve) {
                return pointAtArcProgress(curve.p0, curve.p1, curve.p2, curve.lut, interp);
            }

            return {
                x: pa.x + (pb.x - pa.x) * interp,
                y: pa.y + (pb.y - pa.y) * interp
            };
        }
    }
    return null;
}

// pathBeat 위치의 경로 위 점을 곡선까지 반영해 구한다(롱노트 바가 곡선을 따라 그려지도록).
// 구간을 pathBeat로 찾고, 그 안 진행도를 곡선이면 호장 LUT(pointAtArcProgress)로,
// 직선이면 노드 선형보간으로 계산한다 — getNotePositionFromPathData(시간 기반)의 pathBeat 판.
// 구간 밖으로 넘어가면 마지막 진행 각도를 따라 직선 연장한다(기존 롱노트 동작 유지).
function pathPointAtBeat(currentBeat, pathDirectionNotes, nodePositions, segmentTimes, subdivisions) {
    for (let j = 0; j < pathDirectionNotes.length - 1; j++) {
        const a = pathDirectionNotes[j];
        const b = pathDirectionNotes[j + 1];
        if (a.pathBeat <= currentBeat && currentBeat <= b.pathBeat) {
            const span = b.pathBeat - a.pathBeat;
            const interp = span > 0 ? (currentBeat - a.pathBeat) / span : 0;

            const curve = segmentTimes?.[j]?.curve;
            if (curve) {
                return pointAtArcProgress(curve.p0, curve.p1, curve.p2, curve.lut, interp);
            }
            const pa = nodePositions[j];
            const pb = nodePositions[j + 1];
            return {
                x: pa.x + (pb.x - pa.x) * interp,
                y: pa.y + (pb.y - pa.y) * interp
            };
        }
    }

    // 마지막 구간을 벗어난 경우: 마지막 노트의 진행 각도로 직선 연장.
    const lastNote = pathDirectionNotes[pathDirectionNotes.length - 1];
    if (pathDirectionNotes.length >= 2 && lastNote && currentBeat > lastNote.pathBeat) {
        const lastPos = nodePositions[nodePositions.length - 1];
        const beatDiff = currentBeat - lastNote.pathBeat;
        const subs = subdivisions || parseInt(document.getElementById("subdivisions").value || 16);
        const dist = (8 * beatDiff) / subs;

        const lastHeading = resolvePathHeadings(pathDirectionNotes).pop();
        const [dx, dy] = directionToVectorWithAngle(lastHeading.direction, lastHeading.angleOffset);
        const mag = Math.hypot(dx, dy) || 1;
        return {
            x: lastPos.x + (dx / mag) * dist,
            y: lastPos.y + (dy / mag) * dist
        };
    }
    return null;
}

function stopDemo() {
    isPlaying = false;
    isPaused = false;
    cancelAnimationFrame(animationFrameId);
    demoAudio.pause();
    demoAudio.currentTime = 0;
    elapsedTime = 0;
    resetPlayedNotes();
    resetExecutedEvents();
    spanDemoTime.textContent = "00:00:00 / " + formatTime(demoAudio.duration || 0);
    seekbar.value = 0;
        drawPath();
}

function pauseDemo() {
    if (!isPlaying || isPaused)
        return;
    isPaused = true;
    pauseStartTime = performance.now();
    cancelAnimationFrame(animationFrameId);
    demoAudio.pause();
}

function resumeDemo() {
    if (!isPlaying || !isPaused)
        return;
    isPaused = false;
    const pausedDuration = performance.now() - pauseStartTime;
    startTime += pausedDuration;

    // 일시정지 후 재개할 때는 별도의 상태 리셋이 필요하지 않음
    // 현재 elapsedTime에 맞는 상태가 이미 유지되어 있음
    updateDemo();

    if (elapsedTime >= MUSIC_START_TIME) {
        demoAudio.play();
    } else {
        setTimeout(() => {
            if (!isPaused) {
                demoAudio.play();
            }
        }, (MUSIC_START_TIME - elapsedTime) * 1000);
    }
}

// IndexedDB 및 저장/로드 관련 함수들
const DB_NAME = 'ChartEditorDB';
const DB_VERSION = 1;
const AUDIO_STORE = 'audioFiles';

let db = null;

async function initDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            db = request.result;
            resolve(db);
        };

        request.onupgradeneeded = (e) => {
            const database = e.target.result;
            if (!database.objectStoreNames.contains(AUDIO_STORE)) {
                database.createObjectStore(AUDIO_STORE);
            }
        };
    });
}

async function saveAudioFile(file) {
    if (!db) await initDB();

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([AUDIO_STORE], 'readwrite');
        const store = transaction.objectStore(AUDIO_STORE);

        const audioData = {
            file: file,
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified
        };

        const request = store.put(audioData, 'currentAudio');
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

async function loadAudioFileFromDB() {
    if (!db) await initDB();

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([AUDIO_STORE], 'readonly');
        const store = transaction.objectStore(AUDIO_STORE);
        const request = store.get('currentAudio');

        request.onsuccess = () => {
            const result = request.result;
            if (result && result.file) {
                resolve(result.file);
            } else {
                resolve(null);
            }
        };
        request.onerror = () => reject(request.error);
    });
}

function saveToStorage() {
    const preDelayValue = parseInt(document.getElementById("pre-delay").value || 0);
    const bpmValue = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisionsValue = parseInt(document.getElementById("subdivisions").value || 16);
    const speedMultiplierValue = parseFloat(document.getElementById("speed-multiplier").value || 1.0);

    const saveData = {
        notes: notes,
        events: getAllEvents(),
        bpm: bpmValue,
        subdivisions: subdivisionsValue,
        audioFileName: savedAudioFile ? savedAudioFile.name : null,
        audioFileSize: savedAudioFile ? savedAudioFile.size : null,
        audioFileType: savedAudioFile ? savedAudioFile.type : null,
        preDelay: preDelayValue,
        speedMultiplier: speedMultiplierValue,
        // 적정 배속(패턴 전역). 위 speedMultiplier(미리보기 전용)와 다른 값이다.
        speed: getSpeedValue(),
        signature: signatures
    };
    localStorage.setItem("autosave_notes", JSON.stringify(saveData));
}

function loadFromStorage() {
    const saved = localStorage.getItem("autosave_notes");
    if (saved) {
        try {
            const parsed = JSON.parse(saved);

            // 배열을 통째로 갈아끼우므로 인덱스 기반 선택은 무효다(감사 B-2).
            resetSelectionState();

            if (Array.isArray(parsed)) {
                // 구 형식: 배열만 있는 경우 - 현재 UI 설정값 사용
                const currentBpm = parseFloat(document.getElementById("bpm").value || 120);
                const currentSubdivisions = parseInt(document.getElementById("subdivisions").value || 16);

                notes.splice(0, notes.length, ...parsed);
                notes.forEach(note => {
                    if (note.isLong === undefined) note.isLong = false;
                    if (note.longTime === undefined) note.longTime = 0;
                    // 기존 노트에 BPM/subdivision이 없으면 현재 설정값으로 초기화
                    if (note.bpm === undefined) note.bpm = currentBpm;
                    if (note.subdivisions === undefined) note.subdivisions = currentSubdivisions;
                    // Fade 필드 초기화 (하위 호환성: number를 boolean으로 변환)
                    if (note.fade === undefined) {
                        note.fade = false;
                    } else if (typeof note.fade === 'number') {
                        note.fade = note.fade > 0;
                    }
                    // wait 필드 초기화 (Node 타입만)
                    if (note.type === "node" && note.wait === undefined) note.wait = false;
                    // beatReset 필드 초기화 (모든 타입)
                    if (note.beatReset === undefined) note.beatReset = false;
                });

                // 캐시 무효화
                invalidatePathCache();

                // 구 형식에서는 EventList 데이터가 없으므로 클리어
                clearAllEvents();
            } else if (parsed.notes && Array.isArray(parsed.notes)) {
                // 신 형식: 전역 BPM/subdivision이 포함된 경우
                const globalBpm = parsed.bpm || 120;
                const globalSubdivisions = parsed.subdivisions || 16;

                notes.splice(0, notes.length, ...parsed.notes);
                notes.forEach(note => {
                    if (note.isLong === undefined) note.isLong = false;
                    if (note.longTime === undefined) note.longTime = 0;
                    // 기존 노트에 BPM/subdivision이 없으면 로드된 전역값으로 초기화
                    if (note.bpm === undefined) note.bpm = globalBpm;
                    if (note.subdivisions === undefined) note.subdivisions = globalSubdivisions;
                    // Fade 필드 초기화 (하위 호환성: number를 boolean으로 변환)
                    if (note.fade === undefined) {
                        note.fade = false;
                    } else if (typeof note.fade === 'number') {
                        note.fade = note.fade > 0;
                    }
                    // wait 필드 초기화 (Node 타입만)
                    if (note.type === "node" && note.wait === undefined) note.wait = false;
                    // beatReset 필드 초기화 (모든 타입)
                    if (note.beatReset === undefined) note.beatReset = false;
                });

                // 캐시 무효화
                invalidatePathCache();

                if (parsed.bpm !== undefined) {
                    document.getElementById("bpm").value = parsed.bpm;
                    document.getElementById("bpm").dataset.previousValue = parsed.bpm;
                }
                if (parsed.subdivisions !== undefined) {
                    document.getElementById("subdivisions").value = parsed.subdivisions;
                    document.getElementById("subdivisions").dataset.previousValue = parsed.subdivisions;
                }

                if (parsed.preDelay !== undefined) {
                    const adjustedPreDelay = parsed.preDelay;
                    document.getElementById("pre-delay").value = adjustedPreDelay;
                }

                if (parsed.speedMultiplier !== undefined) {
                    document.getElementById("speed-multiplier").value = parsed.speedMultiplier;
                    speedMultiplier = parsed.speedMultiplier;
                }

                // 적정 배속(패턴 전역). 구 autosave에는 없으므로 기본값으로 둔다.
                const savedSpeedField = document.getElementById("speed");
                if (savedSpeedField) {
                    savedSpeedField.value = Number.isFinite(parsed.speed) ? parsed.speed : DEFAULT_SPEED;
                }

                // EventList 데이터 로드
                if (parsed.events && Array.isArray(parsed.events)) {
                    loadEventsFromJson(parsed.events);
                } else {
                    clearAllEvents();
                }

                // Signature 구간 복원 (없으면 빈 상태 유지)
                signatures.length = 0;
                if (Array.isArray(parsed.signature)) {
                    parsed.signature.forEach((s) => {
                        if (s && typeof s.startTime === "number" && typeof s.endTime === "number") {
                            signatures.push({ startTime: s.startTime, endTime: s.endTime });
                        }
                    });
                }

                loadAudioFileFromDB().then(audioFile => {
                    if (audioFile) {
                        console.log('Auto-loading saved audio file:', audioFile.name);

                        const fileInput = document.getElementById("audio-file");
                        const dt = new DataTransfer();
                        dt.items.add(audioFile);
                        fileInput.files = dt.files;

                        if (audioFileURL) URL.revokeObjectURL(audioFileURL);
                        audioFileURL = URL.createObjectURL(audioFile);
                        demoAudio.src = audioFileURL;
                        demoAudio.volume = musicVolume;
                        demoAudio.load();

                        const container = fileInput.parentElement;
                        let indicator = container.querySelector('.file-indicator');
                        if (!indicator) {
                            indicator = document.createElement('div');
                            indicator.className = 'file-indicator';
                            indicator.style.cssText = 'margin-top: 5px; font-size: 12px; color: #4CAF50; font-weight: bold;';
                            container.appendChild(indicator);
                        }
                        indicator.textContent = `자동 복원: ${audioFile.name}`;

                        processAudioFile(audioFile);
                    }
                }).catch(err => {
                    console.warn('Failed to load audio from IndexedDB:', err);
                });
            }
        } catch (e) {
            console.error("불러오기 실패:", e);
        }
    }
}

// validateChart function moved to notes.js module


// UI 관련 함수들

// Fade 시간을 계산 (이전 노트와 현재 노트의 시간 차이)
function calculateFadeDuration(noteIndex) {
    const globalBpm = parseFloat(document.getElementById("bpm").value || 120);

    // 현재 노트의 시간
    const currentTime = calculateNoteTime(noteIndex);

    // 이전 편집 가능 노트 찾기
    for (let i = noteIndex - 1; i >= 0; i--) {
        const prevNote = notes[i];
        const canEdit = ["direction", "longdirection", "both", "longboth", "node"].includes(prevNote.type);
        if (canEdit) {
            const prevTime = calculateNoteTime(i);
            return currentTime - prevTime;
        }
    }

    // 이전 노트가 없으면 현재 시간 자체가 fade 시간
    return currentTime;
}

// Fade 구간에서 시간으로부터 beat를 역산
// fadeStartTime부터 targetTime까지의 경과 시간을 고려하여 beat 계산
function timeToBeatInFade(targetTime, fadeStartTime, fadeEndTime, startBpm, endBpm, subdivisions) {
    const fadeDuration = fadeEndTime - fadeStartTime;

    if (targetTime <= fadeStartTime) {
        // fade 시작 전
        return targetTime * (startBpm / 60) * subdivisions;
    } else if (targetTime >= fadeEndTime) {
        // fade 종료 후
        const fadeStartBeat = fadeStartTime * (startBpm / 60) * subdivisions;
        const fadeBeat = fadeDuration * ((startBpm + endBpm) / 2 / 60) * subdivisions;
        const afterFadeDuration = targetTime - fadeEndTime;
        const afterFadeBeat = afterFadeDuration * (endBpm / 60) * subdivisions;
        return fadeStartBeat + fadeBeat + afterFadeBeat;
    } else {
        // fade 구간 내
        const beforeFadeBeat = fadeStartTime * (startBpm / 60) * subdivisions;
        const timeInFade = targetTime - fadeStartTime;

        // 적분을 사용한 beat 계산
        // v(t) = startBpm + (endBpm - startBpm) * (t / fadeDuration)
        // beat = ∫ v(t) / 60 * subdivisions dt
        // = subdivisions / 60 * [startBpm * t + (endBpm - startBpm) * t^2 / (2 * fadeDuration)]

        const t = timeInFade;
        const fadeBeats = (subdivisions / 60) * (
            startBpm * t +
            (endBpm - startBpm) * t * t / (2 * fadeDuration)
        );

        return beforeFadeBeat + fadeBeats;
    }
}

// Tab 노트가 fade 구간에 있는지 확인하고 fade 정보를 반환
function getTabNoteFadeInfo(note, index) {
    const globalBpm = parseFloat(document.getElementById("bpm").value || 120);
    const globalSubdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    // 이전 편집 가능 노트 찾기
    let prevBpm = globalBpm;
    let prevIndex = -1;

    for (let i = index - 1; i >= 0; i--) {
        const prevNote = notes[i];
        const canEdit = ["direction", "longdirection", "both", "longboth", "node"].includes(prevNote.type);
        if (canEdit) {
            prevBpm = prevNote.bpm || globalBpm;
            prevIndex = i;
            break;
        }
    }

    // 다음 편집 가능 노트 찾기
    let nextBpm = globalBpm;
    let nextFade = false;
    let nextSubdivisions = globalSubdivisions;
    let nextIndex = -1;

    for (let i = index + 1; i < notes.length; i++) {
        const nextNote = notes[i];
        const canEdit = ["direction", "longdirection", "both", "longboth", "node"].includes(nextNote.type);
        if (canEdit) {
            nextBpm = nextNote.bpm || globalBpm;
            nextFade = nextNote.fade || false;
            nextSubdivisions = nextNote.subdivisions || globalSubdivisions;
            nextIndex = i;
            break;
        }
    }

    // fade 구간 확인
    if (nextFade && Math.abs(prevBpm - nextBpm) > 0.01 && nextIndex !== -1 && prevIndex !== -1) {
        const currentNote = notes[index];

        // fadeDirectTime이 이미 있는 노트는 항상 fade 구간에 있다고 판정
        if (currentNote.hasOwnProperty('fadeDirectTime')) {
            const prevTime = calculateNoteTime(prevIndex);
            const nextTime = calculateNoteTime(nextIndex);
            return {
                inFade: true,
                fadeStartTime: prevTime,
                fadeEndTime: nextTime,
                startBpm: prevBpm,
                endBpm: nextBpm,
                subdivisions: nextSubdivisions
            };
        }

        // fadeDirectTime이 없는 노트는 일반적인 방식으로 fade 구간 판정
        const noteTime = calculateNoteTime(index);
        const prevTime = calculateNoteTime(prevIndex);
        const nextTime = calculateNoteTime(nextIndex);
        const fadeStartTime = prevTime;
        const fadeEndTime = nextTime;

        if (noteTime >= fadeStartTime && noteTime <= fadeEndTime) {
            return {
                inFade: true,
                fadeStartTime: fadeStartTime,
                fadeEndTime: fadeEndTime,
                startBpm: prevBpm,
                endBpm: nextBpm,
                subdivisions: nextSubdivisions
            };
        }
    }

    return { inFade: false };
}

// Tab 노트의 정확한 시간을 계산 (모든 이전 노트의 BPM/fade 고려)
function calculateNoteTime(targetIndex) {
    const globalBpm = parseFloat(document.getElementById("bpm").value || 120);
    const globalSubdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    const targetNote = notes[targetIndex];

    // fade 구간의 tab/longtab 노트이고 직접 시간값이 있으면 그것을 사용
    if ((targetNote.type === "tab" || targetNote.type === "longtab") &&
        targetNote.hasOwnProperty('fadeDirectTime')) {
        const preDelaySeconds = getPreDelaySeconds();
        return targetNote.fadeDirectTime - preDelaySeconds;
    }

    const targetBeat = targetNote.beat;

    // BPM 변경 지점들을 수집
    const bpmChanges = [];
    let currentBpm = globalBpm;
    let currentSubdivisions = globalSubdivisions;
    let prevBeat = 0;

    for (let i = 0; i <= targetIndex; i++) {
        const note = notes[i];
        const canEdit = ["direction", "longdirection", "both", "longboth", "node"].includes(note.type);

        if (canEdit && note.beat <= targetBeat) {
            bpmChanges.push({
                beat: note.beat,
                bpm: note.bpm || globalBpm,
                subdivisions: note.subdivisions || globalSubdivisions,
                fade: note.fade || false,
                prevBpm: currentBpm,
                prevSubdivisions: currentSubdivisions
            });
            currentBpm = note.bpm || globalBpm;
            currentSubdivisions = note.subdivisions || globalSubdivisions;
        }
    }

    // 시간 누적 계산
    let accumulatedTime = 0;
    let lastBeat = 0;
    let lastBpm = globalBpm;
    let lastSubdivisions = globalSubdivisions;

    for (let i = 0; i < bpmChanges.length; i++) {
        const change = bpmChanges[i];

        // 이전 구간의 시간 계산
        if (change.beat > lastBeat) {
            const beatDiff = change.beat - lastBeat;
            const time = beatToTime(beatDiff, lastBpm, lastSubdivisions);
            accumulatedTime += time;
        }

        lastBeat = change.beat;
        lastBpm = change.bpm;
        lastSubdivisions = change.subdivisions;
    }

    // 마지막 구간 (마지막 BPM 변경 지점부터 타겟까지)
    if (targetBeat > lastBeat) {
        const beatDiff = targetBeat - lastBeat;
        const time = beatToTime(beatDiff, lastBpm, lastSubdivisions);
        accumulatedTime += time;
    }

    return accumulatedTime;
}

// fade 구간에서 특정 시간의 BPM을 보간으로 계산
function calculateBpmAtTime(targetTime, fadeStartTime, fadeEndTime, startBpm, endBpm) {
    if (targetTime <= fadeStartTime) {
        return startBpm;
    } else if (targetTime >= fadeEndTime) {
        return endBpm;
    } else {
        // 선형 보간
        const fadeDuration = fadeEndTime - fadeStartTime;
        const timeInFade = targetTime - fadeStartTime;
        const fadeProgress = timeInFade / fadeDuration;
        return startBpm + (endBpm - startBpm) * fadeProgress;
    }
}

// fade 구간의 tab/longtab 노트의 beat, bpm, subdivision을 시간값에 기반하여 업데이트
function updateFadeNoteParameters(note, index, targetFinalTime, fadeInfo) {
    const preDelaySeconds = getPreDelaySeconds();
    const targetOriginalTime = targetFinalTime - preDelaySeconds;

    // fade 구간에서 해당 시간의 BPM 계산
    const interpolatedBpm = calculateBpmAtTime(
        targetOriginalTime,
        fadeInfo.fadeStartTime,
        fadeInfo.fadeEndTime,
        fadeInfo.startBpm,
        fadeInfo.endBpm
    );

    // fade 구간을 만든 노트의 subdivision 사용
    const targetSubdivisions = fadeInfo.subdivisions;

    // 해당 BPM과 subdivision으로 목표 시간이 나오게 하는 beat 값 역산
    const targetBeat = timeToBeat(targetOriginalTime, interpolatedBpm, targetSubdivisions);

    // float 정밀도로 반올림 (소수점 6자리까지)
    const roundedBeat = Math.round(targetBeat * 1000000) / 1000000;
    const roundedBpm = Math.round(interpolatedBpm * 1000000) / 1000000;

    // 노트 파라미터 업데이트
    note.beat = roundedBeat;
    note.bpm = roundedBpm;
    note.subdivisions = targetSubdivisions;
    note.fadeDirectTime = targetFinalTime;

    console.log(`Updated fade note: time=${targetFinalTime.toFixed(3)}s, beat=${roundedBeat}, bpm=${roundedBpm.toFixed(2)}, subdivisions=${targetSubdivisions}`);
}

// fade 구간에서 벗어난 노트들의 fadeDirectTime 제거
function cleanupFadeDirectTimes() {
    notes.forEach((note, index) => {
        if ((note.type === "tab" || note.type === "longtab") &&
            note.hasOwnProperty('fadeDirectTime')) {

            // 임시로 fadeDirectTime을 제거해서 원래 방식으로 fade 구간 판정
            const tempFadeDirectTime = note.fadeDirectTime;
            delete note.fadeDirectTime;

            const fadeInfo = getTabNoteFadeInfo(note, index);

            if (fadeInfo.inFade) {
                // 여전히 fade 구간에 있으면 fadeDirectTime 복원
                note.fadeDirectTime = tempFadeDirectTime;
            }
            // fade 구간에서 벗어났으면 fadeDirectTime은 제거된 상태로 유지
        }
    });
}

// Tab 노트들의 BPM/Subdivisions 값을 다음 편집 가능 노트에서 상속받도록 업데이트
function updateTabNotesInheritance() {
    const globalBpm = parseFloat(document.getElementById("bpm").value || 120);
    const globalSubdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    notes.forEach((note, index) => {
        // Tab 계열 노트만 처리 (fade 구간의 직접 시간값이 있는 노트는 제외)
        if ((note.type === "tab" || note.type === "longtab") &&
            !note.hasOwnProperty('fadeDirectTime')) {
            // 이전 편집 가능 노트 찾기
            let prevBpm = globalBpm;
            let prevSubdivisions = globalSubdivisions;
            let prevIndex = -1;

            for (let i = index - 1; i >= 0; i--) {
                const prevNote = notes[i];
                const canEdit = ["direction", "longdirection", "both", "longboth", "node"].includes(prevNote.type);
                if (canEdit) {
                    prevBpm = prevNote.bpm || globalBpm;
                    prevSubdivisions = prevNote.subdivisions || globalSubdivisions;
                    prevIndex = i;
                    break;
                }
            }

            // 다음 편집 가능 노트 찾기
            let nextBpm = globalBpm;
            let nextSubdivisions = globalSubdivisions;
            let nextFade = false;
            let nextIndex = -1;

            for (let i = index + 1; i < notes.length; i++) {
                const nextNote = notes[i];
                const canEdit = ["direction", "longdirection", "both", "longboth", "node"].includes(nextNote.type);
                if (canEdit) {
                    nextBpm = nextNote.bpm || globalBpm;
                    nextSubdivisions = nextNote.subdivisions || globalSubdivisions;
                    nextFade = nextNote.fade || false;
                    nextIndex = i;
                    break;
                }
            }

            // Tab 노트의 정확한 시간 계산
            const noteTime = calculateNoteTime(index);

            let inheritedBpm = nextBpm;
            let inheritedSubdivisions = nextSubdivisions;

            // fade가 있고 BPM이 다르면 보간 계산
            if (nextFade && Math.abs(prevBpm - nextBpm) > 0.01 && nextIndex !== -1 && prevIndex !== -1) {
                const prevTime = calculateNoteTime(prevIndex);
                const nextTime = calculateNoteTime(nextIndex);
                const fadeStartTime = prevTime;
                const fadeEndTime = nextTime;
                const fadeDuration = fadeEndTime - fadeStartTime;

                if (noteTime >= fadeStartTime && noteTime <= fadeEndTime) {
                    // Tab 노트가 fade 구간 안에 있음 - BPM 보간
                    const fadeProgress = (noteTime - fadeStartTime) / fadeDuration;
                    inheritedBpm = prevBpm + (nextBpm - prevBpm) * fadeProgress;
                } else if (noteTime < fadeStartTime) {
                    // fade 시작 전 - 이전 BPM 사용
                    inheritedBpm = prevBpm;
                    inheritedSubdivisions = prevSubdivisions;
                }
                // noteTime > fadeEndTime인 경우는 이미 nextBpm 사용
            }

            // Tab 노트에 상속받은 값 설정
            note.bpm = inheritedBpm;
            note.subdivisions = inheritedSubdivisions;
        }
    });
}

// 최적화된 렌더링 스케줄러 (디바운싱)
// 성능 최적화된 렌더링 스케줄러
function scheduleRender(flags = {}) {
    // 플래그 업데이트
    if (flags.noteList) pendingRenderFlags.noteList = true;
    if (flags.eventList) pendingRenderFlags.eventList = true;
    if (flags.canvas) pendingRenderFlags.canvas = true;
    if (flags.timeline) pendingRenderFlags.timeline = true;

    if (renderScheduled) return;

    renderScheduled = true;

    // Idle 상태에서 렌더링 수행 (성능 향상)
    const performRender = () => {
        const startTime = performance.now();

        try {
            // 우선순위가 높은 렌더링부터 처리
            if (pendingRenderFlags.canvas) {
                drawPath();
            }

            // DOM 업데이트는 배치로 처리
            const domUpdates = [];

            if (pendingRenderFlags.noteList) {
                domUpdates.push(() => renderNoteListImmediate());
            }

            if (pendingRenderFlags.eventList) {
                domUpdates.push(() => renderEventListImmediate());
            }

            if (pendingRenderFlags.timeline) {
                domUpdates.push(() => renderTimelineImmediate());
            }

            // DOM 업데이트 배치 실행
            if (domUpdates.length > 0) {
                // 레이아웃 스래싱 방지를 위한 읽기/쓰기 분리
                domUpdates.forEach(update => update());
            }


            const endTime = performance.now();
            const renderTime = endTime - startTime;

            // 성능 모니터링 (개발 모드에서만)
            if (renderTime > 16) { // 16ms 이상이면 경고
                console.warn(`Render time exceeded 16ms: ${renderTime.toFixed(2)}ms`);
            }

        } catch (error) {
            console.error('Render error:', error);
        } finally {
            // 플래그 초기화
            pendingRenderFlags = {
                noteList: false,
                eventList: false,
                canvas: false,
                timeline: false,
            };
            renderScheduled = false;
        }
    };

    // 브라우저가 한가할 때 렌더링 수행
    if (window.requestIdleCallback) {
        window.requestIdleCallback(performRender, { timeout: 16 });
    } else {
        // requestIdleCallback을 지원하지 않는 브라우저는 requestAnimationFrame 사용
        requestAnimationFrame(performRender);
    }
}

// 중복 검사 최적화 함수 (O(n²) → O(n))
// 같은 구간(sectionIndex)에서 beat-bpm-subdivisions가 모두 같을 때만 중복으로 판단
// 구간이 다르거나(beatReset 전후) bpm/subdivisions가 다르면 중복이 아님
function findDuplicateNoteIndices(notes, bpm, subdivisions) {
    const duplicateIndices = new Set();
    const noteMap = new Map(); // key: "sectionIndex-beat-bpm-subdivisions", value: [indices]

    notes.forEach((note, index) => {
        const noteBpm = note.bpm || bpm;
        const noteSubdivisions = note.subdivisions || subdivisions;
        const sectionIdx = note.sectionIndex !== undefined ? note.sectionIndex : 0;
        const key = `${sectionIdx}-${note.beat}-${noteBpm}-${noteSubdivisions}`;

        if (!noteMap.has(key)) {
            noteMap.set(key, []);
        }
        noteMap.get(key).push(index);
    });

    // 2개 이상인 경우만 중복으로 처리
    for (const indices of noteMap.values()) {
        if (indices.length > 1) {
            indices.forEach(idx => duplicateIndices.add(idx));
        }
    }

    return duplicateIndices;
}

// renderNoteList 래퍼 함수 (디바운싱 적용)
function renderNoteList() {
    // 타임라인도 같은 notes를 본다. 갱신 지점이 29곳이라 각각 고치는 대신
    // 공용 진입점인 여기서 함께 예약한다 — 표와 타임라인이 갈릴 여지를 없앤다.
    // 타임라인 탭이 닫혀 있으면 renderTimelineImmediate가 즉시 빠져나간다.
    scheduleRender({ noteList: true, timeline: true });
}

// ── 타임라인 (Phase 1-2) ──────────────────────────────────────────────────
//
// 뷰 전용 상태다. 차트 데이터가 아니므로 저장·직렬화되지 않는다.
// 스냅 단위를 전역 subdivisions와 분리해 두는 이유는 설계 §1-2-C 참조 —
// 전역값을 바꾸면 기존 노트의 시간이 재해석되기 때문이다.
const timelineState = createTimelineState();
let timelineResizeObserver = null;
// 추종 스크롤이 스스로 낸 scroll 이벤트를 걸러내는 플래그(아래 setupTimelineControls).
let timelineSelfScroll = false;
// 멀리 뛰는 스크롤에서 **목표 위치를 먼저 그리게** 하는 임시 override.
// null이면 컨테이너의 실제 scrollTop이 진실이다(평소 경로).
let timelineScrollOverride = null;
// 드래그 이동 상태. null이면 드래그 중이 아니다.
let timelineDrag = null;
// 드래그로 실제 이동이 있었는지 — 직후의 click을 삼켜 중복 삽입을 막는다.
let timelineDragMoved = false;
// 차트 종료 노트(direction/none) 핸들 드래그 상태.
let chartEndDrag = null;
// 롱노트 길이 조절(꼬리 끝 손잡이) 드래그 상태.
let longGripDrag = null;
// 이벤트 마커 드래그(시간 이동) 상태. 그룹이면 indices가 여럿이다.
let timelineEventDrag = null;

function renderTimelineView() {
    scheduleRender({ timeline: true });
}

// 실제 그리기. **notes를 읽기만 한다** — renderNoteListImmediate와 달리
// cleanup/상속 갱신을 호출하지 않는다(감사 B-4: "스크롤이 데이터를 편집한다").
function renderTimelineImmediate() {
    // 팝아웃 대비: 매번 조회한다. init 시점에 캡처하면 창 분리 후 엉뚱한 문서를 가리킨다.
    const container = document.getElementById("timeline-grid");
    if (!container) return;

    // 숨겨진 탭은 clientHeight가 0이라 뷰포트를 잴 수 없다 — 그려봐야 1행만 나온다.
    // 탭을 열면 switchTab이, 크기가 잡히면 ResizeObserver가 다시 부른다.
    //
    // offsetParent로 판정하지 않는 이유: #sidebar가 position:fixed라 숨겨진
    // 자손도 offsetParent가 null이 아니다. clientHeight가 실제 신호다.
    if (!container.isConnected || container.clientHeight === 0) return;

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    // 보통은 컨테이너가 진실이다. 다만 **멀리 뛰는 스크롤**은 아직 그 높이가 없어
    // 대입이 잘리므로, 목표를 먼저 그려야 한다(scrollTimelineToBeat 주석 참조).
    // 그때만 이 override로 목표 위치를 알려준다.
    if (timelineScrollOverride !== null) {
        timelineState.scrollTop = timelineScrollOverride;
    } else {
        timelineState.scrollTop = container.scrollTop;
    }

    renderTimeline(container, {
        notes,
        bpm,
        subdivisions,
        selectedIndex: selectedNoteIndex,
        state: timelineState,
        // 이 박자 뒤의 행은 옅게 깐다 — 종료 노트가 멀리 있는 차트의 빈 구간이
        // "안 그려진 것"으로 오인되지 않게 한다(데이터는 무변경).
        contentEndBeat: chartContentEndAbsoluteBeat(),
        // 이벤트 열. **eventTime은 preDelay 후 축**이라 렌더러가 preDelay를 빼서
        // 박자로 환산한다(timeline-model.js의 buildEventPlacements 주석 참조).
        // shared/events.js는 Unity 계약이라 읽기만 한다(D-001).
        events: getAllEvents(),
        preDelaySeconds: getPreDelaySeconds()
    });

    updateTimelinePlayhead(container, bpm, subdivisions);
    updateChartEndHandle(container, bpm, subdivisions);

    const info = document.getElementById("timeline-info");
    if (info) {
        // 대형 차트에서 종료 노트가 콘텐츠보다 훨씬 뒤에 있으면 그 사실을 밝힌다.
        const contentEnd = chartContentEndAbsoluteBeat();
        const chartEnd = chartEndAbsoluteBeat();
        const gap = Math.round(chartEnd - contentEnd);
        const gapText = gap > subdivisions * 8 ? ` · 끝까지 빈 ${gap}박` : "";
        info.textContent =
            `${notes.length}노트 · ${beatsPerRowOf(timelineState, subdivisions)}박/행${gapText}`;
    }
}

// 재생 헤드. 재생 중이 아니면 숨긴다.
function updateTimelinePlayhead(container, bpm, subdivisions) {
    const doc = container.ownerDocument;
    let head = container.querySelector(".timeline-playhead");

    if (!isPlaying) {
        if (head) head.remove();
        return;
    }

    if (!head) {
        head = doc.createElement("div");
        head.className = "timeline-playhead";
        container.appendChild(head);
    }

    // elapsedTime은 finalTime(= originalTime + preDelay)과 같은 시간축이다
    // (`checkNoteHits`가 둘을 직접 비교한다). 타임라인의 박자 축은 preDelay 적용 전이라
    // preDelaySeconds를 빼야 헤드가 앞서 달리지 않는다.
    const offset = playheadOffsetPx(
        elapsedTime, getPreDelaySeconds(), bpm, subdivisions, timelineState
    );
    head.style.top = `${offset}px`;

    // 추종 스크롤(병목 5). 헤드가 화면 안이면 스크롤을 건드리지 않는다 —
    // 매 프레임 재중앙화하면 화면이 흔들려 작업이 어렵다.
    //
    // scrollTop을 쓰면 scroll 이벤트가 나고, 그 핸들러가 다시 렌더를 예약한다.
    // 자기가 만든 스크롤임을 표시해 그 한 번을 건너뛴다(무한 루프는 아니지만
    // 재생 중 프레임마다 렌더가 두 번 도는 것을 막는다).
    const next = followScrollTop(offset, container.clientHeight, container.scrollTop);
    if (next !== container.scrollTop) {
        timelineSelfScroll = true;
        container.scrollTop = next;
    }
}

// 종료 노트 핸들 — 타임라인 최하단(종료 노트 위치)에 걸치는 가로 바.
// **여기서 notes를 만들거나 고치지 않는다**(B-4). 유지는 편집 경로의
// ensureChartEndNote가 맡고, 이 함수는 있는 것을 그리기만 한다.
function updateChartEndHandle(container, bpm, subdivisions) {
    const doc = container.ownerDocument;
    let handle = container.querySelector(".timeline-end-handle");

    const absoluteBeats = calculateAbsoluteBeats(notes, bpm, subdivisions);
    const endIndex = findChartEndNoteIndex(notes, absoluteBeats);

    if (endIndex < 0) {
        if (handle) handle.remove();
        return;
    }

    if (!handle) {
        handle = doc.createElement("div");
        handle.className = "timeline-end-handle";
        const label = doc.createElement("span");
        label.className = "timeline-end-handle-label";
        label.textContent = "▲ 차트 끝 (drag)";
        handle.appendChild(label);
        handle.addEventListener("pointerdown", onChartEndHandlePointerDown);
        container.appendChild(handle);
    }

    const beatsPerRow = beatsPerRowOf(timelineState, subdivisions);
    const rowHeight = timelineState.rowHeight || 24;
    handle.style.top = `${(absoluteBeats[endIndex] / beatsPerRow) * rowHeight}px`;
    handle.title = `차트 끝 — #${endIndex}, beat ${notes[endIndex].beat}`;
}

// 줌(행 높이) 한계. 🔴 **슬라이더(index.html)와 Ctrl+휠이 같은 값을 써야 한다** —
// 갈리면 휠로는 못 가는 구간이 슬라이더로만 도달해 렌더가 그 높이를 전제하지 않은
// 상태가 된다. 위쪽을 크게 잡은 이유는 대형 차트에서 노트를 개별로 봐야 하기 때문이다.
const TIMELINE_ZOOM_MIN = 8;
const TIMELINE_ZOOM_MAX = 240;

function clampTimelineZoom(px) {
    return Math.max(TIMELINE_ZOOM_MIN, Math.min(TIMELINE_ZOOM_MAX, px));
}

// 종료 노트의 절대 박자. 없으면 마지막 콘텐츠 끝으로 떨어진다.
function chartEndAbsoluteBeat() {
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const absoluteBeats = calculateAbsoluteBeats(notes, bpm, subdivisions);
    const endIndex = findChartEndNoteIndex(notes, absoluteBeats);
    return endIndex >= 0 ? absoluteBeats[endIndex] : chartContentEndAbsoluteBeat();
}

// **실제 노트**가 끝나는 절대 박자(종료 노트 제외, 롱노트 꼬리 포함).
// 종료 노트가 멀리 있는 차트에서 "어디까지가 내용인가"를 보여준다.
function chartContentEndAbsoluteBeat() {
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const absoluteBeats = calculateAbsoluteBeats(notes, bpm, subdivisions);
    const endIndex = findChartEndNoteIndex(notes, absoluteBeats);
    return minimumChartEndBeat(notes, absoluteBeats, endIndex, subdivisions);
}

// 특정 절대 박자를 화면 가운데쯤에 오게 스크롤한다. **데이터를 바꾸지 않는다.**
//
// 🔴 **순서가 중요하다.** 가상 스크롤이라 컨테이너의 실제 높이는 *지금 그려진 행*까지만
// 있다. 멀리 뛸 때 `scrollTop`을 먼저 대입하면 브라우저가 **현재 scrollHeight로
// 잘라버려**(overflow-y:auto) 목표보다 앞에서 멈춘다. 게다가 `timelineSelfScroll`이
// 그 scroll 이벤트를 삼켜 **보정할 기회조차 사라진다** — 스크롤이 부정확했던 이유다.
//
// 그래서 ① 목표 위치를 state에 먼저 넣고 ② **즉시** 렌더해 그 구간의 행과 스페이서를
// 만들고 ③ 그다음 scrollTop을 대입한다. 이러면 대입 시점에 높이가 이미 확보돼 있다.
function scrollTimelineToBeat(absoluteBeat) {
    const container = document.getElementById("timeline-grid");
    if (!container) return;

    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const beatsPerRow = beatsPerRowOf(timelineState, subdivisions);
    const rowHeight = timelineState.rowHeight || 24;

    const y = (absoluteBeat / beatsPerRow) * rowHeight;
    // 가운데쯤에 두면 앞뒤 맥락이 함께 보인다. 위쪽으로 넘치면 0으로 잘린다.
    const target = Math.max(0, y - container.clientHeight / 2);

    // ① 목표 위치를 렌더에 알린다. override가 없으면 renderTimelineImmediate가
    //    container.scrollTop(= 아직 옛 위치)을 읽어 덮어버린다.
    timelineScrollOverride = target;
    try {
        // ② 즉시 그린다(예약이 아니다). scheduleRender는 다음 틱에 돌아
        //    ③의 대입이 먼저 일어나 다시 잘린다.
        renderTimelineImmediate();
    } finally {
        timelineScrollOverride = null;   // 예외가 나도 남기지 않는다
    }

    // ③ 이제 목표 구간의 행·스페이서가 있으니 대입이 잘리지 않는다.
    //    self 플래그는 **여기서** 세운다 — 위에서 세우면 렌더가 내는 scroll 이벤트가
    //    플래그를 먹어치워 정작 이 대입의 이벤트를 못 걸러낸다.
    timelineSelfScroll = true;
    container.scrollTop = target;

    // 실제로 도달한 위치를 state에 되돌려 놓는다(차트 끝이라 잘렸을 수 있다).
    // 안 맞추면 다음 렌더가 도달하지 못한 위치를 기준으로 그린다.
    timelineState.scrollTop = container.scrollTop;
}

// 타임라인 UI 배선. 스크롤·리사이즈·툴바.
function setupTimelineControls() {
    const container = document.getElementById("timeline-grid");
    if (!container) return;

    // 가상 스크롤은 스크롤할 때마다 다시 그려야 한다.
    // 단 추종 스크롤이 자기가 낸 이벤트는 건너뛴다(방금 그린 직후라 다시 그릴 게 없다).
    container.addEventListener("scroll", () => {
        if (timelineSelfScroll) {
            timelineSelfScroll = false;
            return;
        }
        renderTimelineView();
    }, { passive: true });

    // ResizeObserver를 쓰는 이유: 기존 노트 리스트는 resize 리스너를 메인 window에
    // 붙여 팝아웃 창의 리사이즈를 못 받는다(설계 §1-6). 컨테이너를 직접 관찰하면
    // 창이 어느 문서에 있든 갱신되고, 탭 전환으로 높이가 0 → 실제값이 될 때도 걸린다.
    if (typeof ResizeObserver !== "undefined") {
        timelineResizeObserver?.disconnect();
        timelineResizeObserver = new ResizeObserver(() => {
            renderTimelineView();
            // 인스펙터는 position:fixed라 컨테이너가 움직이면 좌표를 다시 잡아야 한다.
            positionTimelineInspector();
        });
        timelineResizeObserver.observe(container);
    }

    // 사이드바 리사이즈·창 스크롤로도 컨테이너의 화면 위치가 바뀐다.
    container.ownerDocument.defaultView?.addEventListener(
        "resize", () => positionTimelineInspector()
    );

    const rowUnit = document.getElementById("timeline-row-unit");
    rowUnit?.addEventListener("change", (e) => {
        timelineState.rowUnitIndex = parseInt(e.target.value, 10) || 0;
        renderTimelineView();
    });

    const zoom = document.getElementById("timeline-zoom");
    zoom?.addEventListener("input", (e) => {
        timelineState.rowHeight = clampTimelineZoom(parseInt(e.target.value, 10) || 24);
        renderTimelineView();
    });

    // 차트 끝으로 이동. 대형 차트는 종료 노트가 콘텐츠보다 훨씬 뒤에 있어
    // (실측 LT0004-NM: 콘텐츠 1917박 vs 종료 3950박) 휠로 내려가기 어렵다.
    // **데이터는 건드리지 않는다** — 보기만 옮긴다.
    document.getElementById("timeline-goto-end")?.addEventListener("click", () => {
        scrollTimelineToBeat(chartEndAbsoluteBeat());
    });
    document.getElementById("timeline-goto-content-end")?.addEventListener("click", () => {
        scrollTimelineToBeat(chartContentEndAbsoluteBeat());
    });

    // Ctrl+휠 줌. 맨휠은 **스크롤로 남긴다** — 세로로 긴 목록이라 휠 스크롤이
    // 주된 이동 수단이고, 그걸 뺏으면 오히려 쓰기 불편해진다.
    //
    // 커서 아래의 박자를 고정한 채 확대/축소한다(앵커 줌). 안 그러면 줌할 때마다
    // 보던 위치를 잃는다. passive:false여야 preventDefault로 브라우저 확대를 막는다.
    container.addEventListener("wheel", (e) => {
        if (!e.ctrlKey) return;
        e.preventDefault();

        const rect = container.getBoundingClientRect();
        const cursorY = e.clientY - rect.top;              // 뷰포트 기준
        const before = container.scrollTop + cursorY;      // 커서가 가리키던 콘텐츠 좌표

        const old = timelineState.rowHeight || 24;
        // 휠 한 칸에 ~10%씩. deltaY가 음수면(위로) 확대.
        const next = clampTimelineZoom(Math.round(old * (e.deltaY < 0 ? 1.1 : 0.9)));
        if (next === old) return;

        timelineState.rowHeight = next;

        // 같은 콘텐츠 지점이 커서 아래에 오도록 스크롤을 보정한다.
        timelineSelfScroll = true;
        container.scrollTop = Math.max(0, before * (next / old) - cursorY);

        const zoomInput = document.getElementById("timeline-zoom");
        if (zoomInput) zoomInput.value = String(next);   // 슬라이더와 값을 맞춘다

        renderTimelineView();
    }, { passive: false });

    const snap = document.getElementById("timeline-snap");
    snap?.addEventListener("change", (e) => {
        // 뷰 전용 상태다(§1-2-C). 전역 subdivisions를 건드리면 기존 노트의 시간이
        // 재해석되므로(updateNotesForTimeBasedChange) 격자만 바꾼다.
        const div = parseInt(e.target.value, 10) || 1;
        timelineState.snapDivisor = div;
        renderTimelineView();
    });

    // 노트 클릭 → 선택 / 빈 격자 클릭 → 삽입.
    // 위임이라 가상 스크롤로 행이 갈려도 계속 동작한다.
    container.addEventListener("click", (e) => {
        // 드래그가 막 끝난 직후의 click은 무시한다 — 옮긴 자리에 노트가 하나 더 생긴다.
        if (timelineDragMoved) {
            timelineDragMoved = false;
            return;
        }

        // 🔴 e.target을 그대로 믿으면 안 된다. pointerdown에서 컨테이너가 포인터를
        // 캡처하면(드래그가 재렌더에도 끊기지 않게 하는 장치) 뒤따르는 click의
        // 타깃이 **컨테이너로 바뀐다.** 그러면 .timeline-note를 못 찾아 노트를
        // 눌러도 인스펙터가 안 뜨고, 빈 격자 클릭으로 오인된다.
        // 좌표로 실제 엘리먼트를 다시 찾는다.
        const doc = container.ownerDocument;
        const hit = (e.target === container && doc.elementFromPoint)
            ? doc.elementFromPoint(e.clientX, e.clientY)
            : e.target;

        // 인스펙터 내부 클릭은 여기서 처리하지 않는다(자기 입력이 처리한다).
        if (hit?.closest?.("#timeline-inspector")) return;

        const marker = hit?.closest?.(".timeline-note");
        if (marker) {
            const index = parseInt(marker.dataset.noteIndex, 10);
            if (Number.isInteger(index)) {
                focusNoteAtIndex(index);
                // 타임라인 위에 겹쳐 띄운다 — 격자의 노트와 값을 함께 본다.
                openTimelineInspector(index);
            }
            return;
        }

        // 손잡이를 눌렀다 뗀 것(길이 변화 없음)은 삽입이 아니다.
        if (hit?.closest?.(".timeline-long-grip")) return;

        // 이벤트 마커 → 목록 팝업. 개별 항목은 거기서 다룬다(합의된 조작).
        const eventMarker = hit?.closest?.(".timeline-event");
        if (eventMarker) {
            openTimelineEventPopup(eventMarker, e.clientX, e.clientY);
            return;
        }

        // 🔴 **이벤트 열에서는 노트를 만들지 않는다.** 이 가드가 없으면 이벤트 열의
        // 빈 자리를 누를 때마다 노트가 생긴다 — 아래 삽입 과녁이 `.timeline-row`라
        // 이벤트 셀도 그 안에 있기 때문이다(D-035와 같은 함정).
        if (hit?.closest?.(".timeline-event-cell")) {
            closeTimelineInspector();
            return;
        }

        // 🔴 **좌클릭만 삽입한다.** 우클릭도 click 이벤트를 내는 경로가 있어
        // (button 0이 아닌 click), 이 가드가 없으면 우클릭 메뉴를 여는 동시에
        // 노트가 하나 생긴다(사용자 보고). 보조 버튼·수식키 클릭도 제외한다.
        if (e.button !== 0 || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;

        // 삽입 대상은 **행 영역**이다. 셀을 요구하지는 않되(행 라벨·셀 사이 여백에서도
        // 먹혀야 한다) 격자 안 아무데나로 넓히지는 않는다 — 스페이서·재생 헤드·
        // 차트 끝 핸들·스크롤바 옆까지 삽입 과녁이 되면 클릭마다 노트가 생긴다.
        const row = hit?.closest?.(".timeline-row");
        if (!row) return;

        closeTimelineInspector();
        insertNoteAtCell(e);
    });

    // 우클릭 → **만들 노트를 골라서** 삽입한다(사용자 요구).
    // 좌클릭 삽입(툴바의 "삽입" 타입)은 그대로 남긴다 — 같은 타입을 연달아 찍을 때
    // 매번 메뉴를 여는 것이 오히려 느리다. 둘은 보완 관계다.
    container.addEventListener("contextmenu", (e) => {
        const doc = container.ownerDocument;
        const hit = (e.target === container && doc.elementFromPoint)
            ? doc.elementFromPoint(e.clientX, e.clientY)
            : e.target;

        // 인스펙터 안에서는 브라우저 기본 메뉴를 살린다(입력값 복사·붙여넣기).
        if (hit?.closest?.("#timeline-inspector")) return;

        // 행 영역 밖(툴바·스페이서 여백 등)은 브라우저 기본 메뉴를 살린다.
        // 셀은 요구하지 않는다 — 좌클릭 삽입과 **같은 기준**을 쓴다.
        if (!hit?.closest?.(".timeline-row")) return;

        e.preventDefault();

        // 이미 있는 노트를 우클릭하면 삽입이 아니라 **그 노트를 다룬다.**
        const marker = hit?.closest?.(".timeline-note");
        if (marker) {
            const index = parseInt(marker.dataset.noteIndex, 10);
            if (Number.isInteger(index)) showTimelineNoteMenu(e.clientX, e.clientY, index);
            return;
        }

        // 이벤트 열에서는 노트 삽입 메뉴를 내지 않는다 — 좌클릭 삽입을 막은 것과
        // 같은 이유다(이벤트 생성은 이벤트 리스트 탭이 맡는다).
        if (hit?.closest?.(".timeline-event-cell")) return;

        showTimelineInsertMenu(e.clientX, e.clientY, e);
    });

    // 드래그 이동(병목 2). 스냅은 항상 걸린다(§1-2-D) — 실수로 박자를 깨뜨릴 수 없게.
    container.addEventListener("pointerdown", onTimelinePointerDown);
}

// 격자 우클릭 → 만들 노트 종류를 고른다.
//
// 🔴 **클릭 좌표를 사본으로 보존한다.** 메뉴를 여는 사이 스크롤이 일어나도 처음 누른
// 자리에 생겨야 한다 — 메뉴 클릭 시점의 좌표를 쓰면 엉뚱한 박자에 찍힌다.
function showTimelineInsertMenu(clientX, clientY, e) {
    // 좌표만 담은 사본. 원본 이벤트는 메뉴가 열리는 동안 재사용할 수 없다.
    const at = { clientY: e.clientY, clientX: e.clientX };

    const items = TIMELINE_INSERT_TYPES.map(t => ({
        label: t.label,
        onClick: () => insertNoteAtCell(at, t.type)
    }));

    showContextMenu(clientX, clientY, items);
}

// 우클릭으로 만들 수 있는 노트 종류. 툴바의 "삽입" 선택기보다 넓다 —
// 롱노트 계열을 여기서 바로 만들 수 있게 했다(표에서 Type을 바꾸지 않아도 되게).
const TIMELINE_INSERT_TYPES = [
    { type: "tab", label: "Tab 삽입" },
    { type: "longtab", label: "Long Tab 삽입" },
    { type: "direction", label: "Direction 삽입" },
    { type: "longdirection", label: "Long Direction 삽입" },
    { type: "both", label: "Both 삽입" },
    { type: "longboth", label: "Long Both 삽입" },
    { type: "node", label: "Node 삽입" }
];

// 기존 노트 우클릭 → 그 노트에 대한 동작.
function showTimelineNoteMenu(clientX, clientY, index) {
    const note = notes[index];
    if (!note) return;

    const items = [
        { label: "인스펙터 열기", onClick: () => { selectTimelineNote(index); openTimelineInspector(index); } }
    ];

    // 시작 노트는 삭제가 막혀 있다(B-7) — 메뉴에도 내지 않는다.
    if (!isChartStartNote(note, index)) {
        items.push({ separator: true });
        items.push({
            label: "삭제",
            onClick: () => {
                selectTimelineNote(index);
                deleteSelectedNotes();
            }
        });
    }

    showContextMenu(clientX, clientY, items);
}

// ── 타임라인 입력 (Phase 1-3) ────────────────────────────────────────────

// 클릭 좌표 → 스냅된 절대 박자. 삽입·드래그가 공유한다.
function timelineAbsoluteBeatFromEvent(container, clientY, subdivisions) {
    const beatsPerRow = beatsPerRowOf(timelineState, subdivisions);
    const rowHeight = timelineState.rowHeight || 24;

    // 컨테이너 기준 오프셋 + 스크롤 = 콘텐츠 좌표. getBoundingClientRect를 쓰는 이유는
    // 팝아웃 창에서도 그 문서 기준으로 올바른 값이 나오기 때문이다.
    const rect = container.getBoundingClientRect();
    const contentY = clientY - rect.top + container.scrollTop;

    const row = Math.floor(contentY / rowHeight);
    const fraction = (contentY - row * rowHeight) / rowHeight;

    const raw = rowToBeat(row, fraction, beatsPerRow);
    return snapBeat(raw, timelineSnapBeats(subdivisions));
}

// 스냅 한 칸의 크기(기준 subdivisions 단위). 1/4이면 4분음표 = subdivisions/4.
function timelineSnapBeats(subdivisions) {
    const divisor = timelineState.snapDivisor || 4;
    return Math.max(1, Math.round(subdivisions / divisor));
}

// 빈 격자 클릭 → 그 자리에 노트를 만든다. 병목 1의 해소 지점 —
// "근처 노트 선택 → q → Beat 셀 입력" 3단계가 1클릭이 된다.
// 커서가 가리키는 자리에 노트를 만든다.
//
// 🔴 **빈 셀을 요구하지 않는다**(사용자 요구). 예전에는 `.timeline-cell`을 못 찾으면
// 조용히 아무 일도 하지 않아, 행 라벨 위·셀 사이 여백·스크롤바 옆을 누르면 먹히지
// 않았다. 지금은 **커서 좌표만으로** 박자와 열을 정한다 — 격자 안이면 항상 생긴다.
//
// `explicitType`을 주면 툴바의 "삽입" 선택기 대신 그 타입으로 만든다
// (우클릭 메뉴가 이 경로를 쓴다 — 툴바를 먼저 바꿀 필요가 없게 한다).
function insertNoteAtCell(e, explicitType) {
    const container = document.getElementById("timeline-grid");
    if (!container) return;

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    const absoluteBeat = timelineAbsoluteBeatFromEvent(container, e.clientY, subdivisions);
    if (absoluteBeat < 0) return;

    const absoluteBeats = calculateAbsoluteBeats(notes, bpm, subdivisions);
    const insertionIndex = findInsertionIndex(absoluteBeats, notes, absoluteBeat);

    // 삽입 지점이 속한 구간의 오프셋을 빼야 노트에 저장할 지역 beat가 나온다.
    // 앞 노트가 아니라 **뒤 노트**의 구간을 따라야 한다 — 이유는 모델의
    // sectionOffsetForInsertion 주석 참조(틀리면 구간 하나만큼 어긋난다).
    const offsets = calculateSectionOffsetBeats(notes, bpm, subdivisions);
    const sectionOffset = sectionOffsetForInsertion(offsets, insertionIndex, notes.length);

    const prev = insertionIndex > 0 ? notes[insertionIndex - 1] : null;
    const noteSubdivisions = prev?.subdivisions || subdivisions;

    const { beat, exact } = absoluteBeatToNoteBeat(
        absoluteBeat, sectionOffset, noteSubdivisions, subdivisions
    );
    if (!exact) {
        // §1-2-D: 정수 beat를 유지하되 표현 불가는 알린다. 더 세밀한 위치가 필요하면
        // subdivisions를 올리는 것이 이 에디터의 정규 경로다.
        console.warn(
            `타임라인 삽입: beat ${beat}로 반올림했다(subdivisions ${noteSubdivisions}에서 정확히 표현 불가).`
        );
    }

    // 타입 결정 순서: ① 우클릭 메뉴로 명시한 것 → ② **커서가 가리킨 열** →
    // ③ 툴바의 "삽입" 선택기.
    //
    // ②를 넣은 이유: 커서 위치에서 작동해야 한다는 요구는 세로(박자)뿐 아니라
    // 가로(열)에도 적용된다. Both 열을 눌렀는데 Tab이 생기면 놀란다.
    // 좌우 드래그가 열↔타입을 매핑하는 것과 같은 규칙을 쓴다.
    const column = timelineColumnAtClientX(container, e.clientX);
    const columnType = column !== null ? timelineTypeForColumn(column, false) : null;

    const type = explicitType
        || columnType
        || document.getElementById("timeline-insert-type")?.value
        || "tab";

    // 롱노트 계열은 길이가 0이면 롱이 아니다. 기본 한 칸(스냅 단위)을 준다 —
    // applyNoteTypeChange가 쓰는 규칙과 같은 취지다.
    //
    // 🔴 `longTime`은 **그 노트 자신의 subdivisions 단위**다(CONTRACT-NOTES §2).
    // 여기서 `noteSubdivisions`를 쓰는 것이 맞는 이유: addNote가 명시 beat 경로에서
    // `inheritedSubdivisions = prev?.subdivisions || subdivisions`로 **같은 값**을
    // 노트에 넣는다. 둘이 갈리면 12분박 구간에서 길이가 어긋난다.
    const isLong = type.startsWith("long");
    const longTime = isLong ? timelineSnapBeats(noteSubdivisions) : 0;

    // addNote가 saveState()·저장·재렌더까지 처리한다. 여기서 notes를 직접 만지지 않는다.
    addNote({ type, isLong, longTime, _explicitBeat: beat, _insertionIndex: insertionIndex });

    // 종료 노트보다 뒤에 찍었으면 끝을 뒤로 민다 — 차트가 노트 위에서 끊기지 않게.
    if (ensureChartEndNote()) {
        invalidatePathCache();
        saveToStorage();
        renderNoteList();
    }
}

// 좌우 드래그로 바꿀 수 있는 타입의 순서. 타임라인 **열 순서와 같아야 한다**
// (timeline.js의 COLUMNS) — 노트가 열을 건너 이동하는 것처럼 보여야 하기 때문이다.
// 롱노트 여부는 유지한다: longtab을 오른쪽으로 끌면 longdirection이 된다.
const TIMELINE_TYPE_COLUMNS = [
    { short: "tab", long: "longtab" },
    { short: "direction", long: "longdirection" },
    { short: "both", long: "longboth" },
    { short: "node", long: null }        // Node에는 롱 변형이 없다
];

function timelineColumnOfType(type) {
    const i = TIMELINE_TYPE_COLUMNS.findIndex(c => c.short === type || c.long === type);
    return i < 0 ? 0 : i;
}

// 열 인덱스 → 타입. 롱노트면 롱 변형을 고르되, 없으면(Node) 짧은 쪽으로 떨어진다.
function timelineTypeForColumn(columnIndex, wasLong) {
    const col = TIMELINE_TYPE_COLUMNS[columnIndex];
    if (!col) return null;
    return (wasLong && col.long) ? col.long : col.short;
}

// 화면 X좌표가 어느 노트 열 위인지 판정한다. 못 찾으면 null.
//
// elementFromPoint를 쓰지 않는 이유: 포인터 캡처 중에는 이벤트 타깃이 컨테이너로
// 고정되고, 마커 위를 지날 때는 셀 대신 마커가 잡힌다. 대신 **행 하나의 셀 폭을
// 재서** 계산한다 — 모든 행이 같은 flex 레이아웃이라 어느 행을 재도 결과가 같다.
function timelineColumnAtClientX(container, clientX) {
    // 🔴 **이벤트 열을 제외한다.** 이벤트 셀도 `.timeline-cell`이라 그대로 훑으면
    // 인덱스 4가 나와 `timelineTypeForColumn`이 null을 내거나(삽입 폴백으로 샘)
    // 아래 "가장 가까운 끝 열" 폴백이 노트를 **이벤트 열로 끌고 간다.**
    // 노트 타입 매핑에 참여하는 것은 `COLUMNS`뿐이다.
    const cells = container.querySelector(".timeline-row")
        ?.querySelectorAll(".timeline-cell:not(.timeline-event-cell)");
    if (!cells || cells.length === 0) return null;

    for (let i = 0; i < cells.length; i++) {
        const rect = cells[i].getBoundingClientRect();
        if (clientX >= rect.left && clientX < rect.right) return i;
    }

    // 격자 밖으로 나갔으면 가장 가까운 끝 열로 붙인다(드래그가 끊기지 않게).
    const first = cells[0].getBoundingClientRect();
    if (clientX < first.left) return 0;
    return cells.length - 1;
}

// ── 롱노트 길이 조절 (꼬리 끝 손잡이 드래그) ────────────────────────────
//
// `longTime`은 **그 노트 자신의 subdivisions 단위**다(내부 shape 기준.
// JSON은 초라서 다르다 — CONTRACT-NOTES.md §2). 타임라인의 절대 박자는 기준
// subdivisions 단위이므로 그대로 넣으면 12분박 노트에서 길이가 어긋난다.
function onLongGripPointerDown(e, index) {
    if (!Number.isInteger(index) || !notes[index]) return;

    const container = document.getElementById("timeline-grid");
    if (!container) return;

    e.preventDefault();
    e.stopPropagation();

    longGripDrag = { index, saved: false };

    // 노트 드래그와 같은 이유로 컨테이너에 건다 — 재렌더에도 살아남아야 한다.
    container.setPointerCapture?.(e.pointerId);
    container.addEventListener("pointermove", onLongGripPointerMove);
    container.addEventListener("pointerup", onLongGripPointerUp, { once: true });
    container.addEventListener("pointercancel", onLongGripPointerUp, { once: true });
}

function onLongGripPointerMove(e) {
    if (!longGripDrag) return;

    const container = document.getElementById("timeline-grid");
    if (!container) return;

    const note = notes[longGripDrag.index];
    if (!note) return;

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    // 커서 위치(절대 박자, 기준 단위) − 노트 시작 = 길이.
    const absoluteBeats = calculateAbsoluteBeats(notes, bpm, subdivisions);
    const startBeat = absoluteBeats[longGripDrag.index];
    const cursorBeat = timelineAbsoluteBeatFromEvent(container, e.clientY, subdivisions);

    // 기준 단위 길이 → 노트 자신의 subdivisions 단위로 환산한다.
    const noteSubdivisions = note.subdivisions || subdivisions;
    const rawLength = (cursorBeat - startBeat) * (noteSubdivisions / subdivisions);
    // 0 이하로는 못 줄인다 — 길이 0은 롱노트가 아니다. 최소 1칸을 남긴다.
    const nextLong = Math.max(1, Math.round(rawLength));

    if (nextLong === note.longTime) return;

    if (!longGripDrag.saved) {
        saveState();
        longGripDrag.saved = true;
    }

    note.longTime = nextLong;
    // 놓은 직후의 click을 삼킨다 — 안 그러면 그 자리에 노트가 하나 생긴다
    // (손잡이는 .timeline-note가 아니라서 클릭 핸들러의 삽입 분기로 떨어진다).
    timelineDragMoved = true;

    invalidatePathCache();
    drawPath();
    renderTimelineView();   // 드래그 중에는 타임라인만. 표는 놓을 때 한 번.
}

function onLongGripPointerUp() {
    document.getElementById("timeline-grid")
        ?.removeEventListener("pointermove", onLongGripPointerMove);

    if (!longGripDrag) return;
    const saved = longGripDrag.saved;
    const index = longGripDrag.index;
    longGripDrag = null;

    if (saved) {
        // 길이를 늘여 차트 끝을 넘었으면 종료 노트를 뒤로 민다.
        ensureChartEndNote();
        saveToStorage();
        renderNoteList();
        // 인스펙터가 열려 있으면 Length 값이 낡았으므로 다시 그린다.
        // removed
    }
}

// 누른 노트를 **즉시** 선택 표시한다(pointerdown 경로).
//
// `focusNoteAtIndex`를 쓰지 않는 이유: 그쪽은 경로 재계산·Unity 좌표 갱신·표 전체
// 재렌더까지 한다. 드래그를 시작할 때마다 그 비용을 내면 첫 이동이 눈에 띄게 늦다.
// 여기서는 **선택 상태와 타임라인 하이라이트만** 갱신하고, 나머지는 click이
// (드래그가 아니었다면) 또는 pointerup의 renderNoteList()가 맡는다.
function selectTimelineNote(index) {
    if (selectedNoteIndex === index) return;   // 이미 빛나고 있으면 할 일이 없다

    selectedNoteIndex = index;
    // 표의 다중 선택과 어긋나지 않게 단일 선택으로 맞춘다(표 클릭과 같은 처우).
    selectedNoteIndices.clear();
    selectedNoteIndices.add(index);

    renderTimelineView();                      // 격자만 다시 그린다(표는 무겁다)
}

// ── 이벤트 열 드래그 (시간 이동만) ──────────────────────────────────────
//
// 합의된 조작은 **드래그로 시간 이동만**이다. 종류·파라미터 편집은 이벤트 리스트
// 탭이 맡는다. 그룹(같은 시각)은 **통째로** 움직인다 — 개별 분리는 목록 팝업에서.
//
// 🔴 `shared/events.js`는 Unity 계약이라 수정하지 않는다(D-001). `updateEvent`를
// 호출만 한다.
function onTimelineEventPointerDown(e, marker) {
    const indices = parseEventIndices(marker);
    if (indices.length === 0) return;

    const container = document.getElementById("timeline-grid");
    if (!container) return;

    e.preventDefault();
    e.stopPropagation();

    // 노트 드래그와 같은 이유로 플래그를 **가드보다 먼저** 내린다(D-029) —
    // 이전 드래그의 true가 남으면 뒤따르는 click이 통째로 삼켜져 팝업이 안 뜬다.
    timelineDragMoved = false;

    const events = getAllEvents();
    timelineEventDrag = {
        indices,
        // 시작 시점의 eventTime을 그대로 기억한다. 그룹은 **같은 시각**이므로
        // 하나만 기억하면 되지만, 안전하게 인덱스별로 담는다.
        startTimes: indices.map(i => events[i]?.eventTime ?? 0),
        saved: false,
        pointerId: e.pointerId
    };

    // 캡처·리스너는 **컨테이너**에 건다 — 재렌더로 마커 노드가 사라져도
    // 드래그가 끊기지 않게(노트 드래그와 같은 이유). 캡처는 실제 이동까지 미룬다.
    container.addEventListener("pointermove", onTimelineEventPointerMove);
    container.addEventListener("pointerup", onTimelineEventPointerUp, { once: true });
    container.addEventListener("pointercancel", onTimelineEventPointerUp, { once: true });
}

function onTimelineEventPointerMove(e) {
    if (!timelineEventDrag) return;

    const container = document.getElementById("timeline-grid");
    if (!container) return;

    if (!timelineEventDrag.captured) {
        container.setPointerCapture?.(timelineEventDrag.pointerId);
        timelineEventDrag.captured = true;
    }

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    // 커서 → 스냅된 절대 박자 → 다시 **eventTime 축(preDelay 후)**으로 되돌린다.
    const absoluteBeat = timelineAbsoluteBeatFromEvent(container, e.clientY, subdivisions);
    if (absoluteBeat < 0) return;

    const nextTime = absoluteBeatToEventTime(
        absoluteBeat, getPreDelaySeconds(), bpm, subdivisions
    );

    const events = getAllEvents();
    const current = events[timelineEventDrag.indices[0]]?.eventTime;
    if (current === undefined || Math.abs(current - nextTime) < 1e-9) return;

    // undo 스냅샷은 **첫 이동에서 한 번만** — 매 프레임 남기면 히스토리가 잠긴다
    // (노트 드래그와 같은 관행).
    if (!timelineEventDrag.saved) {
        saveState();
        timelineEventDrag.saved = true;
    }

    // 그룹은 같은 시각이므로 전부 같은 값으로 옮긴다.
    for (const i of timelineEventDrag.indices) {
        const ev = events[i];
        if (!ev) continue;
        updateEvent(i, { ...ev, eventTime: nextTime });
    }

    // 놓은 직후의 click이 팝업을 열지 않게 삼킨다.
    timelineDragMoved = true;

    renderTimelineView();   // 드래그 중에는 타임라인만. 이벤트 리스트는 놓을 때.
}

function onTimelineEventPointerUp() {
    document.getElementById("timeline-grid")
        ?.removeEventListener("pointermove", onTimelineEventPointerMove);

    if (!timelineEventDrag) return;
    const saved = timelineEventDrag.saved;
    timelineEventDrag = null;

    if (saved) {
        saveToStorage();
        // 이벤트 리스트 탭의 Time 값이 낡았으므로 함께 갱신한다.
        scheduleRender({ eventList: true });
    }
}

// 이벤트 마커 클릭 → **목록 팝업**. 개별 항목을 여기서 다룬다(합의된 조작).
//
// 전체 편집(종류·파라미터)은 **이벤트 리스트 탭**이 맡는다 — 여기서는 무엇이 있는지
// 보여주고, 그룹에서 **하나만 떼어내는** 일만 한다. 겹친 이벤트는 격자에서 개별로
// 집을 수 없기 때문에 이 경로가 필요하다.
function openTimelineEventPopup(marker, clientX, clientY) {
    const indices = parseEventIndices(marker);
    if (indices.length === 0) return;

    // 이벤트가 하나뿐이면 **팝업을 거치지 않는다.** 고를 것이 없는데 메뉴를 띄우면
    // 클릭이 두 번 필요해진다 — 요구는 "클릭하면 그 위치로 이동"이다.
    // 그룹일 때만 무엇을 고를지 물어본다.
    if (indices.length === 1) {
        focusEventInList(indices[0]);
        return;
    }

    const events = getAllEvents();
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    const items = [];

    for (const i of indices) {
        const ev = events[i];
        if (!ev) continue;

        const id = ev.eventId ? `-${ev.eventId}` : "";
        items.push({
            label: `${ev.eventType || "?"}${id} · ${ev.eventTime}s`,
            onClick: () => {
                // 이벤트 리스트 탭으로 보내 편집하게 한다 — 전체 편집은 그쪽 몫이다.
                focusEventInList(i);
            }
        });
    }

    // 그룹일 때만 분리를 낸다 — 하나뿐이면 뗄 것이 없다.
    if (indices.length > 1) {
        items.push({ separator: true });

        for (const i of indices) {
            const ev = events[i];
            if (!ev) continue;

            const id = ev.eventId ? `-${ev.eventId}` : "";
            items.push({
                label: `↧ ${ev.eventType || "?"}${id} 를 한 칸 뒤로 분리`,
                onClick: () => {
                    saveState();
                    // 스냅 한 칸만큼 뒤로 민다 — 겹침이 풀려 개별로 집을 수 있게 된다.
                    // 박자 축에서 한 칸을 재고 다시 eventTime 축으로 되돌린다.
                    const snap = timelineSnapBeats(subdivisions);
                    const beat = eventTimeToAbsoluteBeat(
                        ev.eventTime, getPreDelaySeconds(), bpm, subdivisions
                    );
                    const moved = absoluteBeatToEventTime(
                        beat + snap, getPreDelaySeconds(), bpm, subdivisions
                    );
                    updateEvent(i, { ...ev, eventTime: moved });

                    saveToStorage();
                    renderTimelineView();
                    scheduleRender({ eventList: true });
                }
            });
        }
    }

    showContextMenu(clientX, clientY, items);
}

// 이벤트 리스트 탭을 열고 해당 이벤트를 **선택·강조하고 캔버스를 그 자리로 옮긴다.**
// 전체 편집은 이벤트 리스트가 맡는다는 합의에 따른 연결 지점이다.
//
// 이벤트 리스트에서 항목을 클릭했을 때와 **같은 결과**여야 한다(`handleEventClick`의
// 단순 클릭 분기) — 들어온 경로가 격자냐 목록이냐에 따라 동작이 갈리면 안 된다.
// 그래서 `lastClickedEventIndex`까지 함께 맞춘다(다음 Shift+클릭의 기준점이다).
function focusEventInList(index) {
    // 🔴 **선택은 switchTab 뒤에 한다.** `switchTab("events")`는 진입할 때
    // `selectedNoteIndices`를 비우고 `renderEventList()`(디바운스 예약)를 부른다.
    // 선택을 앞에 두면 그 예약 렌더가 우리가 맞춘 스크롤 위치를 덮을 수 있다.
    switchTab("events");

    selectedEventIndices.clear();
    selectedEventIndices.add(index);
    lastClickedEventIndex = index;

    // 🔴 **즉시 그린다.** `scheduleRender`/`renderEventList`는 rAF 뒤라, 예약만 하고
    // 아래에서 DOM을 찾으면 **아직 옛 목록**이다(선택 클래스가 붙지 않는다).
    renderEventListImmediate();

    // 🔴 **선택 항목이 화면 밖이면 강조가 보이지 않는다.** 이벤트가 100개를 넘으면
    // 가상 스크롤이 켜져(`virtualScrollState.event`) 보이는 범위만 DOM에 존재하므로,
    // 스크롤을 맞추지 않으면 "강조가 안 된다"로 보인다.
    scrollEventListToIndex(index);

    // 캔버스를 이벤트 위치로 옮긴다(사용자 요구 1). 이 함수가 중앙 이동 + 2초 강조를
    // 이미 담당한다 — 목록 클릭도 같은 함수를 쓰므로 두 경로의 동작이 같아진다.
    focusEventAtIndex(index);
}

// 이벤트 목록을 해당 인덱스가 보이도록 스크롤한다.
//
// 가상 스크롤은 **보이는 범위만 DOM에 넣으므로** 화면 밖 항목은 `querySelector`로
// 찾을 수 없다. 그래서 DOM을 찾기 전에 **먼저 스크롤 위치를 계산해 대입**하고,
// 다시 그린 뒤에 `scrollIntoView`로 다듬는다(항목마다 높이가 달라 계산값은 근사다).
function scrollEventListToIndex(index) {
    const container = document.getElementById("event-list");
    if (!container) return;

    const state = virtualScrollState.event;
    const virtualized = state.enabled && getAllEvents().length > 100;

    if (virtualized) {
        // 목표 항목을 화면 중앙쯤에 두는 scrollTop. 높이는 평균값이라 근사지만,
        // 이 값이면 목표가 **렌더 범위 안에 들어와** DOM에 존재하게 된다.
        const itemHeight = state.itemHeight || 120;
        const target = index * itemHeight - container.clientHeight / 2 + itemHeight / 2;
        container.scrollTop = Math.max(0, target);

        // 새 scrollTop 기준으로 다시 그려야 목표 항목이 실제로 DOM에 들어온다.
        renderEventListImmediate();
    }

    const item = container.querySelector(`[data-event-index="${index}"]`);
    if (item) item.scrollIntoView({ block: "nearest" });
}

// 마커에 실린 인덱스 목록을 읽는다. 그룹이면 여럿이다.
function parseEventIndices(marker) {
    return String(marker?.dataset?.eventIndices || "")
        .split(",")
        .map(s => parseInt(s, 10))
        .filter(Number.isInteger);
}

// 드래그 이동. pointer 이벤트를 쓰는 이유는 캡처(setPointerCapture)로
// 컨테이너 밖으로 나가도 이동이 이어지기 때문이다.
function onTimelinePointerDown(e) {
    // 길이 조절 손잡이가 먼저다 — 노트 드래그보다 우선한다.
    const grip = e.target.closest?.(".timeline-long-grip");
    if (grip) {
        onLongGripPointerDown(e, parseInt(grip.dataset.noteIndex, 10));
        return;
    }

    // 이벤트 마커 드래그(시간 이동). 노트 드래그와 배타적이다.
    const eventMarker = e.target.closest?.(".timeline-event");
    if (eventMarker) {
        onTimelineEventPointerDown(e, eventMarker);
        return;
    }

    const marker = e.target.closest?.(".timeline-note");
    if (!marker) return;

    const index = parseInt(marker.dataset.noteIndex, 10);
    if (!Number.isInteger(index) || !notes[index]) return;

    // 🔴 **드래그를 막는 노트라도 이 플래그는 반드시 내려야 한다.**
    // click 핸들러가 timelineDragMoved를 보고 "방금 드래그가 끝났다"고 판단해
    // 클릭을 삼키는데, 여기서 그냥 return하면 **이전 드래그의 true가 남아 있다.**
    // 그러면 시작 노트나 beatReset 노트를 눌렀을 때 클릭이 통째로 먹혀
    // **인스펙터가 뜨지 않는다**(실제로 보고된 증상).
    timelineDragMoved = false;

    // 🔴 **선택은 여기서(pointerdown) 한다.** click은 드래그가 **끝난 뒤에** 오므로
    // 거기서만 선택하면 드래그하는 내내 **직전에 고른 노트가 빛난다**(보고된 증상).
    // 아래 가드(시작 노트·beatReset)보다 **앞**이다 — 옮길 수 없는 노트도 누르면
    // 즉시 빛나야 한다. 선택과 이동 가능 여부는 별개다.
    //
    // click 핸들러도 계속 focusNoteAtIndex를 부른다(인스펙터를 함께 여는 경로라
    // 지울 수 없다). 같은 인덱스를 두 번 고르는 것은 무해하다.
    selectTimelineNote(index);

    // 시작 노트는 드래그로 옮기지 않는다 — 기존 표에서도 삭제가 막혀 있는 보호 대상이다(B-7).
    if (isChartStartNote(notes[index], index)) return;

    // beatReset 노트는 **구간의 길이 그 자체**다. 옮기면 뒤따르는 모든 구간이
    // 통째로 밀린다 — 드래그 한 번으로 차트 절반이 이동하는 셈이라 막는다.
    // 이 값은 인스펙터나 표의 Beat 셀에서 의도적으로 고치는 것이 맞다.
    if (notes[index].beatReset) {
        console.warn("beatReset 노트는 타임라인 드래그로 옮기지 않는다(뒤 구간이 전부 밀린다). 인스펙터/표에서 편집할 것.");
        return;
    }

    const container = document.getElementById("timeline-grid");
    if (!container) return;

    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const startBeat = timelineAbsoluteBeatFromEvent(container, e.clientY, subdivisions);

    timelineDrag = {
        index,
        startBeat,
        lastBeat: startBeat,
        saved: false,
        // 좌우 드래그로 타입을 바꾸기 위한 기준점. 시작 시점의 열과 X를 기억한다.
        startX: e.clientX,
        startColumn: timelineColumnOfType(notes[index].type),
        lastColumn: timelineColumnOfType(notes[index].type)
    };
    timelineDragMoved = false;
    timelineDrag.pointerId = e.pointerId;

    // 🔴 포인터 캡처와 리스너는 **컨테이너**에 건다. 마커에 걸면 안 된다 —
    // 이동할 때마다 격자를 다시 그려 **마커 노드가 사라지고**, 그 순간 캡처와
    // 리스너가 함께 날아가 드래그가 한 칸 만에 멈춘다.
    //
    // 다만 **캡처는 실제로 움직일 때까지 미룬다**(onTimelinePointerMove가 건다).
    // 여기서 바로 캡처하면 그냥 클릭했을 때도 뒤따르는 click의 타깃이 컨테이너로
    // 바뀌어, 노트를 눌러도 인스펙터가 안 뜬다.
    container.addEventListener("pointermove", onTimelinePointerMove);
    container.addEventListener("pointerup", onTimelinePointerUp, { once: true });
    container.addEventListener("pointercancel", onTimelinePointerUp, { once: true });
}

function onTimelinePointerMove(e) {
    if (!timelineDrag) return;

    const container = document.getElementById("timeline-grid");
    if (!container) return;

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    const note = notes[timelineDrag.index];
    if (!note) return;

    const absoluteBeat = timelineAbsoluteBeatFromEvent(container, e.clientY, subdivisions);
    // 좌우 위치 → 열. 커서가 실제로 어느 열 위에 있는지로 판정한다.
    const targetColumn = timelineColumnAtClientX(container, e.clientX);

    const beatChanged = absoluteBeat !== timelineDrag.lastBeat;
    const columnChanged = targetColumn !== null && targetColumn !== timelineDrag.lastColumn;
    if (!beatChanged && !columnChanged) return;   // 같은 칸이면 할 일이 없다

    // 첫 이동에서 한 번만 undo 스냅샷을 남긴다. 매 프레임 남기면 히스토리가 잠긴다.
    if (!timelineDrag.saved) {
        saveState();
        timelineDrag.saved = true;
    }

    // 실제로 움직인 시점에 캡처한다(pointerdown에서 하면 단순 클릭까지 리타깃된다).
    if (!timelineDrag.captured) {
        container.setPointerCapture?.(timelineDrag.pointerId);
        timelineDrag.captured = true;
    }

    if (beatChanged) {
        // 🔴 **드래그는 자기 구간 안에서만 움직인다.** `sectionIndex`가 배열 순서에서
        // 파생되는데(recomputeSectionIndices) 드래그는 배열을 재배치하지 않으므로,
        // 경계를 넘으면 지역 beat만 새 구간 기준이 되고 오프셋은 옛 구간이라
        // **노트가 옛 구간의 처음으로 되돌아간다**(clampBeatToSection 주석 참조).
        // beatReset 드래그·시작 노트 드래그와 같은 처우로 막는다.
        const offsets = calculateSectionOffsetBeats(notes, bpm, subdivisions);
        const sectionOffset = offsets[timelineDrag.index] || 0;
        const starts = sectionStartBeats(notes, bpm, subdivisions);
        const clamped = clampBeatToSection(absoluteBeat, sectionOffset, starts);
        const noteSubdivisions = note.subdivisions || subdivisions;

        const { beat } = absoluteBeatToNoteBeat(
            clamped, sectionOffset, noteSubdivisions, subdivisions
        );
        if (beat >= 0) {
            note.beat = beat;              // beat는 항상 정수다(§5 검증 7-1)
            timelineDrag.lastBeat = absoluteBeat;
            reinheritDraggedTabBpm(note, timelineDrag.index, clamped, bpm, subdivisions);
        }
    }

    if (columnChanged) {
        // 좌우 드래그 = 타입 변경. 롱노트 여부는 유지한다(longtab → longdirection).
        // 부수 필드 규칙은 표의 Type 드롭다운과 **같은 함수**를 쓴다.
        const nextType = timelineTypeForColumn(targetColumn, note.isLong);
        if (nextType && nextType !== note.type) {
            applyNoteTypeChange(note, nextType);
        }
        timelineDrag.lastColumn = targetColumn;
    }

    timelineDragMoved = true;

    invalidatePathCache();
    drawPath();
    // 드래그 중에는 타임라인만 다시 그린다. renderNoteList()는 14열 표까지 새로
    // 만들어 무겁고, 표에 입력 중이던 값도 날린다. 표 갱신은 드래그가 끝날 때 한 번.
    renderTimelineView();

    // 값 툴팁. invalidatePathCache() **뒤에** 불러야 구간 오프셋이 새 위치 기준으로
    // 다시 계산된다 — 앞에서 부르면 한 프레임 낡은 시간을 보여준다.
    showTimelineDragTooltip(timelineDrag.index, e.clientX, e.clientY);
}

// 노트 타입을 바꿀 때 따라와야 하는 부수 필드를 한 곳에서 처리한다.
//
// 표의 Type 드롭다운·타임라인 좌우 드래그·인스펙터가 **모두 이 함수를 부른다.**
// 규칙(isLong·longTime 기본값·direction 유무·wait·beatReset)이 흩어지면 어느
// 경로로 바꿨느냐에 따라 노트 모양이 갈린다.
//
// 노트 하나만 고친다. 히스토리·저장·렌더는 호출부가 맡는다.
function applyNoteTypeChange(n, newType) {
    n.type = newType;

    n.isLong = ["longtab", "longdirection", "longboth"].includes(newType);

    // 롱노트로 바뀌었는데 길이가 없으면 1박을 준다(표의 동작과 같다).
    if (n.isLong && !n.longTime) {
        n.longTime = parseInt(document.getElementById("subdivisions").value || 16);
    }

    if (["direction", "longdirection", "both", "longboth", "node"].includes(newType)) {
        if (!n.direction) n.direction = "none";
    } else {
        // 방향이 필요 없는 타입이면 필드를 지운다.
        delete n.direction;
    }

    if (newType === "node" && !n.hasOwnProperty("wait")) n.wait = false;
    if (!n.hasOwnProperty("beatReset")) n.beatReset = false;
}

// ── 차트 종료 노트 (direction/none) ──────────────────────────────────────
//
// 계약상 차트는 `direction`/`none` 노트로 끝나야 한다(`ensureFinalDirectionNote`).
// 그 계약 함수는 **저장·불러오기 시점에만** 불렸다 — 편집 중에는 종료 노트가 없거나,
// 뒤에 노트를 더 찍으면 종료 노트가 그 앞에 남아 **차트가 노트 위에서 끊긴다.**
//
// 여기서는 편집 중에도 종료 노트를 유지하고, 타임라인 최하단의 **핸들**로 직접
// 끌 수 있게 한다. `shared/`는 호출만 하고 수정하지 않는다(D-001).

// 종료 노트를 보장한다. 없으면 계약 함수로 만들고, 마지막 노트보다 앞이면 뒤로 민다.
// 반환: 무언가 바꿨으면 true.
function ensureChartEndNote() {
    if (notes.length === 0) return false;

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    const absoluteBeats = calculateAbsoluteBeats(notes, bpm, subdivisions);
    let endIndex = findChartEndNoteIndex(notes, absoluteBeats);

    // 시작 노트가 유일한 direction/none이면 종료 노트로 쓰지 않는다
    // (노트 1개짜리 차트에서 시작=끝이 되는 것을 막는다).
    if (endIndex >= 0 && isChartStartNote(notes[endIndex], endIndex)) endIndex = -1;

    if (endIndex < 0) {
        // 계약 함수가 "마지막 노트 + 3초(롱노트면 1초)" 규칙으로 만들어 준다.
        // 그 간격 규칙을 여기서 다시 쓰지 않는 이유는 계약과 갈리지 않게 하기 위함이다.
        ensureFinalDirectionNote(notes, bpm, subdivisions);
        return true;
    }

    // 이미 있으면 위치만 확인한다. 뒤에 노트를 더 찍었으면 종료 노트를 뒤로 민다.
    const minBeat = minimumChartEndBeat(notes, absoluteBeats, endIndex, subdivisions);
    if (absoluteBeats[endIndex] >= minBeat) return false;

    const offsets = calculateSectionOffsetBeats(notes, bpm, subdivisions);
    const note = notes[endIndex];
    const noteSubdivisions = note.subdivisions || subdivisions;
    // 마지막 노트에 딱 붙이지 않고 한 마디 뒤에 둔다 — 계약의 "3초 뒤" 취지를
    // 박자 축에서 근사한 것이다(BPM이 바뀌어도 화면에서 일정하다).
    const { beat } = absoluteBeatToNoteBeat(
        minBeat + subdivisions * 4, offsets[endIndex] || 0, noteSubdivisions, subdivisions
    );

    if (beat === note.beat) return false;
    note.beat = beat;
    return true;
}

// 종료 노트 핸들 드래그. 마지막 노트보다 앞으로는 갈 수 없다.
function onChartEndHandlePointerDown(e) {
    const container = document.getElementById("timeline-grid");
    if (!container) return;

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    const absoluteBeats = calculateAbsoluteBeats(notes, bpm, subdivisions);
    const endIndex = findChartEndNoteIndex(notes, absoluteBeats);
    if (endIndex < 0) return;

    e.preventDefault();
    e.stopPropagation();

    chartEndDrag = { index: endIndex, saved: false };

    // 노트 드래그와 같은 이유로 **컨테이너**에 건다 — 핸들 노드는 재렌더에서
    // 살아남지만(오버레이 보존), 컨테이너에 걸어야 규칙이 하나로 유지된다.
    container.setPointerCapture?.(e.pointerId);
    container.addEventListener("pointermove", onChartEndHandlePointerMove);
    container.addEventListener("pointerup", onChartEndHandlePointerUp, { once: true });
    container.addEventListener("pointercancel", onChartEndHandlePointerUp, { once: true });
}

function onChartEndHandlePointerMove(e) {
    if (!chartEndDrag) return;

    const container = document.getElementById("timeline-grid");
    if (!container) return;

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    const note = notes[chartEndDrag.index];
    if (!note) return;

    const absoluteBeats = calculateAbsoluteBeats(notes, bpm, subdivisions);

    // 마지막 노트(롱노트면 그 끝)보다 앞으로는 못 간다 — 차트가 노트 위에서 끊긴다.
    const minBeat = minimumChartEndBeat(notes, absoluteBeats, chartEndDrag.index, subdivisions);
    const wanted = timelineAbsoluteBeatFromEvent(container, e.clientY, subdivisions);
    const target = Math.max(minBeat, wanted);

    const offsets = calculateSectionOffsetBeats(notes, bpm, subdivisions);
    const noteSubdivisions = note.subdivisions || subdivisions;
    const { beat } = absoluteBeatToNoteBeat(
        target, offsets[chartEndDrag.index] || 0, noteSubdivisions, subdivisions
    );
    if (beat < 0 || beat === note.beat) return;

    // 첫 이동에서 한 번만 undo 스냅샷을 남긴다(드래그 이동과 같은 처우).
    if (!chartEndDrag.saved) {
        saveState();
        chartEndDrag.saved = true;
    }

    note.beat = beat;
    invalidatePathCache();
    drawPath();
    renderTimelineView();   // 드래그 중에는 타임라인만. 표는 놓을 때 한 번.
}

function onChartEndHandlePointerUp() {
    document.getElementById("timeline-grid")
        ?.removeEventListener("pointermove", onChartEndHandlePointerMove);

    if (!chartEndDrag) return;
    const saved = chartEndDrag.saved;
    chartEndDrag = null;
    if (saved) {
        saveToStorage();
        renderNoteList();
    }
}

// ── 타임라인 인스펙터 (Phase 1-4) ────────────────────────────────────────
//
// 노트 클릭 시 **타임라인 위에 겹쳐** 뜨는 편집 팝업이다. 타임라인과 함께 보여야
// 격자 위의 노트와 그 값을 눈으로 대조할 수 있다(사용자 요구).
//
// **기존 표의 셀 생성부(약 700줄)는 건드리지 않는다.** 설계 §1-4는 셀 빌더를
// `buildNoteCell`로 추출해 공유하자고 했으나, 실제 코드에서는 셀들이 `fadeInfo`·
// `sectionOffset`·`originalTime` 같은 지역값을 루프 중간에 만들어 뒤 셀이 쓴다.
// 14개로 쪼개려면 그 값을 전부 ctx로 엮어야 하고, 그 수술이 fade Time 셀·Angle
// 상속 경고·Curve 프리셋에 회귀를 낼 위험이 실익보다 크다고 판단했다.
// → 인스펙터는 **자주 쓰는 필드만** 자체 입력으로 다루고, 나머지는 표에서 편집한다.
//   값 변경은 표와 같은 경로(`saveState` → 값 대입 → `renderNoteList`)를 탄다.

// 인스펙터가 다루는 필드. 여기 없는 것(fade 등)은 표에서 편집한다.
const INSPECTOR_DIRECTIONS = [
    "none", "up", "down", "left", "right", "upleft", "upright", "downleft", "downright"
];

// BPM을 직접 가질 수 있는 타입. Tab 계열은 **다음 편집 가능 노트의 값을 상속**하므로
// 여기 없다(updateTabNotesInheritance가 그 상속을 수행한다).
//
// 🔴 표의 BPM 셀과 인스펙터가 **같은 목록**을 봐야 한다. 갈리면 한쪽에서만 편집이
// 되거나, 상속 노트에 BPM을 직접 박아 이후 계산이 어긋난다.
const BPM_EDITABLE_TYPES = ["direction", "longdirection", "both", "longboth", "node"];

// BPM 대입 규칙. 표의 BPM 셀과 인스펙터가 **모두 이 함수를 부른다**
// (applyNoteTypeChange와 같은 이유 — 규칙이 흩어지면 경로마다 결과가 갈린다).
//
// 다중 선택이면 선택된 노트 중 **BPM을 가질 수 있는 것만** 함께 바꾼다.
// 히스토리·저장·렌더는 호출부가 맡는다.
function applyBpmToNotes(index, newBpm) {
    if (selectedNoteIndices.has(index) && selectedNoteIndices.size > 1) {
        selectedNoteIndices.forEach(idx => {
            const n = notes[idx];
            if (n && BPM_EDITABLE_TYPES.includes(n.type)) n.bpm = newBpm;
        });
    } else if (notes[index]) {
        notes[index].bpm = newBpm;
    }

    // Tab 노트는 이 값을 상속하므로 즉시 다시 계산해야 표시와 경로가 맞는다.
    updateTabNotesInheritance();
}

// subdivisions(분박) 변경 규칙. **표의 Sub 셀과 인스펙터가 모두 이 함수를 부른다**
// (applyBpmToNotes·applyNoteTypeChange와 같은 이유).
//
// 🔴 **시간을 유지한 채 분박만 바꾼다**(§1-2-D, D-018). `beat`는 "subdivisions 단위의
// 개수"이므로 분박만 바꾸면 노트가 시간축에서 튄다:
//
//   beat 1200 @ sub4  →  sub8로 바꾸면
//     beat 그대로   → 시간이 **절반**으로 (노트가 앞으로 점프)
//     beat 2400     → 같은 시간 유지  ← 정답
//
// 정수로 떨어지지 않으면 `inexact`에 담아 돌려준다 — 호출부가 사용자에게 알린다.
// **조용히 반올림해 옮기지 않는다.**
//
// @returns {{changed:number[], inexact:Array<{index:number, beat:number}>}}
function applySubdivisionsToNotes(index, newSubdivisions) {
    const globalBpm = parseFloat(document.getElementById("bpm").value || 120);
    const globalSub = parseInt(document.getElementById("subdivisions").value || 16);

    // 다중 선택이면 분박을 가질 수 있는 노트만 함께 바꾼다(BPM 경로와 같은 처우).
    //
    // 🔴 단일 대상도 **반드시 걸러낸다.** Tab 계열은 분박을 소유하지 않고 상속하므로
    // (`updateTabNotesInheritance`가 렌더마다 덮어쓴다), 여기서 beat만 환산해두면
    // 다음 상속이 **분박만 되돌려** 노트가 엉뚱한 위치로 튄다(사용자 보고).
    // UI에서 막는 것과 별개로 이 함수 자체가 거부해야 경로가 늘어도 안전하다.
    const targets = (selectedNoteIndices.has(index) && selectedNoteIndices.size > 1)
        ? [...selectedNoteIndices]
        : [index];

    const changed = [];
    const inexact = [];

    for (const i of targets) {
        const n = notes[i];
        if (!n) continue;
        // Tab 계열은 상속 전용 — 여기서 바꾸면 상속이 되돌려 노트가 튄다.
        if (!SUBDIVISIONS_EDITABLE_TYPES.includes(n.type)) continue;

        const prev = n.subdivisions || globalSub;
        if (prev === newSubdivisions) continue;

        const bpm = n.bpm || globalBpm;
        const { beat, exact } = retimeBeatForSubdivisions(n.beat, bpm, prev, newSubdivisions);

        n.subdivisions = newSubdivisions;
        n.beat = beat;
        changed.push(i);
        if (!exact) inexact.push({ index: i, beat });
    }

    return { changed, inexact };
}

// 분박을 **가질 수 있는** 타입. Tab 계열은 여기 없다 — 상속만 받는다.
// 🔴 표의 canEditSubdivisions와 인스펙터가 같은 목록을 봐야 한다(BPM과 같은 이유).
const SUBDIVISIONS_EDITABLE_TYPES = ["direction", "longdirection", "both", "longboth", "node"];

// 분박 변경으로 **노트가 시간축에서 옮겨진 뒤** 그 노트를 따라가 보여준다(사용자 요구 3).
// 타임라인은 그 행으로 스크롤하고, 캔버스는 그 노트를 중심에 둔다.
//
// 캔버스 중심 이동은 `focusNoteAtIndex`가 이미 한다(경로 재계산 + viewOffset 보정 +
// drawPath + Unity 좌표 갱신). **같은 일을 두 번 짜지 않는다** — 그 함수가 쓰는
// 좌표 산출이 캔버스의 유일한 기준이라 손으로 옮기면 갈린다.
function focusNoteAfterRetime(index) {
    if (!notes[index]) return;

    // 캔버스 + 표 하이라이트. 경로 캐시는 호출부가 이미 무효화했다.
    focusNoteAtIndex(index);

    // 타임라인: 옮겨진 절대 박자로 스크롤. 분박이 바뀌어 행이 달라졌으므로
    // 여기서 다시 계산한다(focusNoteAtIndex는 타임라인을 모른다).
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const absoluteBeats = calculateAbsoluteBeats(notes, bpm, subdivisions);
    if (absoluteBeats[index] !== undefined) scrollTimelineToBeat(absoluteBeats[index]);

    // 🔴 **인스펙터를 다시 그린다.** 재렌더는 인스펙터를 의도적으로 **보존**하므로
    // (값 편집 중에 닫히지 않게 — D-024) 분박·beat가 바뀌어도 화면의 숫자는 옛 값
    // 그대로 남는다. 다시 클릭해야 맞는 값이 보이던 위화감의 원인이다(사용자 보고).
    if (document.getElementById("timeline-inspector")) openTimelineInspector(index);
}

// 드래그로 옮긴 **Tab 계열** 노트가 새 위치의 BPM을 상속하도록 다시 계산한다.
//
// 🔴 왜 `updateTabNotesInheritance`로는 안 되는가: 그 함수는 **배열 인덱스**로 앞뒤
// 선언 노트를 훑는다(:3196). 타임라인 드래그는 배열 위치를 바꾸지 않으므로
// (자동 정렬 없음 — §1-2-C), 노트를 다른 구간으로 끌어도 배열상으로는 옛 자리에
// 남아 **옛 구간의 BPM을 계속 준다.** 그래서 시간 축으로 찾는 별도 경로가 필요하다.
//
// **Tab·longTab만 건드린다**(사용자 결정 2026-07-30). Direction·Both·Node의 `bpm`은
// "이 지점부터 BPM이 바뀐다"는 **선언**이라, 덮어쓰면 그 노트 뒤 구간 전체의 BPM이
// 바뀐다 — 노트 하나를 옮겼는데 차트 뒷부분이 통째로 변속되는 셈이다.
//
// ⚠️ fade 보간은 여기서 하지 않는다. 드래그 중 매 프레임 돌아야 해서 가볍게 두었고,
// 드래그가 끝나면 `renderNoteList()` → `updateTabNotesInheritance()`가 배열 기준으로
// 다시 계산한다. 정확한 최종값은 그쪽이 낸다(사용자가 `노트 정렬`로 순서를 맞춘 뒤).
function reinheritDraggedTabBpm(note, index, absoluteBeat, globalBpm, globalSubdivisions) {
    if (note.type !== "tab" && note.type !== "longtab") return;
    // fade 구간의 직접 시간값을 가진 노트는 상속 대상이 아니다(updateTabNotesInheritance와 동일 규칙).
    if (note.hasOwnProperty("fadeDirectTime")) return;

    const absoluteBeats = calculateAbsoluteBeats(notes, globalBpm, globalSubdivisions);
    const governing = governingBpmDeclaration(notes, absoluteBeats, absoluteBeat, index);

    note.bpm = governing?.bpm ?? globalBpm;
    note.subdivisions = governing?.subdivisions ?? globalSubdivisions;
}

function closeTimelineInspector() {
    document.getElementById("timeline-inspector")?.remove();
}

function openTimelineInspector(index) {
    const note = notes[index];
    if (!note) return;

    const container = document.getElementById("timeline-grid");
    if (!container) return;

    closeTimelineInspector();

    // 팝아웃 대비: 컨테이너가 실제로 있는 문서에 만든다(§1-6).
    const doc = container.ownerDocument;
    const panel = doc.createElement("div");
    panel.id = "timeline-inspector";
    panel.className = "timeline-inspector";

    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const globalBpm = parseFloat(document.getElementById("bpm").value || 120);

    // 값 변경의 공통 경로. 표의 셀 핸들러와 같은 순서를 지킨다:
    // undo 스냅샷 → 값 대입 → 캐시 무효화 → 저장 → 재렌더.
    const commit = (mutate) => {
        saveState();
        mutate();
        invalidatePathCache();
        saveToStorage();
        drawPath();
        renderNoteList();   // 표와 타임라인이 함께 갱신된다
    };

    const field = (label, control) => {
        const row = doc.createElement("label");
        row.className = "timeline-inspector-row";
        const span = doc.createElement("span");
        span.textContent = label;
        row.append(span, control);
        return row;
    };

    // 헤더 — 인덱스와 타입을 함께 보여 표와 대조할 수 있게 한다.
    const header = doc.createElement("div");
    header.className = "timeline-inspector-header";
    const title = doc.createElement("strong");
    title.textContent = `#${index} ${note.type}`;
    // beatReset이면 헤더에 명시한다 — 이 노트 이후 beat가 0부터 다시 세어진다는 것을
    // 인스펙터만 보고도 알 수 있어야 한다(격자의 주황 표시와 짝을 이룬다).
    if (note.beatReset) {
        const badge = doc.createElement("span");
        badge.className = "timeline-inspector-reset-badge";
        badge.textContent = "RESET";
        badge.title = "이 노트 이후 beat가 0부터 다시 시작한다(구간 경계)";
        title.appendChild(badge);
    }
    const closeBtn = doc.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "timeline-inspector-close";
    closeBtn.textContent = "✕";
    closeBtn.addEventListener("click", closeTimelineInspector);
    header.append(title, closeBtn);
    panel.appendChild(header);

    // Type — 표의 드롭다운과 같은 목록·같은 부수 필드 규칙(applyNoteTypeChange).
    // 롱노트로 바꾸는 정규 경로다. 길이는 아래 Length에서 조정한다.
    const typeSelect = doc.createElement("select");
    [
        { value: "tab", label: "Tab" },
        { value: "longtab", label: "LTab" },
        { value: "direction", label: "Direction" },
        { value: "longdirection", label: "LDir" },
        { value: "both", label: "Both" },
        { value: "longboth", label: "LBoth" },
        { value: "node", label: "Node" }
    ].forEach(o => {
        const opt = doc.createElement("option");
        opt.value = o.value;
        opt.textContent = o.label;
        if (note.type === o.value) opt.selected = true;
        typeSelect.appendChild(opt);
    });
    typeSelect.addEventListener("change", () => {
        commit(() => applyNoteTypeChange(note, typeSelect.value));
        openTimelineInspector(index);   // 롱노트가 되면 Length 칸이 생긴다 — 다시 그린다
    });
    panel.appendChild(field("Type", typeSelect));

    // Length — 롱노트일 때만. 단위는 **박자**다(내부 shape 기준. JSON은 초라 다르다).
    if (note.isLong) {
        const longInput = doc.createElement("input");
        longInput.type = "number";
        longInput.step = "1";
        longInput.min = "1";
        longInput.value = note.longTime || subdivisions;
        longInput.addEventListener("change", () => {
            const v = Math.round(parseFloat(longInput.value));
            if (!Number.isFinite(v) || v < 1) { longInput.value = note.longTime; return; }
            commit(() => {
                note.longTime = v;
                // 길이를 늘여 차트 끝을 넘었으면 종료 노트를 뒤로 민다.
                ensureChartEndNote();
            });
        });
        panel.appendChild(field("Length", longInput));
    }

    // Beat — 정수 유지(§1-2-D). 세밀한 위치가 필요하면 subdivisions를 올린다.
    const beatInput = doc.createElement("input");
    beatInput.type = "number";
    beatInput.step = "1";
    beatInput.value = note.beat;
    beatInput.addEventListener("change", () => {
        const v = Math.round(parseFloat(beatInput.value));
        if (!Number.isFinite(v) || v < 0) { beatInput.value = note.beat; return; }
        commit(() => { note.beat = v; });
    });
    panel.appendChild(field("Beat", beatInput));

    // Subdivisions — **시간 유지 보정**(§1-2-D). 이 에디터에서 세부 비트를 표현하는
    // 정규 수단이다. beat를 그대로 두면 노트가 튄다(D-018).
    //
    // 🔴 **Tab 계열은 분박을 편집할 수 없다**(사양. 표의 canEditSubdivisions와 동일).
    // Tab은 앞 노트의 값을 **상속**하며, `updateTabNotesInheritance`가 렌더마다
    // `note.subdivisions`를 덮어쓴다. 편집을 허용하면 그 덮어쓰기가 **beat는 그대로
    // 두고 분박만 바꿔** 노트가 엉뚱한 위치로 튄다(사용자 보고).
    const canEditSub = SUBDIVISIONS_EDITABLE_TYPES.includes(note.type);
    if (canEditSub) {
        const subInput = doc.createElement("input");
        subInput.type = "number";
        subInput.step = "1";
        subInput.min = "1";
        subInput.value = note.subdivisions || subdivisions;
        subInput.addEventListener("change", () => {
            const next = Math.round(parseFloat(subInput.value));
            const prev = note.subdivisions || subdivisions;
            if (!Number.isFinite(next) || next < 1) { subInput.value = prev; return; }

            const bpm = note.bpm || parseFloat(document.getElementById("bpm").value || 120);
            const { beat, exact } = retimeBeatForSubdivisions(note.beat, bpm, prev, next);
            if (!exact) {
                // 정수로 안 떨어지면 알린다 — 반올림해서 조용히 옮기지 않는다.
                const ok = confirm(
                    `${prev} → ${next}로 바꾸면 beat를 ${beat}로 반올림해야 한다(정확히 표현 불가).\n` +
                    `노트가 아주 조금 움직인다. 진행할까?`
                );
                if (!ok) { subInput.value = prev; return; }
            }
            // 대입 규칙은 표의 Sub 셀과 **같은 함수**를 쓴다(다중 선택·시간 유지 보정 포함).
            commit(() => { applySubdivisionsToNotes(index, next); });
            // 옮겨진 노트를 타임라인·캔버스에서 따라가 보여준다(사용자 요구 3).
            focusNoteAfterRetime(index);
        });
        panel.appendChild(field("Sub", subInput));
    } else {
        // 상속값을 회색 읽기 전용으로 보여준다(BPM의 Tab 처우와 같다).
        const subRO = doc.createElement("span");
        subRO.className = "timeline-inspector-readonly";
        subRO.textContent = String(note.subdivisions || subdivisions);
        subRO.title = "Tab 계열은 앞 노트의 분박을 상속한다 — 직접 바꿀 수 없다";
        panel.appendChild(field("Sub", subRO));
    }

    // BPM — 표와 **같은 규칙**: direction/both/node 계열만 편집 가능하고,
    // Tab 계열은 "다음 BPM 편집 가능 노트"의 값을 상속받으므로 읽기 전용으로 보여준다.
    // 편집 가능 타입 목록은 표의 canEditBpm과 한 벌이어야 한다.
    const canEditBpm = BPM_EDITABLE_TYPES.includes(note.type);
    if (canEditBpm) {
        const bpmInput = doc.createElement("input");
        bpmInput.type = "number";
        bpmInput.min = "60";
        bpmInput.max = "300";
        bpmInput.step = "0.1";
        bpmInput.value = note.bpm || globalBpm;
        bpmInput.addEventListener("change", () => {
            const newBpm = parseFloat(bpmInput.value) || globalBpm;
            // 범위는 표와 같이 #min-bpm/#max-bpm을 따른다(입력 min/max와 별개다).
            const minBpm = parseFloat(document.getElementById("min-bpm").value || 1);
            const maxBpm = parseFloat(document.getElementById("max-bpm").value || 999);
            if (!(newBpm >= minBpm && newBpm <= maxBpm)) {
                bpmInput.value = note.bpm || globalBpm;   // 복원 (표와 같은 처리)
                return;
            }
            saveState();
            applyBpmToNotes(index, newBpm);
            invalidatePathCache();
            saveToStorage();
            drawPath();
            renderNoteList();
        });
        panel.appendChild(field("BPM", bpmInput));
    } else {
        // Tab 계열 — 표와 같이 상속값을 회색으로 보여주기만 한다.
        const bpmText = doc.createElement("span");
        bpmText.className = "timeline-inspector-readonly";
        bpmText.textContent = String(note.bpm || globalBpm);
        bpmText.title = "Tab 노트는 다음 BPM 편집 가능 노트의 값을 추종합니다";
        panel.appendChild(field("BPM", bpmText));
    }

    // Direction — 경로 노트에만 의미가 있다.
    const dirSelect = doc.createElement("select");
    INSPECTOR_DIRECTIONS.forEach(d => {
        const opt = doc.createElement("option");
        opt.value = d;
        opt.textContent = d;
        if ((note.direction || "none") === d) opt.selected = true;
        dirSelect.appendChild(opt);
    });
    dirSelect.addEventListener("change", () => {
        commit(() => { note.direction = dirSelect.value; });
    });
    panel.appendChild(field("Dir", dirSelect));

    // Angle — 계약이 ±15로 clamp한다(MAX_ANGLE_OFFSET). 범위를 UI에도 걸어둔다.
    const angleInput = doc.createElement("input");
    angleInput.type = "number";
    angleInput.step = "1";
    angleInput.min = String(-MAX_ANGLE_OFFSET);
    angleInput.max = String(MAX_ANGLE_OFFSET);
    angleInput.value = note.angleOffset || 0;
    angleInput.addEventListener("change", () => {
        const v = clampAngleOffset(angleInput.value);
        angleInput.value = v;
        // 기본값이면 필드를 지운다 — 기존 차트가 무수정 재현되도록(D-003).
        commit(() => { if (!v) delete note.angleOffset; else note.angleOffset = v; });
    });
    panel.appendChild(field("Angle", angleInput));

    // Curve — 곡선을 받는 타입에만 보인다. 기존 프리셋을 그대로 쓴다.
    if (noteAcceptsCurve(note)) {
        const curveSelect = doc.createElement("select");
        CURVE_PRESETS.forEach((preset, i) => {
            const opt = doc.createElement("option");
            opt.value = String(i);
            opt.textContent = preset.label;
            const cur = note.bezier || { curvature: 0, bias: 0 };
            if ((cur.curvature || 0) === preset.curvature && (cur.bias || 0) === preset.bias) {
                opt.selected = true;
            }
            curveSelect.appendChild(opt);
        });
        curveSelect.addEventListener("change", () => {
            const preset = CURVE_PRESETS[parseInt(curveSelect.value, 10)];
            if (!preset) return;
            // applyCurveToNotes가 기본값 제거·다중선택까지 처리하는 단일 경로다.
            saveState();
            applyCurveToNotes(index, preset.curvature, preset.bias);
        });
        panel.appendChild(field("Curve", curveSelect));
    }

    // Wait — Node 타입만(표와 같다). 정지 노드는 길이 0 구간이라 곡선이 없다.
    if (note.type === "node") {
        const waitBox = doc.createElement("input");
        waitBox.type = "checkbox";
        waitBox.checked = note.wait || false;
        waitBox.addEventListener("change", () => {
            commit(() => { note.wait = waitBox.checked; });
            // wait가 켜지면 Curve 칸이 사라진다(noteAcceptsCurve) — 다시 그린다.
            openTimelineInspector(index);
        });
        panel.appendChild(field("Wait", waitBox));
    }

    // Reset — 모든 타입(표와 같다). 이 노트 이후 beat를 0부터 재시작한다.
    //
    // ⚠️ beatReset은 **구간 길이 그 자체**라 뒤 구간 전체가 밀린다. 그래서 드래그로는
    // 막아뒀지만(§1-3), 표에서 되는 편집이므로 인스펙터에서도 열어둔다 — 여기서는
    // 의도적으로 체크하는 것이지 실수로 끌린 것이 아니다.
    const resetBox = doc.createElement("input");
    resetBox.type = "checkbox";
    resetBox.checked = note.beatReset || false;
    resetBox.title = "이 노트 이후 beat를 0부터 재시작";
    resetBox.addEventListener("change", () => {
        commit(() => { note.beatReset = resetBox.checked; });
    });
    panel.appendChild(field("Reset", resetBox));

    const hint = doc.createElement("div");
    hint.className = "timeline-inspector-hint";
    hint.textContent = "좌우 드래그로도 타입을 바꿀 수 있다. fade는 NoteList 탭에서.";
    panel.appendChild(hint);

    container.appendChild(panel);
    positionTimelineInspector();
}

// 인스펙터를 격자 **위에 겹쳐** 놓는다.
//
// position:fixed라 좌표를 직접 잡아야 한다. 컨테이너의 화면상 위치를 기준으로
// 우측 상단에 붙이고, 폭이 좁으면 넘치지 않게 안쪽으로 당긴다.
// 스크롤·리사이즈·창 분리로 컨테이너가 움직이면 다시 불러야 한다.
function positionTimelineInspector() {
    const panel = document.getElementById("timeline-inspector");
    const container = document.getElementById("timeline-grid");
    if (!panel || !container) return;

    const rect = container.getBoundingClientRect();
    const width = panel.offsetWidth || 168;

    // 스크롤바를 피해 6px 안쪽. 컨테이너가 인스펙터보다 좁으면 왼쪽 끝에 맞춘다.
    const left = Math.max(rect.left + 4, rect.right - width - 14);
    panel.style.left = `${left}px`;
    panel.style.top = `${rect.top + 6}px`;

    // 컨테이너보다 길어지면 아래가 잘리므로 최대 높이를 제한한다(내용은 스크롤).
    panel.style.maxHeight = `${Math.max(80, rect.height - 12)}px`;
}

// ── 드래그 툴팁 (BPM · beat · 실제 시작) ────────────────────────────────
//
// 드래그 중에는 표가 갱신되지 않으므로(무거워서 끝날 때 한 번만 그린다) 지금 노트가
// 어디에 놓이는지 볼 방법이 없다. 커서 옆에 세 값을 띄워 그 공백을 메운다.
//
// 🔴 값은 **표의 Time 셀과 같은 식**으로 구한다:
//   originalTime = sectionOffset(초) + beatToTime(beat, noteBpm, noteSubdivisions)
// 여기서 갈리면 툴팁과 표가 다른 시간을 말하게 된다. `beat`도 노트 자신의
// subdivisions 단위이므로 기준 단위로 환산하지 않고 **그대로** 보여준다(표와 동일).

// 표시할 세 값을 계산한다. DOM을 만들지 않으므로 툴팁 없이도 부를 수 있다.
function timelineDragReadout(index) {
    const note = notes[index];
    if (!note) return null;

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

    // 노트별 BPM/subdivisions가 우선이다(차트에 4분박·12분박이 섞인다).
    const noteBpm = note.bpm || bpm;
    const noteSubdivisions = note.subdivisions || subdivisions;

    // 구간 오프셋은 **초 단위** 캐시를 쓴다. 드래그가 매 이동마다
    // invalidatePathCache()를 부르므로 이 값은 항상 최신이다.
    const sectionOffset = getOrComputeSectionOffsets()[index] || 0;
    const originalTime = sectionOffset + beatToTime(note.beat, noteBpm, noteSubdivisions);

    // 🔴 격자의 행 라벨은 **절대 박자**이고 표의 Beat 셀은 **구간 지역 beat**다.
    // beatReset이 있는 차트에서는 이 둘이 다르므로 **둘 다** 보여준다. 하나만
    // 보여주면 툴팁이 바로 옆 행 라벨과 어긋나 보인다.
    const absoluteBeat = calculateAbsoluteBeats(notes, bpm, subdivisions)[index];

    return {
        bpm: noteBpm,
        beat: note.beat,
        subdivisions: noteSubdivisions,
        absoluteBeat,
        originalTime
    };
}

// 절대 박자는 분박 정규화 때문에 소수가 될 수 있다(sub=12 노트를 sub=16 기준으로
// 환산하면 딱 떨어지지 않는다). 정수면 정수로, 아니면 소수 둘째 자리까지만 보인다.
function formatTooltipBeat(beat) {
    return Number.isInteger(beat) ? String(beat) : beat.toFixed(2).replace(/\.?0+$/, "");
}

// 툴팁을 만들거나 갱신한다. 커서 옆에 붙인다.
function showTimelineDragTooltip(index, clientX, clientY) {
    const readout = timelineDragReadout(index);
    if (!readout) return;

    const container = document.getElementById("timeline-grid");
    if (!container) return;

    // 팝아웃 대비: 컨테이너가 실제로 있는 문서에 만든다(§1-6).
    const doc = container.ownerDocument;
    let tip = doc.getElementById("timeline-drag-tooltip");
    if (!tip) {
        tip = doc.createElement("div");
        tip.id = "timeline-drag-tooltip";
        tip.className = "timeline-drag-tooltip";
        doc.body.appendChild(tip);
    }

    // 인스펙터와 같은 이유로 position:fixed다 — 격자가 스크롤돼도 커서를 따라야 한다.
    // 값 세 줄. beat는 그 노트의 분박 단위임을 함께 적는다(표와 단위를 맞춘다).
    tip.replaceChildren();
    const line = (label, value) => {
        const row = doc.createElement("div");
        row.className = "timeline-drag-tooltip-row";
        const k = doc.createElement("span");
        k.className = "timeline-drag-tooltip-key";
        k.textContent = label;
        const v = doc.createElement("span");
        v.className = "timeline-drag-tooltip-value";
        v.textContent = value;
        row.append(k, v);
        tip.appendChild(row);
    };

    line("BPM", `${readout.bpm}`);
    // Beat = 표와 같은 지역 beat(단위: 그 노트의 분박). 행 = 격자 라벨과 같은 절대 박자.
    line("Beat", `${readout.beat} / ${readout.subdivisions}`);
    if (Number.isFinite(readout.absoluteBeat) && readout.absoluteBeat !== readout.beat) {
        line("행", `${formatTooltipBeat(readout.absoluteBeat)}`);
    }
    line("Time", `${readout.originalTime.toFixed(3)}s`);

    positionTimelineDragTooltip(tip, clientX, clientY);
}

// 커서 오른쪽 아래에 붙이되 화면 밖으로 나가면 반대쪽으로 접는다.
function positionTimelineDragTooltip(tip, clientX, clientY) {
    const doc = tip.ownerDocument;
    const view = doc.defaultView;
    const width = tip.offsetWidth || 108;
    const height = tip.offsetHeight || 52;
    const margin = 14;

    let left = clientX + margin;
    let top = clientY + margin;

    // 오른쪽/아래로 넘치면 커서 반대편에 놓는다(커서를 가리지 않게 margin 유지).
    if (view && left + width > view.innerWidth - 4) left = clientX - width - margin;
    if (view && top + height > view.innerHeight - 4) top = clientY - height - margin;

    tip.style.left = `${Math.max(4, left)}px`;
    tip.style.top = `${Math.max(4, top)}px`;
}

function hideTimelineDragTooltip() {
    // 🔴 팝아웃 상태에서는 툴팁이 **분리된 창의 문서**에 있다. 메인 document만
    // 뒤지면 못 찾아 툴팁이 남는다. 격자가 실제로 있는 문서에서 지운다.
    const doc = document.getElementById("timeline-grid")?.ownerDocument;
    doc?.getElementById("timeline-drag-tooltip")?.remove();
}

function onTimelinePointerUp() {
    // pointermove는 once가 아니므로 직접 뗀다. 안 떼면 드래그가 끝난 뒤에도
    // 마우스를 움직일 때마다 핸들러가 돈다.
    document.getElementById("timeline-grid")
        ?.removeEventListener("pointermove", onTimelinePointerMove);

    // 툴팁은 **아래 guard보다 먼저** 지운다. pointercancel처럼 timelineDrag이 이미
    // 비어 있는 경로로 들어와도 툴팁이 화면에 남으면 안 된다.
    hideTimelineDragTooltip();

    if (!timelineDrag) return;

    const moved = timelineDragMoved;
    const movedIndex = timelineDrag.index;
    timelineDrag = null;

    if (moved) {
        // 좌우 드래그로 타입이 바뀌었을 수 있다. 인스펙터가 열려 있으면 값이
        // 낡았으므로 같은 노트로 다시 그린다.
        if (document.getElementById("timeline-inspector")) {
            openTimelineInspector(movedIndex);
        }
        // 드래그로 배열의 시간 순서가 깨졌을 수 있다. 자동 정렬은 하지 않는다 —
        // sortNotesByTime은 인덱스를 바꿔 선택·undo와 충돌하므로 수동 유지가
        // 이 저장소의 결정이다(§1-2-C). 사용자가 `노트 정렬` 버튼으로 정리한다.
        //
        // 끝난 시점에 한 번만 표를 갱신한다(드래그 중에는 타임라인만 그렸다).
        ensureChartEndNote();
        saveToStorage();
        renderNoteList();
    }
}

// 특정 노트의 Curve 셀 숫자만 화면에 반영한다(캔버스 핸들 드래그 → 표 동기화용).
// 목록 전체를 다시 그리면 사용자가 다른 셀에 입력하던 내용이 날아가므로,
// 바뀐 행의 입력 값만 직접 갱신한다. 그 행이 가상 스크롤 범위 밖이면 할 일이 없다
// (다음 렌더에서 올바른 값으로 만들어진다).
function syncCurveCellInputs(noteIndex) {
    const note = notes[noteIndex];
    if (!note) return;
    const sel = `#note-list [data-note-index="${noteIndex}"][data-note-field=`;
    const curvInput = document.querySelector(`${sel}"curvature"]`);
    const biasInput = document.querySelector(`${sel}"bias"]`);
    // 편집 중인 입력은 덮어쓰지 않는다 — 사용자가 방금 친 값이 우선이다.
    if (curvInput && curvInput !== document.activeElement) {
        curvInput.value = note.bezier?.curvature || 0;
    }
    if (biasInput && biasInput !== document.activeElement) {
        biasInput.value = note.bezier?.bias || 0;
    }
    // 권장 범위 경고 배지도 같은 셀 안에서 함께 맞춘다.
    curvInput?.__syncCurveWarning?.();
}

// 셀 입력 핸들러 안에서 목록을 다시 그려야 할 때 쓰는 래퍼.
//
// renderNoteListImmediate는 tbody를 통째로 지우고 새로 만들기 때문에, 입력 중인
// 엘리먼트가 그대로 파괴된다. change 이벤트는 number 입력의 스피너 화살표·↑↓ 키에도
// 발생하므로 값을 연속 조정하면 매 스텝마다 포커스가 날아가 "입력이 초기화된다"로 보인다.
// 편집 중이던 셀을 (행 인덱스, 컬럼 키)로 기억했다가 재생성 후 같은 셀에 되돌린다.
//
// 값 변경이 자기 셀에만 영향을 준다면 이 함수도 부르지 말고 아예 다시 그리지 않는 것이
// 옳다(Curve/Angle이 그 경우). 다른 행까지 바뀔 때만 쓴다.
function renderNoteListPreservingFocus() {
    const active = document.activeElement;
    const field = active?.dataset?.noteField;
    // 노트 목록 셀에 포커스가 없으면 평소대로 처리한다.
    if (!field) {
        renderNoteList();
        return;
    }

    const noteIndex = active.dataset.noteIndex;
    // number 입력에서 selection* 접근은 브라우저에 따라 예외를 던진다(Chrome).
    let selectionStart = null;
    let selectionEnd = null;
    try {
        selectionStart = active.selectionStart;
        selectionEnd = active.selectionEnd;
    } catch {
        /* 커서 위치를 못 읽으면 포커스만 복원한다 */
    }

    // 디바운스를 거치면 복원 시점이 밀려 그 사이 입력을 다시 놓친다. 즉시 그린다.
    renderNoteListImmediate();

    const restored = document.querySelector(
        `#note-list [data-note-index="${noteIndex}"][data-note-field="${field}"]`,
    );
    if (!restored) return; // 가상 스크롤 범위 밖으로 나갔으면 복원할 대상이 없다.

    restored.focus();
    // number 입력은 selectionStart 접근이 브라우저에 따라 막혀 있어 방어적으로 처리한다.
    if (selectionStart !== null && selectionStart !== undefined) {
        try {
            restored.setSelectionRange(selectionStart, selectionEnd);
        } catch {
            /* 선택 영역 복원 불가 — 포커스만으로 충분하다 */
        }
    }
}

// 실제 렌더링 함수 (즉시 실행)
function renderNoteListImmediate() {
    // 시간 입력 직후가 아닐 때만 cleanup 실행
    if (!window.skipNextCleanup) {
        // fade 구간에서 벗어난 노트들의 fadeDirectTime 제거
        cleanupFadeDirectTimes();
    }

    // Tab 노트들의 상속 값을 먼저 업데이트
    updateTabNotesInheritance();

    const tbody = document.getElementById("note-list");
    tbody.innerHTML = "";

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const preDelaySeconds = getPreDelaySeconds();
    const allSectionOffsets = getOrComputeSectionOffsets();

    // 중복 검사 최적화 적용
    const duplicateNoteIndices = findDuplicateNoteIndices(notes, bpm, subdivisions);

    // Virtual Scrolling: 렌더링할 범위 계산
    const totalNotes = notes.length;
    const noteState = virtualScrollState.note;
    // scrollTop 기준 startIndex는 리스트가 줄어들 때(예: Clear All 후 노트 1개)
    // 예전 스크롤 값 때문에 목록 길이를 넘어설 수 있다. 그러면 for 루프가 한 번도
    // 돌지 않아 리스트가 통째로 안 보인다. 마지막 페이지 시작점으로 clamp한다.
    const window_ = noteState.visibleCount + noteState.bufferCount * 2;
    const maxStart = Math.max(0, totalNotes - window_);
    const startIndex = Math.min(
        maxStart,
        Math.max(0, Math.floor(noteState.scrollTop / noteState.itemHeight) - noteState.bufferCount),
    );
    const endIndex = Math.min(totalNotes, startIndex + window_);

    // 상단 스페이서 (스크롤 위치 유지용)
    if (startIndex > 0) {
        const offsetY = startIndex * noteState.itemHeight;
        const spacerTop = document.createElement("tr");
        spacerTop.style.height = `${offsetY}px`;
        spacerTop.style.pointerEvents = "none";
        tbody.appendChild(spacerTop);
    }

    // Virtual Scrolling: 보이는 범위만 렌더링
    for (let index = startIndex; index < endIndex; index++) {
        const note = notes[index];
        if (!note) continue;
        const tr = document.createElement("tr");
        let className = "tab-note";
        if (note.type === "direction" || note.type === "longdirection") {
            className = "dir-note";
        } else if (note.type === "both" || note.type === "longboth") {
            className = "both-note";
        } else if (note.type === "longtab") {
            className = "long-tab-note";
        } else if (note.type === "node") {
            className = "node-note";
        }
        tr.className = className;
        tr.dataset.index = index;
        if (index === selectedNoteIndex) {
            tr.classList.add("highlight");
        }

        // 다중 선택된 노트 표시
        if (selectedNoteIndices.has(index)) {
            tr.classList.add("selected");
        }

        // 중복 beat 값인 경우 빨간색으로 표시 (같은 BPM, subdivision인 경우에만)
        if (duplicateNoteIndices.has(index)) {
            tr.classList.add("duplicate-beat");
        }

        // beatReset 노드는 구간 경계 시각화
        if (note.beatReset) {
            tr.classList.add("beat-reset-node");
        }

        const tdIndex = document.createElement("td");
        const sIdx = note.sectionIndex !== undefined ? note.sectionIndex : '?';
        tdIndex.textContent = index;
        tdIndex.title = `구간 ${sIdx}`;

        const tdType = document.createElement("td");
        const typeSelect = document.createElement("select");
        typeSelect.style.fontSize = "11px";
        typeSelect.style.width = "70px";

        const typeOptions = [
            { value: "tab", label: "Tab" },
            { value: "longtab", label: "LTab" },
            { value: "direction", label: "Direction" },
            { value: "longdirection", label: "LDir" },
            { value: "both", label: "Both" },
            { value: "longboth", label: "LBoth" },
            { value: "node", label: "Node" }
        ];

        typeOptions.forEach(option => {
            const opt = document.createElement("option");
            opt.value = option.value;
            opt.textContent = option.label;
            if (note.type === option.value) {
                opt.selected = true;
            }
            typeSelect.appendChild(opt);
        });

        typeSelect.addEventListener("change", () => {
            // 변경 전 상태를 히스토리에 저장
            saveState();

            const oldType = note.type;
            const newType = typeSelect.value;

            // 부수 필드 규칙은 applyNoteTypeChange 한 곳에 있다 —
            // 타임라인의 좌우 드래그·인스펙터도 같은 함수를 부른다.
            const updateNoteType = (n) => applyNoteTypeChange(n, newType);

            // 다중 선택된 노트들의 타입 일괄 변경
            if (selectedNoteIndices.has(index) && selectedNoteIndices.size > 1) {
                selectedNoteIndices.forEach(idx => {
                    if (idx < notes.length) {
                        updateNoteType(notes[idx]);
                    }
                });
            } else {
                updateNoteType(note);
            }

            saveToStorage();
            drawPath();
            renderNoteList();
             });

        tdType.appendChild(typeSelect);

        // Tab 노트가 fade 구간에 있는지 확인 (한 번만 계산)
        const isTabNote = note.type === "tab" || note.type === "longtab";
        const fadeInfo = isTabNote ? getTabNoteFadeInfo(note, index) : { inFade: false };

        const tdBeat = document.createElement("td");
        const inputBeat = document.createElement("input");
        inputBeat.type = "number";
        inputBeat.step = "1";
        inputBeat.value = note.beat;
        inputBeat.dataset.noteIndex = index;
        inputBeat.dataset.noteField = "beat";

        // fade 구간의 tab/longtab 노트는 beat 입력 비활성화
        if (fadeInfo.inFade) {
            inputBeat.disabled = true;
            inputBeat.style.backgroundColor = "#f5f5f5";
            inputBeat.style.color = "#999";
            inputBeat.title = "Fade 구간에서는 시간값으로 beat가 자동 계산됩니다";
        }
        inputBeat.addEventListener("change", () => {
            // 변경 전 상태를 히스토리에 저장
            saveState();

            const oldBeat = note.beat;
            const newBeat = parseFloat(inputBeat.value);
            const diff = newBeat - oldBeat;

            if (isBatchEditEnabled) {
                // 현재 노트는 입력된 값으로 직접 설정
                note.beat = newBeat;
                if (note.hasOwnProperty('fadeDirectTime')) {
                    delete note.fadeDirectTime;
                }
                // 이후 노트들은 차이값만큼 조정
                notes.forEach((n, i) => {
                    if (i > index) {
                        n.beat += diff;
                        // beat가 변경된 노트들의 fadeDirectTime도 제거
                        if (n.hasOwnProperty('fadeDirectTime')) {
                            delete n.fadeDirectTime;
                        }
                    }
                });
            } else if (selectedNoteIndices.has(index) && selectedNoteIndices.size > 1) {
                // 다중 선택된 노트들의 beat 일괄 조정
                selectedNoteIndices.forEach(idx => {
                    if (idx < notes.length) {
                        notes[idx].beat += diff;
                        // beat가 변경된 노트들의 fadeDirectTime 제거
                        if (notes[idx].hasOwnProperty('fadeDirectTime')) {
                            delete notes[idx].fadeDirectTime;
                        }
                    }
                });
            } else {
                note.beat = newBeat;
                // beat 값이 변경되면 fade 구간의 직접 시간값 제거
                if (note.hasOwnProperty('fadeDirectTime')) {
                    delete note.fadeDirectTime;
                }
            }

            saveToStorage();
            drawPath();
            // beat는 이후 노트의 Time/중복표시까지 바꾸므로 목록 재생성이 필요하다.
            renderNoteListPreservingFocus();
                        });
        tdBeat.appendChild(inputBeat);

        const tdTime = document.createElement("td");

        // 각 노트의 BPM/subdivision 사용 (구간 오프셋 포함)
        const noteBpm = note.bpm || bpm;
        const noteSubdivisions = note.subdivisions || subdivisions;
        const sectionOffset = allSectionOffsets[index] || 0;
        const originalTime = sectionOffset + beatToTime(note.beat, noteBpm, noteSubdivisions);

        if (note.beat === 0 && note.type === "direction" && sectionOffset === 0) {
            tdTime.textContent = `${originalTime.toFixed(3)}s`;
            tdTime.style.color = '#666';
            tdTime.title = '게임 시작점';
        } else if (fadeInfo.inFade) {
            // Tab 노트가 fade 구간에 있으면 자유로운 시간 입력 필드 표시
            let finalTime;
            if (note.hasOwnProperty('fadeDirectTime')) {
                // 이미 저장된 직접 시간값이 있으면 그것을 사용
                finalTime = note.fadeDirectTime;
            } else {
                // 처음이면 계산된 시간값을 저장
                finalTime = originalTime + preDelaySeconds;
                note.fadeDirectTime = finalTime;
            }

            const inputTime = document.createElement("input");
            inputTime.type = "number";
            inputTime.step = "0.001";
            // fade 구간 노트는 시간 제한 없이 자유롭게 입력 가능
            inputTime.value = finalTime.toFixed(3);
            inputTime.style.width = "80px";
            inputTime.style.fontSize = "11px";
            inputTime.style.color = "#FF9800";
            inputTime.title = "Fade 구간 내 자유 시간 입력 (제한 없음)";
            inputTime.dataset.noteIndex = index;
            inputTime.dataset.noteField = "fadeTime";

            inputTime.addEventListener("change", () => {
                saveState();

                const targetFinalTime = parseFloat(inputTime.value);

                // fade 구간의 tab/longtab 노트의 모든 파라미터를 시간값에 기반하여 업데이트
                updateFadeNoteParameters(note, index, targetFinalTime, fadeInfo);

                saveToStorage();
                drawPath();

                // 시간 입력 직후에는 cleanup을 건너뛰도록 플래그 설정
                window.skipNextCleanup = true;
                // beat가 시간값에서 역산되므로 목록 재생성이 필요하다.
                renderNoteListPreservingFocus();

                // 다음 렌더링에서는 cleanup 실행
                setTimeout(() => {
                    window.skipNextCleanup = false;
                }, 100);
             });

            tdTime.appendChild(inputTime);

            const hintDiv = document.createElement("div");
            hintDiv.style.fontSize = "10px";
            hintDiv.style.color = "#999";
            hintDiv.textContent = `(fade: ${fadeInfo.startBpm.toFixed(0)}→${fadeInfo.endBpm.toFixed(0)} BPM)`;
            tdTime.appendChild(hintDiv);
        } else {
            const finalTime = originalTime + preDelaySeconds;
            tdTime.innerHTML = `\n        <div style="color: #4CAF50; font-weight: bold;">${finalTime.toFixed(3)}s</div>\n        <div style="font-size: 11px; color: #999;">원본: ${originalTime.toFixed(3)}s</div>\n    `;
            tdTime.title = `원본: ${originalTime.toFixed(3)}s → 최종: ${finalTime.toFixed(3)}s (pre-delay: ${preDelaySeconds > 0 ? '+' : ''}${preDelaySeconds.toFixed(3)}s)`;
        }

        const tdLong = document.createElement("td");
        if (note.isLong) {
            const inputLongTime = document.createElement("input");
            inputLongTime.type = "number";
            inputLongTime.step = "1";
            inputLongTime.min = "1";
            inputLongTime.value = note.longTime || subdivisions;
            inputLongTime.title = "비트 단위 길이";
            inputLongTime.addEventListener("change", () => {
                // 변경 전 상태를 히스토리에 저장
                saveState();

                note.longTime = parseInt(inputLongTime.value) || subdivisions;
                saveToStorage();
                drawPath();
                                    });
            tdLong.appendChild(inputLongTime);
        } else {
            tdLong.textContent = "-";
        }

        const tdDir = document.createElement("td");
        if (["direction", "longdirection", "both", "longboth", "node"].includes(note.type)) {
            const select = document.createElement("select");
            ["none", "up", "down", "left", "right", "upleft", "upright", "downleft", "downright"].forEach(d => {
                const opt = document.createElement("option");
                opt.value = d;
                opt.textContent = d;
                if ((note.direction || "none") === d)
                    opt.selected = true;
                select.appendChild(opt);
            });
            select.addEventListener("change", () => {
                // 변경 전 상태를 히스토리에 저장
                saveState();

                const newDirection = select.value;

                // 다중 선택된 노트 중 방향 지정이 가능한 노트만 변경
                if (selectedNoteIndices.has(index) && selectedNoteIndices.size > 1) {
                    selectedNoteIndices.forEach(idx => {
                        if (idx < notes.length) {
                            const n = notes[idx];
                            // 방향을 가질 수 있는 타입인지 확인
                            if (["direction", "longdirection", "both", "longboth", "node"].includes(n.type)) {
                                n.direction = newDirection;
                            }
                        }
                    });
                } else {
                    note.direction = newDirection;
                }

                invalidatePathCache();
                saveToStorage();
                drawPath();
                                    });
            tdDir.appendChild(select);
        } else {
            tdDir.textContent = "-";
        }

        // Angle 컬럼 (angleOffset). 경로를 만드는 노트에서만 편집 가능
        const tdAngle = document.createElement("td");
        if (["direction", "longdirection", "both", "longboth", "node"].includes(note.type)) {
            const angleInput = document.createElement("input");
            angleInput.type = "number";
            angleInput.min = -MAX_ANGLE_OFFSET;
            angleInput.max = MAX_ANGLE_OFFSET;
            angleInput.step = 0.5;
            angleInput.value = note.angleOffset || 0;
            angleInput.style.width = "48px";
            angleInput.style.fontSize = "11px";
            angleInput.dataset.noteIndex = index;
            angleInput.dataset.noteField = "angle";

            // direction 미지정 노트는 자기 각도가 무시되고 직전 값을 상속한다.
            // 입력을 막지는 않는다 — 방향을 나중에 지운 노트에 값이 남아 있으면 지울 수단이 필요하다.
            const inherits = !note.direction || note.direction === "none";
            if (inherits) {
                angleInput.title = note.angleOffset
                    ? `상속 중 — 이 값(${note.angleOffset}도)은 무시됩니다. direction을 지정하거나 0으로 정리하세요.`
                    : "상속 중 — direction이 미지정이라 직전 노트의 각도를 따릅니다.";
                if (note.angleOffset) {
                    angleInput.style.background = "#FFF3E0";
                    angleInput.style.borderColor = "#FF9800";
                }
            } else {
                angleInput.title = "진행 각도 보정 (도). 양수 = 반시계.";
            }

            // 상속 중 노트에서 "무시되는 값" 강조와 정리 버튼의 표시 여부는 각도 값에만
            // 달려 있다. 목록을 다시 그리지 않고 이 셀 안에서 직접 갱신한다.
            const syncInheritWarning = () => {
                const stale = inherits && note.angleOffset;
                angleInput.style.background = stale ? "#FFF3E0" : "";
                angleInput.style.borderColor = stale ? "#FF9800" : "";
                if (inherits) {
                    angleInput.title = note.angleOffset
                        ? `상속 중 — 이 값(${note.angleOffset}도)은 무시됩니다. direction을 지정하거나 0으로 정리하세요.`
                        : "상속 중 — direction이 미지정이라 직전 노트의 각도를 따릅니다.";
                }
                clearBtn.style.display = stale ? "" : "none";
            };

            angleInput.addEventListener("change", () => {
                saveState();
                const newAngle = clampAngleOffset(angleInput.value);
                angleInput.value = newAngle;

                if (selectedNoteIndices.has(index) && selectedNoteIndices.size > 1) {
                    selectedNoteIndices.forEach(idx => {
                        const n = notes[idx];
                        if (n && ["direction", "longdirection", "both", "longboth", "node"].includes(n.type)) {
                            n.angleOffset = newAngle;
                        }
                    });
                } else {
                    note.angleOffset = newAngle;
                }

                invalidatePathCache();
                saveToStorage();
                drawPath();
                // 각도는 경로만 바꾸고 다른 행의 표시값을 건드리지 않는다 → 재생성 불필요.
                syncInheritWarning();
            });
            tdAngle.appendChild(angleInput);

            // 다중 선택 일괄 편집 시 화면 밖 행은 다음 렌더에서 자연히 맞춰진다.
            const clearBtn = document.createElement("button");
            clearBtn.textContent = "0";
            clearBtn.title = "무시되는 각도 값을 0으로 정리";
            clearBtn.style.cssText = "margin-left:2px;font-size:10px;padding:0 4px;";
            clearBtn.addEventListener("click", () => {
                saveState();
                note.angleOffset = 0;
                angleInput.value = 0;
                invalidatePathCache();
                saveToStorage();
                drawPath();
                syncInheritWarning();
            });
            tdAngle.appendChild(clearBtn);
            if (!inherits || !note.angleOffset) clearBtn.style.display = "none";
        } else {
            tdAngle.textContent = "-";
            tdAngle.style.color = "#999";
        }

        // Curve 컬럼 (bezier.curvature / bezier.bias). 경로를 만드는 노트에서만 편집 가능.
        // 곡선은 이 노트에서 "나가는" 구간에 붙는다(솔버가 a.bezier를 쓴다).
        // wait Node는 길이 0 구간이라 곡선이 정의되지 않으므로 비활성.
        const tdCurve = document.createElement("td");
        const isWaitNode = note.type === "node" && note.wait;
        const canEditCurve = noteAcceptsCurve(note);

        // 우클릭 시 곡선 프리셋 메뉴 (편집 가능한 노트에서만)
        if (canEditCurve) {
            tdCurve.addEventListener("contextmenu", (e) => {
                e.preventDefault();
                showCurvePresetMenu(e.clientX, e.clientY, index);
            });
            tdCurve.title = "우클릭: 곡선 프리셋";
        }

        if (canEditCurve) {
            const curv = note.bezier?.curvature || 0;
            const bias = note.bezier?.bias || 0;

            // bezier를 항상 노트에 만들지 않는다 — 기본값(0,0)이면 필드 부재로 남겨
            // 기존 차트가 무수정 재현되게 한다(applyCurveToNotes가 이 규칙을 담당).
            const applyBezier = (newCurv, newBias) => applyCurveToNotes(index, newCurv, newBias);

            const makeInput = (value, field, title, onChange) => {
                const input = document.createElement("input");
                input.type = "number";
                input.step = 0.05;
                input.value = value;
                input.title = title;
                input.style.width = "44px";
                input.style.fontSize = "11px";
                input.dataset.noteIndex = index;
                input.dataset.noteField = field;
                input.addEventListener("change", () => {
                    saveState();
                    onChange(Number(input.value) || 0);
                    invalidatePathCache();
                    saveToStorage();
                    drawPath();          // 곡선 변경 → 뒤 경로 전체 실시간 재계산/프리뷰
                    // 곡선 값은 캔버스 경로에만 영향을 주고 다른 행의 표시값을 바꾸지
                    // 않는다. 목록을 다시 그리면 편집 중인 이 입력이 파괴되므로 하지 않는다
                    // (number 입력은 스피너 화살표·↑↓ 키마다 change가 발생한다).
                    syncCurveWarning();
                });
                return input;
            };

            const wrap = document.createElement("div");
            wrap.style.cssText = "display:flex;align-items:center;gap:2px;";

            const curvInput = makeInput(curv, "curvature", "curvature (곡률). 양수 = 반시계로 부풀림. 권장 ±0.5.",
                (v) => applyBezier(v, note.bezier?.bias || 0));
            const biasInput = makeInput(bias, "bias", "bias (제어점 전후 치우침). 0 = 중앙.",
                (v) => applyBezier(note.bezier?.curvature || 0, v));

            // curvature가 권장 범위를 넘으면 경고 배지(하드 제약 아님, §3-5/Q2).
            // 배지는 항상 만들어 두고 표시 여부만 토글한다 — 값이 바뀔 때마다 셀을
            // 다시 만들지 않기 위해서다.
            const badge = document.createElement("span");
            badge.textContent = "⚠";
            badge.style.cssText = "color:#FF9800;cursor:help;";

            const syncCurveWarning = () => {
                const c = note.bezier?.curvature || 0;
                const over = Math.abs(c) > CURVATURE_SOFT_LIMIT;
                curvInput.style.borderColor = over ? "#FF9800" : "";
                curvInput.style.background = over ? "#FFF3E0" : "";
                badge.style.display = over ? "" : "none";
                if (over) {
                    badge.title = `curvature ${c} 는 권장 범위(±${CURVATURE_SOFT_LIMIT})를 벗어납니다 — 곡선이 과할 수 있습니다.`;
                }
            };

            wrap.append(curvInput, biasInput, badge);
            syncCurveWarning();
            // 캔버스 드래그·프리셋 등 셀 밖에서 값이 바뀔 때도 배지를 맞출 수 있게 노출한다.
            curvInput.__syncCurveWarning = syncCurveWarning;
            tdCurve.appendChild(wrap);
        } else {
            tdCurve.textContent = "-";
            tdCurve.style.color = "#999";
            if (isWaitNode) tdCurve.title = "정지(wait) Node는 곡선이 정의되지 않습니다.";
        }

        // BPM 컬럼 추가 (Direction, Both, Node 계열 노트에서만 editable)
        const tdBpm = document.createElement("td");
        const canEditBpm = BPM_EDITABLE_TYPES.includes(note.type);

        if (canEditBpm) {
            const inputBpm = document.createElement("input");
            inputBpm.type = "number";
            inputBpm.min = "60";
            inputBpm.max = "300";
            inputBpm.step = "0.1";
            inputBpm.value = note.bpm || bpm;
            inputBpm.style.width = "60px";
            inputBpm.style.fontSize = "11px";
            inputBpm.dataset.noteIndex = index;
            inputBpm.dataset.noteField = "bpm";
            inputBpm.addEventListener("change", () => {
                const newBpm = parseFloat(inputBpm.value) || bpm;
                const minBpm = parseFloat(document.getElementById("min-bpm").value || 1);
                const maxBpm = parseFloat(document.getElementById("max-bpm").value || 999);
                if (newBpm >= minBpm && newBpm <= maxBpm) {
                    // 변경 전 상태를 히스토리에 저장
                    saveState();

                    // 다중 선택·상속 갱신 규칙은 인스펙터와 **같은 함수**를 쓴다.
                    applyBpmToNotes(index, newBpm);

                    saveToStorage();
                    drawPath();
                    // BPM은 Tab 노트 상속값과 이후 Time 표시를 바꾸므로 재생성이 필요하다.
                    renderNoteListPreservingFocus();
                     } else {
                    inputBpm.value = note.bpm || bpm; // 복원
                }
            });
            tdBpm.appendChild(inputBpm);
        } else {
            // Tab 계열 노트는 이미 상속받은 BPM 값을 표시
            tdBpm.textContent = note.bpm || bpm;
            tdBpm.style.color = "#999";
            tdBpm.style.fontStyle = "italic";
            tdBpm.title = "Tab 노트는 다음 BPM 편집 가능 노트의 값을 추종합니다";
        }

        // Subdivisions 컬럼 추가 (Direction, Both, Node 계열 노트에서만 editable)
        const tdSubdivisions = document.createElement("td");
        // 목록은 한 곳에서 온다 — 인스펙터와 갈리면 한쪽에서만 편집돼 상속이 되돌린다.
        const canEditSubdivisions = SUBDIVISIONS_EDITABLE_TYPES.includes(note.type);

        if (canEditSubdivisions) {
            const selectSubdivisions = document.createElement("select");
            [2, 4, 8, 12, 16, 24, 32, 48].forEach(subValue => {
                const opt = document.createElement("option");
                opt.value = subValue;
                opt.textContent = `${subValue}분박`;
                if ((note.subdivisions || subdivisions) === subValue) {
                    opt.selected = true;
                }
                selectSubdivisions.appendChild(opt);
            });
            selectSubdivisions.style.fontSize = "11px";
            selectSubdivisions.style.width = "65px";
            selectSubdivisions.dataset.noteIndex = index;
            selectSubdivisions.dataset.noteField = "subdivisions";
            selectSubdivisions.addEventListener("change", () => {
                // 변경 전 상태를 히스토리에 저장
                saveState();

                const newSubdivisions = parseInt(selectSubdivisions.value);

                // 🔴 **시간을 유지한 채 분박만 바꾼다**(§1-2-D, D-018).
                // beat는 "subdivisions 단위의 개수"라 분박만 바꾸면 노트가 시간축에서
                // 튄다(4→8이면 절반 앞으로). beat를 함께 배로 늘려야 제자리다.
                // 규칙은 인스펙터와 **같은 함수**를 쓴다 — 흩어지면 경로마다 갈린다.
                applySubdivisionsToNotes(index, newSubdivisions);

                updateTabNotesInheritance(); // Tab 노트들의 상속 값 업데이트
                invalidatePathCache();       // 분박이 바뀌면 구간 오프셋·경로가 낡는다
                saveToStorage();
                drawPath();
                // subdivisions도 상속·Time 표시에 영향을 준다.
                renderNoteListPreservingFocus();
                // 옮겨진 노트를 타임라인·캔버스에서 따라가 보여준다(사용자 요구 3).
                focusNoteAfterRetime(index);
             });
            tdSubdivisions.appendChild(selectSubdivisions);
        } else {
            // Tab 계열 노트는 이미 상속받은 Subdivisions 값을 표시
            tdSubdivisions.textContent = `${note.subdivisions || subdivisions}분박`;
            tdSubdivisions.style.color = "#999";
            tdSubdivisions.style.fontStyle = "italic";
            tdSubdivisions.title = "Tab 노트는 다음 Subdivisions 편집 가능 노트의 값을 추종합니다";
        }

        // Fade 컬럼 추가 (Direction, Both, Node 계열 노트에서만 editable)
        const tdFade = document.createElement("td");
        const canEditFade = ["direction", "longdirection", "both", "longboth", "node"].includes(note.type);

        if (canEditFade) {
            const fadeCheckbox = document.createElement("input");
            fadeCheckbox.type = "checkbox";
            fadeCheckbox.checked = note.fade || false;
            fadeCheckbox.title = "BPM 페이드 활성화 (이전 노트부터 현재 노트까지 점진적 변경)";
            fadeCheckbox.addEventListener("change", () => {
                // 변경 전 상태를 히스토리에 저장
                saveState();

                const newFade = fadeCheckbox.checked;

                // 다중 선택된 노트 중 Fade 변경이 가능한 노트만 변경
                if (selectedNoteIndices.has(index) && selectedNoteIndices.size > 1) {
                    selectedNoteIndices.forEach(idx => {
                        if (idx < notes.length) {
                            const n = notes[idx];
                            // Fade를 가질 수 있는 타입인지 확인
                            if (["direction", "longdirection", "both", "longboth", "node"].includes(n.type)) {
                                n.fade = newFade;
                            }
                        }
                    });
                } else {
                    note.fade = newFade;
                }

                saveToStorage();
                drawPath();
                renderNoteList();
             });
            tdFade.appendChild(fadeCheckbox);
        } else {
            tdFade.textContent = "-";
            tdFade.style.color = "#999";
        }

        // Wait 컬럼 추가 (Node 타입만)
        const tdWait = document.createElement("td");
        if (note.type === "node") {
            const waitCheckbox = document.createElement("input");
            waitCheckbox.type = "checkbox";
            waitCheckbox.checked = note.wait || false;
            waitCheckbox.addEventListener("change", () => {
                // 변경 전 상태를 히스토리에 저장
                saveState();

                note.wait = waitCheckbox.checked;
                saveToStorage();
                drawPath();
            });
            tdWait.appendChild(waitCheckbox);
        } else {
            tdWait.textContent = "-";
        }

        // Reset 컬럼 추가 (모든 타입)
        const tdReset = document.createElement("td");
        const resetCheckbox = document.createElement("input");
        resetCheckbox.type = "checkbox";
        resetCheckbox.checked = note.beatReset || false;
        resetCheckbox.title = "이 노트 이후 beat를 0부터 재시작";
        resetCheckbox.addEventListener("change", () => {
            saveState();
            note.beatReset = resetCheckbox.checked;
            invalidatePathCache();
            saveToStorage();
            drawPath();
            renderNoteList();
        });
        tdReset.appendChild(resetCheckbox);

        const tdDelete = document.createElement("td");
        const btn = document.createElement("button");
        btn.textContent = "삭제";
        btn.disabled = isChartStartNote(note, index);   // 인덱스가 아니라 속성으로 판정(감사 B-7)
        btn.addEventListener("click", () => {
            // 변경 전 상태를 히스토리에 저장
            saveState();

            notes.splice(index, 1);

            // 캐시 무효화
            invalidatePathCache();

            saveToStorage();
            drawPath();
            renderNoteList();
                        });
        tdDelete.appendChild(btn);

        tr.append(tdIndex, tdType, tdBeat, tdTime, tdLong, tdDir, tdAngle, tdCurve, tdBpm, tdSubdivisions, tdFade, tdWait, tdReset, tdDelete);
        tr.addEventListener("click", (e) => {
            if (["INPUT", "SELECT", "BUTTON"].includes(e.target.tagName))
                return;

            if (e.shiftKey && lastClickedNoteIndex !== null) {
                // Shift 클릭: 범위 선택
                const start = Math.min(lastClickedNoteIndex, index);
                const end = Math.max(lastClickedNoteIndex, index);
                for (let i = start; i <= end; i++) {
                    selectedNoteIndices.add(i);
                }
                lastClickedNoteIndex = index;
                renderNoteList();
            } else if (e.ctrlKey || e.metaKey) {
                // Ctrl/Cmd 클릭: 다중 선택
                if (selectedNoteIndices.has(index)) {
                    selectedNoteIndices.delete(index);
                } else {
                    selectedNoteIndices.add(index);
                }
                lastClickedNoteIndex = index;
                renderNoteList();
            } else {
                // 일반 클릭: 포커스
                lastClickedNoteIndex = index;
                focusNoteAtIndex(index);
            }
        });
        tbody.appendChild(tr);
    }

    // 하단 스페이서 (스크롤 위치 유지용)
    if (endIndex < totalNotes) {
        const remainingHeight = (totalNotes - endIndex) * noteState.itemHeight;
        const spacerBottom = document.createElement("tr");
        spacerBottom.style.height = `${remainingHeight}px`;
        spacerBottom.style.pointerEvents = "none";
        tbody.appendChild(spacerBottom);
    }
}

function focusNoteAtIndex(index) {
    if (index < 0 || index >= notes.length) {
        selectedNoteIndex = null;
        drawPath();
        renderNoteList();
        updateSelectedNoteUnityCoordinates();
        return;
    }

    selectedNoteIndex = index;
    console.log('focusNoteAtIndex - selectedNoteIndex set to:', selectedNoteIndex);
    const note = notes[index];

    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const preDelaySeconds = getPreDelaySeconds();

    const offsets = getOrComputeSectionOffsets();
    const noteWithOffset = { ...note, _sectionOffset: offsets[index] || 0 };
    const pathBeat = calculatePathBeat(noteWithOffset, preDelaySeconds, bpm, subdivisions);

    const directionNotes = getDirectionNotesWithOffsets(bpm, subdivisions);

    const pathDirectionNotes = directionNotes.map((n, i) => {
        const pBeat = calculatePathBeat(n, preDelaySeconds, bpm, subdivisions);
        let finalTime;
        const sectionOffset = n._sectionOffset || 0;
        if (n.beat === 0 && n.type === "direction" && sectionOffset === 0) {
            finalTime = 0;
        } else {
            const noteBpm = n.bpm || bpm;
            const noteSubdivisions = n.subdivisions || subdivisions;
            const originalTime = sectionOffset + beatToTime(n.beat, noteBpm, noteSubdivisions);
            finalTime = originalTime + preDelaySeconds;
        }
        return { ...n, pathBeat: pBeat, finalTime: finalTime };
    }).sort((a, b) => a.finalTime - b.finalTime);

    const { nodePositions, segmentTimes } = calculateNodePositions(pathDirectionNotes, bpm, subdivisions);

    let noteFinalTime;
    const noteSectionOffset = offsets[index] || 0;
    if (note.beat === 0 && note.type === "direction" && noteSectionOffset === 0) {
        noteFinalTime = preDelaySeconds;
    } else if ((note.type === "tab" || note.type === "longtab") &&
               note.hasOwnProperty('fadeDirectTime')) {
        noteFinalTime = note.fadeDirectTime;
    } else {
        const noteBpm = note.bpm || bpm;
        const noteSubdivisions = note.subdivisions || subdivisions;
        noteFinalTime = noteSectionOffset + beatToTime(note.beat, noteBpm, noteSubdivisions) + preDelaySeconds;
    }
    const noteCanvasPos = getNotePositionFromPathData(noteFinalTime, pathDirectionNotes, nodePositions, segmentTimes);

    if (noteCanvasPos) {
        viewOffset.x = canvas.width / 2 - noteCanvasPos.x * zoom;
        viewOffset.y = canvas.height / 2 - noteCanvasPos.y * zoom;
    }

    // 노트 리스트 하이라이트 업데이트
    const tbody = document.getElementById("note-list");
    Array.from(tbody.children).forEach((row) => {
        const rowIndex = parseInt(row.dataset.index, 10);
        if (rowIndex === index) {
            row.classList.add("highlight");
            row.scrollIntoView({ behavior: "smooth", block: "nearest" });
        } else {
            row.classList.remove("highlight");
        }
    });

    drawPath();

    // Unity 좌표 정보 업데이트
    updateSelectedNoteUnityCoordinates();
}

function focusEventAtIndex(index) {
    const events = getAllEvents();
    if (index < 0 || index >= events.length) {
        return;
    }

    const event = events[index];
    if (!event || typeof event.eventTime !== 'number') {
        return;
    }

    // 이벤트 시간에 해당하는 위치 계산
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const preDelaySeconds = getPreDelaySeconds();

    const directionNotes = getDirectionNotesWithOffsets(bpm, subdivisions);

    const pathDirectionNotes = calculatePathDirectionNotes(directionNotes, bpm, subdivisions, preDelaySeconds);
    const pathData = calculateNodePositions(pathDirectionNotes, bpm, subdivisions);
    const nodePositions = pathData.nodePositions;

    // 이벤트 위치 계산
    const pos = getNotePositionFromPathData(event.eventTime, pathDirectionNotes, nodePositions, pathData.segmentTimes);
    if (!pos) return;

    // 화면 중앙으로 이동
    viewOffset.x = canvas.width / 2 - pos.x * zoom;
    viewOffset.y = canvas.height / 2 - pos.y * zoom;

    // 이벤트 강조 타이머 설정
    highlightedEventIndex = index;
    highlightedEventTimer = 2.0; // 2초간 강조

    drawPath();

    // 그리기 루프 시작 (강조 효과를 위해)
    if (!isDrawLoopRunning) {
        isDrawLoopRunning = true;
        drawLoop();
    }
}



function setupToggleFeatures() {
    const sidebar = document.getElementById('sidebar');
    const sidebarToggle = document.getElementById('sidebar-toggle');
    const controlBar = document.getElementById('control-bar');
    const controlBarToggle = document.getElementById('control-bar-toggle');
    const main = document.getElementById('main');

    sidebarToggle.addEventListener('click', () => {
        const isHidden = sidebar.classList.contains('hidden');

        if (isHidden) {
            sidebar.classList.remove('hidden');
            sidebarToggle.classList.remove('hidden');
            main.classList.remove('sidebar-hidden');
            sidebarToggle.textContent = '◀';
        } else {
            sidebar.classList.add('hidden');
            sidebarToggle.classList.add('hidden');
            main.classList.add('sidebar-hidden');
            sidebarToggle.textContent = '▶';
        }

        setTimeout(() => {
            resizeCanvas();
            drawPath();
        }, 300);
    });

    controlBarToggle.addEventListener('click', () => {
        const isHidden = controlBar.classList.contains('hidden');

        if (isHidden) {
            controlBar.classList.remove('hidden');
            controlBarToggle.classList.remove('hidden');
            controlBarToggle.textContent = '×';
        } else {
            controlBar.classList.add('hidden');
            controlBarToggle.classList.add('hidden');
            controlBarToggle.textContent = '⚙';
        }
    });
}

function setupResizerFeatures() {
    const sidebar = document.getElementById('sidebar');
    const sidebarResizer = document.getElementById('sidebar-resizer');
    const sidebarToggle = document.querySelector('.sidebar-toggle');
    const main = document.getElementById('main');

    const controlBar = document.getElementById('control-bar');
    const controlBarResizer = document.getElementById('control-bar-resizer');

    let isResizing = false;
    let activeResizer = null; // 어떤 리사이저가 활성화된지 추적
    let startX = 0;
    let startWidth = 0;

    // 사이드바 리사이저
    sidebarResizer.addEventListener('mousedown', (e) => {
        isResizing = true;
        activeResizer = 'sidebar';
        startX = e.clientX;
        startWidth = parseInt(getComputedStyle(sidebar).width);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        e.preventDefault();
        e.stopPropagation();
    });

    // 컨트롤바 리사이저
    controlBarResizer.addEventListener('mousedown', (e) => {
        isResizing = true;
        activeResizer = 'controlbar';
        startX = e.clientX;
        startWidth = parseInt(getComputedStyle(controlBar).width);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        e.preventDefault();
        e.stopPropagation();
    });

    document.addEventListener('mousemove', (e) => {
        if (!isResizing || !activeResizer) return;

        const deltaX = e.clientX - startX;

        // 사이드바 리사이저
        if (activeResizer === 'sidebar') {
            // 뷰포트보다 넓어지면 토글 버튼이 화면 밖으로 나가 사이드바를 못 접는다.
            // 화면 너비의 80%를 넘지 않게 상한을 건다.
            const cap = Math.min(800, Math.floor(window.innerWidth * 0.8));
            const newWidth = Math.max(300, Math.min(cap, startWidth + deltaX));

            sidebar.style.width = newWidth + 'px';
            sidebarResizer.style.left = (newWidth - 2) + 'px';
            sidebarToggle.style.left = newWidth + 'px';
            main.style.marginLeft = newWidth + 'px';

            // CSS 변수로 사이드바 너비 업데이트
            document.documentElement.style.setProperty('--sidebar-width', newWidth + 'px');
        }

        // 컨트롤바 리사이저
        if (activeResizer === 'controlbar') {
            const newWidth = Math.max(200, Math.min(500, startWidth - deltaX));
            controlBar.style.width = newWidth + 'px';
        }

        e.preventDefault();
    });

    document.addEventListener('mouseup', () => {
        if (isResizing) {
            isResizing = false;
            activeResizer = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';

            // 캔버스와 웨이브폼 다시 그리기
            setTimeout(() => {
                resizeCanvas();
 {
                                        }
                drawPath();
            }, 50);
        }
    });

    // 마우스가 페이지를 벗어났을 때도 리사이징 중지
    document.addEventListener('mouseleave', () => {
        if (isResizing) {
            isResizing = false;
            activeResizer = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
    });
}

function setupNoteButtons() {
    // 노트 버튼들은 이벤트 델리게이션으로 처리됨
    // 기존 개별 addEventListener 제거하여 중복 실행 방지
}

function setupVolumeControls() {
    const musicVolumeSlider = document.getElementById("music-volume");
    const sfxVolumeSlider = document.getElementById("sfx-volume");
    const musicVolumeDisplay = document.getElementById("music-volume-display");
    const sfxVolumeDisplay = document.getElementById("sfx-volume-display");

    musicVolumeSlider.addEventListener("input", (e) => {
        musicVolume = parseInt(e.target.value) / 100;
        demoAudio.volume = musicVolume;
        musicVolumeDisplay.textContent = e.target.value + "%";
        localStorage.setItem("musicVolume", musicVolume);
    });

    sfxVolumeSlider.addEventListener("input", (e) => {
        sfxVolume = parseInt(e.target.value) / 100;
        sfxVolumeDisplay.textContent = e.target.value + "%";

        [...tabSoundPool, ...directionSoundPool].forEach(audio => {
            audio.volume = sfxVolume;
        });

        localStorage.setItem("sfxVolume", sfxVolume);
    });

    const savedMusicVolume = localStorage.getItem("musicVolume");
    const savedSfxVolume = localStorage.getItem("sfxVolume");

    if (savedMusicVolume !== null) {
        musicVolume = parseFloat(savedMusicVolume);
        musicVolumeSlider.value = Math.round(musicVolume * 100);
        musicVolumeDisplay.textContent = Math.round(musicVolume * 100) + "%";
        demoAudio.volume = musicVolume;
    }

    if (savedSfxVolume !== null) {
        sfxVolume = parseFloat(savedSfxVolume);
        sfxVolumeSlider.value = Math.round(sfxVolume * 100);
        sfxVolumeDisplay.textContent = Math.round(sfxVolume * 100) + "%";
    }
}

function setupSubdivisionsOptions() {
    const subdivisionsSelect = document.getElementById("subdivisions");
    if (!subdivisionsSelect) {
        console.warn("subdivisions select element not found");
        return;
    }

    subdivisionsSelect.innerHTML = '';

    const subdivisionsOptions = [{
        value: 2,
        label: "2"
    }, {
        value: 4,
        label: "4"
    }, {
        value: 8,
        label: "8"
    }, {
        value: 12,
        label: "12"
    }, {
        value: 16,
        label: "16"
    }, {
        value: 24,
        label: "24"
    }, {
        value: 32,
        label: "32"
    }, {
        value: 48,
        label: "48"
    }
    ];

    subdivisionsOptions.forEach(option => {
        const optionElement = document.createElement("option");
        optionElement.value = option.value;
        optionElement.textContent = option.label;
        subdivisionsSelect.appendChild(optionElement);
    });

    subdivisionsSelect.value = "16";
    subdivisionsSelect.dataset.previousValue = "16";
}

function showSuccessMessage(message) {
    const successDiv = document.createElement('div');
    successDiv.innerHTML = message;
    successDiv.style.cssText = `\n        position: fixed; top: 10px; left: 50%; transform: translateX(-50%);\n        z-index: 9999; padding: 12px 24px;\n        background: linear-gradient(135deg, #4CAF50, #45a049);\n        color: white; border-radius: 6px; font-size: 14px; font-weight: bold;\n        box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);\n        opacity: 0; transition: opacity 0.3s ease;\n    `;

    document.body.appendChild(successDiv);

    setTimeout(() => successDiv.style.opacity = '1', 10);
    setTimeout(() => {
        successDiv.style.opacity = '0';
        setTimeout(() => successDiv.remove(), 300);
    }, 3000);
}

function createAudioSystemButton() {
    const button = document.createElement('button');
    button.id = 'init-improved-audio';
    button.innerHTML = '🎵 개선된 오디오 시스템 시작';
    button.style.cssText = `\n        position: fixed; top: 10px; left: 50%; transform: translateX(-50%);\n        z-index: 9999; padding: 15px 30px;\n        background: linear-gradient(135deg, #4CAF50, #45a049);\n        color: white; border: none; border-radius: 8px;\n        font-size: 16px; font-weight: bold; cursor: pointer;\n        box-shadow: 0 4px 16px rgba(76, 175, 80, 0.4);\n        transition: all 0.3s ease;\n    `;

    button.addEventListener('click', async () => {
        try {
            button.disabled = true;
            button.innerHTML = '🔄 초기화 중...';

            if (typeof AudioSyncModule === 'undefined') {
                throw new Error('AudioSyncModule이 로드되지 않았습니다. audio-sync-module.js 파일을 확인해주세요.');
            }

            const success = await AudioSyncModule.init();

            if (success) {
                showSuccessMessage('✅ 개선된 오디오 시스템이 활성화되었습니다!');
                button.remove();

                console.log('🎉 개선된 오디오 시스템 활성화 완료');

                setTimeout(() => AudioSyncModule.logStatus(), 1000);

            } else {
                throw new Error('오디오 시스템 초기화 실패');
            }

        } catch (error) {
            console.error('❌ 오디오 시스템 초기화 오류:', error);
            alert(`오디오 시스템 초기화에 실패했습니다.\n\n오류: ${error.message}\n\n브라우저를 새로고침한 후 다시 시도해주세요.`);

            button.disabled = false;
            button.innerHTML = '🎵 개선된 오디오 시스템 시작';
        }
    });

    return button;
}

function getNotePosition(beat) {
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const preDelaySeconds = getPreDelaySeconds();

    let pathBeat = beat;

    const directionNotes = notes.filter(n =>
        n.type === "direction" ||
        n.type === "both" ||
        n.type === "longdirection" ||
        n.type === "longboth"
    ).sort((a, b) => a.beat - b.beat);
    const pathDirectionNotes = directionNotes.map((note, index) => {
        let noteBeat;
        if (note.beat === 0 && note.type === "direction") {
            noteBeat = 0;
        } else {
            // 각 노트의 개별 BPM/subdivision 사용
            const noteBpm = note.bpm || bpm;
            const noteSubdivisions = note.subdivisions || subdivisions;
            const originalTime = beatToTime(note.beat, noteBpm, noteSubdivisions);
            const adjustedTime = originalTime + preDelaySeconds;
            // pathBeat 계산은 전역 BPM/subdivision으로 통일 (경로 생성을 위해)
            noteBeat = timeToBeat(adjustedTime, bpm, subdivisions);
        }
        return {
            ...note,
            pathBeat: noteBeat
        };
    }).sort((a, b) => a.pathBeat - b.pathBeat);

    let pos = {
        x: 0,
        y: 0
    };
    const nodePositions = [pos];

    for (let i = 0; i < pathDirectionNotes.length - 1; i++) {
        const a = pathDirectionNotes[i];
        const b = pathDirectionNotes[i + 1];
        const dBeat = b.pathBeat - a.pathBeat;
        const dist = (8 * dBeat) / subdivisions;
        const [dx, dy] = directionToVector(a.direction);
        const mag = Math.hypot(dx, dy) || 1;
        const next = {
            x: pos.x + (dx / mag) * dist,
            y: pos.y + (dy / mag) * dist
        };
        if (a.pathBeat <= pathBeat && pathBeat <= b.pathBeat) {
            const interp = (pathBeat - a.pathBeat) / (b.pathBeat - a.pathBeat);
            return {
                x: pos.x + (next.x - pos.x) * interp,
                y: pos.y + (next.y - pos.y) * interp
            };
        }
        pos = next;
        nodePositions.push(pos);
    }
    return null;
}


function drawLoop() {
    let needsRedraw = false;

    if (highlightedNoteTimer > 0) {
        highlightedNoteTimer -= 1 / 60;
        if (highlightedNoteTimer <= 0) {
            highlightedNoteIndex = null;
            highlightedNoteTimer = 0;
        }
        needsRedraw = true;
    }

    if (highlightedEventTimer > 0) {
        highlightedEventTimer -= 1 / 60;
        if (highlightedEventTimer <= 0) {
            highlightedEventIndex = null;
            highlightedEventTimer = 0;
        }
        needsRedraw = true;
    }

    if (pathHighlightTimer > 0) {
        pathHighlightTimer -= 1 / 60;
        if (pathHighlightTimer <= 0) {
            pathHighlightTimer = 0;
        }
        needsRedraw = true;
    }

    // 단일 drawPath 호출로 모든 하이라이트 효과를 처리 (GPU 성능 최적화)
    if (needsRedraw) {
        drawPath();
    }

    if (highlightedNoteTimer > 0 || highlightedEventTimer > 0 || pathHighlightTimer > 0) {
        globalAnimationFrameId = requestAnimationFrame(drawLoop);
    } else {
        isDrawLoopRunning = false;
    }
}

// 노트 추가 로직
// noteProps에 `_explicitBeat`(구간 지역 beat)와 `_insertionIndex`가 있으면
// beat 추정을 건너뛰고 그 자리에 넣는다 — 격자 클릭 삽입용(설계 §1-4).
//
// **인자를 안 주는 기존 호출(키보드 q~d, 컨트롤바 버튼)은 동작이 그대로다.**
// 두 필드는 밑에서 제거하므로 노트에 새어 나가지 않는다.
function addNote(noteProps) {
    try {
        const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
        let newBeat;
        let insertionIndex;

        const hasExplicitBeat = noteProps && typeof noteProps._explicitBeat === "number";

        if (hasExplicitBeat) {
            // 격자 클릭: 위치가 이미 정해져 있다. 추정하지 않는다.
            newBeat = noteProps._explicitBeat;
            insertionIndex = Number.isInteger(noteProps._insertionIndex)
                ? Math.max(0, Math.min(notes.length, noteProps._insertionIndex))
                : notes.length;
        } else if (selectedNoteIndex !== null && selectedNoteIndex < notes.length) {
            const selectedNote = notes[selectedNoteIndex];

            // 선택된 노트와 직전 노트의 간격을 계산
            let interval;
            if (selectedNoteIndex > 0) {
                const previousNote = notes[selectedNoteIndex - 1];
                const addOffsets = getOrComputeSectionOffsets();
                const selectedOffset = addOffsets[selectedNoteIndex] || 0;
                const previousOffset = addOffsets[selectedNoteIndex - 1] || 0;
                if (selectedOffset === previousOffset) {
                    // 같은 구간 — beat 차이를 간격으로 사용
                    interval = selectedNote.beat - previousNote.beat;
                } else {
                    // 다른 구간 — 기본 간격 사용
                    interval = subdivisions;
                }
            } else {
                // 첫 번째 노트라면 기본 간격 사용
                interval = subdivisions;
            }

            // 간격이 0 이하라면 기본 간격 사용
            if (interval <= 0) {
                interval = subdivisions;
            }

            // 선택된 노트 + 간격으로 새 노트 beat 설정
            newBeat = selectedNote.beat + interval;

            // 선택된 노트 바로 다음 위치에 삽입
            insertionIndex = selectedNoteIndex + 1;
        } else {
            // 현재 BPM/Subdivisions 가져오기 (maxBeat 계산에 필요)
            const bpm = parseFloat(document.getElementById("bpm").value || 120);
            // subdivisions는 이미 함수 상단에서 선언됨

            insertionIndex = notes.length;

            // 마지막 구간의 노트들만 기준으로 maxBeat 계산
            const addOffsets = getOrComputeSectionOffsets();
            const lastSectionOffset = addOffsets.length > 0 ? addOffsets[addOffsets.length - 1] : 0;
            const lastSectionNotes = notes.filter((n, i) => (addOffsets[i] || 0) === lastSectionOffset);
            const maxBeat = Math.max(0, ...lastSectionNotes.map(n => {
                let endBeat = n.beat;
                if (n.isLong && n.longTime > 0) {
                    // 롱노트 길이를 해당 노트의 BPM/subdivision으로 시간 변환 후 전역 기준으로 재변환
                    const noteBpm = n.bpm || bpm;
                    const noteSubdivisions = n.subdivisions || subdivisions;
                    const longTimeInSeconds = beatToTime(n.longTime, noteBpm, noteSubdivisions);
                    const longTimeInGlobalBeats = timeToBeat(longTimeInSeconds, bpm, subdivisions);
                    endBeat = n.beat + longTimeInGlobalBeats;
                }
                return endBeat;
            }));
            newBeat = maxBeat + subdivisions;
        }

        // BPM/Subdivisions 상속 로직
        let inheritedBpm, inheritedSubdivisions;
        if (hasExplicitBeat) {
            // 격자 클릭은 선택과 무관한 자리를 지목한다. 멀리 있는 선택 노트에서
            // 상속하면 엉뚱한 BPM이 붙으므로, **삽입 지점 직전 노트**를 따른다.
            // 앞에 아무것도 없으면 전역값.
            const prev = insertionIndex > 0 ? notes[insertionIndex - 1] : null;
            inheritedBpm = prev?.bpm || parseFloat(document.getElementById("bpm").value || 120);
            inheritedSubdivisions = prev?.subdivisions || subdivisions;
        } else if (selectedNoteIndex !== null && selectedNoteIndex !== -1 && notes[selectedNoteIndex]) {
            // 선택된 노트가 있으면 그 노트의 BPM/subdivision을 상속
            const selectedNote = notes[selectedNoteIndex];
            inheritedBpm = selectedNote.bpm || parseFloat(document.getElementById("bpm").value || 120);
            inheritedSubdivisions = selectedNote.subdivisions || parseInt(document.getElementById("subdivisions").value || 16);
        } else {
            // 선택된 노트가 없으면 현재 전역 값 사용
            inheritedBpm = parseFloat(document.getElementById("bpm").value || 120);
            inheritedSubdivisions = parseInt(document.getElementById("subdivisions").value || 16);
        }

        const newNote = {
            ...noteProps,
            beat: newBeat,
            bpm: inheritedBpm,          // 상속받은 BPM 저장
            subdivisions: inheritedSubdivisions  // 상속받은 subdivision 저장
        };

        // 호출 규약용 사설 필드는 노트에 남기지 않는다. 직렬화 화이트리스트가
        // 걸러주긴 하지만(noteToJsonFormat), 메모리상의 노트도 깨끗해야
        // cloneNote·undo 경로에서 되살아나지 않는다.
        delete newNote._explicitBeat;
        delete newNote._insertionIndex;

        if (newNote.type === "direction" || newNote.type === "longdirection" || newNote.type === "both" || newNote.type === "longboth") {
            const addBpm = parseFloat(document.getElementById("bpm").value || 120);
            const addDirOffsets = getOrComputeSectionOffsets();
            const precedingDirectionNotes = notes
                .slice(0, insertionIndex)
                .filter(n => n.type === "direction" || n.type === "longdirection" || n.type === "both" || n.type === "longboth")
                .sort((a, b) => {
                    const idxA = notes.indexOf(a);
                    const idxB = notes.indexOf(b);
                    const tA = (addDirOffsets[idxA] || 0) + beatToTime(a.beat, a.bpm || addBpm, a.subdivisions || subdivisions);
                    const tB = (addDirOffsets[idxB] || 0) + beatToTime(b.beat, b.bpm || addBpm, b.subdivisions || subdivisions);
                    return tA - tB;
                });

            const lastDirNote = precedingDirectionNotes.length > 0 ? precedingDirectionNotes[precedingDirectionNotes.length - 1] : null;
            newNote.direction = lastDirNote ? lastDirNote.direction : "none";
        } else if (newNote.type === "node") {
            // Node 노트는 기본 'none' (이전 방향을 추종하는 기존 동작)
            newNote.direction = "none";
        }

        if (newNote.isLong) {
            newNote.longTime = newNote.longTime || subdivisions;
        }

        // 변경 전 상태를 히스토리에 저장
        saveState();

        notes.splice(insertionIndex, 0, newNote);

        // 캐시 무효화
        invalidatePathCache();

        saveToStorage();
        drawPath();
        renderNoteList();
 
        focusNoteAtIndex(insertionIndex);
    } catch (error) {
        console.error('Error in addNote:', error);
        console.error('noteProps:', noteProps);
        console.error('selectedNoteIndex:', selectedNoteIndex);
        console.error('notes.length:', notes.length);
    }
}

// BPM/Subdivisions 변경 시 시간 기반으로 노트 업데이트
function updateNotesForTimeBasedChange(oldBpm, oldSubdivisions, newBpm, newSubdivisions) {
    console.log(`Updating notes from BPM ${oldBpm}/${oldSubdivisions} to ${newBpm}/${newSubdivisions}`);

    notes.forEach(note => {
        // fade 구간의 직접 시간값이 있는 노트는 beat 업데이트를 건너뜀
        if ((note.type === "tab" || note.type === "longtab") &&
            note.hasOwnProperty('fadeDirectTime')) {
            console.log(`Skipping beat update for fade note with fadeDirectTime: ${note.fadeDirectTime}`);
            return;
        }

        const timeInSeconds = beatToTime(note.beat, oldBpm, oldSubdivisions);
        note.beat = timeToBeat(timeInSeconds, newBpm, newSubdivisions);

        if (note.isLong && note.longTime > 0) {
            const longTimeInSeconds = beatToTime(note.longTime, oldBpm, oldSubdivisions);
            note.longTime = timeToBeat(longTimeInSeconds, newBpm, newSubdivisions);
            console.log(`Long note length: ${longTimeInSeconds.toFixed(3)}s -> ${note.longTime} beats`);
        }

        console.log(`Note: ${timeInSeconds.toFixed(3)}s -> beat ${note.beat}`);
    });

    ensureInitialDirectionNote(notes);
}

// BPM 제약 조건 업데이트 함수
function updateBpmConstraints() {
    const minBpm = parseFloat(document.getElementById("min-bpm").value || 1);
    const maxBpm = parseFloat(document.getElementById("max-bpm").value || 999);
    const bpmField = document.getElementById("bpm");

    if (bpmField) {
        bpmField.min = minBpm;
        bpmField.max = maxBpm;
    }

    console.log(`BPM constraints updated: ${minBpm} - ${maxBpm}`);
}

// BPM 필드 변경 핸들러
function handleBpmChange(newBpm) {
    const oldBpm = parseFloat(document.getElementById("bpm").dataset.previousValue || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const isBatchEdit = document.getElementById("batch-edit-toggle").checked;

    console.log(`BPM changed from ${oldBpm} to ${newBpm}, batch edit: ${isBatchEdit}`);

    // 체크박스가 체크되어 있을 때만 기존 노트들 업데이트
    if (isBatchEdit && oldBpm !== newBpm && notes.length > 0) {
        // 모든 노트의 BPM을 새로운 값으로 업데이트
        notes.forEach(note => {
            note.bpm = newBpm;
        });
        updateNotesForTimeBasedChange(oldBpm, subdivisions, newBpm, subdivisions);
    }

    document.getElementById("bpm").dataset.previousValue = newBpm;

    saveToStorage();
    drawPath();
    renderNoteList();
            updateCoordinateInfo();
}

// Subdivisions 필드 변경 핸들러
function handleSubdivisionsChange(newSubdivisions) {
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const oldSubdivisions = parseInt(document.getElementById("subdivisions").dataset.previousValue || 16);
    const isBatchEdit = document.getElementById("batch-edit-toggle").checked;

    console.log(`Subdivisions changed from ${oldSubdivisions} to ${newSubdivisions}, batch edit: ${isBatchEdit}`);

    // 체크박스가 체크되어 있을 때만 기존 노트들 업데이트
    if (isBatchEdit && oldSubdivisions !== newSubdivisions && notes.length > 0) {
        // 모든 노트의 subdivisions을 새로운 값으로 업데이트
        notes.forEach(note => {
            note.subdivisions = newSubdivisions;
        });
        updateNotesForTimeBasedChange(bpm, oldSubdivisions, bpm, newSubdivisions);
    }

    document.getElementById("subdivisions").dataset.previousValue = newSubdivisions;

    saveToStorage();
    drawPath();
    renderNoteList();
        }

// Pre-delay 변경 핸들러
function handlePreDelayChange() {
    const preDelayField = document.getElementById("pre-delay");
    const newPreDelayMs = parseInt(preDelayField.value || 0);
    const oldPreDelayMs = parseInt(preDelayField.dataset.previousValue || 0);
    const preDelayDiffMs = newPreDelayMs - oldPreDelayMs;

    console.log(`Pre-delay changed from ${oldPreDelayMs}ms to ${newPreDelayMs}ms (diff: ${preDelayDiffMs}ms)`);

    // 모든 이벤트의 시간을 pre-delay 변경량만큼 조정
    if (preDelayDiffMs !== 0) {
        const preDelayDiffSeconds = preDelayDiffMs / 1000;
        const allEvents = getAllEvents();

        allEvents.forEach(event => {
            if (event && typeof event.eventTime === 'number') {
                event.eventTime += preDelayDiffSeconds;
            }
        });

        console.log(`Updated ${allEvents.length} events by ${preDelayDiffSeconds}s`);
    }

    // 현재 값을 이전 값으로 업데이트
    preDelayField.dataset.previousValue = newPreDelayMs;

    saveToStorage();
    renderNoteList();
    renderEventList();
        }

// 배속 변경 핸들러
function handleSpeedMultiplierChange(newSpeedMultiplier) {
    console.log(`Speed multiplier changed to ${newSpeedMultiplier}x`);

    speedMultiplier = Math.max(1.0, Math.min(3.0, newSpeedMultiplier));

    // 캐시 무효화 및 UI 업데이트
    invalidatePathCache();
    saveToStorage();
    renderNoteList();
    drawPath();
    updateCoordinateInfo();
}

// 좌표 정보 업데이트
function updateCoordinateInfo() {
    const bpm = parseFloat(document.getElementById("bpm").value || 120);

    // Unity 공식으로 정확한 계산
    const unityUnitsPerBeat = calculateUnityMovementPerBeat(bpm, speedMultiplier);
    const editorUnitsPerBeat = 24.0 * speedMultiplier; // 에디터의 현재 공식
    const conversionRatio = unityUnitsPerBeat / editorUnitsPerBeat;

    // UI 업데이트
    const editorUnitsSpan = document.getElementById("editor-units-per-beat");
    const gameUnitsSpan = document.getElementById("game-units-per-beat");
    const ratioSpan = document.getElementById("conversion-ratio");

    if (editorUnitsSpan) editorUnitsSpan.textContent = editorUnitsPerBeat.toFixed(1);
    if (gameUnitsSpan) gameUnitsSpan.textContent = unityUnitsPerBeat.toFixed(2);
    if (ratioSpan) ratioSpan.textContent = conversionRatio.toFixed(3);

    // 선택된 노트의 Unity 좌표 업데이트
    updateSelectedNoteUnityCoordinates();
}

// 선택된 노트의 Unity 좌표 계산 및 표시
function updateSelectedNoteUnityCoordinates() {
    const unityXSpan = document.getElementById("selected-note-unity-x");
    const unityYSpan = document.getElementById("selected-note-unity-y");
    const totalDistanceSpan = document.getElementById("selected-note-total-distance");

    if (!unityXSpan || !unityYSpan || !totalDistanceSpan) return;

    if (selectedNoteIndex === null || selectedNoteIndex >= notes.length) {
        unityXSpan.textContent = "-";
        unityYSpan.textContent = "-";
        totalDistanceSpan.textContent = "-";
        return;
    }

    const selectedNote = notes[selectedNoteIndex];
    const bpm = parseFloat(document.getElementById("bpm").value || 120);
    const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
    const preDelaySeconds = getPreDelaySeconds();

    try {
        // Unity 노드 위치들 계산
        const unityNodePositions = calculateUnityNodePositions(bpm, subdivisions, preDelaySeconds);

        if (unityNodePositions && unityNodePositions.length > 0) {
            // 선택된 노트의 Unity 위치 계산
            const noteUnityPosition = calculateNoteUnityPosition(selectedNote, unityNodePositions, bpm, subdivisions, preDelaySeconds);

            if (noteUnityPosition) {
                unityXSpan.textContent = noteUnityPosition.x.toFixed(2);
                unityYSpan.textContent = noteUnityPosition.y.toFixed(2);

                // 총 이동 거리 계산
                const totalDistance = Math.hypot(noteUnityPosition.x, noteUnityPosition.y);
                totalDistanceSpan.textContent = totalDistance.toFixed(2);
            } else {
                unityXSpan.textContent = "계산 불가";
                unityYSpan.textContent = "계산 불가";
                totalDistanceSpan.textContent = "-";
            }
        } else {
            unityXSpan.textContent = "-";
            unityYSpan.textContent = "-";
            totalDistanceSpan.textContent = "-";
        }
    } catch (error) {
        console.error("Unity 좌표 계산 오류:", error);
        unityXSpan.textContent = "오류";
        unityYSpan.textContent = "오류";
        totalDistanceSpan.textContent = "-";
    }
}

// Unity 노드 위치들 계산 (Unity 공식 사용)
function calculateUnityNodePositions(bpm, subdivisions, preDelaySeconds) {
    const directionNotes = getDirectionNotesWithOffsets(bpm, subdivisions);

    if (directionNotes.length === 0) return [];

    const pathDirectionNotes = calculatePathDirectionNotes(directionNotes, bpm, subdivisions, preDelaySeconds);
    const unityNodePositions = [];
    const headings = resolvePathHeadings(pathDirectionNotes);
    let currentPosition = { x: 0, y: 0 };
    unityNodePositions.push({ ...currentPosition });

    for (let i = 0; i < pathDirectionNotes.length - 1; i++) {
        const fromNote = pathDirectionNotes[i];
        const toNote = pathDirectionNotes[i + 1];
        const deltaTime = toNote.finalTime - fromNote.finalTime;

        if (toNote.type === "node" && toNote.wait) {
            // Wait 노드는 이동하지 않음
            unityNodePositions.push({ ...currentPosition });
        } else {
            // Unity 공식으로 거리 계산
            const distance = calculateUnityNodeDistance(deltaTime, bpm, speedMultiplier);

            // Unity 정규화된 방향 벡터
            const { direction, angleOffset } = headings[i];
            const [rawX, rawY] = directionToVectorWithAngle(direction, angleOffset);
            const mag = Math.hypot(rawX, rawY) || 1;
            const [dx, dy] = [rawX / mag, rawY / mag];

            // 다음 위치 계산
            const nextPosition = {
                x: currentPosition.x + dx * distance,
                y: currentPosition.y + dy * distance
            };

            currentPosition = nextPosition;
            unityNodePositions.push({ ...currentPosition });
        }
    }

    return unityNodePositions;
}

// 특정 노트의 Unity 위치 계산
function calculateNoteUnityPosition(note, unityNodePositions, bpm, subdivisions, preDelaySeconds) {
    const directionNotes = getDirectionNotesWithOffsets(bpm, subdivisions);

    if (directionNotes.length === 0) return null;

    const pathDirectionNotes = calculatePathDirectionNotes(directionNotes, bpm, subdivisions, preDelaySeconds);
    const timing = getNoteTimingParams(note, bpm, subdivisions);
    const noteIndex = notes.indexOf(note);
    const noteSectionOffset = (noteIndex >= 0) ? (getOrComputeSectionOffsets()[noteIndex] || 0) : 0;
    const noteTime = noteSectionOffset + beatToTime(note.beat, timing.bpm, timing.subdivisions) + preDelaySeconds;

    // 노트가 속한 구간 찾기
    for (let i = 0; i < pathDirectionNotes.length - 1; i++) {
        const fromNode = pathDirectionNotes[i];
        const toNode = pathDirectionNotes[i + 1];

        if (noteTime >= fromNode.finalTime && noteTime <= toNode.finalTime) {
            // Unity Lerp 방식으로 위치 계산
            const previousNodePosition = unityNodePositions[i];
            const nextNodePosition = unityNodePositions[i + 1];

            return calculateUnityNotePosition(
                noteTime,
                fromNode.finalTime,
                toNode.finalTime,
                previousNodePosition,
                nextNodePosition
            );
        }
    }

    return null;
}

// EventList UI 렌더링
// renderEventList 래퍼 함수 (디바운싱 적용)
function renderEventList() {
    scheduleRender({ eventList: true });
}

// 접기 상태를 메모리에 저장 (DOM 순회 제거)
const collapseStatesMemoryCache = {
    params: new Map(), // eventIndex -> boolean
    dialog: new Map()  // eventIndex -> boolean
};

// 옵션 요소 캐싱 (중복 생성 방지)
const optionElementCache = {
    eventTypes: null,
    dialogItemTypes: null
};

// EventType 옵션 캐시 생성
function getCachedEventTypeOptions() {
    if (!optionElementCache.eventTypes) {
        const eventTypes = getEventTypes();
        const fragment = document.createDocumentFragment();

        eventTypes.forEach(type => {
            const option = document.createElement("option");
            option.value = type;
            option.textContent = type;
            const description = getEventTypeDescription(type);
            if (description) {
                option.title = description;
            }
            fragment.appendChild(option);
        });

        optionElementCache.eventTypes = fragment;
    }

    // cloneNode로 재사용
    return optionElementCache.eventTypes.cloneNode(true);
}


// DialogItemType 옵션 캐시 생성
function getCachedDialogItemTypeOptions() {
    if (!optionElementCache.dialogItemTypes) {
        const itemTypes = getDialogItemTypes();
        const fragment = document.createDocumentFragment();

        itemTypes.forEach(type => {
            const option = document.createElement("option");
            option.value = type.type;
            option.textContent = type.type;
            fragment.appendChild(option);
        });

        optionElementCache.dialogItemTypes = fragment;
    }

    return optionElementCache.dialogItemTypes.cloneNode(true);
}

// 실제 렌더링 함수 (즉시 실행)
function renderEventListImmediate() {
    const startTime = performance.now();
    const events = getAllEvents();
    const state = virtualScrollState.event;

    // 100개 이하면 전체 렌더링, 100개 초과면 가상 스크롤링
    let result;
    if (state.enabled && events.length > 100) {
        result = renderEventListVirtualized();
    } else {
        result = renderEventListImmediate_Original();
    }

    const endTime = performance.now();
    const renderTime = endTime - startTime;

    // 성능 로그 (100개 이상일 때만 출력)
    if (events.length > 100) {
        console.log(`[EventList 렌더링] ${events.length}개 이벤트, ${renderTime.toFixed(2)}ms, 방식: ${state.enabled ? '가상스크롤링' : '전체렌더링'}`);
    }

    return result;
}

// 가상 스크롤링 렌더링 함수
function renderEventListVirtualized() {
    const container = document.getElementById("event-list");
    if (!container) return;

    const events = getAllEvents();
    const eventTypes = getEventTypes();
    const state = virtualScrollState.event;

    // 컨테이너 높이 업데이트
    state.containerHeight = container.clientHeight;

    // 현재 스크롤 위치에서 보이는 범위 계산
    const scrollTop = container.scrollTop;
    state.scrollTop = scrollTop;

    // 평균 아이템 높이 계산 (캐시된 높이 기반)
    if (state.itemHeights.size > 0) {
        const heights = Array.from(state.itemHeights.values());
        const avgHeight = heights.reduce((a, b) => a + b, 0) / heights.length;
        state.itemHeight = avgHeight;
    }

    const itemHeight = state.itemHeight;
    const overscan = state.overscan;

    // 보이는 범위의 시작과 끝 인덱스 계산.
    // 노트 리스트와 같은 함정: 이벤트가 줄어들면 예전 scrollTop 때문에 startIndex가
    // 목록을 넘어서 아무것도 안 보일 수 있다. 마지막 페이지 시작점으로 clamp.
    const visibleCount = Math.ceil(state.containerHeight / itemHeight) + overscan * 2;
    const maxStart = Math.max(0, events.length - visibleCount);
    const startIndex = Math.min(
        maxStart,
        Math.max(0, Math.floor(scrollTop / itemHeight) - overscan),
    );
    const endIndex = Math.min(
        events.length - 1,
        Math.ceil((scrollTop + state.containerHeight) / itemHeight) + overscan
    );

    state.renderedRange = { start: startIndex, end: endIndex };

    // 전체 높이를 유지하기 위한 spacer 계산
    const totalHeight = events.length * itemHeight;
    const topSpacerHeight = startIndex * itemHeight;

    // 기존 렌더링된 아이템들의 인덱스 확인
    const existingItems = new Map();
    container.querySelectorAll('.event-item').forEach(item => {
        const index = parseInt(item.getAttribute('data-event-index'));
        if (!isNaN(index)) {
            existingItems.set(index, item);
        }
    });

    // DocumentFragment 사용하여 배치 DOM 업데이트
    const fragment = document.createDocumentFragment();

    // Top spacer
    let topSpacer = container.querySelector('.virtual-scroll-top-spacer');
    if (!topSpacer) {
        topSpacer = document.createElement('div');
        topSpacer.className = 'virtual-scroll-top-spacer';
    }
    topSpacer.style.height = `${topSpacerHeight}px`;

    // 렌더링할 아이템들
    const itemsToRender = [];
    for (let i = startIndex; i <= endIndex; i++) {
        if (existingItems.has(i)) {
            // 이미 렌더링된 아이템 재사용
            itemsToRender.push(existingItems.get(i));
            existingItems.delete(i);
        } else {
            // 새 아이템 생성
            const event = events[i];
            const eventDiv = createEventElement(event, i);
            itemsToRender.push(eventDiv);
        }
    }

    // Bottom spacer
    let bottomSpacer = container.querySelector('.virtual-scroll-bottom-spacer');
    if (!bottomSpacer) {
        bottomSpacer = document.createElement('div');
        bottomSpacer.className = 'virtual-scroll-bottom-spacer';
    }
    const bottomSpacerHeight = Math.max(0, totalHeight - topSpacerHeight - (endIndex - startIndex + 1) * itemHeight);
    bottomSpacer.style.height = `${bottomSpacerHeight}px`;

    // DOM 업데이트 (한 번에)
    container.innerHTML = '';
    container.appendChild(topSpacer);
    itemsToRender.forEach(item => container.appendChild(item));
    container.appendChild(bottomSpacer);

    // 아이템 높이 측정 및 캐싱 (다음 렌더링에 사용)
    requestAnimationFrame(() => {
        container.querySelectorAll('.event-item').forEach(item => {
            const index = parseInt(item.getAttribute('data-event-index'));
            if (!isNaN(index)) {
                const height = item.offsetHeight;
                if (height > 0) {
                    state.itemHeights.set(index, height);
                }
            }
        });
    });

    // 접기 상태 복원 (메모리 캐시 사용)
    restoreCollapseStatesFromMemory();
}

// 메모리 캐시에서 접기 상태 저장
function saveCollapseStatesToMemory() {
    // Parameters 접기 상태 저장
    document.querySelectorAll('.params-container').forEach(container => {
        const eventIndex = container.closest('.event-item')?.dataset.eventIndex;
        if (eventIndex !== undefined) {
            collapseStatesMemoryCache.params.set(
                parseInt(eventIndex),
                container.classList.contains('collapsed')
            );
        }
    });

    // Dialog 접기 상태 저장
    document.querySelectorAll('.dialog-content').forEach(content => {
        const eventIndex = content.closest('.event-item')?.dataset.eventIndex;
        if (eventIndex !== undefined) {
            collapseStatesMemoryCache.dialog.set(
                parseInt(eventIndex),
                content.classList.contains('collapsed')
            );
        }
    });
}

// 메모리 캐시에서 접기 상태 복원
function restoreCollapseStatesFromMemory() {
    // Parameters 접기 상태 복원
    document.querySelectorAll('.params-container').forEach(container => {
        const eventIndex = container.closest('.event-item')?.dataset.eventIndex;
        if (eventIndex !== undefined) {
            const isCollapsed = collapseStatesMemoryCache.params.get(parseInt(eventIndex));
            if (isCollapsed !== undefined) {
                const toggle = container.querySelector('.params-toggle');
                if (isCollapsed) {
                    container.classList.add('collapsed');
                    if (toggle) toggle.textContent = "▶";
                } else {
                    container.classList.remove('collapsed');
                    if (toggle) toggle.textContent = "▼";
                }
            }
        }
    });

    // Dialog 접기 상태 복원
    document.querySelectorAll('.dialog-content').forEach(content => {
        const eventIndex = content.closest('.event-item')?.dataset.eventIndex;
        if (eventIndex !== undefined) {
            const isCollapsed = collapseStatesMemoryCache.dialog.get(parseInt(eventIndex));
            if (isCollapsed !== undefined) {
                const toggle = content.parentElement?.querySelector('.dialog-toggle');
                if (isCollapsed) {
                    content.classList.add('collapsed');
                    if (toggle) toggle.textContent = "▶";
                } else {
                    content.classList.remove('collapsed');
                    if (toggle) toggle.textContent = "▼";
                }
            }
        }
    });
}

// 새로운 이벤트 하나만 리스트에 추가하는 최적화된 함수
function appendSingleEventToList(eventIndex) {
    const container = document.getElementById("event-list");
    const events = getAllEvents();
    const event = events[eventIndex];

    if (!event) return;

    const eventDiv = createEventElement(event, eventIndex);
    container.appendChild(eventDiv);

    // 새로 추가된 이벤트로 스크롤
    eventDiv.scrollIntoView({ behavior: 'smooth', block: 'end' });
}

// 이벤트 요소 생성 함수 (재사용 가능)
function createEventElement(event, eventIndex) {
    const eventTypes = getEventTypes();

    const eventDiv = document.createElement("div");
    eventDiv.className = "event-item";

    // 선택된 이벤트 표시
    if (selectedEventIndices.has(eventIndex)) {
        eventDiv.classList.add("selected");
    }

    // 이벤트 아이템 클릭 리스너 (한 번만 등록)
    attachEventClickListener(eventDiv, eventIndex);

    const eventHeader = document.createElement("div");
    eventHeader.className = "event-header";

    // 배치 DOM 조작을 위한 document fragment 사용
    const fragment = document.createDocumentFragment();

    // Event Type 드롭다운 생성
    const typeElements = createEventTypeElements(event, eventTypes);
    fragment.appendChild(typeElements.typeLabel);

    // Event ID 입력 요소 생성
    const idElements = createEventIdElements(event, eventIndex);
    fragment.appendChild(idElements.idLabel);

    // Event Time 입력 요소 생성
    const timeElements = createEventTimeElements(event, eventIndex);
    fragment.appendChild(timeElements.timeLabel);

    // 버튼들 생성
    const buttonElements = createEventButtonElements(event, eventIndex);
    fragment.appendChild(buttonElements.predefinedParamsBtn);
    fragment.appendChild(buttonElements.deleteBtn);

    eventHeader.appendChild(fragment);
    eventDiv.appendChild(eventHeader);

    // 파라미터 섹션 생성
    const paramsSection = createEventParamsSection(event, eventIndex);
    eventDiv.appendChild(paramsSection);

    return eventDiv;
}

// 이벤트 클릭 리스너는 이제 이벤트 델리게이션으로 처리됨 (성능 최적화)
function attachEventClickListener(eventDiv, eventIndex) {
    // 이벤트 델리게이션으로 처리되므로 개별 리스너 불필요
    // eventDiv에 data 속성으로 인덱스 저장
    eventDiv.setAttribute('data-event-index', eventIndex);
}

// 이벤트 클릭 처리 로직 분리
function handleEventClick(e, eventIndex) {
    const container = document.getElementById("event-list");

    if (e.shiftKey && lastClickedEventIndex !== null) {
        const start = Math.min(lastClickedEventIndex, eventIndex);
        const end = Math.max(lastClickedEventIndex, eventIndex);

        // DOM만 업데이트 (전체 리렌더링 방지)
        for (let i = start; i <= end; i++) {
            selectedEventIndices.add(i);
            const eventItem = container.querySelector(`[data-event-index="${i}"]`);
            if (eventItem) {
                eventItem.classList.add("selected");
            }
        }
        lastClickedEventIndex = eventIndex;
    } else if (e.ctrlKey || e.metaKey) {
        // DOM만 업데이트 (전체 리렌더링 방지)
        if (selectedEventIndices.has(eventIndex)) {
            selectedEventIndices.delete(eventIndex);
            const eventItem = container.querySelector(`[data-event-index="${eventIndex}"]`);
            if (eventItem) {
                eventItem.classList.remove("selected");
            }
        } else {
            selectedEventIndices.add(eventIndex);
            const eventItem = container.querySelector(`[data-event-index="${eventIndex}"]`);
            if (eventItem) {
                eventItem.classList.add("selected");
            }
        }
        lastClickedEventIndex = eventIndex;
    } else {
        // 단순 클릭: 선택 상태 초기화 후 해당 이벤트만 선택
        // DOM만 업데이트 (전체 리렌더링 방지)
        const allItems = container.querySelectorAll('.event-item');
        allItems.forEach(item => item.classList.remove("selected"));

        selectedEventIndices.clear();
        selectedEventIndices.add(eventIndex);

        const eventItem = container.querySelector(`[data-event-index="${eventIndex}"]`);
        if (eventItem) {
            eventItem.classList.add("selected");
        }

        lastClickedEventIndex = eventIndex;
        focusEventAtIndex(eventIndex);
    }
}

// Event Type 요소들 생성
function createEventTypeElements(event, eventTypes) {
    const typeLabel = document.createElement("label");
    typeLabel.textContent = "Type: ";
    const typeSelect = document.createElement("select");
    typeSelect.className = "event-type-select";

    // 옵션들을 배치로 추가
    const fragment = document.createDocumentFragment();
    eventTypes.forEach(type => {
        const option = document.createElement("option");
        option.value = type;
        option.textContent = type;
        option.selected = type === event.eventType;

        const description = getEventTypeDescription(type);
        if (description) {
            option.title = description;
        }
        fragment.appendChild(option);
    });
    typeSelect.appendChild(fragment);

    const currentDescription = getEventTypeDescription(event.eventType);
    if (currentDescription) {
        typeSelect.title = currentDescription;
    }

    // 이벤트 델리게이션으로 처리되므로 개별 리스너 불필요
    typeLabel.appendChild(typeSelect);

    return { typeLabel, typeSelect };
}

// Type 변경 처리 함수
function handleTypeChange(e, event) {
    event.eventType = e.target.value;
    const newDescription = getEventTypeDescription(e.target.value);
    e.target.title = newDescription || '';

    event.eventId = '';
    saveToStorage();

    // 전체 리렌더링 대신 해당 이벤트만 업데이트
    requestAnimationFrame(() => renderEventList());
}

// Event ID 요소들 생성
function createEventIdElements(event, eventIndex) {
    const idLabel = document.createElement("label");
    idLabel.textContent = "ID: ";

    const idInput = createEventIdInputElement(event);
    idLabel.appendChild(idInput);

    return { idLabel, idInput };
}

// Event ID 입력 요소 생성 (독립된 함수)
function createEventIdInputElement(event) {
    const isCustom = isCustomEventType(event.eventType);
    let idInput;

    if (isCustom) {
        // custom 타입이면 텍스트 입력
        idInput = document.createElement("input");
        idInput.type = "text";
        idInput.className = "event-id-input";
        idInput.value = event.eventId;

        // 이벤트 델리게이션으로 처리됨
    } else {
        // 사전 정의된 타입이면 드롭다운
        idInput = document.createElement("select");
        idInput.className = "event-id-select";

        const eventIds = getEventIdsByType(event.eventType);

        // Document fragment를 사용한 배치 추가
        const fragment = document.createDocumentFragment();

        // 빈 옵션 추가 (선택 안함)
        const emptyOption = document.createElement("option");
        emptyOption.value = "";
        emptyOption.textContent = "-- 선택 --";
        fragment.appendChild(emptyOption);

        // 사전 정의된 EventId 옵션들 추가
        eventIds.forEach(id => {
            const option = document.createElement("option");
            option.value = id;
            option.textContent = id;
            option.selected = id === event.eventId;
            fragment.appendChild(option);
        });

        // 현재 값이 목록에 없으면 "기타" 옵션으로 추가
        if (event.eventId && !eventIds.includes(event.eventId)) {
            const customOption = document.createElement("option");
            customOption.value = event.eventId;
            customOption.textContent = `${event.eventId} (사용자 정의)`;
            customOption.selected = true;
            fragment.appendChild(customOption);
        }

        idInput.appendChild(fragment);

        // 이벤트 델리게이션으로 처리됨
    }

    return idInput;
}

// Event Time 요소들 생성
function createEventTimeElements(event, eventIndex) {
    const timeLabel = document.createElement("label");
    timeLabel.textContent = "Time: ";
    const timeInput = document.createElement("input");
    timeInput.type = "number";
    timeInput.step = "0.001";
    timeInput.value = event.eventTime;
    timeInput.className = "event-time-input";

    // 이벤트 델리게이션으로 처리됨

    timeLabel.appendChild(timeInput);
    return { timeLabel, timeInput };
}

// Event 버튼들 생성
function createEventButtonElements(event, eventIndex) {
    const predefinedParamsBtn = document.createElement("button");
    predefinedParamsBtn.textContent = "기본 파라미터";
    predefinedParamsBtn.className = "predefined-params-btn";
    // 이벤트 델리게이션으로 처리됨

    const deleteBtn = document.createElement("button");
    deleteBtn.textContent = "삭제";
    deleteBtn.className = "delete-event-btn";
    // 이벤트 델리게이션으로 처리됨

    return { predefinedParamsBtn, deleteBtn };
}

// Event 파라미터 섹션 생성
function createEventParamsSection(event, eventIndex) {
    const paramsContainer = document.createElement("div");
    paramsContainer.className = "params-container";

    const isDialog = isDialogEvent(event);

    // Dialog 이벤트인 경우 특별한 UI 생성
    if (isDialog) {
        return createDialogItemsSection(event, eventIndex);
    }

    const paramsLabel = document.createElement("div");
    paramsLabel.className = "params-label";
    paramsLabel.innerHTML = '<span class="params-toggle">▼</span> Parameters';

    // 토글 기능은 이벤트 델리게이션으로 처리됨

    const paramsContent = document.createElement("div");
    paramsContent.className = "params-content";

    const paramsList = document.createElement("div");
    paramsList.className = "params-list";

    // 파라미터들 렌더링
    event.eventParams.forEach((param, paramIndex) => {
        const paramDiv = createEventParamElement(event, eventIndex, param, paramIndex);
        paramsList.appendChild(paramDiv);
    });

    const addParamBtn = document.createElement("button");
    addParamBtn.textContent = "파라미터 추가";
    addParamBtn.className = "add-param-btn";
    // 이벤트 델리게이션으로 처리됨

    paramsContent.appendChild(paramsList);
    paramsContent.appendChild(addParamBtn);
    paramsContainer.appendChild(paramsLabel);
    paramsContainer.appendChild(paramsContent);

    return paramsContainer;
}

// Event 파라미터 요소 생성
function createEventParamElement(event, eventIndex, param, paramIndex) {
    const paramDiv = document.createElement("div");
    paramDiv.className = "param-item";

    const paramNameInput = document.createElement("input");
    paramNameInput.type = "text";
    paramNameInput.placeholder = "파라미터 이름";
    paramNameInput.value = param.paramName;
    paramNameInput.className = "param-name-input";

    const paramValueInput = document.createElement("input");
    paramValueInput.type = "text";
    paramValueInput.placeholder = "파라미터 값";
    paramValueInput.value = param.paramValue;
    paramValueInput.className = "param-value-input";

    const deleteParamBtn = document.createElement("button");
    deleteParamBtn.textContent = "삭제";
    deleteParamBtn.className = "delete-param-btn";

    // 이벤트 델리게이션으로 처리됨

    paramDiv.appendChild(paramNameInput);
    paramDiv.appendChild(paramValueInput);
    paramDiv.appendChild(deleteParamBtn);

    return paramDiv;
}

// 메모리 누수 방지를 위한 이벤트 리스너 정리
function cleanupEventListeners(container) {
    if (!container) return;

    const elements = container.querySelectorAll('input, select, button');
    elements.forEach(element => {
        if (element.parentNode) {
            // Clone하여 모든 이벤트 리스너 제거
            const clone = element.cloneNode(true);
            element.parentNode.replaceChild(clone, element);
        }
    });
}

// 접기 상태 저장/복원 함수들
function saveCollapseStates() {
    const states = new Map();

    // Parameters 접기 상태 저장
    document.querySelectorAll('.params-container').forEach((container, index) => {
        const eventIndex = container.closest('.event-item')?.dataset.eventIndex;
        if (eventIndex !== undefined) {
            states.set(`params-${eventIndex}`, container.classList.contains('collapsed'));
        }
    });

    // Dialog 접기 상태 저장
    document.querySelectorAll('.dialog-content').forEach((content, index) => {
        const eventIndex = content.closest('.event-item')?.dataset.eventIndex;
        if (eventIndex !== undefined) {
            states.set(`dialog-${eventIndex}`, content.classList.contains('collapsed'));
        }
    });

    return states;
}

function restoreCollapseStates(states) {
    if (!states) return;

    // Parameters 접기 상태 복원
    document.querySelectorAll('.params-container').forEach(container => {
        const eventIndex = container.closest('.event-item')?.dataset.eventIndex;
        if (eventIndex !== undefined) {
            const isCollapsed = states.get(`params-${eventIndex}`);
            if (isCollapsed !== undefined) {
                const toggle = container.querySelector('.params-toggle');
                if (isCollapsed) {
                    container.classList.add('collapsed');
                    if (toggle) toggle.textContent = "▶";
                } else {
                    container.classList.remove('collapsed');
                    if (toggle) toggle.textContent = "▼";
                }
            }
        }
    });

    // Dialog 접기 상태 복원
    document.querySelectorAll('.dialog-content').forEach(content => {
        const eventIndex = content.closest('.event-item')?.dataset.eventIndex;
        if (eventIndex !== undefined) {
            const isCollapsed = states.get(`dialog-${eventIndex}`);
            if (isCollapsed !== undefined) {
                const toggle = content.parentElement?.querySelector('.dialog-toggle');
                if (isCollapsed) {
                    content.classList.add('collapsed');
                    if (toggle) toggle.textContent = "▶";
                } else {
                    content.classList.remove('collapsed');
                    if (toggle) toggle.textContent = "▼";
                }
            }
        }
    });
}

// 기존 이벤트 렌더링 로직 (가상 스크롤링 비활성화)
function renderEventListImmediate_Original() {
    const container = document.getElementById("event-list");
    if (!container) {
        return;
    }

    // 접기 상태 저장
    const collapseStates = saveCollapseStates();

    // 기존 요소들 제거 (이벤트 위임을 사용하므로 개별 리스너 정리 불필요)
    container.innerHTML = "";

    const events = getAllEvents();
    const eventTypes = getEventTypes();

    // DocumentFragment 생성 (배치 DOM 업데이트)
    const fragment = document.createDocumentFragment();

    events.forEach((event, eventIndex) => {
        const eventDiv = document.createElement("div");
        eventDiv.className = "event-item";
        // data-event-index 속성 추가하여 이벤트 위임에서 사용
        eventDiv.setAttribute('data-event-index', eventIndex);

        // 선택된 이벤트 표시
        if (selectedEventIndices.has(eventIndex)) {
            eventDiv.classList.add("selected");
        }

        const eventHeader = document.createElement("div");
        eventHeader.className = "event-header";

        // Event Type 드롭다운
        const typeLabel = document.createElement("label");
        typeLabel.textContent = "Type: ";
        const typeSelect = document.createElement("select");
        typeSelect.className = "event-type-select";
        eventTypes.forEach(type => {
            const option = document.createElement("option");
            option.value = type;
            option.textContent = type;
            option.selected = type === event.eventType;

            // 설명이 있으면 title 속성에 추가
            const description = getEventTypeDescription(type);
            if (description) {
                option.title = description;
            }

            typeSelect.appendChild(option);
        });
        // 현재 선택된 값의 설명을 select에 표시
        const currentDescription = getEventTypeDescription(event.eventType);
        if (currentDescription) {
            typeSelect.title = currentDescription;
        }

        // 이벤트 위임으로 처리되므로 개별 리스너 불필요
        typeLabel.appendChild(typeSelect);

        // Event ID 입력 (타입에 따라 드롭다운 또는 텍스트 입력)
        const idLabel = document.createElement("label");
        idLabel.textContent = "ID: ";

        function createEventIdInput() {
            const isCustom = isCustomEventType(event.eventType);
            let idInput;

            if (isCustom) {
                // custom 타입이면 텍스트 입력
                idInput = document.createElement("input");
                idInput.type = "text";
                idInput.className = "event-id-input";
                idInput.value = event.eventId;
                // 이벤트 위임으로 처리되므로 개별 리스너 불필요
            } else {
                // 사전 정의된 타입이면 드롭다운
                idInput = document.createElement("select");
                idInput.className = "event-id-select";

                const eventIds = getEventIdsByType(event.eventType);

                // 빈 옵션 추가 (선택 안함)
                const emptyOption = document.createElement("option");
                emptyOption.value = "";
                emptyOption.textContent = "-- 선택 --";
                idInput.appendChild(emptyOption);

                // 사전 정의된 EventId 옵션들 추가
                eventIds.forEach(id => {
                    const option = document.createElement("option");
                    option.value = id;
                    option.textContent = id;
                    option.selected = id === event.eventId;
                    idInput.appendChild(option);
                });

                // 현재 값이 목록에 없으면 "기타" 옵션으로 추가
                if (event.eventId && !eventIds.includes(event.eventId)) {
                    const customOption = document.createElement("option");
                    customOption.value = event.eventId;
                    customOption.textContent = `${event.eventId} (사용자 정의)`;
                    customOption.selected = true;
                    idInput.appendChild(customOption);
                }

                // 이벤트 위임으로 처리되므로 개별 리스너 불필요
            }

            return idInput;
        }

        const idInput = createEventIdInput();
        idLabel.appendChild(idInput);

        // Event Time 입력
        const timeLabel = document.createElement("label");
        timeLabel.textContent = "Time: ";
        const timeInput = document.createElement("input");
        timeInput.type = "number";
        timeInput.className = "event-time-input";
        timeInput.step = "0.1";
        timeInput.value = event.eventTime;
        timeInput.addEventListener("change", (e) => {
            const newTime = parseFloat(e.target.value) || 0;
            const oldTime = event.eventTime;
            const timeDiff = newTime - oldTime;

            // 여러 이벤트가 선택되어 있고, 현재 이벤트가 선택된 이벤트 중 하나일 때
            if (selectedEventIndices.has(eventIndex) && selectedEventIndices.size > 1) {
                // 변경 전 상태를 히스토리에 저장
                saveState();

                // 선택된 모든 이벤트의 시간을 동일한 증감치만큼 조정
                const updates = [];
                selectedEventIndices.forEach(idx => {
                    const eventToUpdate = getEventAtIndex(idx);
                    if (eventToUpdate) {
                        updates.push({
                            index: idx,
                            event: {
                                ...eventToUpdate,
                                eventTime: eventToUpdate.eventTime + timeDiff
                            }
                        });
                    }
                });
                updateMultipleEvents(updates);
            } else {
                // 단일 이벤트 업데이트
                updateEvent(eventIndex, {
                    ...event,
                    eventTime: newTime
                });
            }

            saveToStorage();
            // DOM 재렌더링을 지연시켜 이벤트 처리가 완료된 후 실행
            requestAnimationFrame(() => {
                scheduleRender({ eventList: true });
            });
        });
        timeLabel.appendChild(timeInput);

        // 사전 정의된 파라미터 추가 버튼 (Event ID가 설정되어 있을 때만 표시)
        const predefinedParamsBtn = document.createElement("button");
        predefinedParamsBtn.textContent = "기본 파라미터 추가";
        predefinedParamsBtn.className = "add-predefined-params-btn";
        predefinedParamsBtn.style.fontSize = "11px";
        predefinedParamsBtn.style.padding = "2px 6px";

        // 사전 정의된 파라미터가 있는지 확인
        const predefinedParams = getPredefinedParamsForEventId(event.eventType, event.eventId);
        if (predefinedParams.length > 0 && event.eventId) {
            predefinedParamsBtn.style.display = "inline-block";
            predefinedParamsBtn.title = `${predefinedParams.map(p => p.paramName).join(', ')} 파라미터 추가`;
        } else {
            predefinedParamsBtn.style.display = "none";
        }

        // 기본 파라미터 버튼 이벤트는 handleEventListClick에서 위임으로 처리

        // 삭제 버튼
        const deleteBtn = document.createElement("button");
        deleteBtn.textContent = "Delete";
        deleteBtn.className = "delete-event-btn";
        // 삭제 버튼 이벤트는 handleEventListClick에서 위임으로 처리

        eventHeader.appendChild(typeLabel);
        eventHeader.appendChild(idLabel);
        eventHeader.appendChild(timeLabel);
        eventHeader.appendChild(predefinedParamsBtn);
        eventHeader.appendChild(deleteBtn);

        // Parameters 섹션 생성 (dialog 지원)
        const paramsContainer = createEventParamsSection(event, eventIndex);

        eventDiv.appendChild(eventHeader);
        eventDiv.appendChild(paramsContainer);

        // DocumentFragment에 추가 (DOM 조작 최소화)
        fragment.appendChild(eventDiv);
    });

    // DocumentFragment를 한 번에 컨테이너에 추가 (단일 reflow)
    container.appendChild(fragment);

    // 접기 상태 복원 (다음 프레임에서 실행하여 DOM이 완전히 업데이트된 후 실행)
    requestAnimationFrame(() => {
        restoreCollapseStates(collapseStates);
    });
}

// 탭 전환 함수
function switchTab(tabName) {
    // 사이드바가 별도 창으로 분리돼 있으면 탭·콘텐츠 노드가 팝업 문서에 있다.
    // document(메인)에서 찾으면 못 찾아 .active 토글이 어긋나 스크롤 영역이
    // 갈라진다. 탭 콘텐츠가 실제로 있는 문서를 기준으로 조작한다.
    const notesTab = document.getElementById('notes-tab');
    const doc = notesTab ? notesTab.ownerDocument : document;

    const tabButtons = doc.querySelectorAll('.tab-button');
    const tabContents = doc.querySelectorAll('.tab-content');

    tabButtons.forEach(btn => btn.classList.remove('active'));
    tabContents.forEach(content => content.classList.remove('active'));

    const tabButton = doc.querySelector(`[data-tab="${tabName}"]`);
    const tabContent = doc.getElementById(`${tabName}-tab`);

    if (tabButton) tabButton.classList.add('active');
    if (tabContent) tabContent.classList.add('active');

    if (tabName === 'events') {
        // 노트 선택 해제
        selectedNoteIndices.clear();
        lastClickedNoteIndex = null;
        renderEventList();
    } else if (tabName === 'notes') {
        // 이벤트 선택 해제
        selectedEventIndices.clear();
        lastClickedEventIndex = null;
        renderNoteList();
    } else if (tabName === 'timeline') {
        // 숨겨져 있는 동안은 clientHeight가 0이라 그릴 수 없었다. 이제 측정이 되므로
        // 다시 그린다. (선택 상태는 표와 공유하므로 해제하지 않는다.)
        renderTimelineView();
    }
}

// 초기화
// 이벤트 델리게이션 시스템 (성능 최적화)
function setupEventDelegation() {
    // 이벤트 리스트 컨테이너에 대한 단일 이벤트 리스너
    const eventListContainer = document.getElementById("event-list");
    if (eventListContainer) {
        eventListContainer.addEventListener("click", handleEventListClick);
        eventListContainer.addEventListener("change", handleEventListChange);

        // 드래그 앤 드롭 이벤트 추가
        eventListContainer.addEventListener("dragstart", handleDialogItemDragStart);
        eventListContainer.addEventListener("dragover", handleDialogItemDragOver);
        eventListContainer.addEventListener("drop", handleDialogItemDrop);
        eventListContainer.addEventListener("dragend", handleDialogItemDragEnd);

        // 가상 스크롤링을 위한 스크롤 이벤트 리스너 (스로틀링 적용)
        let scrollTimeout = null;
        eventListContainer.addEventListener("scroll", () => {
            // 접기 상태를 메모리에 저장 (스크롤 전에)
            saveCollapseStatesToMemory();

            // 스로틀링: 스크롤 중에는 너무 자주 렌더링하지 않음
            if (scrollTimeout) return;

            scrollTimeout = setTimeout(() => {
                const events = getAllEvents();
                const state = virtualScrollState.event;

                // 가상 스크롤링이 활성화되고 이벤트가 100개 초과일 때만
                if (state.enabled && events.length > 100) {
                    renderEventListVirtualized();
                }

                scrollTimeout = null;
            }, 50); // 50ms 디바운스
        });
    }

    // 사이드바 이벤트 델리게이션
    const sidebar = document.getElementById("sidebar");
    if (sidebar) {
        sidebar.addEventListener("click", handleSidebarClick);
        // change는 위임하지 않는다 — 사이드바 입력은 전부 직접 리스너가 처리한다(감사 B-3).
    } else {
        console.error('Sidebar element not found!');
    }

    // 컨트롤 바 이벤트 델리게이션 (노트 버튼들이 여기에 있음)
    const controlBar = document.getElementById("control-bar");
    if (controlBar) {
        controlBar.addEventListener("click", handleSidebarClick);
    } else {
        console.error('Control-bar element not found!');
    }

    // 메인 영역 이벤트 델리게이션
    const main = document.getElementById("main");
    if (main) {
        main.addEventListener("click", handleMainClick);
    }
}

// 이벤트 리스트 클릭 처리
function handleEventListClick(e) {
    const target = e.target;
    const eventItem = target.closest('.event-item');

    if (!eventItem) return;

    const eventIndex = parseInt(eventItem.getAttribute('data-event-index')) ||
        (eventItem.parentNode ? Array.from(eventItem.parentNode.children).indexOf(eventItem) : -1);

    if (eventIndex < 0) return;

    // 삭제 버튼
    if (target.classList.contains('delete-event-btn')) {
        if (confirm("이벤트를 삭제하시겠습니까?")) {
            if (removeEvent(eventIndex)) {
                scheduleRender({ eventList: true });
                saveToStorage();
            }
        }
        return;
    }

    // 기본 파라미터 버튼
    if (target.classList.contains('predefined-params-btn') || target.classList.contains('add-predefined-params-btn')) {
        applyPredefinedParams(eventIndex);
        scheduleRender({ eventList: true });
        saveToStorage();
        return;
    }

    // 파라미터 추가 버튼
    if (target.classList.contains('add-param-btn')) {
        addEventParam(eventIndex);
        scheduleRender({ eventList: true });
        saveToStorage();
        return;
    }

    // 파라미터 삭제 버튼
    if (target.classList.contains('delete-param-btn')) {
        const paramItem = target.closest('.param-item');
        if (paramItem) {
            const paramIndex = parseInt(paramItem.getAttribute('data-param-index')) ||
                (paramItem.parentNode ? Array.from(paramItem.parentNode.children).indexOf(paramItem) : -1);
            removeEventParam(eventIndex, paramIndex);
            scheduleRender({ eventList: true });
            saveToStorage();
        }
        return;
    }

    // 파라미터 토글
    if (target.classList.contains('params-toggle') || target.closest('.params-label')) {
        const paramsContainer = eventItem.querySelector('.params-container');
        if (!paramsContainer) return;

        const toggle = paramsContainer.querySelector('.params-toggle');

        // CSS와 일치하도록 paramsContainer에 collapsed 클래스 토글
        paramsContainer.classList.toggle("collapsed");

        // 토글 아이콘 업데이트
        if (paramsContainer.classList.contains("collapsed")) {
            toggle.textContent = "▶";
        } else {
            toggle.textContent = "▼";
        }

        // 메모리 캐시에 상태 저장 (증분 업데이트)
        collapseStatesMemoryCache.params.set(
            eventIndex,
            paramsContainer.classList.contains("collapsed")
        );
        return;
    }

    // Dialog 토글
    if (target.classList.contains('dialog-toggle') || target.closest('.dialog-label')) {
        const dialogContainer = eventItem.querySelector('.dialog-container');
        if (!dialogContainer) return;

        const content = dialogContainer.querySelector('.dialog-content');
        const toggle = dialogContainer.querySelector('.dialog-toggle');

        // content에 collapsed 클래스 토글 (dialog는 기존 방식 유지)
        content.classList.toggle("collapsed");

        // 토글 아이콘 업데이트
        if (content.classList.contains("collapsed")) {
            toggle.textContent = "▶";
        } else {
            toggle.textContent = "▼";
        }

        // 메모리 캐시에 상태 저장 (증분 업데이트)
        collapseStatesMemoryCache.dialog.set(
            eventIndex,
            content.classList.contains("collapsed")
        );
        return;
    }

    // Dialog 아이템 추가 버튼
    if (target.classList.contains('add-dialog-item-btn')) {
        const itemType = 'text'; // 기본 타입
        addDialogItem(eventIndex, itemType);
        scheduleRender({ eventList: true });
        saveToStorage();
        return;
    }

    // Dialog 아이템 삭제 버튼
    if (target.classList.contains('delete-dialog-item-btn')) {
        const itemIndex = parseInt(target.dataset.itemIndex);
        if (confirm("Dialog 아이템을 삭제하시겠습니까?")) {
            if (removeDialogItem(eventIndex, itemIndex)) {
                scheduleRender({ eventList: true });
                saveToStorage();
            }
        }
        return;
    }

    // 이벤트 아이템 선택
    if (!["INPUT", "SELECT", "BUTTON"].includes(target.tagName)) {
        handleEventClick(e, eventIndex);
    }
}

// 이벤트 리스트 변경 처리 (디바운싱 적용)
const eventChangeTimeouts = new Map();

function handleEventListChange(e) {
    const target = e.target;
    const eventItem = target.closest('.event-item');

    if (!eventItem) return;

    const eventIndex = parseInt(eventItem.getAttribute('data-event-index')) ||
        (eventItem.parentNode ? Array.from(eventItem.parentNode.children).indexOf(eventItem) : -1);

    if (eventIndex < 0) return;

    const events = getAllEvents();
    const event = events[eventIndex];

    if (!event) return;

    // 기존 타이머 클리어
    if (eventChangeTimeouts.has(target)) {
        clearTimeout(eventChangeTimeouts.get(target));
    }

    // 디바운싱된 처리
    const timeoutId = setTimeout(() => {
        if (target.classList.contains('event-type-select')) {
            event.eventType = target.value;
            const newDescription = getEventTypeDescription(target.value);
            target.title = newDescription || '';
            event.eventId = '';
            saveToStorage();
            requestAnimationFrame(() => renderEventList());
        } else if (target.classList.contains('event-id-input')) {
            event.eventId = target.value;
            saveToStorage();
        } else if (target.classList.contains('event-id-select')) {
            event.eventId = target.value;
            // 사전 정의된 파라미터 자동 추가
            applyPredefinedParams(eventIndex);
            saveToStorage();
            scheduleRender({ eventList: true }); // UI 새로고침으로 새로 추가된 파라미터들 표시
        } else if (target.classList.contains('event-time-input')) {
            const newTime = parseFloat(target.value) || 0;
            const oldTime = event.eventTime;
            const timeDiff = newTime - oldTime;

            // 여러 이벤트가 선택되어 있고, 현재 이벤트가 선택된 이벤트 중 하나일 때
            if (selectedEventIndices.has(eventIndex) && selectedEventIndices.size > 1) {
                // 선택된 모든 이벤트의 시간을 동일한 증감치만큼 조정
                const updates = [];
                selectedEventIndices.forEach(idx => {
                    const eventToUpdate = getEventAtIndex(idx);
                    if (eventToUpdate) {
                        updates.push({
                            index: idx,
                            event: {
                                ...eventToUpdate,
                                eventTime: eventToUpdate.eventTime + timeDiff
                            }
                        });
                    }
                });
                updateMultipleEvents(updates);
            } else {
                // 단일 이벤트 업데이트
                event.eventTime = newTime;
            }
            saveToStorage();
        } else if (target.classList.contains('param-name-input')) {
            const paramItem = target.closest('.param-item');
            if (paramItem) {
                const paramIndex = paramItem.parentNode ? Array.from(paramItem.parentNode.children).indexOf(paramItem) : -1;
                if (event.eventParams[paramIndex]) {
                    event.eventParams[paramIndex].paramName = target.value;
                }
            }
            saveToStorage();
        } else if (target.classList.contains('param-value-input')) {
            const paramItem = target.closest('.param-item');
            if (paramItem) {
                const paramIndex = paramItem.parentNode ? Array.from(paramItem.parentNode.children).indexOf(paramItem) : -1;
                if (event.eventParams[paramIndex]) {
                    event.eventParams[paramIndex].paramValue = target.value;
                }
            }
            saveToStorage();
        } else if (target.classList.contains('dialog-item-type-select')) {
            const itemIndex = parseInt(target.dataset.itemIndex);
            const newType = target.value;

            if (event.dialogItems && event.dialogItems[itemIndex]) {
                // 새로운 타입의 아이템으로 교체
                const newItem = createDialogItem(newType);
                newItem.index = itemIndex;
                event.dialogItems[itemIndex] = newItem;
                saveToStorage();
                scheduleRender({ eventList: true });
            }
        } else if (target.classList.contains('dialog-item-field-input')) {
            const itemIndex = parseInt(target.dataset.itemIndex);
            const fieldName = target.dataset.fieldName;

            if (event.dialogItems && event.dialogItems[itemIndex]) {
                let value = target.value;

                // 타입에 따른 값 변환
                const field = getDialogItemFields(event.dialogItems[itemIndex].type)
                    .find(f => f.fieldName === fieldName);
                if (field && field.fieldType === 'float') {
                    value = parseFloat(value) || 0.0;
                }

                event.dialogItems[itemIndex][fieldName] = value;
                saveToStorage();
            }
        }

        eventChangeTimeouts.delete(target);
    }, 300);

    eventChangeTimeouts.set(target, timeoutId);
}

// 사이드바 클릭 처리
function handleSidebarClick(e) {
    const target = e.target;

    // Note 버튼들
    if (target.id === 'add-tab') {
        e.preventDefault();
        e.stopPropagation();
        addNote({ type: "tab", isLong: false, longTime: 0 });
        return;
    }
    if (target.id === 'add-dir') {
        e.preventDefault();
        e.stopPropagation();
        addNote({ type: "direction", isLong: false, longTime: 0 });
        return;
    }
    if (target.id === 'add-both') {
        e.preventDefault();
        e.stopPropagation();
        addNote({ type: "both", isLong: false, longTime: 0 });
        return;
    }
    if (target.id === 'add-node') {
        e.preventDefault();
        e.stopPropagation();
        addNote({ type: "node", isLong: false, longTime: 0 });
        return;
    }
    if (target.id === 'add-long-tab') {
        e.preventDefault();
        e.stopPropagation();
        addNote({ type: "longtab", isLong: true });
        return;
    }
    if (target.id === 'add-long-dir') {
        e.preventDefault();
        e.stopPropagation();
        addNote({ type: "longdirection", isLong: true });
        return;
    }
    if (target.id === 'add-long-both') {
        e.preventDefault();
        e.stopPropagation();
        addNote({ type: "longboth", isLong: true });
        return;
    }

    // Event 버튼들
    if (target.id === 'add-event') {
        const eventIndex = addEvent();
        appendSingleEventToList(eventIndex);
        saveToStorage();
        return;
    }
    if (target.id === 'duplicate-events') {
        if (selectedEventIndices.size === 0) {
            alert("복제할 이벤트를 선택해주세요.");
            return;
        }

        // 변경 전 상태를 히스토리에 저장
        saveState();

        const selectedIndices = Array.from(selectedEventIndices).sort((a, b) => a - b);
        const maxIndex = Math.max(...selectedIndices);
        const clonedEvents = [];

        // 선택된 이벤트들을 복제
        selectedIndices.forEach(index => {
            const event = getEventAtIndex(index);
            if (event) {
                const clonedEvent = cloneEvent(event);
                clonedEvents.push(clonedEvent);
            }
        });

        // 가장 뒤에 있는 선택된 항목 바로 뒤에 삽입
        const insertIndex = maxIndex + 1;
        const newIndices = insertMultipleEvents(insertIndex, clonedEvents);

        // 복제된 이벤트들을 선택
        selectedEventIndices.clear();
        newIndices.forEach(index => selectedEventIndices.add(index));

        saveToStorage();
        renderEventList();
        drawPath();
        return;
    }
    if (target.id === 'clear-event-selection') {
        selectedEventIndices.clear();
        lastClickedEventIndex = null;
        renderEventList();
        return;
    }

    // 기타 버튼들...
}

// 사이드바 change 위임 핸들러는 통째로 제거했다(감사 B-3).
//
// bpm·subdivisions·pre-delay·speed-multiplier는 전부 **직접 리스너**가 처리한다
// (handleBpmChange·handleSubdivisionsChange·handlePreDelayChange·handleSpeedMultiplierChange).
// 위임 분기는 그 위에 얹힌 중복이었고, 그중 bpm·subdivisions 분기는 선언되지 않은
// 전역에 대입해서 strict mode(모듈)에서 ReferenceError를 던졌다 — 값 반영은
// 직접 리스너가 이미 끝낸 뒤라 동작은 멀쩡했지만 BPM을 바꿀 때마다 콘솔에
// uncaught 오류가 남았다.
//
// 새 사이드바 입력을 추가할 때도 직접 리스너 방식을 따를 것.

// 메인 영역 클릭 처리
function handleMainClick(e) {
    const target = e.target;

    // Canvas 관련 처리는 기존 방식 유지
    if (target.tagName === 'CANVAS') {
        return;
    }

    // 기타 메인 영역 클릭 처리...
}

document.addEventListener("DOMContentLoaded", async () => {


    // 이벤트 델리게이션 시스템 설정 (성능 최적화)
    setupEventDelegation();

    // Undo/Redo 시스템 초기화
    initializeUndoRedo();

    try {
        await initDB();
    } catch (err) {
        console.warn('IndexedDB initialization failed:', err);
    }

    setupSubdivisionsOptions();

    viewOffset = {
        x: canvas.clientWidth / 2,
        y: canvas.clientHeight / 2
    };


    // Virtual Scrolling 초기화
    const noteListContainer = document.querySelector("#notes-tab table");
    if (noteListContainer) {
        const scrollContainer = noteListContainer.closest(".tab-content");
        if (scrollContainer) {
            // 스크롤 이벤트 리스너 추가
            scrollContainer.addEventListener("scroll", () => {
                virtualScrollState.note.scrollTop = scrollContainer.scrollTop;
                scheduleRender({ noteList: true });
            });

            // 화면 크기에 따른 visibleCount 계산
            const updateVisibleCount = () => {
                const containerHeight = scrollContainer.clientHeight;
                virtualScrollState.note.visibleCount = Math.ceil(containerHeight / virtualScrollState.note.itemHeight) + 10;
            };
            updateVisibleCount();
            window.addEventListener('resize', updateVisibleCount);
        }
    }

    hasAudioFile = false;
    audioBuffer = null;
    
    const bpmField = document.getElementById("bpm");
    const subdivisionsField = document.getElementById("subdivisions");
    const preDelayField = document.getElementById("pre-delay");

    if (bpmField) {
        bpmField.dataset.previousValue = bpmField.value || "120";
    }
    if (subdivisionsField) {
        subdivisionsField.dataset.previousValue = subdivisionsField.value || "16";
    }
    if (preDelayField) {
        preDelayField.dataset.previousValue = preDelayField.value || "0";
    }

    if (bpmField) {
        bpmField.addEventListener("change", (e) => {
            handleBpmChange(parseFloat(e.target.value || 120));
        });
    }

    if (subdivisionsField) {
        subdivisionsField.addEventListener("change", (e) => {
            handleSubdivisionsChange(parseInt(e.target.value || 16));
        });
    }

    // Min BPM 변경 이벤트 리스너
    const minBpmField = document.getElementById("min-bpm");
    if (minBpmField) {
        minBpmField.addEventListener("change", (e) => {
            updateBpmConstraints();
            saveToStorage();
        });
    }

    // Max BPM 변경 이벤트 리스너
    const maxBpmField = document.getElementById("max-bpm");
    if (maxBpmField) {
        maxBpmField.addEventListener("change", (e) => {
            updateBpmConstraints();
            saveToStorage();
        });
    }

    if (preDelayField) {
        preDelayField.addEventListener("change", handlePreDelayChange);
    }

    const speedMultiplierField = document.getElementById("speed-multiplier");
    if (speedMultiplierField) {
        speedMultiplierField.addEventListener("change", (e) => {
            handleSpeedMultiplierChange(parseFloat(e.target.value || 1.0));
        });
    }

    const addSignatureBtn = document.getElementById("add-signature");
    if (addSignatureBtn) {
        addSignatureBtn.addEventListener("click", () => {
            // 마지막 구간 뒤에 이어 붙이면 대체로 원하는 값에 가깝다.
            const last = signatures[signatures.length - 1];
            const start = last && typeof last.endTime === "number" ? last.endTime : 0;
            signatures.push({ startTime: start, endTime: start + 4 });
            saveToStorage();
            renderSignatureList();
            drawPath();
        });
    }

    renderSignatureList();

    document.getElementById("clear-notes").addEventListener("click", () => {
        if (confirm("모든 데이터를 삭제하시겠습니까?")) {
            // 변경 전 상태를 히스토리에 저장
            saveState();

            localStorage.removeItem("autosave_notes");
            resetSelectionState();   // 지워진 노트를 가리키는 선택을 남기지 않는다(감사 B-2)
            notes.length = 0;
            clearAllEvents();
            signatures.length = 0;
            ensureInitialDirectionNote(notes);
            renderSignatureList();
            drawPath();
            renderNoteList();
            scheduleRender({ eventList: true });
        }
    });

    // 탭 버튼 이벤트 리스너
    document.querySelectorAll('.tab-button').forEach(button => {
        button.addEventListener('click', (e) => {
            const tabName = e.target.getAttribute('data-tab');
            switchTab(tabName);
        });
    });

    // 타임라인 탭 배선 (스크롤·리사이즈·툴바·노트 클릭)
    setupTimelineControls();

    // Add Event 버튼은 이벤트 델리게이션으로 처리됨

    // 복제 및 선택 해제 버튼들은 이벤트 델리게이션으로 처리됨

    // Go to URL 버튼 이벤트 리스너
    document.getElementById("go-to-url").addEventListener("click", () => {
        const url = "https://www.notion.so/ilsang93/TrackEvent-278b81f1cb6580f595a3c31abe3a2187?source=copy_link#278b81f1cb65808cb523ea007e49dcc4";
        if (url && url.trim() !== "" && url.trim() !== "https://") {
            window.open(url.trim(), '_blank');
        }
    });

    // 모두 접기 버튼 이벤트 리스너
    document.getElementById("collapse-all").addEventListener("click", () => {
        const paramsContainers = document.querySelectorAll(".params-container");
        const dialogContents = document.querySelectorAll(".dialog-content");

        paramsContainers.forEach(container => {
            container.classList.add("collapsed");
            const toggle = container.querySelector('.params-toggle');
            if (toggle) toggle.textContent = "▶";
        });

        dialogContents.forEach(content => {
            content.classList.add("collapsed");
            const toggle = content.parentElement.querySelector('.dialog-toggle');
            if (toggle) toggle.textContent = "▶";
        });
    });

    // 모두 펼치기 버튼 이벤트 리스너
    document.getElementById("expand-all").addEventListener("click", () => {
        const paramsContainers = document.querySelectorAll(".params-container");
        const dialogContents = document.querySelectorAll(".dialog-content");

        paramsContainers.forEach(container => {
            container.classList.remove("collapsed");
            const toggle = container.querySelector('.params-toggle');
            if (toggle) toggle.textContent = "▼";
        });

        dialogContents.forEach(content => {
            content.classList.remove("collapsed");
            const toggle = content.parentElement.querySelector('.dialog-toggle');
            if (toggle) toggle.textContent = "▼";
        });
    });

    // Undo/Redo 버튼 이벤트 리스너
    document.getElementById("undo-btn").addEventListener("click", () => {
        if (undo()) {
            console.log('Undo performed via button');
        }
    });

    document.getElementById("redo-btn").addEventListener("click", () => {
        if (redo()) {
            console.log('Redo performed via button');
        }
    });

    document.getElementById("duplicate-notes").addEventListener("click", () => {
        if (selectedNoteIndices.size === 0) {
            alert("복제할 노트를 선택해주세요.");
            return;
        }

        // 변경 전 상태를 히스토리에 저장
        saveState();

        const selectedIndices = Array.from(selectedNoteIndices).sort((a, b) => a - b);
        const maxIndex = Math.max(...selectedIndices);
        const clonedNotes = [];

        // 선택된 노트들을 복제
        selectedIndices.forEach(index => {
            // 계약의 cloneNote를 쓴다 — 손으로 짠 목록이 angleOffset·bezier·fade를
            // 빠뜨려 복제하면 곡선이 사라졌다(감사 B-1).
            clonedNotes.push(cloneNote(notes[index]));
        });

        // 가장 뒤에 있는 선택된 항목 바로 뒤에 삽입
        const newIndices = [];
        clonedNotes.forEach((clonedNote, i) => {
            const insertIndex = maxIndex + 1 + i;
            notes.splice(insertIndex, 0, clonedNote);
            newIndices.push(insertIndex);
        });

        // 복제된 노트들을 선택
        selectedNoteIndices.clear();
        newIndices.forEach(index => selectedNoteIndices.add(index));

        // 캐시 무효화
        invalidatePathCache();

        saveToStorage();
        drawPath();
        renderNoteList();
     });

    // 노트 선택 해제 버튼
    document.getElementById("clear-note-selection").addEventListener("click", () => {
        selectedNoteIndices.clear();
        lastClickedNoteIndex = null;
        renderNoteList();
    });

    document.getElementById("sort-notes").addEventListener("click", () => {
        // 변경 전 상태를 히스토리에 저장
        saveState();

        const bpm = parseFloat(document.getElementById("bpm").value || 120);
        const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);

        // 정렬은 인덱스 **순서를 바꾸므로** 선택을 남기면 다른 노트를 가리킨다(감사 B-2).
        resetSelectionState();

        // 구간 번호(sectionIndex) 1차, 구간 내 상대 시간 2차로 정렬
        const sorted = sortNotesByTime(notes, bpm, subdivisions);
        notes.splice(0, notes.length, ...sorted);

        // 이벤트도 시간 기준으로 정렬
        sortEventsByTime();

        // 캐시 무효화 (내부에서 sectionIndex 재계산 포함)
        invalidatePathCache();

        saveToStorage();
        drawPath();
        renderNoteList();
        renderEventList();
    });

    document.getElementById("export-image").addEventListener("click", () => {
        const bpm = parseFloat(document.getElementById("bpm").value) || 120;
        const subdivisions = parseInt(document.getElementById("subdivisions").value) || 16;
        const preDelaySeconds = (parseInt(document.getElementById("pre-delay").value) || 0) / 1000;
        const level = parseInt(document.getElementById("level").value) || 10;
        const musicName = savedAudioFile ? savedAudioFile.name.replace(/\.[^/.]+$/, "") : "Unknown";
        exportChartSVG({
            notes,
            bpm,
            subdivisions,
            preDelaySeconds,
            speedMultiplier,
            musicName,
            level
        });
    });

    /**
     * Save Json / Commit 공용 페이로드 빌더.
     *
     * 두 경로가 **같은 바이트**를 만들어야 하므로 한 곳에서만 조립한다.
     * 복제하면 한쪽만 고쳐져 계약이 갈라진다.
     *
     * 검증 실패/경고 시 confirm으로 확인하며, 사용자가 취소하면 null을 반환한다.
     * preDelay는 입력값을 **그대로** 담는다 (docs/CONTRACT-NOTES.md §1 — 정규화 금지).
     */
    function buildChartExportData() {
        const bpm = parseFloat(document.getElementById("bpm").value || 120);
        const subdivisions = parseInt(document.getElementById("subdivisions").value || 16);
        const preDelayValue = parseInt(document.getElementById("pre-delay").value || 0);
        const preDelaySeconds = preDelayValue / 1000;

        // 마지막 Direction Type None 노트 확인 및 추가
        ensureFinalDirectionNote(notes, bpm, subdivisions);

        // JSON 저장 전에 시간순으로 정렬 (무작위 순서 방지)
        // 원본 배열은 수정하지 않고 복사본만 내보내기에 사용
        const sortedNotes = sortNotesByTime(notes, bpm, subdivisions);

        const validationResult = validateChart(sortedNotes, bpm, subdivisions, preDelaySeconds);
        if (!validationResult.isValid) {
            const proceedWithErrors = confirm(`차트 검증 실패:\n\n${validationResult.errors.join('\n')}\n\n무시하고 저장하시겠습니까?`);
            if (!proceedWithErrors) {
                return null;
            }
        }

        // 경고 메시지가 있으면 사용자에게 알림
        if (validationResult.warnings && validationResult.warnings.length > 0) {
            const proceedExport = confirm(`다음과 같은 경고가 있습니다:\n\n${validationResult.warnings.join('\n')}\n\n계속 내보내시겠습니까?`);
            if (!proceedExport) {
                return null;
            }
        }

        const validatedNotes = validationResult.notes;
        const validatedSectionOffsets = validationResult.sectionOffsets;

        // 완성된 signature 구간만 내보낸다(끝 > 시작). 에디터 시간(preDelay 전)을
        // JSON에서는 finalTime(= 에디터시간 + preDelay초)으로 변환해 저장한다.
        const validSignatures = signatures
            .filter((s) => typeof s.startTime === "number" && typeof s.endTime === "number"
                && s.endTime > s.startTime)
            .map((s) => ({
                // 부동소수 오차가 JSON에 쌓이지 않게 0.001초로 반올림한다.
                startTime: Math.round((s.startTime + preDelaySeconds) * 1000) / 1000,
                endTime: Math.round((s.endTime + preDelaySeconds) * 1000) / 1000,
            }));

        const levelValue = parseInt(document.getElementById("level").value || 10);
        const minBpm = parseFloat(document.getElementById("min-bpm").value || 60);
        const maxBpm = parseFloat(document.getElementById("max-bpm").value || 300);
        const speedValue = getSpeedValue();

        return {
            diffIndex: 5,
            level: levelValue,
            bpm: bpm,
            minbpm: minBpm,
            maxbpm: maxBpm,
            subdivisions: subdivisions,
            preDelay: preDelayValue,
            // 적정 배속(패턴 전역). 🔴 **값이 1.0이어도 항상 내보낸다** —
            // signature·angleOffset·bezier의 "기본값이면 생략" 규칙을 **따르지 않는다.**
            //
            // 이유(client 회신 2026-07-31, lead 확정): Unity는 이 키의 **유무**로
            // "제작자가 정한 값" ↔ "구 패턴"을 판정하고, 없으면 **SO 값으로 폴백**한다.
            // 실측으로 69곡 중 **46곡(67%)이 SO `trackSpeed` ≠ 1.0**이다(1.5가 19곡).
            // 따라서 1.0을 생략하면 제작자가 **의도적으로 고른 1.0**이 SO의 1.5로
            // **조용히 덮인다.** 생략 규칙이 통하는 다른 필드들과 달리, 이 필드는
            // 부재가 "기본값"이 아니라 **"다른 값을 쓰라"**는 뜻이다.
            //
            // 기존 263차트의 바이트는 그대로다 — 그 차트들을 에디터로 열어
            // **다시 저장하지 않는 한** 이 경로를 타지 않는다. 다시 저장하면 그 차트는
            // 그 시점부터 "값이 확정된 차트"가 되며, 그것이 의도된 동작이다.
            //
            // ⚠️ 서버 투영(charts·export·svn)은 **NULL일 때만** 생략한다 —
            // DB가 미지정(NULL)과 확정(1.0)을 구별하므로 그쪽은 이미 올바르다.
            // 여기를 되돌리면 **에디터 저장본만 서버 투영과 갈린다.**
            speed: speedValue,
            // finalTime 기준 구간. 끝이 시작보다 큰 것만 내보낸다(불완전 입력 제외).
            // 구간이 하나도 없으면 **필드를 아예 넣지 않는다** — 기존 차트
            // (전부 이 필드가 없다)의 바이트를 그대로 유지하기 위해서다.
            // angleOffset/bezier가 기본값일 때 생략하는 것과 같은 규칙이다.
            ...(validSignatures.length ? { signature: validSignatures } : {}),
            noteList: validatedNotes.map((n, i) => {
                // noteToJsonFormat 함수를 사용하여 fadeDirectTime 및 sectionOffset 지원
                const sectionOffset = validatedSectionOffsets ? (validatedSectionOffsets[i] || 0) : 0;
                return noteToJsonFormat(n, bpm, subdivisions, preDelaySeconds, sectionOffset);
            }),
            eventList: eventsToJson(),
            metadata: {
                description: "Music starts at 3 seconds, with pre-delay correction",
                timingExplanation: "finalTime = 3.0 + originalTime + preDelay (except for beat 0 direction note)",
                preDelayUnit: "milliseconds",
                longTimeUnit: "longTime values are in seconds (calculated using each note's individual BPM/subdivisions)",
                bpmExplanation: "Top-level bpm/subdivisions are global defaults. Each note has individual bpm/subdivisions for variable tempo support",
                validationApplied: "Chart validated and auto-corrected",
                eventListDescription: "Events are separate from notes and contain custom game logic triggers",
                exportedAt: new Date().toISOString()
            }
        };
    }

    document.getElementById("save-json").addEventListener("click", () => {
        const exportData = buildChartExportData();
        if (!exportData) {
            return;
        }

        // XX_MUSICNAME 형태의 기본 파일명 생성
        let defaultFilename = "XX";
        if (savedAudioFile && savedAudioFile.name) {
            // 음원 파일명에서 확장자 제거
            const musicName = savedAudioFile.name.replace(/\.[^/.]+$/, "");
            defaultFilename = `XX_${musicName}`;
        }

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: "application/json"
        });

        // 파일 다이얼로그를 통한 저장
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${defaultFilename}.json`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(a.href);
    });

    /**
     * 차트 JSON을 에디터 상태에 적용한다.
     *
     * Load Json(로컬 파일)과 서버 체크아웃이 **같은 경로**를 쓰도록 분리했다.
     * 복제하면 한쪽만 고쳐져 로드 결과가 갈라진다.
     *
     * 호출 전 saveState()로 되돌리기 지점을 남길 것.
     */
    function applyChartJson(json) {
                    // 차트를 통째로 갈아끼우므로 인덱스 기반 선택은 무효다(감사 B-2).
                    resetSelectionState();

                    notes.length = 0;

                    const bpm = json.bpm || 120;
                    const subdivisions = json.subdivisions || 16;

                    // signature 구간 복원. JSON은 finalTime이므로 에디터 시간
                    // (preDelay 전)으로 되돌린다: editorTime = finalTime - preDelay초.
                    // preDelay는 이 함수 뒤에서 UI에 반영되므로 json에서 직접 계산한다.
                    const loadedPreDelaySec = (json.preDelay !== undefined ? json.preDelay : 0) / 1000;
                    // 부동소수 오차 누적 방지: 입력 정밀도(0.001초)로 반올림한다.
                    const round3 = (v) => Math.round(v * 1000) / 1000;
                    signatures.length = 0;
                    if (Array.isArray(json.signature)) {
                        json.signature.forEach((s) => {
                            if (s && typeof s.startTime === "number" && typeof s.endTime === "number") {
                                signatures.push({
                                    startTime: round3(s.startTime - loadedPreDelaySec),
                                    endTime: round3(s.endTime - loadedPreDelaySec),
                                });
                            }
                        });
                    }

                    let preDelayMs;
                    if (json.preDelay !== undefined) {
                        preDelayMs = json.preDelay;
                    } else {
                        preDelayMs = 0;
                    }

                    json.noteList.forEach(n => {
                        // 계약 변환기를 그대로 쓴다. 손으로 짠 사본이 angleOffset·bezier를
                        // 빠뜨려 로드할 때마다 곡선이 사라졌다(감사 B-1).
                        const noteData = jsonToNoteFormat(n, bpm, subdivisions);

                        // fade의 구버전 호환(숫자 → boolean)은 계약 변환기에 없으므로 여기서 유지한다.
                        if (typeof n.fade === 'number') {
                            noteData.fade = n.fade > 0;
                        }

                        // beatReset은 node 외 타입에도 필요하다(계약 변환기는 node에만 붙인다).
                        if (noteData.beatReset === undefined) {
                            noteData.beatReset = n.isBeatReset || false;
                        }

                        // JSON에 절대시간 정보가 있으면 정렬용으로 임시 저장
                        // originalTime과 finalTime 중 사용 가능한 것을 우선 사용
                        if (n.originalTime !== undefined) {
                            noteData._sortTime = n.originalTime;
                        } else if (n.finalTime !== undefined) {
                            noteData._sortTime = n.finalTime;
                        } else if (n.time !== undefined) {
                            noteData._sortTime = n.time;
                        }

                        notes.push(noteData);
                    });

                    // JSON 데이터가 무작위 순서로 들어있을 수 있으므로 절대시간 기준으로 먼저 정렬
                    // 이를 통해 beatReset 노트가 올바른 위치에 배치되도록 보장
                    if (notes.length > 0 && notes[0]._sortTime !== undefined) {
                        // 절대시간 정보가 있는 경우: 절대시간 기준 정렬
                        notes.sort((a, b) => {
                            const timeA = a._sortTime !== undefined ? a._sortTime : 0;
                            const timeB = b._sortTime !== undefined ? b._sortTime : 0;
                            return timeA - timeB;
                        });
                    } else {
                        // 절대시간 정보가 없는 경우 (구버전 JSON): beat 기준으로 정렬 (최선의 노력)
                        notes.sort((a, b) => a.beat - b.beat);
                    }

                    // 정렬용 임시 필드 제거
                    notes.forEach(n => {
                        delete n._sortTime;
                    });

                    document.getElementById("bpm").value = bpm;
                    document.getElementById("subdivisions").value = subdivisions;
                    document.getElementById("pre-delay").value = preDelayMs;

                    // previousValue도 업데이트하여 로드 후 변경 감지가 올바르게 작동하도록 함
                    const preDelayField = document.getElementById("pre-delay");
                    if (preDelayField) {
                        preDelayField.dataset.previousValue = preDelayMs;
                    }

                    // Level 값 설정 (없으면 기본값 10)
                    const levelValue = json.level || 10;
                    document.getElementById("level").value = levelValue;
                    document.getElementById("level-display").textContent = levelValue;

                    // 적정 배속 (키가 없는 기존 차트는 기본값으로 되돌린다 —
                    // 앞서 연 차트의 값이 남아 새 차트에 새는 것을 막는다).
                    const speedField = document.getElementById("speed");
                    if (speedField) {
                        speedField.value = Number.isFinite(json.speed) ? json.speed : DEFAULT_SPEED;
                    }

                    // Min/Max BPM 값 설정 (없으면 기본값 사용)
                    const minBpm = json.minbpm || 60;
                    const maxBpm = json.maxbpm || 300;
                    document.getElementById("min-bpm").value = minBpm;
                    document.getElementById("max-bpm").value = maxBpm;

                    // BPM 제약 조건 업데이트
                    updateBpmConstraints();

                    document.getElementById("bpm").dataset.previousValue = bpm;
                    document.getElementById("subdivisions").dataset.previousValue = subdivisions;

                    // EventList 로드
                    if (json.eventList && Array.isArray(json.eventList)) {
                        loadEventsFromJson(json.eventList);
                    } else {
                        clearAllEvents();
                    }

                    // 초기 direction 노트 확인 및 추가
                    ensureInitialDirectionNote(notes);
                    // 마지막 direction 노트 확인 및 추가
                    ensureFinalDirectionNote(notes, bpm, subdivisions);

                    // sectionIndex 재계산 후 캐시 무효화
                    invalidatePathCache();

                    saveToStorage();
                    renderSignatureList();
                    drawPath();
                    renderNoteList();
                    scheduleRender({ eventList: true });
    }

    document.getElementById("load-json").addEventListener("click", () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".json";
        input.addEventListener("change", (e) => {
            const file = e.target.files[0];
            if (!file)
                return;

            const reader = new FileReader();
            reader.onload = (ev) => {
                try {
                    const json = JSON.parse(ev.target.result);
                    if (!json.noteList || !Array.isArray(json.noteList)) {
                        alert("올바른 JSON 파일이 아닙니다.");
                        return;
                    }

                    // 변경 전 상태를 히스토리에 저장
                    saveState();

                    applyChartJson(json);
                } catch (err) {
                    alert("불러오기 중 오류 발생: " + err.message);
                }
            };
            reader.readAsText(file);
        });
        input.click();
    });

    // ── 노트/이벤트 리스트 별도 창 ────────────────────────────────────
    //
    // 사이드바가 520px 고정인데 노트 테이블은 컬럼이 14개라 입력 필드가
    // 눌린다. 그래서 리스트를 넓은 별도 창으로 뺄 수 있게 한다.
    //
    // **렌더러는 건드리지 않는다.** renderNoteList/renderEventList는
    // document.getElementById("note-list") 같은 식으로 노드를 찾는데,
    // 노드를 복제해서 두 벌로 만들면 렌더 경로가 갈라진다. 대신 **실제 노드를
    // 팝업으로 옮기고**(adoptNode) 닫을 때 원위치시킨다. 렌더러 입장에서는
    // 같은 노드 하나가 계속 있을 뿐이다.
    //
    // 옮긴 뒤에도 메인 문서의 getElementById가 그 노드를 찾을 수 있어야 하므로
    // 아래에서 조회를 팝업 문서까지 확장한다.

    let listWindow = null;

    /**
     * document.getElementById를 감싸서, 메인 문서에 없으면 팝업 문서도 본다.
     *
     * 렌더러 수십 곳을 고치는 대신 조회 지점 한 곳만 확장하는 방식이다.
     * 팝업이 닫히면 노드가 돌아오므로 이 우회는 자연히 쓰이지 않게 된다.
     */
    const nativeGetElementById = document.getElementById.bind(document);
    document.getElementById = function (id) {
        const found = nativeGetElementById(id);
        if (found) return found;
        if (listWindow && !listWindow.closed) {
            return listWindow.document.getElementById(id);
        }
        return null;
    };

    // 사이드바 전체를 팝업으로 옮긴다 — 복구를 위해 원위치를 기억한다.
    let sidebarHome = null;

    function openListWindow() {
        if (listWindow && !listWindow.closed) {
            listWindow.focus();
            return;
        }

        const sidebar = nativeGetElementById("sidebar");
        if (!sidebar) return;

        const win = window.open("", "wl-sidebar",
            "width=560,height=900,menubar=no,toolbar=no,location=no,status=no");
        if (!win) {
            alert("팝업이 차단되었습니다. 이 사이트의 팝업을 허용해 주세요.");
            return;
        }
        listWindow = win;

        // 에디터 스타일을 그대로 물려받아야 같은 모습으로 보인다.
        win.document.title = "WL Editor — 사이드 패널";
        const head = win.document.head;
        document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
            head.appendChild(node.cloneNode(true));
        });

        // 팝업에서는 사이드바가 고정 위치가 아니라 창 전체를 채운다.
        const style = win.document.createElement("style");
        style.textContent = `
            body { margin: 0; background: #333; }
            #sidebar { position: static !important; width: 100% !important;
                       height: 100vh !important; left: 0 !important;
                       border-right: none !important; }
            #sidebar-resizer, #sidebar-toggle { display: none !important; }
        `;
        head.appendChild(style);

        // 원위치 기억 후, 실제 사이드바 노드를 팝업으로 이동(복제가 아니다).
        sidebarHome = { parent: sidebar.parentNode, next: sidebar.nextSibling };
        win.document.body.appendChild(win.document.adoptNode(sidebar));

        // 사이드바가 빠졌으니 메인의 왼쪽 여백을 없애 빈 공간이 남지 않게 한다.
        document.body.classList.add("sidebar-popped");

        document.getElementById("popout-lists").textContent = "⧉ 창 복귀";

        /** 사이드바를 원위치로 되돌린다. 팝업이 어떻게 닫히든 실행돼야 한다. */
        const restore = () => {
            const el = listWindow?.document.getElementById("sidebar");
            if (el && sidebarHome) {
                try {
                    sidebarHome.parent.insertBefore(document.adoptNode(el), sidebarHome.next);
                } catch {
                    sidebarHome.parent.appendChild(document.adoptNode(el));
                }
            }
            listWindow = null;
            sidebarHome = null;
            document.body.classList.remove("sidebar-popped");
            const btn = nativeGetElementById("popout-lists");
            if (btn) btn.textContent = "⧉ 창 분리";
            renderNoteList();
            scheduleRender({ eventList: true });
        };

        win.addEventListener("beforeunload", restore);
        // 메인 창을 닫거나 새로고침하면 팝업도 정리한다 (고아 창 방지).
        window.addEventListener("beforeunload", () => {
            if (listWindow && !listWindow.closed) listWindow.close();
        });

        // 옮긴 직후 한 번 다시 그린다.
        renderNoteList();
        scheduleRender({ eventList: true });
    }

    document.getElementById("popout-lists").addEventListener("click", () => {
        if (listWindow && !listWindow.closed) {
            listWindow.close();   // beforeunload -> restore
        } else {
            openListWindow();
        }
    });

    // ── 서버 연동: Commit / Checkout / History ─────────────────────────
    //
    // 낙관적 잠금: 커밋 시 체크아웃 기준이 된 head 버전을 함께 보낸다.
    // 서버가 409를 주면 다른 사람이 먼저 커밋한 것이므로, **현재 작업을
    // 절대 덮어쓰지 않고** 사용자에게 알린 뒤 선택을 맡긴다.

    const serverState = {
        chartId: null,          // 현재 연결된 차트
        musicName: null,
        headVersionId: null,    // 체크아웃 기준 버전 (커밋 시 그대로 전송)
        headVersionNumber: null,
        path: null,             // 파일 브라우저에서 연 경로 (없으면 미연결)
        trackCode: null,        // svn 경로에서 유도한 현재 트랙
        trackName: null,
        difficulty: null
    };

    // 새로고침/재실행 후에도 마지막으로 연 파일 연결이 유지되도록 저장한다.
    const SERVER_STATE_KEY = "wl_editor_server_state";

    function persistServerState() {
        try {
            if (!serverState.chartId) {
                localStorage.removeItem(SERVER_STATE_KEY);
            } else {
                localStorage.setItem(SERVER_STATE_KEY, JSON.stringify(serverState));
            }
        } catch { /* 용량 초과 등은 무시 */ }
    }

    /**
     * 저장된 서버 연결을 복원한다.
     *
     * 노트 데이터(autosave_notes)는 별도로 복원되므로, 여기서는 연결 정보만
     * 되살린다. 서버 HEAD가 로컬 기준 버전과 달라졌으면(그새 누가 커밋) 사용자에게
     * 알리되 로컬 편집을 덮어쓰지는 않는다.
     */
    async function restoreServerState() {
        let saved;
        try {
            saved = JSON.parse(localStorage.getItem(SERVER_STATE_KEY) || "null");
        } catch { saved = null; }
        if (!saved || !saved.chartId) return;

        // 로그인·권한이 유효한지 먼저 확인. 아니면 조용히 연결만 비운다.
        try {
            await api.me();
        } catch {
            localStorage.removeItem(SERVER_STATE_KEY);
            return;
        }

        Object.assign(serverState, saved);
        updateStatus();

        // 서버의 현재 HEAD를 확인해, 로컬 기준과 다르면 안내한다(덮어쓰지 않음).
        try {
            const res = await api.getChart(serverState.chartId);
            if (res.versionId && String(res.versionId) !== String(serverState.headVersionId)) {
                statusEl.title = `${serverState.path ?? ""}\n⚠ 서버에 새 버전 v${res.versionNumber}이 있습니다. ` +
                    `커밋 시 409가 날 수 있어요.\n클릭: 파일 브라우저`;
            }
        } catch {
            // 차트가 사라졌거나 접근 불가 — 연결만 비운다.
            Object.keys(serverState).forEach((k) => { serverState[k] = null; });
            localStorage.removeItem(SERVER_STATE_KEY);
            updateStatus();
        }
    }

    // 난이도 코드 → 표시 이름. 코드(BG/EZ/NM/HD/EX/OV)가 SVN 파일명
    // (`LT0004-NM.json`)에 그대로 들어가는 실제 계약이므로 UI도 코드를 앞세운다.
    // OV는 아직 콘텐츠가 없는 예약 티어다.
    const DIFFICULTY_NAME = {
        BG: "Beginner", EZ: "Easy", NM: "Normal",
        HD: "Hard", EX: "Extreme", OV: "Override"
    };

    const modal = document.getElementById("server-modal");
    const modalTitle = document.getElementById("server-modal-title");
    const modalBody = document.getElementById("server-modal-body");
    const statusEl = document.getElementById("server-status");

    function openModal(title) {
        modalTitle.textContent = title;
        modalBody.innerHTML = "";
        modal.hidden = false;
        return modalBody;
    }

    function closeModal() {
        modal.hidden = true;
        modalBody.innerHTML = "";
    }

    // 페이지 입장 로그인 게이트가 떠 있는 동안에는 모달을 닫지 못하게 막고,
    // 닫으려 하면 로그인 폼을 다시 띄운다.
    let loginGateActive = false;

    function dismissModal() {
        if (loginGateActive) {
            // 게이트 중에는 닫는 대신 로그인 폼을 다시 연다.
            requireLogin();
            return;
        }
        closeModal();
    }

    document.getElementById("server-modal-close").addEventListener("click", dismissModal);
    modal.addEventListener("click", (e) => {
        if (e.target === modal) dismissModal();
    });
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && !modal.hidden) dismissModal();
    });

    /**
     * HTML 이스케이프.
     *
     * 트랙 이름·표시 이름·에러 메시지는 전부 사용자가 넣은 값이다. innerHTML에
     * 그대로 끼워 넣으면 이름이 `<img onerror=...>` 인 트랙 하나로 스크립트가
     * 실행된다. 값을 붙이는 자리에는 반드시 이걸 통과시킨다.
     */
    function esc(value) {
        return String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

    // 서버 드롭다운 메뉴 토글 (파일 브라우저·트랙·관리자를 한 버튼으로 모음)
    const serverMenuBtn = document.getElementById("server-menu-btn");
    const serverMenu = document.getElementById("server-menu");
    if (serverMenuBtn && serverMenu) {
        const closeMenu = () => { serverMenu.hidden = true; };
        serverMenuBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            serverMenu.hidden = !serverMenu.hidden;
        });
        // 메뉴 항목을 고르면 닫는다.
        serverMenu.querySelectorAll("button").forEach((b) =>
            b.addEventListener("click", closeMenu));
        // 바깥을 누르면 닫는다.
        document.addEventListener("click", (e) => {
            if (!serverMenu.hidden && !e.target.closest(".tb-menu")) closeMenu();
        });
    }

    // TrackBase 디버그 진입 — 서빙 환경에 따라 두 경로로 갈린다 (F-1 합의).
    //  · 로컬 dev(http): 에디터 dev 서버가 UI를 갖고 있으므로 팝업으로 연다.
    //  · 운영(https): HTTPS 페이지는 http://127.0.0.1을 하위 리소스로 부를 수 없지만
    //    (mixed content), 그 주소를 새 탭으로 "이동"하는 것은 차단 대상이 아니다.
    //    UI는 로컬 INHOUSE 게임이 127.0.0.1:8787/debug 에서 직접 서빙한다.
    const trackDebugBtn = document.getElementById("trackbase-debug");
    if (trackDebugBtn) {
        trackDebugBtn.hidden = false;
        if (location.protocol === "http:") {
            trackDebugBtn.addEventListener("click", async () => {
                const { openTrackDebugWindow } = await import("../src/trackdebug-window.js");
                openTrackDebugWindow();
            });
        } else {
            trackDebugBtn.addEventListener("click", () => {
                window.open("http://127.0.0.1:8787/debug", "_blank", "noopener");
            });
        }
    }

    /** 모달 안에 메시지 박스를 띄운다. kind: 'ok' | 'warn' | 'error' */
    function showMsg(parent, kind, text) {
        const div = document.createElement("div");
        div.className = `server-msg ${kind}`;
        div.textContent = text;
        parent.appendChild(div);
        return div;
    }

    function updateStatus() {
        // 클릭하면 파일 브라우저를 열어 다른 트랙/파일로 바꿀 수 있게 한다.
        statusEl.style.cursor = "pointer";
        statusEl.onclick = () => {
            // 주의: then(showFileBrowser)로 넘기면 ensureLoggedIn의 resolve 값(true)이
            // path 인자로 들어가 "path must start with..." 에러가 난다. 인자 없이 호출.
            ensureLoggedIn().then(() => showFileBrowser()).catch(() => {});
        };

        if (!serverState.chartId) {
            statusEl.textContent = "📂 파일 없음";
            statusEl.className = "server-status";
            statusEl.title = "클릭해서 파일 브라우저를 엽니다";
            return;
        }
        const v = serverState.headVersionNumber;
        const fileName = serverState.path ? serverState.path.split("/").pop() : serverState.musicName;
        // 트랙이 있으면 "트랙 · 난이도 · 파일 @v", 없으면 파일만.
        const trackLabel = serverState.trackCode
            ? `${serverState.trackCode}${serverState.trackName ? " " + serverState.trackName : ""}`
            : null;
        const diff = serverState.difficulty ? ` · ${serverState.difficulty}` : "";
        statusEl.textContent = trackLabel
            ? `🎵 ${trackLabel}${diff} · ${fileName} @v${v ?? "—"}`
            : `📄 ${fileName} @v${v ?? "—"}`;
        statusEl.className = "server-status ok";
        statusEl.title = `${serverState.path ?? ""}\n커밋 기준 v${v ?? "(없음)"}\n클릭: 다른 파일 열기`;
        persistServerState();
    }

    /** 현재 로그인한 사용자 — 관리자 버튼 노출 판단에 쓴다. */
    let currentUser = null;

    function applyUserToUI(user) {
        currentUser = user;
        const adminBtn = document.getElementById("server-admin");
        adminBtn.hidden = !(user && user.role === "admin");
    }

    /**
     * 비밀번호 변경 화면.
     *
     * 서버가 428을 주는 동안에는 이것 말고 아무것도 할 수 없다
     * (초기 admin/admin 계정이 그 상태로 시작한다).
     */
    function showPasswordChange({ forced = false } = {}) {
        return new Promise((resolve) => {
            const body = openModal(forced ? "비밀번호 변경 필요" : "비밀번호 변경");

            if (forced) {
                showMsg(body, "warn",
                    "초기 비밀번호를 사용 중입니다.\n" +
                    "비밀번호를 변경해야 다른 기능을 쓸 수 있습니다.");
            }

            const form = document.createElement("div");
            form.innerHTML = `
                <label>현재 비밀번호</label>
                <input type="password" id="pw-current" autocomplete="current-password">
                <label>새 비밀번호 (8자 이상)</label>
                <input type="password" id="pw-new" autocomplete="new-password">
                <label>새 비밀번호 확인</label>
                <input type="password" id="pw-confirm" autocomplete="new-password">
            `;
            body.appendChild(form);

            const btn = document.createElement("button");
            btn.textContent = "변경";
            body.appendChild(btn);

            const submit = async () => {
                const cur = form.querySelector("#pw-current").value;
                const next = form.querySelector("#pw-new").value;
                const confirmPw = form.querySelector("#pw-confirm").value;

                if (next.length < 8) {
                    showMsg(body, "warn", "새 비밀번호는 8자 이상이어야 합니다.");
                    return;
                }
                if (next !== confirmPw) {
                    showMsg(body, "warn", "새 비밀번호가 서로 다릅니다.");
                    return;
                }

                btn.disabled = true;
                try {
                    await api.changePassword(cur, next);
                    // 변경 후 상태를 다시 읽어 온다 (must_change_password 해제됨).
                    const me = await api.me();
                    applyUserToUI(me.user);
                    closeModal();
                    resolve(true);
                } catch (err) {
                    btn.disabled = false;
                    showMsg(body, "error", err.message);
                }
            };

            btn.addEventListener("click", submit);
            body.addEventListener("keydown", (e) => {
                if (e.key === "Enter") submit();
            });
            form.querySelector("#pw-current").focus();
        });
    }

    /** 인증이 필요하면 로그인 폼을 띄우고, 성공 시 resolve. */
    async function ensureLoggedIn() {
        try {
            const me = await api.me();
            applyUserToUI(me.user);
            // 비밀번호를 바꾸기 전에는 서버가 428로 전부 막는다.
            if (me.user.mustChangePassword) {
                await showPasswordChange({ forced: true });
            }
            return true;
        } catch (err) {
            if (err.status !== 401) throw err;
        }

        return new Promise((resolve) => {
            const body = openModal("로그인");
            body.innerHTML = `
                <label>이메일 또는 ID</label>
                <input type="text" id="srv-email" autocomplete="username">
                <label>비밀번호</label>
                <input type="password" id="srv-password" autocomplete="current-password">
                <label class="server-checkbox">
                    <input type="checkbox" id="srv-autologin"> 자동 로그인 (이 브라우저에서 로그인 유지)
                </label>
            `;
            // 토글 기본값은 저장된 선호를 따른다. 바꾸면 바로 반영해,
            // 로그인 시점에 알맞은 저장소로 토큰이 들어가게 한다.
            const autoEl = body.querySelector("#srv-autologin");
            autoEl.checked = api.getAutoLogin();
            autoEl.addEventListener("change", () => api.setAutoLogin(autoEl.checked));

            const btn = document.createElement("button");
            btn.textContent = "로그인";
            body.appendChild(btn);

            const regBtn = document.createElement("button");
            regBtn.textContent = "가입 신청";
            regBtn.className = "secondary";
            regBtn.style.marginLeft = "8px";
            body.appendChild(regBtn);

            const submit = async () => {
                const email = body.querySelector("#srv-email").value.trim();
                const password = body.querySelector("#srv-password").value;
                if (!email || !password) return;

                btn.disabled = true;
                try {
                    const user = await api.login(email, password);
                    applyUserToUI(user);
                    if (user.mustChangePassword) {
                        await showPasswordChange({ forced: true });
                    } else {
                        closeModal();
                    }
                    resolve(true);
                } catch (err) {
                    btn.disabled = false;
                    if (err.status === 403) {
                        // 승인 대기 / 비활성 — 자격 증명 오류와 구분해서 알린다.
                        showMsg(body, "warn", err.message);
                    } else {
                        showMsg(body, "error",
                            err.status === 401 ? "아이디 또는 비밀번호가 올바르지 않습니다."
                                               : `로그인 실패: ${err.message}`);
                    }
                }
            };

            regBtn.addEventListener("click", () => showRegister(resolve));
            btn.addEventListener("click", submit);
            body.addEventListener("keydown", (e) => {
                if (e.key === "Enter") submit();
            });
            body.querySelector("#srv-email").focus();
        });
    }

    /**
     * 페이지 입장 로그인 게이트.
     *
     * 로그인하지 않으면 편집기를 쓸 수 없다. 로그인할 때까지 로그인 모달을
     * 계속 띄우고(닫으면 다시 연다), 로그인에 성공하면 게이트를 해제한다.
     * 서버에 아예 닿지 못하면 재시도 버튼을 준다.
     */
    async function requireLogin() {
        loginGateActive = true;
        try {
            await ensureLoggedIn();
            loginGateActive = false;
            // ensureLoggedIn이 로그인 폼을 띄웠다면 성공 후 닫아 준다.
            if (!modal.hidden) closeModal();
            return true;
        } catch (err) {
            // 로컬 개발에서 API가 없으면 게이트를 통과시킨다.
            //
            // 게이트 모달은 화면 전체를 덮으므로(style.css `.server-modal`
            // `inset: 0`), API 서버 없이는 편집기를 **아예 만질 수 없다**.
            // 그런데 노트 편집·undo·붙여넣기·Load/Save JSON은 전부 브라우저
            // 안에서만 도는 로직이라 서버가 필요 없다. DB까지 세우지 않고도
            // 그 부분을 검증할 수 있어야 한다.
            //
            // `import.meta.env.DEV`는 vite가 **빌드 시 리터럴로 치환**하므로
            // 프로덕션 번들에서는 이 분기 전체가 죽은 코드가 되어 제거된다.
            // (확인: `npm run build` 후 dist에 LOCAL_NO_API_BYPASS 문자열 부재)
            //
            // 조건을 좁게 잡은 이유: 서버가 **응답은 하는데** 401/403/428/500인
            // 경우는 진짜 인증·장애 문제이므로 통과시키면 안 된다.
            //
            // "API가 아예 없음"은 두 모습으로 온다:
            //  - status 없음   — fetch 자체가 실패(직접 접속 등)
            //  - 503 + API_UNREACHABLE — vite 프록시를 경유한 경우.
            //    프록시는 ECONNREFUSED를 기본적으로 500 + 빈 본문으로 바꿔 버려
            //    진짜 서버 오류와 구분되지 않으므로, vite.config.js에서 연결
            //    실패만 이 마커로 표시하도록 해 두었다.
            const apiUnreachable =
                err.status === undefined ||
                (err.status === 503 && err.body?.error === "API_UNREACHABLE");
            if (import.meta.env.DEV && apiUnreachable) {
                console.warn(
                    "[LOCAL_NO_API_BYPASS] API에 닿지 못해 로그인 게이트를 건너뜁니다. " +
                    "로컬 개발 전용이며 서버 기능(커밋·체크아웃·음원)은 동작하지 않습니다."
                );
                loginGateActive = false;
                if (!modal.hidden) closeModal();
                applyUserToUI(null);   // 관리자 버튼은 숨긴 채로 둔다
                return false;          // 서버 상태 복원은 시도하지 않는다
            }

            // 401이 아닌 오류(서버 다운 등) — 폼을 못 띄웠으니 재시도를 안내한다.
            const body = openModal("로그인");
            showMsg(body, "error", `서버에 연결할 수 없습니다: ${err.message}`);
            const retry = document.createElement("button");
            retry.textContent = "다시 시도";
            retry.addEventListener("click", () => requireLogin());
            body.appendChild(retry);
            return false;
        }
    }

    /** 가입 신청 — 계정은 만들어지지만 관리자 승인 전까지 쓸 수 없다. */
    function showRegister(resolve) {
        const body = openModal("가입 신청");
        body.innerHTML = `
            <label>이메일 또는 ID</label>
            <input type="text" id="reg-email" autocomplete="username">
            <label>표시 이름</label>
            <input type="text" id="reg-name">
            <label>비밀번호 (8자 이상)</label>
            <input type="password" id="reg-password" autocomplete="new-password">
        `;
        const btn = document.createElement("button");
        btn.textContent = "가입 신청";
        body.appendChild(btn);

        btn.addEventListener("click", async () => {
            const email = body.querySelector("#reg-email").value.trim();
            const displayName = body.querySelector("#reg-name").value.trim();
            const password = body.querySelector("#reg-password").value;

            if (!email || !displayName || password.length < 8) {
                showMsg(body, "warn", "모든 항목을 입력하세요 (비밀번호는 8자 이상).");
                return;
            }

            btn.disabled = true;
            try {
                const res = await api.register(email, password, displayName);
                body.innerHTML = "";
                showMsg(body, "ok",
                    res.message ?? "가입 신청이 접수되었습니다. 관리자 승인 후 사용할 수 있습니다.");
            } catch (err) {
                btn.disabled = false;
                showMsg(body, "error",
                    err.status === 409 ? "이미 등록된 계정입니다." : `신청 실패: ${err.message}`);
            }
        });
    }

    /**
     * 계정 모달 — 로그인한 사용자의 정보, 자동 로그인 토글, 로그아웃.
     *
     * 로그인 상태에서 서버 메뉴의 "계정"으로 열린다. 로그아웃하면 페이지
     * 입장 게이트가 다시 로그인 폼을 띄운다.
     */
    async function showAccountModal() {
        const body = openModal("계정");
        showMsg(body, "ok", "불러오는 중…");

        let user = currentUser;
        try {
            const me = await api.me();
            user = me.user;
            applyUserToUI(user);
        } catch {
            // 세션이 만료됐으면 로그인 게이트로 넘긴다.
            closeModal();
            requireLogin();
            return;
        }

        // 로딩 메시지를 지우고 계정 화면을 그린다(같은 모달, 같은 body).
        const name = esc(user.displayName ?? user.email ?? "");
        const email = esc(user.email ?? "");
        const roleLabel = user.role === "admin" ? "관리자" : "사용자";
        body.innerHTML = `
            <div class="server-account">
                <div class="server-account-name">${name}</div>
                <div class="server-account-meta">${email} · ${roleLabel}</div>
            </div>
            <label class="server-checkbox">
                <input type="checkbox" id="acct-autologin"> 자동 로그인 (이 브라우저에서 로그인 유지)
            </label>
        `;
        const autoEl = body.querySelector("#acct-autologin");
        autoEl.checked = api.getAutoLogin();
        autoEl.addEventListener("change", () => api.setAutoLogin(autoEl.checked));

        const pwBtn = document.createElement("button");
        pwBtn.textContent = "비밀번호 변경";
        pwBtn.className = "secondary";
        pwBtn.addEventListener("click", () => { showPasswordChange(); });
        body.appendChild(pwBtn);

        const outBtn = document.createElement("button");
        outBtn.textContent = "로그아웃";
        outBtn.style.marginLeft = "8px";
        outBtn.addEventListener("click", () => {
            api.logout();
            applyUserToUI(null);
            // 연결 상태도 비워 다음 사용자에게 남지 않게 한다.
            Object.keys(serverState).forEach((k) => { serverState[k] = null; });
            persistServerState();
            updateStatus();
            closeModal();
            // 페이지 입장에는 로그인이 필요하므로 곧바로 로그인 폼을 다시 띄운다.
            requireLogin();
        });
        body.appendChild(outBtn);
    }

    /** 서버 차트를 에디터에 적용. 실패 시 현재 상태는 건드리지 않는다. */
    function checkoutIntoEditor(res) {
        if (!res.chart || !Array.isArray(res.chart.noteList)) {
            throw new Error("서버 응답에 noteList가 없습니다.");
        }

        // 되돌리기 지점 — 체크아웃도 실행 취소 가능해야 한다.
        saveState();
        applyChartJson(res.chart);

        serverState.headVersionId = res.versionId;
        serverState.headVersionNumber = res.versionNumber;
        serverState.musicName = res.musicName;
        updateStatus();
    }

    document.getElementById("server-commit").addEventListener("click", async () => {
        try {
            await ensureLoggedIn();
        } catch (err) {
            alert(`서버에 연결할 수 없습니다: ${err.message}`);
            return;
        }

        if (!serverState.chartId) {
            alert("먼저 History에서 차트를 선택하거나 새로 만드세요.");
            return;
        }

        // Save Json과 동일한 페이로드 — 바이트 동일성의 근거.
        const chart = buildChartExportData();
        if (!chart) return;   // 사용자가 검증 경고에서 취소

        const body = openModal("커밋");
        body.innerHTML = `
            <div style="color:#aaa;font-size:11px;margin-bottom:6px;">
                ${esc(serverState.musicName)} — 기준 v${esc(serverState.headVersionNumber ?? "(최초 커밋)")}
            </div>
            <label>커밋 메시지</label>
            <input type="text" id="srv-commit-msg" placeholder="예: 후렴 노트 배치 수정">
        `;
        const btn = document.createElement("button");
        btn.textContent = "커밋";
        body.appendChild(btn);

        const doCommit = async () => {
            btn.disabled = true;
            const message = body.querySelector("#srv-commit-msg").value.trim();
            try {
                const res = await api.commit(serverState.chartId, {
                    chart,
                    message,
                    expectedHeadVersionId: serverState.headVersionId
                });
                serverState.headVersionId = res.version.id;
                serverState.headVersionNumber = res.version.version_number;
                updateStatus();

                body.innerHTML = "";
                showMsg(body, "ok", `커밋 완료 — v${res.version.version_number}`);
                if (res.warnings && res.warnings.length > 0) {
                    showMsg(body, "warn", `경고:\n${res.warnings.join("\n")}`);
                }
            } catch (err) {
                btn.disabled = false;

                if (err.status === 409) {
                    // 충돌: 현재 작업은 그대로 둔다. 덮어쓰기 없음.
                    body.innerHTML = "";
                    showMsg(body, "warn",
                        "다른 사용자가 먼저 커밋했습니다.\n\n" +
                        "현재 편집 내용은 그대로 유지됩니다. 최신 버전을 확인한 뒤\n" +
                        "직접 반영할지 결정하세요. (체크아웃하면 현재 작업은 사라집니다)");

                    const seeBtn = document.createElement("button");
                    seeBtn.textContent = "히스토리 보기";
                    seeBtn.addEventListener("click", () => showHistory(serverState.chartId));
                    body.appendChild(seeBtn);
                    return;
                }

                if (err.status === 422) {
                    body.innerHTML = "";
                    const errs = err.body?.errors ?? [];
                    showMsg(body, "error",
                        `서버 계약 검증 실패 — 커밋되지 않았습니다.\n\n${errs.join("\n")}`);
                    return;
                }

                if (err.status === 403) {
                    showMsg(body, "error", "이 차트의 소유자만 커밋할 수 있습니다.");
                    return;
                }

                showMsg(body, "error", `커밋 실패: ${err.message}`);
            }
        };

        btn.addEventListener("click", doCommit);
        body.querySelector("#srv-commit-msg").focus();
    });

    /** 버전 히스토리 + 체크아웃 */
    async function showHistory(chartId) {
        const body = openModal("버전 히스토리");
        body.textContent = "불러오는 중…";

        let versions;
        try {
            const res = await api.listVersions(chartId);
            versions = res.versions;
        } catch (err) {
            body.textContent = "";
            showMsg(body, "error", `히스토리를 불러오지 못했습니다: ${err.message}`);
            return;
        }

        body.innerHTML = "";
        if (versions.length === 0) {
            showMsg(body, "warn", "아직 커밋이 없습니다. 첫 커밋을 만들어 보세요.");
            return;
        }

        const list = document.createElement("ul");
        list.className = "version-list";

        versions.forEach((v) => {
            const row = document.createElement("li");
            row.className = "version-row";
            if (String(v.id) === String(serverState.headVersionId)) {
                row.classList.add("is-head");
            }

            const meta = document.createElement("div");
            meta.className = "version-meta";

            const head = document.createElement("div");
            const num = document.createElement("span");
            num.className = "version-num";
            num.textContent = `v${v.version_number}`;
            head.appendChild(num);

            const msg = document.createElement("span");
            msg.className = "version-msg";
            msg.textContent = v.message ? `  ${v.message}` : "  (메시지 없음)";
            head.appendChild(msg);

            const sub = document.createElement("div");
            sub.className = "version-sub";
            const when = new Date(v.created_at).toLocaleString();
            const isHead = String(v.id) === String(serverState.headVersionId);
            sub.textContent = `${v.author_name} · ${when}${isHead ? " · 현재 기준" : ""}`;

            meta.appendChild(head);
            meta.appendChild(sub);
            row.appendChild(meta);

            const btn = document.createElement("button");
            btn.textContent = "체크아웃";
            btn.className = "secondary";
            btn.addEventListener("click", async () => {
                if (!confirm(
                    `v${v.version_number}을(를) 불러옵니다.\n\n` +
                    `현재 편집 중인 내용은 사라집니다. 계속할까요?\n` +
                    `(불러온 뒤 Ctrl+Z로 되돌릴 수 있습니다)`
                )) return;

                btn.disabled = true;
                try {
                    const res = await api.getVersion(chartId, v.version_number);
                    checkoutIntoEditor(res);
                    closeModal();
                } catch (err) {
                    btn.disabled = false;
                    showMsg(body, "error", `체크아웃 실패: ${err.message}`);
                }
            });
            row.appendChild(btn);

            list.appendChild(row);
        });

        body.appendChild(list);

        // 이 패턴(차트)의 HEAD를 파일로 내보낸다 — Save Json과 같은 바이트.
        const exportBtn = document.createElement("button");
        exportBtn.textContent = "패턴 Export (.json)";
        exportBtn.addEventListener("click", () =>
            runExport(exportBtn, body, () => api.exportChart(chartId), "패턴 Export"));
        body.appendChild(exportBtn);
    }

    // ── 파일 브라우저 ─────────────────────────────────────────────────
    //
    // 두 루트: svn/ 는 SVN 저장소 미러(경로가 곧 매핑), work/ 는 자유 공간.
    // 차트를 여기서 열고, 히스토리는 파일별 이력으로 통합했다.

    let browserPath = "svn";   // 현재 보고 있는 디렉터리

    /**
     * 서버 음원을 데모 재생용으로 로드한다.
     *
     * <audio>가 헤더를 못 보내므로 서명 URL을 받아 붙인다(라우트 인증 유지).
     * 로컬 파일 선택과 같은 상태(demoAudio.src, audioBuffer.duration)를 맞춰
     * 재생·시크바가 동일하게 동작하게 한다.
     */
    async function loadServerAudio(sha256) {
        const url = await api.audioUrl(sha256);
        demoAudio.src = url;
        demoAudio.load();
        hasAudioFile = true;
        await new Promise((resolve, reject) => {
            const ok = () => { cleanup(); resolve(); };
            const bad = () => { cleanup(); reject(new Error("음원 로드 실패")); };
            const cleanup = () => {
                demoAudio.removeEventListener("loadedmetadata", ok);
                demoAudio.removeEventListener("error", bad);
            };
            demoAudio.addEventListener("loadedmetadata", ok);
            demoAudio.addEventListener("error", bad);
        });
        audioBuffer = { duration: demoAudio.duration };
        demoAudio.volume = musicVolume;
    }

    /**
     * 디렉터리 선택 다이얼로그.
     *
     * 경로를 직접 치는 대신 폴더 트리를 탐색해 목적지 디렉터리를 고른다.
     * onPick(selectedDirPath) 콜백으로 결과를 넘긴다. rootFilter로 특정 루트만
     * 허용할 수 있다(예: work/ 전용 이동).
     */
    async function pickDirectory({ title = "폴더 선택", startPath = "work", rootFilter = null, onPick }) {
        let cur = startPath;

        async function render() {
            const body = openModal(title);
            body.textContent = "불러오는 중…";
            let data;
            try {
                data = await api.listFiles(cur);
            } catch (err) {
                body.textContent = "";
                showMsg(body, "error", err.message);
                return;
            }
            cur = data.path;
            body.innerHTML = "";

            // 현재 위치 + 루트 전환
            const bar = document.createElement("div");
            bar.style.cssText = "display:flex;gap:6px;align-items:center;margin-bottom:8px;flex-wrap:wrap;";
            const roots = rootFilter ? [rootFilter] : ["svn", "work"];
            roots.forEach((root) => {
                const b = document.createElement("button");
                b.textContent = root === "svn" ? "📦 svn" : "🛠 work";
                b.className = cur.split("/")[0] === root ? "" : "secondary";
                b.style.marginTop = "0";
                b.addEventListener("click", () => { cur = root; render(); });
                bar.appendChild(b);
            });
            const crumb = document.createElement("span");
            crumb.style.cssText = "color:#8ec7ff;font-size:12px;overflow-wrap:anywhere;";
            crumb.textContent = cur;
            bar.appendChild(crumb);
            body.appendChild(bar);

            // 상위로
            if (data.parent && (!rootFilter || data.parent.startsWith(rootFilter))) {
                const up = document.createElement("div");
                up.className = "chart-row";
                up.innerHTML = `<div class="version-meta"><div>📁 ..</div></div>`;
                up.addEventListener("click", () => { cur = data.parent; render(); });
                body.appendChild(up);
            }

            // 하위 폴더만 (파일은 목적지가 아니므로 흐리게 표시)
            data.entries.forEach((entry) => {
                const row = document.createElement("div");
                row.className = "chart-row";
                const meta = document.createElement("div");
                meta.className = "version-meta";
                const t = document.createElement("div");
                if (entry.kind === "folder") {
                    t.textContent = `📁 ${entry.name}`;
                    row.addEventListener("click", () => { cur = entry.path; render(); });
                } else {
                    t.textContent = `📄 ${entry.name}`;
                    t.style.color = "#777";
                    row.style.cursor = "default";
                }
                meta.appendChild(t);
                row.appendChild(meta);
                body.appendChild(row);
            });

            // 이 위치 선택
            const foot = document.createElement("div");
            foot.style.cssText = "margin-top:12px;border-top:1px solid #555;padding-top:10px;";
            const pickBtn = document.createElement("button");
            pickBtn.textContent = `📍 "${cur}" 선택`;
            pickBtn.addEventListener("click", () => onPick(cur));
            foot.appendChild(pickBtn);

            const newBtn = document.createElement("button");
            newBtn.textContent = "＋ 새 폴더";
            newBtn.className = "secondary";
            newBtn.style.marginLeft = "8px";
            newBtn.addEventListener("click", async () => {
                const n = prompt("새 폴더 이름:");
                if (!n) return;
                try { await api.createFolder(`${cur}/${n}`); render(); }
                catch (err) { showMsg(foot, "error", err.status === 409 ? "이미 있는 폴더입니다." : err.message); }
            });
            foot.appendChild(newBtn);
            body.appendChild(foot);
        }

        await render();
    }

    /** 차트를 서버에서 받아 에디터로 연다. */
    async function openChartFromBrowser(entry) {
        try {
            const res = await api.getChart(entry.chartId);
            checkoutIntoEditor(res);
            serverState.chartId = entry.chartId;
            serverState.path = entry.path;
            // svn/ 경로면 트랙·난이도가 경로에 드러난다 — 현재 트랙으로 자동 설정.
            serverState.trackCode = entry.trackCode ?? deriveTrackCode(entry.path);
            serverState.trackName = entry.trackName ?? null;
            serverState.difficulty = entry.difficulty ?? deriveDifficulty(entry.path);
            updateStatus();

            // 패턴에 연결된 음원(차트 자체 또는 트랙 음원)을 데모 재생용으로 자동 로드.
            if (entry.audioSha256) {
                loadServerAudio(entry.audioSha256).catch(() => { /* 음원 없어도 편집은 가능 */ });
            }
            closeModal();
        } catch (err) {
            alert(`열기 실패: ${err.message}`);
        }
    }

    /**
     * work/ 아래에 새 패턴을 만든다.
     *
     * 빈 차트를 해당 경로에 만들고, 에디터를 비운 뒤 그 차트에 연결한다.
     * 이후 편집하고 Commit을 누르면 첫 버전이 이 파일에 저장된다.
     */
    async function createNewPattern(dir) {
        const name = prompt("새 패턴 이름 (.json 자동 추가):");
        if (!name) return;
        const base = name.replace(/\.json$/i, "");
        const path = `${dir}/${base}.json`;

        let chart;
        try {
            const res = await api.createChart(base, path);
            chart = res.chart;
        } catch (err) {
            alert(err.status === 409 ? "그 경로에 이미 파일이 있습니다." : `생성 실패: ${err.message}`);
            return;
        }

        // 에디터를 빈 상태로 초기화한다.
        saveState();
        resetSelectionState();   // 지워진 노트를 가리키는 선택을 남기지 않는다(감사 B-2)
        notes.length = 0;
        clearAllEvents();
        ensureInitialDirectionNote(notes);
        invalidatePathCache();
        drawPath();
        renderNoteList();
        scheduleRender({ eventList: true });

        // 새 차트에 연결 — 아직 커밋 전이므로 head는 없다(최초 커밋).
        serverState.chartId = chart.id;
        serverState.path = path;
        serverState.headVersionId = null;
        serverState.headVersionNumber = null;
        serverState.musicName = base;
        serverState.trackCode = null;
        serverState.trackName = null;
        serverState.difficulty = null;
        updateStatus();
        closeModal();

        alert(`새 패턴 "${base}" 생성됨.\n편집한 뒤 ⬆ Commit으로 저장하세요.`);
    }

    /** svn/LT0004_.../LT0004-NM.json → "LT0004" */
    function deriveTrackCode(path) {
        const m = /^svn\/([A-Za-z]+[0-9]+)_/.exec(path || "");
        return m ? m[1] : null;
    }
    /** svn/.../<code>-NM.json → "NM" */
    function deriveDifficulty(path) {
        const m = /-([A-Z]{2})\.json$/.exec(path || "");
        return m ? m[1] : null;
    }

    function fileIcon(entry) {
        if (entry.kind === "folder") return "📁";
        if (entry.mapping) return "🎵";      // 매핑되는 패턴
        if (entry.name.endsWith(".json")) return "📄";
        return "•";                          // png/wav 등
    }

    /**
     * 저장소가 우리보다 앞선 차트 — path -> {repoRevision, state, deleted, author}.
     *
     * publish 할 때가 아니라 **목록을 볼 때** 알려 주는 것이 요점이다. 편집을
     * 시작하기 전에 알아야 "30분 작업 후 경합"을 피할 수 있다.
     *
     * 한 번 조회하면 캐시한다 — 브라우저를 열 때마다 저장소를 때리면 SVN 서버가
     * 같은 호스트라 부담이 된다. 화면을 새로 고치려면 staleCache = null.
     */
    let staleCache = null;

    /**
     * 저장소를 바꾼 뒤에는 반드시 호출한다 — publish·가져오기·import 모두.
     * 안 하면 방금 해소한 경합이 뱃지에 그대로 남아, 사용자가 이미 처리한 것을
     * 다시 처리하려 든다.
     */
    function invalidateStale() {
        staleCache = null;
    }

    async function loadStale() {
        if (staleCache) return staleCache;
        try {
            const res = await api.svnStale();
            const map = new Map((res.stale ?? []).map((s) => [s.path, s]));
            // 내용은 저장소와 같은데 리비전 번호만 뒤처져 있던 것들.
            // 서버가 기록을 고쳐 두므로 다음부터는 아예 올라오지 않지만,
            // 이번 응답에서는 "최신"으로 보여 줘야 한다.
            for (const u of res.upToDate ?? []) {
                map.set(u.path, { ...u, state: "current" });
            }
            staleCache = map;
        } catch {
            // SVN 미설정(503)·네트워크 실패는 정상 동작이다. 뱃지가 안 보일 뿐,
            // 브라우저 자체는 막지 않는다.
            staleCache = new Map();
        }
        return staleCache;
    }

    async function showFileBrowser(path = browserPath) {
        const body = openModal("파일 브라우저");
        body.textContent = "불러오는 중…";

        let data;
        let stale = new Map();
        try {
            // 목록과 나란히 — stale 조회가 느려도 목록 표시를 늦추지 않는다.
            [data, stale] = await Promise.all([api.listFiles(path), loadStale()]);
        } catch (err) {
            body.textContent = "";
            showMsg(body, "error", `목록을 불러오지 못했습니다: ${err.message}`);
            return;
        }
        browserPath = data.path;
        body.innerHTML = "";

        // 경로 이동 바 (svn / work 전환 + 상위)
        const bar = document.createElement("div");
        bar.style.cssText = "display:flex;gap:6px;align-items:center;margin-bottom:8px;flex-wrap:wrap;";

        const mkRootBtn = (root, label) => {
            const b = document.createElement("button");
            b.textContent = label;
            b.className = browserPath.split("/")[0] === root ? "" : "secondary";
            b.style.marginTop = "0";
            b.addEventListener("click", () => showFileBrowser(root));
            return b;
        };
        bar.appendChild(mkRootBtn("svn", "📦 svn"));
        bar.appendChild(mkRootBtn("work", "🛠 work"));

        const crumb = document.createElement("span");
        crumb.style.cssText = "color:#8ec7ff;font-size:12px;margin-left:8px;overflow-wrap:anywhere;";
        crumb.textContent = browserPath;
        bar.appendChild(crumb);
        body.appendChild(bar);

        if (data.parent) {
            const up = document.createElement("div");
            up.className = "chart-row";
            up.innerHTML = `<div class="version-meta"><div>📁 ..</div></div>`;
            up.addEventListener("click", () => showFileBrowser(data.parent));
            body.appendChild(up);
        }

        if (data.entries.length === 0) {
            showMsg(body, "warn", "비어 있습니다.");
        }

        data.entries.forEach((entry) => {
            const row = document.createElement("div");
            row.className = "chart-row";

            const meta = document.createElement("div");
            meta.className = "version-meta";

            const title = document.createElement("div");
            title.appendChild(document.createTextNode(`${fileIcon(entry)} ${entry.name}`));
            if (entry.hasWarnings) {
                const warn = document.createElement("span");
                warn.textContent = " ⚠";
                warn.title = "가져올 때 계약 검증 경고가 있었습니다 (게임은 정상 사용). 클릭해 상세 확인";
                warn.style.cssText = "color:#FFC107;cursor:help;";
                warn.addEventListener("click", (e) => {
                    e.stopPropagation();
                    const errs = (entry.warnings?.errors ?? []).join("\n");
                    alert(`검증 경고 (${entry.warnings?.at ?? ""}):\n\n${errs}\n\n` +
                          `이 차트는 게임에서 정상 재생됩니다. 편집·재커밋은 가능합니다.`);
                });
                title.appendChild(warn);
            }

            // 저장소가 앞서 있다 — 편집을 시작하기 전에 보이는 것이 요점.
            const st = stale.get(entry.path);
            if (st) {
                const badge = document.createElement("span");
                badge.className =
                    st.state === "dirty" ? "stale-badge dirty"
                    : st.state === "current" ? "stale-badge current"
                    : "stale-badge";
                badge.textContent = st.deleted ? " 저장소에서 삭제됨"
                    : st.state === "current" ? " 최신"
                    : st.state === "dirty" ? ` 경합 r${st.repoRevision}`
                    : ` 저장소 최신 r${st.repoRevision}`;
                badge.title = st.deleted
                    ? `저장소에서 이 파일이 삭제되었습니다 (r${st.repoRevision}${st.author ? `, ${st.author}` : ""}).`
                    : st.state === "current"
                        // 리비전 번호는 달라도 내용이 같다 — 이 파일은 최신이다.
                        ? `저장소와 내용이 같습니다. 리비전 번호만 r${st.publishedRevision} 로 ` +
                          `뒤처져 있었고(저장소 r${st.repoRevision}), 기록을 갱신했습니다. ` +
                          `가져올 것도 올릴 것도 없습니다.`
                    : st.state === "dirty"
                        ? `내가 마지막으로 publish한 r${st.publishedRevision} 이후 저장소가 r${st.repoRevision} 로 바뀌었고, ` +
                          `내 쪽에도 publish 하지 않은 변경이 있습니다${st.author ? ` (저장소 작성자: ${st.author})` : ""}. ` +
                          `publish 패널에서 diff 를 확인하세요.`
                        : `저장소가 r${st.repoRevision} 로 앞서 있습니다${st.author ? ` (${st.author})` : ""}. ` +
                          `내 쪽에는 publish 하지 않은 변경이 없어, 가져오면 잃을 것이 없습니다.`;
                title.appendChild(badge);
            }
            meta.appendChild(title);

            if (entry.kind === "file") {
                const sub = document.createElement("div");
                sub.className = "version-sub";
                const parts = [];
                if (entry.mapping) parts.push(`${entry.difficulty} (${DIFFICULTY_NAME[entry.difficulty] ?? ""})`);
                if (entry.headVersionNumber) parts.push(`v${entry.headVersionNumber}`);
                if (entry.publishedRevision) parts.push(`published r${entry.publishedRevision}`);
                if (entry.ownerName) parts.push(entry.ownerName);
                sub.textContent = parts.join(" · ");
                meta.appendChild(sub);
            }
            row.appendChild(meta);

            if (entry.kind === "folder") {
                // 폴더 액션: svn/ 은 이력·일괄 publish, work/ 은 이름변경·이동·삭제.
                const folderActions = document.createElement("div");
                folderActions.style.cssText = "display:flex;gap:4px;flex-shrink:0;";
                const fmore = document.createElement("button");
                fmore.textContent = "⋯";
                fmore.className = "secondary";
                fmore.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;";
                fmore.title = entry.path.startsWith("svn/")
                    ? "폴더 이력 · 일괄 publish"
                    : "이름변경 · 이동 · 삭제";
                fmore.addEventListener("click", (e) => {
                    e.stopPropagation();
                    showFolderActions(entry.path, () => {
                        invalidateStale();
                        showFileBrowser(browserPath);
                    });
                });
                folderActions.appendChild(fmore);

                const meta2 = row.querySelector(".version-meta");
                if (meta2) { meta2.style.cursor = "pointer"; meta2.addEventListener("click", () => showFileBrowser(entry.path)); }
                else row.addEventListener("click", () => showFileBrowser(entry.path));
                row.appendChild(folderActions);
            } else {
                // 파일 액션 버튼들
                const actions = document.createElement("div");
                actions.style.cssText = "display:flex;gap:4px;flex-shrink:0;";

                if (entry.hasCommits) {
                    const open = document.createElement("button");
                    open.textContent = "열기";
                    open.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;";
                    open.addEventListener("click", (e) => { e.stopPropagation(); openChartFromBrowser(entry); });
                    actions.appendChild(open);
                }

                const more = document.createElement("button");
                more.textContent = "⋯";
                more.className = "secondary";
                more.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;";
                more.addEventListener("click", (e) => {
                    e.stopPropagation();
                    showFileActions(entry, () => {
                        invalidateStale();
                        showFileBrowser(browserPath);
                    });
                });
                actions.appendChild(more);

                row.appendChild(actions);
                if (entry.hasCommits) {
                    row.style.cursor = "pointer";
                    row.addEventListener("click", () => openChartFromBrowser(entry));
                }
            }

            body.appendChild(row);
        });

        // 하단: 폴더 생성 + (work 루트에서) 현재 편집본 저장
        const foot = document.createElement("div");
        foot.style.cssText = "margin-top:12px;border-top:1px solid #555;padding-top:10px;";
        const newFolderBtn = document.createElement("button");
        newFolderBtn.textContent = "새 폴더";
        newFolderBtn.className = "secondary";
        newFolderBtn.addEventListener("click", async () => {
            const name = prompt("새 폴더 이름:");
            if (!name) return;
            try {
                await api.createFolder(`${browserPath}/${name}`);
                showFileBrowser(browserPath);
            } catch (err) {
                showMsg(foot, "error", err.status === 409 ? "이미 있는 폴더입니다." : err.message);
            }
        });
        foot.appendChild(newFolderBtn);

        // work/ 아래: 새 패턴 만들기 — 빈 차트를 이 위치에 만들고 바로 연다.
        if (browserPath === "work" || browserPath.startsWith("work/")) {
            const newPatternBtn = document.createElement("button");
            newPatternBtn.textContent = "＋ 새 패턴";
            newPatternBtn.style.marginLeft = "8px";
            newPatternBtn.addEventListener("click", () => createNewPattern(browserPath));
            foot.appendChild(newPatternBtn);
        }

        // svn 루트: 경합 아닌 것만 일괄로 가져오기.
        //
        // publish 와 달리 관리자 전용이 아니다 — 개별 '가져오기'와 같은 권한이고,
        // 새 버전 행으로 기록되므로 되돌릴 수 있다.
        if (browserPath === "svn") {
            const pullBtn = document.createElement("button");
            pullBtn.textContent = "⬇ 최신 일괄 가져오기";
            pullBtn.style.marginLeft = "8px";
            pullBtn.addEventListener("click", () => showUpdateAll(() => {
                invalidateStale();
                showFileBrowser("svn");
            }));
            foot.appendChild(pullBtn);
        }

        // svn 루트 + 관리자: 전체 publish 버튼 (경고창으로 보호)
        if (browserPath === "svn" && currentUser && currentUser.role === "admin") {
            const allBtn = document.createElement("button");
            allBtn.textContent = "⬆ 전체 SVN publish";
            allBtn.style.marginLeft = "8px";
            allBtn.addEventListener("click", () => showPublishAll(() => {
                invalidateStale();
                showFileBrowser("svn");
            }));
            foot.appendChild(allBtn);
        }

        body.appendChild(foot);
    }

    /**
     * 경합이 아닌 것만 일괄로 가져오기.
     *
     * 무엇을 가져오고 무엇을 왜 건너뛰는지 **먼저 보여 주고** 실행한다.
     * 건너뛴 것(경합·저장소에서 삭제됨)은 개별 publish 패널에서 diff 를 보고
     * 처리해야 하므로, 목록에서 바로 넘어갈 수 있게 한다.
     */
    async function showUpdateAll(onDone) {
        const body = openModal("최신 일괄 가져오기");
        body.innerHTML = `<div class="version-sub">저장소와 비교하는 중…</div>`;

        let dry;
        try {
            dry = await api.svnUpdateAll(true);
        } catch (err) {
            body.innerHTML = "";
            showMsg(body, "error",
                err.status === 503 ? "SVN 연동이 설정되지 않았습니다."
                                   : `비교 실패: ${err.message}`);
            return;
        }

        body.innerHTML = "";
        const head = document.createElement("div");
        head.className = "version-sub";
        head.style.marginBottom = "8px";
        head.textContent = `저장소 r${dry.headRevision ?? "?"}`;
        body.appendChild(head);

        if (dry.count === 0 && dry.skipped.length === 0) {
            showMsg(body, "ok", "모두 최신입니다 — 가져올 것이 없습니다.");
            return;
        }

        if (dry.count > 0) {
            showMsg(body, "ok",
                `${dry.count}개를 가져옵니다. 이 패턴들은 내 쪽에 publish 하지 않은 ` +
                `변경이 없어, 가져와도 잃을 것이 없습니다.`);
            const list = document.createElement("div");
            list.className = "svn-conflict-list";
            for (const path of dry.updated) {
                const rowEl = document.createElement("div");
                rowEl.className = "svn-conflict-row";
                const nameEl = document.createElement("span");
                nameEl.className = "svn-conflict-name";
                nameEl.textContent = path.replace(/^svn\//, "");
                rowEl.appendChild(nameEl);
                list.appendChild(rowEl);
            }
            body.appendChild(list);
        }

        // 건너뛰는 것 — 왜 건너뛰는지와, 어디서 처리하면 되는지.
        if (dry.skipped.length > 0) {
            showMsg(body, "warn",
                `${dry.skipped.length}개는 건너뜁니다 — 양쪽이 갈렸거나 저장소에서 ` +
                `삭제되었습니다. 이건 사람이 확인해야 합니다.`);
            const list = document.createElement("div");
            list.className = "svn-conflict-list";
            for (const s of dry.skipped) {
                const rowEl = document.createElement("div");
                rowEl.className = "svn-conflict-row";

                const nameEl = document.createElement("span");
                nameEl.className = "svn-conflict-name";
                nameEl.textContent = s.path.replace(/^svn\//, "");

                const whyEl = document.createElement("span");
                whyEl.className = "version-sub";
                whyEl.textContent = s.reason === "deleted"
                    ? `저장소에서 삭제됨 (r${s.repoRevision})`
                    : `경합 — 양쪽 변경 (저장소 r${s.repoRevision})`;

                rowEl.append(nameEl, whyEl);

                // 삭제된 것은 publish 패널에서 할 일이 없다(가져올 대상이 없음).
                if (s.reason !== "deleted") {
                    const seeBtn = document.createElement("button");
                    seeBtn.className = "secondary";
                    seeBtn.textContent = "변경 보기";
                    seeBtn.addEventListener("click", () =>
                        showPublish({ path: s.path, name: s.path.split("/").pop() }, onDone));
                    rowEl.appendChild(seeBtn);
                }
                list.appendChild(rowEl);
            }
            body.appendChild(list);
        }

        if (dry.count === 0) return;   // 건너뛸 것만 있으면 실행할 것이 없다.

        const actions = document.createElement("div");
        actions.className = "svn-diff-actions";
        const runBtn = document.createElement("button");
        runBtn.textContent = `${dry.count}개 가져오기`;
        actions.appendChild(runBtn);
        body.appendChild(actions);

        runBtn.addEventListener("click", async () => {
            runBtn.disabled = true;
            showMsg(body, "warn", "가져오는 중…");
            try {
                const res = await api.svnUpdateAll();
                body.innerHTML = "";
                const failed = res.failed?.length ?? 0;
                showMsg(body, failed > 0 ? "warn" : "ok",
                    `${res.updated.length}개를 가져왔습니다` +
                    (res.skipped.length > 0 ? ` · ${res.skipped.length}개 건너뜀` : "") +
                    (failed > 0 ? ` · ${failed}개 실패` : ""));
                // 실패는 조용히 넘기지 않는다 — 어느 것이 왜 실패했는지 보여 준다.
                for (const f of res.failed ?? []) {
                    const line = document.createElement("div");
                    line.className = "version-sub";
                    line.textContent = `${f.path.replace(/^svn\//, "")} — ${f.error}`;
                    body.appendChild(line);
                }
                if (onDone) setTimeout(onDone, failed > 0 ? 2500 : 1000);
            } catch (err) {
                runBtn.disabled = false;
                showMsg(body, "error", `가져오기 실패: ${err.message}`);
            }
        });
    }

    /** 전체 SVN publish — 반드시 경고 확인을 거친다. */
    async function showPublishAll(onDone) {
        // 먼저 몇 개가 대상이고 몇 개가 경합인지 dry-run으로 알아본다.
        let count = null;
        let conflictCount = 0;
        try {
            const dry = await api.svnPublishAll("", true);
            count = dry.count;
            conflictCount = dry.conflicts?.length ?? 0;
        } catch { /* dry-run 실패해도 확인창은 띄운다 */ }

        const warn = count != null
            ? `정말 전체를 SVN에 퍼블리싱 하겠습니까?\n\n대상 패턴 ${count}개가 한 번에 저장소에 커밋됩니다.\n이 작업은 되돌리기 어렵습니다.`
            : `정말 전체를 SVN에 퍼블리싱 하겠습니까?\n\n모든 커밋된 패턴이 한 번에 저장소에 커밋됩니다.\n이 작업은 되돌리기 어렵습니다.`;
        if (!confirm(warn)) return;

        // 경합이 있으면 실행 전에 알린다 — 서버가 막아 주지만, 여기서 미리
        // 보여 줘야 "왜 막혔는지" 모른 채 덮어쓰기를 고르는 일이 없다.
        if (conflictCount > 0 && !confirm(
            `경고: ${conflictCount}개 패턴이 내가 마지막으로 publish한 뒤 저장소에서 변경되었습니다.\n\n` +
            "계속하면 다음 화면에서 어떤 파일인지 확인할 수 있습니다.")) return;

        const body = openModal("전체 SVN publish");
        body.innerHTML = `<label>커밋 메시지 (선택)</label>
            <input type="text" id="pa-msg" placeholder="예: 전체 패턴 배포">`;
        const btn = document.createElement("button");
        btn.textContent = "전체 publish 실행";
        body.appendChild(btn);
        const run = async (force) => {
            btn.disabled = true;
            showMsg(body, "warn", "publish 중… 시간이 걸릴 수 있습니다.");
            try {
                const res = await api.svnPublishAll(
                    body.querySelector("#pa-msg")?.value.trim() ?? "", false, force);
                body.innerHTML = "";
                showMsg(body, "ok",
                    `전체 publish 완료 — r${res.revision ?? "?"}\n` +
                    `패턴 ${res.publishedCount}개 (신규 ${res.created}·수정 ${res.updated})`);
                if (onDone) setTimeout(onDone, 1200);
            } catch (err) {
                btn.disabled = false;
                if (err.status === 409 && err.body?.conflicts) {
                    // 폴더 publish와 같은 패널 — 파일별로 무엇이 바뀌었는지 보고
                    // 덮어쓸지 고른다.
                    showFolderConflicts(null, err.body.conflicts, () => run(true), onDone);
                    return;
                }
                showMsg(body, "error",
                    err.status === 403 ? "관리자만 실행할 수 있습니다." :
                    err.status === 422 ? "publish할 패턴이 없습니다. 먼저 import 하세요." :
                    `전체 publish 실패: ${err.message}`);
            }
        };
        btn.addEventListener("click", () => run(false));
    }

    /** 폴더 액션: svn/ 은 이력·일괄 publish, work/ 은 이름변경·이동·삭제 */
    function showFolderActions(folderPath, onDone) {
        const name = folderPath.split("/").pop();
        const body = openModal(`폴더 — ${name}`);
        const isSvn = folderPath.startsWith("svn/");

        const info = document.createElement("div");
        info.className = "version-sub";
        info.style.marginBottom = "8px";
        info.textContent = folderPath;
        body.appendChild(info);

        const addBtn = (label, cls, handler) => {
            const b = document.createElement("button");
            b.textContent = label;
            if (cls) b.className = cls;
            b.style.cssText = "display:block;width:100%;text-align:left;margin-top:6px;";
            b.addEventListener("click", handler);
            body.appendChild(b);
            return b;
        };

        // work/ 폴더: 이름변경·이동·삭제 (svn/ 은 저장소 구조라 여기서 안 건드린다)
        if (!isSvn) {
            const parentDir = folderPath.slice(0, folderPath.lastIndexOf("/")) || "work";
            addBtn("이름 변경", "secondary", async () => {
                const newName = prompt("새 폴더 이름:", name);
                if (!newName || newName === name) return;
                try {
                    const res = await api.moveFolder(folderPath, `${parentDir}/${newName}`);
                    showMsg(body, "ok", `변경 완료 (차트 ${res.movedCharts}개)`);
                    closeModal(); onDone();
                } catch (err) {
                    showMsg(body, "error", err.status === 409 ? "대상 경로에 이미 뭔가 있습니다." : err.message);
                }
            });
            addBtn("다른 폴더로 이동", "secondary", () => {
                pickDirectory({
                    title: `"${name}" 이동`, startPath: parentDir, rootFilter: "work",
                    onPick: async (dir) => {
                        const to = `${dir}/${name}`;
                        if (to === folderPath || to.startsWith(`${folderPath}/`)) {
                            alert("자기 자신이나 하위로는 옮길 수 없습니다."); return;
                        }
                        try {
                            const res = await api.moveFolder(folderPath, to);
                            showMsg(body, "ok", `이동 완료 (차트 ${res.movedCharts}개)`);
                            closeModal(); onDone();
                        } catch (err) {
                            alert(err.status === 409 ? "대상 경로에 이미 뭔가 있습니다." : err.message);
                        }
                    },
                });
            });
            addBtn("삭제", "secondary", async () => {
                if (!confirm(`폴더 "${name}" 과(와) 그 안의 모든 것을 삭제합니다.\n버전 이력도 함께 사라집니다. 계속할까요?`)) return;
                try {
                    const res = await api.deleteFile(folderPath, true);
                    showMsg(body, "ok", `삭제 완료 (차트 ${res.deletedCharts ?? 0}개)`);
                    closeModal(); onDone();
                } catch (err) {
                    showMsg(body, "error",
                        err.status === 403 ? "내가 소유하지 않은 차트가 포함돼 삭제할 수 없습니다." : err.message);
                }
            });
            return;
        }

        addBtn("변경 이력 (저장소)", "secondary", async () => {
            const h = openModal(`폴더 이력 — ${name}`);
            h.textContent = "불러오는 중…";
            try {
                const repoPath = folderPath.slice("svn/".length);
                const { entries } = await api.svnLog(repoPath, 50);
                h.innerHTML = "";
                if (entries.length === 0) { showMsg(h, "warn", "저장소 이력이 없습니다."); return; }
                const list = document.createElement("ul");
                list.className = "version-list";
                entries.forEach((e) => {
                    const li = document.createElement("li");
                    li.className = "version-row";
                    const m = document.createElement("div");
                    m.className = "version-meta";
                    const hd = document.createElement("div");
                    hd.innerHTML = `<span class="version-num">r${e.revision}</span>`;
                    hd.appendChild(document.createTextNode("  " + (e.message || "(메시지 없음)")));
                    const s = document.createElement("div");
                    s.className = "version-sub";
                    s.textContent = `${e.author ?? "?"} · ${e.date ? new Date(e.date).toLocaleString() : ""}`;
                    m.appendChild(hd); m.appendChild(s); li.appendChild(m); list.appendChild(li);
                });
                h.appendChild(list);
            } catch (err) {
                h.innerHTML = "";
                showMsg(h, "error", err.message);
            }
        });

        // 일괄 publish — 폴더 아래 커밋된 패턴 전부를 한 리비전으로. 관리자만.
        if (currentUser && currentUser.role === "admin") {
        addBtn("⬆ 폴더 일괄 publish", null, () => {
            const p = openModal(`일괄 publish — ${name}`);
            p.innerHTML = `
                <div class="version-sub" style="margin-bottom:8px;">
                    ${esc(folderPath)} 아래의 커밋된 패턴을 한 번에 SVN에 올립니다.
                </div>
                <label>커밋 메시지 (선택)</label>
                <input type="text" id="fpub-msg" placeholder="예: 전 난이도 밸런스 조정">
            `;
            const btn = document.createElement("button");
            btn.textContent = "일괄 publish";
            p.appendChild(btn);
            const run = async (force) => {
                btn.disabled = true;
                try {
                    const res = await api.svnPublishFolder(
                        folderPath, p.querySelector("#fpub-msg").value.trim(), force);
                    p.innerHTML = "";
                    showMsg(p, "ok",
                        `publish 완료 — r${res.revision ?? "?"}\n` +
                        `올린 패턴 ${res.published.length}개` +
                        (res.skipped.length ? `\n제외 ${res.skipped.length}개(패턴 아님)` : ""));
                    if (onDone) setTimeout(onDone, 1000);
                } catch (err) {
                    btn.disabled = false;
                    if (err.status === 409 && err.body?.conflicts) {
                        showFolderConflicts(folderPath, err.body.conflicts, () => run(true), onDone);
                        return;
                    }
                    showMsg(p, "error",
                        err.status === 422 ? "publish할 패턴이 없습니다." : `publish 실패: ${err.message}`);
                }
            };
            btn.addEventListener("click", () => run(false));
        });
        }
    }

    /**
     * work 파일을 정규(svn) 패턴으로 승격한다.
     *
     * 경로를 직접 치는 대신 **트랙 → 난이도**를 골라 표준 경로로 배치한다.
     * 그 자리에 이미 정규 패턴이 있으면, 기존 것을 work/_backup 으로 백업할지
     * 확인한 뒤 교체한다.
     */
    async function promoteToTrack(entry, onDone) {
        const body = openModal("정규 패턴으로 옮기기");
        body.textContent = "트랙 목록 불러오는 중…";

        let tracks;
        try {
            tracks = (await api.listTracks()).tracks;
        } catch (err) {
            body.textContent = "";
            showMsg(body, "error", err.message);
            return;
        }
        body.innerHTML = "";

        const info = document.createElement("div");
        info.className = "version-sub";
        info.style.marginBottom = "8px";
        info.textContent = `${entry.path} → 트랙·난이도를 선택하세요.`;
        body.appendChild(info);

        // 트랙 선택
        const tLabel = document.createElement("label");
        tLabel.textContent = "트랙";
        body.appendChild(tLabel);
        const tSel = document.createElement("select");
        tSel.style.cssText = "width:100%;background:#222;color:#fff;border:1px solid #555;padding:6px;";
        tracks.forEach((t) => {
            const o = document.createElement("option");
            o.value = t.id;
            o.textContent = `${t.code} ${t.name}`;
            tSel.appendChild(o);
        });
        body.appendChild(tSel);

        // 난이도 선택
        const dLabel = document.createElement("label");
        dLabel.textContent = "난이도";
        body.appendChild(dLabel);
        const dSel = document.createElement("select");
        dSel.style.cssText = "width:100%;background:#222;color:#fff;border:1px solid #555;padding:6px;";
        ["BG", "EZ", "NM", "HD", "EX", "OV"].forEach((code) => {
            const o = document.createElement("option");
            o.value = code;
            o.textContent = `${code} (${DIFFICULTY_NAME[code]})`;
            if (code === "NM") o.selected = true;
            dSel.appendChild(o);
        });
        body.appendChild(dSel);

        const go = document.createElement("button");
        go.textContent = "이 위치로 옮기기";
        body.appendChild(go);

        go.addEventListener("click", async () => {
            const trackId = Number(tSel.value);
            const difficulty = dSel.value;
            go.disabled = true;

            const attempt = async (backup) => {
                return api.placePattern(trackId, difficulty, entry.chartId, backup);
            };

            try {
                const res = await attempt(false);
                finishPromote(res);
            } catch (err) {
                if (err.status === 409 && err.body?.needsBackup) {
                    // 기존 정규 패턴이 있다 — 백업 여부 확인.
                    const doBackup = confirm(
                        `그 난이도에 이미 정규 패턴이 있습니다.\n\n` +
                        `기존 패턴을 work/_backup 으로 백업하고 교체할까요?\n` +
                        `(취소하면 아무것도 바꾸지 않습니다)`
                    );
                    if (!doBackup) { go.disabled = false; return; }
                    try {
                        const res = await attempt(true);
                        finishPromote(res);
                    } catch (e2) {
                        go.disabled = false;
                        showMsg(body, "error", e2.message);
                    }
                } else {
                    go.disabled = false;
                    showMsg(body, "error",
                        err.status === 422 ? "커밋이 없는 패턴은 옮길 수 없습니다." : err.message);
                }
            }
        });

        function finishPromote(res) {
            body.innerHTML = "";
            let msg = `정규 패턴으로 옮겼습니다 →\n${res.path}`;
            if (res.backedUpTo) msg += `\n\n기존 패턴은 백업됨:\n${res.backedUpTo}`;
            showMsg(body, "ok", msg);
            // 현재 열려 있는 파일을 옮긴 경우 상단바 상태도 갱신.
            if (serverState.chartId === entry.chartId) {
                serverState.path = res.path;
                serverState.trackCode = deriveTrackCode(res.path);
                serverState.difficulty = res.difficulty;
                updateStatus();
            }
            if (onDone) setTimeout(onDone, 1200);
        }
    }

    function showFileActions(entry, onDone) {
        const body = openModal(entry.name);

        const info = document.createElement("div");
        info.className = "version-sub";
        info.style.marginBottom = "8px";
        info.textContent = entry.path;
        body.appendChild(info);

        const addBtn = (label, cls, handler) => {
            const b = document.createElement("button");
            b.textContent = label;
            if (cls) b.className = cls;
            b.style.cssText = "display:block;width:100%;text-align:left;margin-top:6px;";
            b.addEventListener("click", handler);
            body.appendChild(b);
        };

        if (entry.hasCommits) {
            addBtn("열기", null, () => openChartFromBrowser(entry));
            addBtn("변경 이력", "secondary", () => showFileHistory(entry));
            addBtn("패턴 Export (.json)", "secondary", () =>
                runExport(body.querySelector("button"), body, () => api.exportChart(entry.chartId), "Export"));
        }

        const curDir = entry.path.slice(0, entry.path.lastIndexOf("/"));
        const fileName = entry.path.split("/").pop();

        addBtn("이름 변경", "secondary", async () => {
            const newName = prompt("새 이름:", fileName);
            if (!newName || newName === fileName) return;
            const to = `${curDir}/${newName}`;
            try {
                await api.moveFile(entry.path, to);
                closeModal(); onDone();
            } catch (err) {
                showMsg(body, "error", err.status === 409 ? "대상 경로에 이미 뭔가 있습니다." : err.message);
            }
        });

        addBtn("다른 폴더로 이동", "secondary", () => {
            pickDirectory({
                title: `"${fileName}" 이동`, startPath: curDir,
                onPick: async (dir) => {
                    const to = `${dir}/${fileName}`;
                    if (to === entry.path) { closeModal(); return; }
                    try {
                        await api.moveFile(entry.path, to);
                        closeModal(); onDone();
                    } catch (err) {
                        alert(err.status === 409 ? "대상 경로에 이미 뭔가 있습니다." : err.message);
                    }
                },
            });
        });

        // 복제 — 여러 사람이 하나에서 갈라져 시도하기 위한 fork.
        addBtn("복제 (새 이력으로 fork)", "secondary", () => {
            pickDirectory({
                title: `"${fileName}" 복제 위치`, startPath: curDir,
                onPick: async (dir) => {
                    const base = fileName.replace(/\.json$/i, "");
                    let to = `${dir}/${base}_copy.json`;
                    const custom = prompt("복제본 파일 이름:", `${base}_copy.json`);
                    if (!custom) return;
                    to = `${dir}/${custom}`;
                    try {
                        await api.copyFile(entry.path, to);
                        closeModal(); onDone();
                    } catch (err) {
                        alert(err.status === 409 ? "대상 경로에 이미 뭔가 있습니다." : err.message);
                    }
                },
            });
        });

        // work/ 커밋된 파일: 정규(svn) 패턴으로 승격 — 트랙·난이도를 골라 배치.
        if (entry.path.startsWith("work/") && entry.hasCommits) {
            addBtn("🎵 정규 패턴으로 옮기기", null, () => promoteToTrack(entry, onDone));
        }

        // publish — svn/ 아래 매핑되는 경로만, 관리자만.
        if (entry.mapping && entry.hasCommits && currentUser && currentUser.role === "admin") {
            addBtn("⬆ SVN publish", null, () => showPublish(entry, onDone));
        }

        // 저장소에서 업데이트 — 읽기 방향이라 관리자가 아니어도 쓸 수 있다.
        // (publish와 달리 공유 저장소를 바꾸지 않는다.)
        if (entry.mapping) {
            addBtn("⬇ 저장소에서 업데이트", "secondary", () => showSvnUpdate(entry, onDone));
        }

        addBtn("삭제", "secondary", async () => {
            if (!confirm(`${entry.name} 을(를) 삭제합니다. 버전 이력도 함께 사라집니다.\n계속할까요?`)) return;
            try {
                await api.deleteFile(entry.path);
                closeModal(); onDone();
            } catch (err) {
                showMsg(body, "error",
                    err.status === 403 ? "소유자만 삭제할 수 있습니다." :
                    err.status === 409 ? "비어 있지 않은 폴더는 삭제할 수 없습니다." : err.message);
            }
        });
    }

    /** `x/foo.json` → `x/foo_copy.json` */
    function suggestCopyPath(path) {
        return path.replace(/(\.json)?$/, (ext) => `_copy${ext || ""}`);
    }

    /** SVN 변경 이력 — 파일별. 서버 커밋 이력이 아니라 저장소 이력이다. */
    async function showFileHistory(entry) {
        const body = openModal(`이력 — ${entry.name}`);
        body.textContent = "불러오는 중…";

        // svn/ 아래면 저장소 로그, work/ 면 서버 버전 이력.
        if (entry.path.startsWith("svn/") && entry.mapping) {
            try {
                const repoPath = entry.path.slice("svn/".length);
                const { entries } = await api.svnLog(repoPath, 50);
                body.innerHTML = "";
                if (entries.length === 0) { showMsg(body, "warn", "저장소 이력이 없습니다."); return; }
                const list = document.createElement("ul");
                list.className = "version-list";
                entries.forEach((e) => {
                    const li = document.createElement("li");
                    li.className = "version-row";
                    const m = document.createElement("div");
                    m.className = "version-meta";
                    const h = document.createElement("div");
                    h.innerHTML = `<span class="version-num">r${e.revision}</span>`;
                    h.appendChild(document.createTextNode("  " + (e.message || "(메시지 없음)")));
                    const s = document.createElement("div");
                    s.className = "version-sub";
                    s.textContent = `${e.author ?? "?"} · ${e.date ? new Date(e.date).toLocaleString() : ""}`;
                    m.appendChild(h); m.appendChild(s); li.appendChild(m); list.appendChild(li);
                });
                body.appendChild(list);
            } catch (err) {
                body.innerHTML = "";
                showMsg(body, "error", `저장소 이력을 불러오지 못했습니다: ${err.message}`);
            }
            return;
        }

        // work/ 등: 서버 버전 이력
        try {
            const { versions } = await api.listVersions(entry.chartId);
            body.innerHTML = "";
            if (versions.length === 0) { showMsg(body, "warn", "커밋이 없습니다."); return; }
            const list = document.createElement("ul");
            list.className = "version-list";
            versions.forEach((v) => {
                const li = document.createElement("li");
                li.className = "version-row";
                const m = document.createElement("div");
                m.className = "version-meta";
                const h = document.createElement("div");
                h.innerHTML = `<span class="version-num">v${v.version_number}</span>`;
                h.appendChild(document.createTextNode("  " + (v.message || "(메시지 없음)")));
                const s = document.createElement("div");
                s.className = "version-sub";
                s.textContent = `${v.author_name} · ${new Date(v.created_at).toLocaleString()}`;
                m.appendChild(h); m.appendChild(s); li.appendChild(m);

                const btn = document.createElement("button");
                btn.textContent = "체크아웃";
                btn.className = "secondary";
                btn.style.marginTop = "0";
                btn.addEventListener("click", async () => {
                    if (!confirm(`v${v.version_number}을 불러옵니다. 현재 편집 내용은 사라집니다.`)) return;
                    try {
                        const res = await api.getVersion(entry.chartId, v.version_number);
                        checkoutIntoEditor(res);
                        serverState.chartId = entry.chartId;
                        serverState.path = entry.path;
                        closeModal();
                    } catch (err) { showMsg(body, "error", err.message); }
                });
                li.appendChild(btn);
                list.appendChild(li);
            });
            body.appendChild(list);
        } catch (err) {
            body.innerHTML = "";
            showMsg(body, "error", err.message);
        }
    }

    /**
     * 통합 diff를 DOM으로 그린다. 서버가 보내는 hunk는 이미 앞뒤 3줄 문맥으로
     * 좁혀져 있으므로 여기서는 렌더링만 한다.
     */
    function renderDiff(parent, diff) {
        if (diff.truncated) {
            showMsg(parent, "warn", "파일이 너무 커서 상세 diff를 생략했습니다. 덮어쓰기 전에 저장소 이력을 확인하세요.");
            return;
        }
        if (!diff.hunks.length) {
            showMsg(parent, "ok", "내용이 동일합니다 — 올릴 변경이 없습니다.");
            return;
        }

        const sum = document.createElement("div");
        sum.className = "version-sub";
        sum.style.margin = "8px 0 4px";
        sum.textContent = `+${diff.added} 줄 추가 · -${diff.removed} 줄 삭제 (저장소 → 내 버전)`;
        parent.appendChild(sum);

        const box = document.createElement("div");
        box.className = "svn-diff";
        diff.hunks.forEach((hunk, i) => {
            if (i > 0) {
                const sep = document.createElement("div");
                sep.className = "svn-diff-sep";
                sep.textContent = "⋯";
                box.appendChild(sep);
            }
            for (const row of hunk.rows) {
                const line = document.createElement("div");
                line.className = `svn-diff-line ${row.t === "+" ? "add" : row.t === "-" ? "del" : "ctx"}`;
                const ln = document.createElement("span");
                ln.className = "svn-diff-ln";
                ln.textContent = row.t === "+" ? `+${row.b}` : row.t === "-" ? `-${row.a}` : ` ${row.a}`;
                const tx = document.createElement("span");
                tx.className = "svn-diff-tx";
                tx.textContent = row.text;
                line.append(ln, tx);
                box.appendChild(line);
            }
        });
        parent.appendChild(box);
    }

    /**
     * 저장소 → 에디터 업데이트 패널 (publish의 역방향).
     *
     * 로컬을 덮어쓰기 전에 같은 diff를 보여준다. 로컬 변경이 있는데도 받으면
     * 그 변경이 HEAD에서 밀려나므로, 그 경우에만 경고를 띄운다.
     */
    function showSvnUpdate(entry, onDone) {
        const body = openModal(`저장소에서 업데이트 — ${entry.name}`);
        body.innerHTML = `<div class="version-sub">저장소와 비교하는 중…</div>`;

        api.svnDiff(entry.path).then((cmp) => {
            body.innerHTML = "";

            if (cmp.status === "local-only") {
                showMsg(body, "warn", "저장소에 아직 없는 파일입니다 — 가져올 내용이 없습니다.");
                return;
            }

            const head = document.createElement("div");
            head.className = "version-sub";
            head.style.marginBottom = "8px";
            head.textContent = `${entry.path} — 저장소 r${cmp.repoRevision ?? "?"}`;
            body.appendChild(head);

            if (cmp.status === "identical") {
                showMsg(body, "ok", "저장소와 내용이 동일합니다 — 가져올 변경이 없습니다.");
                return;
            }

            // diff는 '저장소 → 내 버전' 방향이라, 받는 쪽에서는 +/- 의미가
            // 뒤집힌다. 혼동을 막기 위해 방향을 명시한다.
            showMsg(body, "warn",
                cmp.status === "conflict"
                    ? "저장소가 변경되었고, 내 쪽에도 아직 publish하지 않은 변경이 있습니다. 가져오면 내 변경이 HEAD에서 밀려납니다."
                    : "가져오면 아직 publish하지 않은 내 변경이 HEAD에서 밀려납니다.");

            const note = document.createElement("div");
            note.className = "version-sub";
            note.style.margin = "8px 0 0";
            note.textContent = "아래 diff는 '저장소 → 내 버전' 방향입니다. 가져오면 이 변경이 되돌아갑니다.";
            body.appendChild(note);
            renderDiff(body, cmp.diff);

            const actions = document.createElement("div");
            actions.className = "svn-diff-actions";
            const btn = document.createElement("button");
            btn.className = "danger";
            btn.textContent = "저장소 버전 가져오기";
            actions.appendChild(btn);
            body.appendChild(actions);

            btn.addEventListener("click", async () => {
                if (!confirm(
                    "저장소 버전을 에디터로 가져옵니다. 내 최신 변경을 대체합니다.\n\n" +
                    "(새 버전으로 기록되므로 이력에서 되돌릴 수 있습니다.)")) return;
                btn.disabled = true;
                try {
                    const res = await api.svnUpdate(entry.path);
                    body.innerHTML = "";
                    showMsg(body, "ok", `저장소 r${res.revision ?? "?"} 내용을 가져왔습니다.`);
                    if (onDone) setTimeout(onDone, 900);
                } catch (err) {
                    btn.disabled = false;
                    showMsg(body, "error", `가져오기 실패: ${err.message}`);
                }
            });
        }).catch((err) => {
            body.innerHTML = "";
            showMsg(body, "error", `비교 실패: ${err.message}`);
        });
    }

    /**
     * 폴더 일괄 publish가 경합으로 막혔을 때 — 어떤 파일이 왜 막혔는지 보여준다.
     *
     * 일괄 커밋은 원자적이므로 파일별 선택이 불가능하다. 그래서 여기서는
     * "전부 덮어쓰기"만 제공하고, 파일 하나씩 살펴보려면 개별 publish 패널로
     * 넘긴다(거기서 diff와 '가져오기'를 쓸 수 있다).
     */
    function showFolderConflicts(folderPath, conflicts, onForce, onDone) {
        // folderPath 가 null 이면 전체 publish 경합이다(특정 폴더가 아님).
        const body = openModal(folderPath
            ? `경합 — ${folderPath.split("/").pop()}`
            : "경합 — 전체 publish");
        showMsg(body, "warn",
            `내가 마지막으로 publish한 뒤 저장소에서 ${conflicts.length}개 파일이 변경되었습니다.`);

        const list = document.createElement("div");
        list.className = "svn-conflict-list";
        for (const c of conflicts) {
            const rowEl = document.createElement("div");
            rowEl.className = "svn-conflict-row";

            const nameEl = document.createElement("span");
            nameEl.className = "svn-conflict-name";
            nameEl.textContent = c.path.split("/").pop();

            const revEl = document.createElement("span");
            revEl.className = "version-sub";
            revEl.textContent = c.actual === null
                ? "저장소에서 삭제됨"
                : `내 기준 r${c.expected ?? "없음"} → 저장소 r${c.actual}`;

            const seeBtn = document.createElement("button");
            seeBtn.className = "secondary";
            seeBtn.textContent = "변경 보기";
            seeBtn.addEventListener("click", () =>
                showPublish({ path: c.path, name: c.path.split("/").pop() }, onDone));

            rowEl.append(nameEl, revEl, seeBtn);
            list.appendChild(rowEl);
        }
        body.appendChild(list);

        const actions = document.createElement("div");
        actions.className = "svn-diff-actions";
        const forceBtn = document.createElement("button");
        forceBtn.className = "danger";
        forceBtn.textContent = "전부 덮어쓰기";
        forceBtn.addEventListener("click", () => {
            if (!confirm(
                `저장소의 최신 변경 ${conflicts.length}건을 덮어씁니다. 계속할까요?\n\n` +
                "(남의 커밋이 사라질 수 있습니다.)")) return;
            forceBtn.disabled = true;
            onForce();
        });
        actions.appendChild(forceBtn);
        body.appendChild(actions);
    }

    /**
     * publish 패널 — 올리기 전에 저장소와 비교한다.
     *
     * 경합(conflict)이면 diff를 보여주고 "덮어쓰기 / 저장소 버전 가져오기 /
     * 취소" 중에서 고르게 한다. 확인 없이 남의 커밋을 덮지 않는 것이 요점.
     */
    function showPublish(entry, onDone) {
        const body = openModal(`SVN publish — ${entry.name}`);
        body.innerHTML = `<div class="version-sub">저장소와 비교하는 중…</div>`;

        api.svnDiff(entry.path).then((cmp) => {
            body.innerHTML = "";

            const head = document.createElement("div");
            head.className = "version-sub";
            head.style.marginBottom = "8px";
            head.textContent =
                cmp.status === "local-only"
                    ? `${entry.path} → 저장소에 없는 새 파일로 추가됩니다.`
                    : `${entry.path} — 저장소 r${cmp.repoRevision ?? "?"} / 마지막 publish r${cmp.publishedRevision ?? "없음"}`;
            body.appendChild(head);

            if (cmp.status === "conflict") {
                showMsg(body, "warn",
                    "내가 마지막으로 publish한 뒤 저장소가 변경되었습니다. " +
                    "그대로 올리면 아래 '저장소' 쪽 변경이 사라집니다.");
            }
            if (cmp.status !== "identical") renderDiff(body, cmp.diff);
            else showMsg(body, "ok", "저장소와 내용이 동일합니다 — 올릴 변경이 없습니다.");

            if (cmp.status === "identical") return;

            const label = document.createElement("label");
            label.textContent = "커밋 메시지 (선택)";
            const input = document.createElement("input");
            input.type = "text";
            input.id = "pub-msg";
            input.placeholder = "예: 후렴 노트 수정";
            body.append(label, input);

            const row = document.createElement("div");
            row.className = "svn-diff-actions";
            body.appendChild(row);

            const conflicted = cmp.status === "conflict";
            const pubBtn = document.createElement("button");
            pubBtn.textContent = conflicted ? "저장소를 덮어쓰기" : "publish";
            if (conflicted) pubBtn.className = "danger";
            row.appendChild(pubBtn);

            // 경합일 때만 의미가 있는 탈출구: 남의 버전을 받아 내 쪽을 맞춘다.
            let pullBtn = null;
            if (conflicted) {
                pullBtn = document.createElement("button");
                pullBtn.className = "secondary";
                pullBtn.textContent = "저장소 버전 가져오기";
                row.appendChild(pullBtn);
            }

            const setBusy = (on) => {
                pubBtn.disabled = on;
                if (pullBtn) pullBtn.disabled = on;
            };

            pubBtn.addEventListener("click", async () => {
                // 덮어쓰기는 되돌리기 어려우므로 한 번 더 확인한다.
                if (conflicted && !confirm(
                    "저장소의 최신 변경을 덮어씁니다. 계속할까요?\n\n" +
                    "(남의 커밋이 사라질 수 있습니다.)")) return;
                setBusy(true);
                try {
                    const res = await api.svnPublish(entry.path, input.value.trim(), conflicted);
                    body.innerHTML = "";
                    showMsg(body, "ok", `publish 완료 — r${res.revision ?? "?"}`);
                    if (onDone) setTimeout(onDone, 800);
                } catch (err) {
                    setBusy(false);
                    // 비교한 뒤 커밋 사이에 또 누군가 올린 경우.
                    if (err.status === 409) {
                        showMsg(body, "warn", "그 사이 저장소가 또 변경되었습니다. 다시 비교합니다…");
                        setTimeout(() => showPublish(entry, onDone), 900);
                    } else {
                        showMsg(body, "error", `publish 실패: ${err.message}`);
                    }
                }
            });

            if (pullBtn) {
                pullBtn.addEventListener("click", async () => {
                    if (!confirm(
                        "저장소 버전을 에디터로 가져옵니다. 내 최신 변경을 대체합니다.\n\n" +
                        "(새 버전으로 기록되므로 이력에서 되돌릴 수 있습니다.)")) return;
                    setBusy(true);
                    try {
                        const res = await api.svnUpdate(entry.path);
                        body.innerHTML = "";
                        showMsg(body, "ok", `저장소 r${res.revision ?? "?"} 내용을 가져왔습니다.`);
                        if (onDone) setTimeout(onDone, 900);
                    } catch (err) {
                        setBusy(false);
                        showMsg(body, "error", `가져오기 실패: ${err.message}`);
                    }
                });
            }
        }).catch((err) => {
            body.innerHTML = "";
            showMsg(body, "error", `비교 실패: ${err.message}`);
        });
    }

    document.getElementById("server-files").addEventListener("click", async () => {
        try {
            await ensureLoggedIn();
        } catch (err) {
            alert(`서버에 연결할 수 없습니다: ${err.message}`);
            return;
        }
        showFileBrowser();
    });

    /** 차트 목록 → 선택 또는 신규 생성 */
    async function showChartPicker() {
        const body = openModal("차트 선택");
        body.textContent = "불러오는 중…";

        let charts;
        try {
            const res = await api.listCharts();
            charts = res.charts;
        } catch (err) {
            body.textContent = "";
            showMsg(body, "error", `차트 목록을 불러오지 못했습니다: ${err.message}`);
            return;
        }

        body.innerHTML = "";

        if (charts.length === 0) {
            showMsg(body, "warn", "아직 차트가 없습니다. 아래에서 새로 만드세요.");
        }

        charts.forEach((c) => {
            const row = document.createElement("div");
            row.className = "chart-row";
            if (String(c.id) === String(serverState.chartId)) row.classList.add("active");

            const meta = document.createElement("div");
            meta.className = "version-meta";
            const name = document.createElement("div");
            name.textContent = c.music_name;
            const sub = document.createElement("div");
            sub.className = "version-sub";
            sub.textContent = `${c.owner_name}${c.is_owner ? " (내 차트)" : " (담당)"}`;
            meta.appendChild(name);
            meta.appendChild(sub);
            row.appendChild(meta);

            row.addEventListener("click", () => {
                serverState.chartId = c.id;
                serverState.musicName = c.music_name;
                // head는 아직 체크아웃하지 않았으므로 알 수 없다.
                // 히스토리에서 체크아웃해야 커밋 기준이 잡힌다.
                serverState.headVersionId = c.head_version_id ?? null;
                serverState.headVersionNumber = null;
                updateStatus();
                showHistory(c.id);
            });

            body.appendChild(row);
        });

        const sep = document.createElement("div");
        sep.style.cssText = "margin-top:14px;border-top:1px solid #555;padding-top:10px;";
        sep.innerHTML = `
            <label>새 차트 만들기 (음원 이름)</label>
            <input type="text" id="srv-new-name" placeholder="예: Moonlight">
        `;
        const createBtn = document.createElement("button");
        createBtn.textContent = "생성";
        createBtn.addEventListener("click", async () => {
            const musicName = sep.querySelector("#srv-new-name").value.trim();
            if (!musicName) return;
            createBtn.disabled = true;
            try {
                const res = await api.createChart(musicName);
                serverState.chartId = res.chart.id;
                serverState.musicName = res.chart.music_name;
                serverState.headVersionId = null;   // 최초 커밋
                serverState.headVersionNumber = null;
                updateStatus();
                closeModal();
                alert(`차트 "${musicName}" 생성됨. 이제 Commit으로 첫 버전을 올리세요.`);
            } catch (err) {
                createBtn.disabled = false;
                showMsg(body, "error", `생성 실패: ${err.message}`);
            }
        });
        sep.appendChild(createBtn);
        body.appendChild(sep);
    }

    // (구 "History" 버튼은 파일 브라우저로 통합되어 제거됨. showChartPicker도
    //  더 이상 진입점이 없다 — 파일 브라우저가 대체한다.)

    // ── 트랙 관리 / 난이도 매핑 / Export ────────────────────────────────
    //
    // 트랙 = 게임이 다루는 음원 1개. 난이도는 최대 6종이며, 난이도마다
    // 패턴(차트)은 여럿일 수 있지만 실제로 나가는 것은 **활성 패턴 1개**다.

    /** Export 버튼 공용 핸들러 — 진행/결과를 한 곳에서 처리한다. */
    async function runExport(btn, body, fn, label) {
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = "내보내는 중…";
        try {
            const res = await fn();
            let msg = `${label} 완료 — ${res.filename} (${Math.round(res.size / 1024)} KB)`;
            if (res.skipped) {
                showMsg(body, "warn",
                    `${msg}\n\n다음 항목은 커밋이 없어 제외되었습니다:\n${res.skipped}`);
            } else {
                showMsg(body, "ok", msg);
            }
        } catch (err) {
            if (err.status === 422) {
                showMsg(body, "warn", `내보낼 것이 없습니다: ${err.message}`);
            } else {
                showMsg(body, "error", `${label} 실패: ${err.message}`);
            }
        } finally {
            btn.disabled = false;
            btn.textContent = original;
        }
    }

    /**
     * 음원 미리듣기.
     *
     * <audio>는 Authorization 헤더를 못 보내므로 서명 URL을 받아 붙인다
     * (web/src/api.js audioUrl 참조). 라우트 인증은 그대로다.
     */
    function buildAudioPreview(track) {
        const wrap = document.createElement("div");
        wrap.style.cssText = "margin-top:8px;";

        if (!track.audio_sha256) {
            wrap.innerHTML = `<div class="version-sub">음원 없음</div>`;
            return wrap;
        }

        const audio = document.createElement("audio");
        audio.controls = true;
        audio.style.cssText = "width:100%;height:32px;";
        wrap.appendChild(audio);

        api.audioUrl(track.audio_sha256)
            .then((url) => { audio.src = url; })
            .catch((err) => {
                wrap.innerHTML = `<div class="version-sub">음원을 불러오지 못했습니다: ${esc(err.message)}</div>`;
            });

        return wrap;
    }

    /** 트랙 편집 — 코드·이름 변경, 음원 업로드/교체. 소유자에게만 보인다. */
    function buildTrackEditor(track, msgTarget, onSaved) {
        const box = document.createElement("div");
        box.style.cssText =
            "margin-top:10px;padding:10px;background:#2a2a2a;border:1px solid #555;border-radius:4px;";

        box.innerHTML = `
            <div style="font-size:11px;color:#aaa;margin-bottom:6px;">트랙 편집</div>
            <label>코드</label>
            <input type="text" id="trk-edit-code" value="${esc(track.code)}">
            <label>이름</label>
            <input type="text" id="trk-edit-name" value="${esc(track.name)}">
            <label>음원 (mp3 / ogg / wav, 최대 2GB)</label>
            <input type="file" id="trk-edit-audio" accept="audio/*"
                   style="font-size:11px;width:100%;">
        `;

        box.appendChild(buildAudioPreview(track));

        const saveBtn = document.createElement("button");
        saveBtn.textContent = "저장";
        box.appendChild(saveBtn);

        const status = document.createElement("span");
        status.style.cssText = "margin-left:8px;font-size:11px;color:#aaa;";
        box.appendChild(status);

        saveBtn.addEventListener("click", async () => {
            const code = box.querySelector("#trk-edit-code").value.trim();
            const name = box.querySelector("#trk-edit-name").value.trim();
            const file = box.querySelector("#trk-edit-audio").files[0];

            if (!code || !name) {
                showMsg(msgTarget, "warn", "코드와 이름은 비울 수 없습니다.");
                return;
            }

            // 코드는 Export 경로(LT0004_이름/LT0004-NM.json)에 그대로 들어간다.
            if (code !== track.code &&
                !confirm(
                    `코드를 "${track.code}" → "${code}" 로 바꿉니다.\n\n` +
                    `Export 경로와 파일명이 함께 바뀝니다:\n` +
                    `  ${code}_${name}/${code}-NM.json\n\n계속할까요?`
                )) {
                return;
            }

            saveBtn.disabled = true;
            try {
                const changes = { code, name };

                if (file) {
                    status.textContent = "음원 업로드 중…";
                    const res = await api.uploadAudio(file);
                    changes.audioId = res.audio.id;
                    status.textContent = res.deduped
                        ? "동일 음원이 이미 있어 재사용합니다."
                        : "업로드 완료.";
                }

                await api.updateTrack(track.id, changes);
                onSaved();
            } catch (err) {
                saveBtn.disabled = false;
                status.textContent = "";
                if (err.status === 409) {
                    showMsg(msgTarget, "error", `코드 "${code}"는 이미 사용 중입니다.`);
                } else if (err.status === 413) {
                    showMsg(msgTarget, "error", "음원 파일이 2GB를 넘습니다.");
                } else if (err.status === 415) {
                    showMsg(msgTarget, "error", "지원하지 않는 형식입니다 (mp3 / ogg / wav).");
                } else {
                    showMsg(msgTarget, "error", `저장 실패: ${err.message}`);
                }
            }
        });

        return box;
    }

    /** 트랙 상세 — 난이도 6종 각각에 활성 패턴을 지정한다. */
    async function showTrackDetail(trackId) {
        const body = openModal("트랙 상세");
        body.textContent = "불러오는 중…";

        let data;
        try {
            data = await api.getTrack(trackId);
        } catch (err) {
            body.textContent = "";
            showMsg(body, "error", `트랙을 불러오지 못했습니다: ${err.message}`);
            return;
        }

        const { track, difficulties } = data;
        body.innerHTML = "";

        const header = document.createElement("div");
        header.style.cssText = "margin-bottom:10px;";
        header.innerHTML = `
            <div style="font-weight:bold;font-size:14px;">${esc(track.code)}_${esc(track.name)}</div>
            <div class="version-sub">${esc(track.owner_name)}${track.is_owner ? " (내 트랙)" : " (담당)"}</div>
        `;
        body.appendChild(header);

        // 편집은 누구나 (담당은 표시용) — 서버도 소유자를 검사하지 않는다.
        header.appendChild(buildTrackEditor(track, body, () => showTrackDetail(trackId)));

        difficulties.forEach((slot) => {
            const row = document.createElement("div");
            row.className = "version-row";

            const meta = document.createElement("div");
            meta.className = "version-meta";

            const title = document.createElement("div");
            // "NM (Normal)" — the code is what ships in the filename, so it leads.
            const tierName = DIFFICULTY_NAME[slot.difficulty];
            title.innerHTML = `<span class="version-num">${slot.difficulty}</span>` +
                (tierName ? ` <span style="color:#999;font-size:11px;">${esc(tierName)}</span>` : "");
            meta.appendChild(title);

            // 배송되는(=svn) 패턴과 work 후보를 구분해 보여준다.
            const shipped = slot.patterns.find((p) => p.is_svn);
            const candidates = slot.patterns.filter((p) => !p.is_svn);

            const sub = document.createElement("div");
            sub.className = "version-sub";
            if (shipped) {
                const ver = shipped.head_version_number ? `v${shipped.head_version_number}` : "커밋 없음";
                const pub = shipped.published_revision ? ` · published r${shipped.published_revision}` : "";
                sub.textContent = `배송: ${shipped.path.split("/").pop()} (${ver})${pub}`;
            } else {
                sub.textContent = candidates.length ? "배송 패턴 없음 (아래 후보를 배치하세요)" : "패턴 없음";
            }
            meta.appendChild(sub);
            row.appendChild(meta);

            // 배송 패턴 열기 버튼
            if (shipped && shipped.head_version_number) {
                const openBtn = document.createElement("button");
                openBtn.textContent = "열기";
                openBtn.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;flex-shrink:0;";
                openBtn.addEventListener("click", () =>
                    openChartFromBrowser({ chartId: shipped.id, path: shipped.path, audioSha256: track.audio_sha256 }));
                row.appendChild(openBtn);
            }
            body.appendChild(row);

            // work 후보들: 각각 "여기로 배치"(work→svn 이동+이름변경) 버튼.
            candidates.forEach((p) => {
                const crow = document.createElement("div");
                crow.className = "version-row";
                crow.style.paddingLeft = "20px";
                const cm = document.createElement("div");
                cm.className = "version-meta";
                const ct = document.createElement("div");
                ct.style.fontSize = "11px";
                ct.appendChild(document.createTextNode("↳ " + p.path));
                cm.appendChild(ct);
                crow.appendChild(cm);

                if (track.is_owner || true) {  // 편집은 누구나 (담당은 표시용)
                    const place = document.createElement("button");
                    place.textContent = "여기로 배치";
                    place.title = `${slot.difficulty} 배송 패턴으로 설정 (svn 경로로 이동)`;
                    place.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;flex-shrink:0;";
                    place.addEventListener("click", async () => {
                        if (shipped && !confirm(`${slot.difficulty}에 이미 배송 패턴이 있습니다. 이 파일로 교체하려면 기존 것을 먼저 옮기거나 지워야 합니다.\n계속 시도할까요?`)) return;
                        try {
                            const res = await api.placePattern(trackId, slot.difficulty, p.id);
                            showMsg(body, "ok", `배치 완료 → ${res.path}`);
                            showTrackDetail(trackId);
                        } catch (err) {
                            showMsg(body, "error",
                                err.status === 409 ? "이미 그 난이도에 패턴이 있습니다." :
                                err.status === 422 ? "커밋이 없는 패턴은 배치할 수 없습니다." : err.message);
                        }
                    });
                    crow.appendChild(place);
                }
                body.appendChild(crow);
            });
        });

        const exportBtn = document.createElement("button");
        exportBtn.textContent = "이 트랙 Export";
        exportBtn.addEventListener("click", () =>
            runExport(exportBtn, body, () => api.exportTrack(trackId), "트랙 Export"));
        body.appendChild(exportBtn);

        const backBtn = document.createElement("button");
        backBtn.textContent = "← 트랙 목록";
        backBtn.className = "secondary";
        backBtn.style.marginLeft = "8px";
        backBtn.addEventListener("click", showTrackManager);
        body.appendChild(backBtn);
    }

    /** 트랙 목록 + 생성 + 전체 Export */
    /** 담당자 지정 (관리자). 담당은 편집 권한이 아니라 조정용 표시일 뿐이다. */
    async function showAssigneePicker(track) {
        const body = openModal(`담당자 지정 — ${track.code}_${track.name}`);
        body.textContent = "불러오는 중…";

        let users;
        try {
            users = (await api.listUsers()).users.filter((u) => u.status === "active");
        } catch (err) {
            body.textContent = "";
            showMsg(body, "error", err.message);
            return;
        }
        body.innerHTML = "";
        showMsg(body, "warn",
            "담당 지정은 조정용 표시일 뿐입니다. 담당이 아니어도 누구나 수정할 수 있습니다.");

        const set = async (assigneeId) => {
            try {
                await api.setTrackAssignee(track.id, assigneeId);
                closeModal();
                showTrackManager();
            } catch (err) {
                showMsg(body, "error", err.message);
            }
        };

        const none = document.createElement("button");
        none.textContent = "담당 없음";
        none.className = "secondary";
        none.style.cssText = "display:block;width:100%;text-align:left;margin-top:6px;";
        none.addEventListener("click", () => set(null));
        body.appendChild(none);

        users.forEach((u) => {
            const b = document.createElement("button");
            b.textContent = `${u.display_name} (${u.email})`;
            if (String(u.id) !== String(track.assignee_id)) b.className = "secondary";
            b.style.cssText = "display:block;width:100%;text-align:left;margin-top:6px;";
            b.addEventListener("click", () => set(u.id));
            body.appendChild(b);
        });
    }

    let trackFilterMine = false;   // "내 담당 트랙만" 토글 상태

    async function showTrackManager() {
        const body = openModal("트랙 관리");
        body.textContent = "불러오는 중…";

        let tracks;
        try {
            const res = await api.listTracks(trackFilterMine);
            tracks = res.tracks;
        } catch (err) {
            body.textContent = "";
            showMsg(body, "error", `트랙 목록을 불러오지 못했습니다: ${err.message}`);
            return;
        }

        body.innerHTML = "";

        // 담당 트랙 필터 토글
        const filterBar = document.createElement("div");
        filterBar.style.cssText = "margin-bottom:8px;";
        const mineBtn = document.createElement("button");
        mineBtn.textContent = trackFilterMine ? "☑ 내 담당 트랙만" : "☐ 내 담당 트랙만";
        mineBtn.className = trackFilterMine ? "" : "secondary";
        mineBtn.style.marginTop = "0";
        mineBtn.addEventListener("click", () => {
            trackFilterMine = !trackFilterMine;
            showTrackManager();
        });
        filterBar.appendChild(mineBtn);
        body.appendChild(filterBar);

        if (tracks.length === 0) {
            showMsg(body, "warn", trackFilterMine
                ? "담당으로 지정된 트랙이 없습니다."
                : "아직 트랙이 없습니다. 아래에서 새로 만드세요.");
        }

        const isAdmin = currentUser && currentUser.role === "admin";

        tracks.forEach((t) => {
            const row = document.createElement("div");
            row.className = "chart-row";
            if (t.is_mine) row.classList.add("active");

            const meta = document.createElement("div");
            meta.className = "version-meta";
            const name = document.createElement("div");
            name.appendChild(document.createTextNode(`${t.code}_${t.name}`));
            if (t.is_mine) {
                const tag = document.createElement("span");
                tag.textContent = " ★ 내 담당";
                tag.style.cssText = "color:#FFC107;font-size:11px;";
                name.appendChild(tag);
            }
            const sub = document.createElement("div");
            sub.className = "version-sub";
            const bits = [t.owner_name, `패턴 ${t.pattern_count}개`];
            if (t.assignee_name) bits.push(`담당: ${t.assignee_name}`);
            sub.textContent = bits.join(" · ");
            meta.appendChild(name);
            meta.appendChild(sub);
            row.appendChild(meta);

            // 관리자는 담당자 지정 버튼을 본다.
            if (isAdmin) {
                const assignBtn = document.createElement("button");
                assignBtn.textContent = "담당 지정";
                assignBtn.className = "secondary";
                assignBtn.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;flex-shrink:0;";
                assignBtn.addEventListener("click", (e) => {
                    e.stopPropagation();
                    showAssigneePicker(t);
                });
                row.appendChild(assignBtn);
            }

            meta.style.cursor = "pointer";
            meta.addEventListener("click", () => showTrackDetail(t.id));
            body.appendChild(row);
        });

        // 새 트랙
        const create = document.createElement("div");
        create.style.cssText = "margin-top:14px;border-top:1px solid #555;padding-top:10px;";
        create.innerHTML = `
            <label>새 트랙 — 코드 (예: LT0000, OT0012)</label>
            <input type="text" id="trk-code" placeholder="LT0000">
            <label>이름</label>
            <input type="text" id="trk-name" placeholder="Moonlight">
        `;
        const createBtn = document.createElement("button");
        createBtn.textContent = "트랙 생성";
        createBtn.addEventListener("click", async () => {
            const code = create.querySelector("#trk-code").value.trim();
            const name = create.querySelector("#trk-name").value.trim();
            if (!code || !name) {
                showMsg(body, "warn", "코드와 이름을 모두 입력하세요.");
                return;
            }
            createBtn.disabled = true;
            try {
                const res = await api.createTrack(code, name);
                showTrackDetail(res.track.id);
            } catch (err) {
                createBtn.disabled = false;
                showMsg(body, "error",
                    err.status === 409 ? `코드 "${code}"는 이미 존재합니다.`
                                       : `생성 실패: ${err.message}`);
            }
        });
        create.appendChild(createBtn);
        body.appendChild(create);

        // 전체 Export
        const all = document.createElement("div");
        all.style.cssText = "margin-top:14px;border-top:1px solid #555;padding-top:10px;";
        const allBtn = document.createElement("button");
        allBtn.textContent = "전체 트랙 Export (ZIP)";
        allBtn.addEventListener("click", () =>
            runExport(allBtn, all, () => api.exportAllTracks(), "전체 Export"));
        all.appendChild(allBtn);
        body.appendChild(all);
    }

    document.getElementById("server-tracks").addEventListener("click", async () => {
        try {
            await ensureLoggedIn();
        } catch (err) {
            alert(`서버에 연결할 수 없습니다: ${err.message}`);
            return;
        }
        showTrackManager();
    });

    // ── 관리자: 사용자 승인 / 권한 / 비밀번호 초기화 ──────────────────
    //
    // 가입은 자유롭게 하되 **승인 전까지는 아무것도 할 수 없다.**
    // 서버가 모든 라우트에서 상태를 재확인하므로 이 UI는 표시용일 뿐,
    // 여기서 버튼을 숨기는 것이 보안 경계는 아니다.

    const STATUS_LABEL = {
        pending: "승인 대기",
        active: "활성",
        disabled: "비활성"
    };

    /** 서버에 저장된 이벤트 정의를 불러와 에디터에 적용한다. */
    async function loadEventConfig() {
        try {
            const res = await api.getEventConfig();
            // customized일 때만 오버라이드를 건다. 기본값이면 shared 그대로 쓴다.
            eventConfigOverride = res.customized ? res.config : null;
        } catch { /* 실패해도 기본값으로 동작한다 */ }
    }

    /**
     * 이벤트 정의 관리 (관리자).
     *
     * 타입·ID·사전정의 파라미터를 추가/수정/삭제한다. 이 설정은 에디터가
     * "무엇을 제안하는가"만 바꾼다 — 검증은 형태만 보므로 기존 차트에
     * 영향이 없다. 그 점을 화면에도 적어 둔다.
     */
    async function showEventConfigManager() {
        const body = openModal("이벤트 정의 관리");
        body.textContent = "불러오는 중…";

        let data;
        try {
            data = await api.getEventConfig();
        } catch (err) {
            body.textContent = "";
            showMsg(body, "error", err.message);
            return;
        }

        // 작업용 사본 — 저장을 눌러야 서버에 반영된다.
        const cfg = JSON.parse(JSON.stringify(data.config));
        let selectedType = cfg.eventTypes[0] ?? null;

        function render() {
            body.innerHTML = "";

            const note = document.createElement("div");
            note.className = "version-sub";
            note.style.marginBottom = "8px";
            note.textContent = data.customized
                ? `사용자 설정 사용 중 (수정: ${data.updatedByName ?? "?"} · ${data.updatedAt ? new Date(data.updatedAt).toLocaleString() : ""})`
                : "기본 설정(shared/event-config.js) 사용 중";
            body.appendChild(note);

            showMsg(body, "warn",
                "이 설정은 에디터의 선택지만 바꿉니다. 검증은 형태만 보므로 " +
                "항목을 지워도 기존 차트가 무효가 되지 않습니다.");

            // ── 이벤트 타입 목록
            const typeBox = document.createElement("div");
            typeBox.style.cssText = "margin-top:10px;";
            const th = document.createElement("div");
            th.style.cssText = "font-weight:bold;font-size:12px;margin-bottom:4px;";
            th.textContent = "이벤트 타입";
            typeBox.appendChild(th);

            cfg.eventTypes.forEach((type) => {
                const row = document.createElement("div");
                row.className = "chart-row";
                if (type === selectedType) row.classList.add("active");

                const meta = document.createElement("div");
                meta.className = "version-meta";
                const name = document.createElement("div");
                name.textContent = type;
                const sub = document.createElement("div");
                sub.className = "version-sub";
                const ids = cfg.eventIdsByType[type] || [];
                sub.textContent = `${ids.length}개 ID · ${cfg.typeDescriptions[type] || "설명 없음"}`;
                meta.appendChild(name); meta.appendChild(sub);
                meta.style.cursor = "pointer";
                meta.addEventListener("click", () => { selectedType = type; render(); });
                row.appendChild(meta);

                const del = document.createElement("button");
                del.textContent = "삭제";
                del.className = "secondary";
                del.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;flex-shrink:0;";
                del.addEventListener("click", (e) => {
                    e.stopPropagation();
                    if (!confirm(`이벤트 타입 "${type}"과 그 ID 목록을 삭제합니다.\n(기존 차트에는 영향 없음) 계속할까요?`)) return;
                    cfg.eventTypes = cfg.eventTypes.filter((t) => t !== type);
                    delete cfg.eventIdsByType[type];
                    delete cfg.typeDescriptions[type];
                    Object.keys(cfg.predefinedParams)
                        .filter((k) => k.startsWith(`${type}-`))
                        .forEach((k) => delete cfg.predefinedParams[k]);
                    if (selectedType === type) selectedType = cfg.eventTypes[0] ?? null;
                    render();
                });
                row.appendChild(del);
                typeBox.appendChild(row);
            });

            const addType = document.createElement("button");
            addType.textContent = "＋ 타입 추가";
            addType.className = "secondary";
            addType.addEventListener("click", () => {
                const name = prompt("새 이벤트 타입 이름 (예: timewarp):");
                if (!name || !name.trim()) return;
                const t = name.trim();
                if (cfg.eventTypes.includes(t)) { alert("이미 있는 타입입니다."); return; }
                cfg.eventTypes.push(t);
                cfg.eventIdsByType[t] = [];
                const desc = prompt(`"${t}" 설명 (선택):`, "");
                if (desc) cfg.typeDescriptions[t] = desc;
                selectedType = t;
                render();
            });
            typeBox.appendChild(addType);
            body.appendChild(typeBox);

            // ── 선택한 타입의 ID 목록
            if (selectedType) {
                const idBox = document.createElement("div");
                idBox.style.cssText = "margin-top:14px;border-top:1px solid #555;padding-top:10px;";
                const ih = document.createElement("div");
                ih.style.cssText = "font-weight:bold;font-size:12px;margin-bottom:4px;";
                ih.textContent = `"${selectedType}" 의 이벤트 ID`;
                idBox.appendChild(ih);

                (cfg.eventIdsByType[selectedType] || []).forEach((id) => {
                    const key = `${selectedType}-${id}`;
                    const params = cfg.predefinedParams[key] || [];

                    const row = document.createElement("div");
                    row.className = "version-row";
                    const meta = document.createElement("div");
                    meta.className = "version-meta";
                    const n = document.createElement("div");
                    n.innerHTML = `<span class="version-num">${esc(id)}</span>`;
                    const s = document.createElement("div");
                    s.className = "version-sub";
                    s.textContent = params.length
                        ? params.map((p) => `${p.paramName}:${p.paramType || "string"}`).join(", ")
                        : "파라미터 없음";
                    meta.appendChild(n); meta.appendChild(s);
                    row.appendChild(meta);

                    const editP = document.createElement("button");
                    editP.textContent = "파라미터";
                    editP.className = "secondary";
                    editP.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;flex-shrink:0;";
                    editP.addEventListener("click", () => editParams(key, render));
                    row.appendChild(editP);

                    const delId = document.createElement("button");
                    delId.textContent = "삭제";
                    delId.className = "secondary";
                    delId.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;flex-shrink:0;";
                    delId.addEventListener("click", () => {
                        if (!confirm(`"${id}" 를 삭제합니다. 계속할까요?`)) return;
                        cfg.eventIdsByType[selectedType] =
                            cfg.eventIdsByType[selectedType].filter((x) => x !== id);
                        delete cfg.predefinedParams[key];
                        render();
                    });
                    row.appendChild(delId);
                    idBox.appendChild(row);
                });

                const addId = document.createElement("button");
                addId.textContent = "＋ ID 추가";
                addId.className = "secondary";
                addId.addEventListener("click", () => {
                    const id = prompt(`"${selectedType}" 에 추가할 이벤트 ID:`);
                    if (!id || !id.trim()) return;
                    const v = id.trim();
                    cfg.eventIdsByType[selectedType] = cfg.eventIdsByType[selectedType] || [];
                    if (cfg.eventIdsByType[selectedType].includes(v)) { alert("이미 있는 ID입니다."); return; }
                    cfg.eventIdsByType[selectedType].push(v);
                    render();
                });
                idBox.appendChild(addId);
                body.appendChild(idBox);
            }

            // ── 저장 / 기본값 복원
            const foot = document.createElement("div");
            foot.style.cssText = "margin-top:14px;border-top:1px solid #555;padding-top:10px;";
            const save = document.createElement("button");
            save.textContent = "저장";
            save.addEventListener("click", async () => {
                save.disabled = true;
                try {
                    await api.saveEventConfig(cfg);
                    eventConfigOverride = cfg;      // 에디터에 즉시 반영
                    scheduleRender({ eventList: true });
                    showMsg(foot, "ok", "저장했습니다. 에디터에 바로 반영됩니다.");
                    data.customized = true;
                } catch (err) {
                    save.disabled = false;
                    showMsg(foot, "error",
                        err.status === 400 ? `형식 오류: ${(err.body?.errors ?? []).join(", ")}` : err.message);
                }
            });
            foot.appendChild(save);

            const reset = document.createElement("button");
            reset.textContent = "기본값으로 되돌리기";
            reset.className = "secondary";
            reset.style.marginLeft = "8px";
            reset.addEventListener("click", async () => {
                if (!confirm("사용자 설정을 지우고 기본값(shared/event-config.js)으로 되돌립니다.\n계속할까요?")) return;
                try {
                    await api.resetEventConfig();
                    eventConfigOverride = null;
                    scheduleRender({ eventList: true });
                    closeModal();
                } catch (err) {
                    showMsg(foot, "error", err.message);
                }
            });
            foot.appendChild(reset);
            body.appendChild(foot);
        }

        /** 특정 "<type>-<id>" 의 사전정의 파라미터 편집 */
        function editParams(key, onDone) {
            const pbody = openModal(`파라미터 — ${key}`);
            const list = cfg.predefinedParams[key] ? [...cfg.predefinedParams[key]] : [];

            function draw() {
                pbody.innerHTML = "";
                const hint = document.createElement("div");
                hint.className = "version-sub";
                hint.style.marginBottom = "8px";
                hint.textContent = "이벤트를 새로 만들 때 자동으로 추가될 파라미터입니다.";
                pbody.appendChild(hint);

                list.forEach((p, i) => {
                    const row = document.createElement("div");
                    row.className = "version-row";
                    const meta = document.createElement("div");
                    meta.className = "version-meta";

                    const nameIn = document.createElement("input");
                    nameIn.type = "text";
                    nameIn.value = p.paramName ?? "";
                    nameIn.placeholder = "파라미터 이름";
                    nameIn.addEventListener("input", () => { list[i].paramName = nameIn.value; });
                    meta.appendChild(nameIn);

                    const typeSel = document.createElement("select");
                    typeSel.style.cssText = "width:100%;margin-top:4px;background:#222;color:#fff;border:1px solid #555;padding:4px;font-size:11px;";
                    ["float", "int", "string", "bool", "vector2", "vector3", "color", "enum"].forEach((t) => {
                        const o = document.createElement("option");
                        o.value = t; o.textContent = t;
                        if ((p.paramType ?? "string") === t) o.selected = true;
                        typeSel.appendChild(o);
                    });
                    typeSel.addEventListener("change", () => { list[i].paramType = typeSel.value; });
                    meta.appendChild(typeSel);
                    row.appendChild(meta);

                    const del = document.createElement("button");
                    del.textContent = "삭제";
                    del.className = "secondary";
                    del.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;flex-shrink:0;";
                    del.addEventListener("click", () => { list.splice(i, 1); draw(); });
                    row.appendChild(del);
                    pbody.appendChild(row);
                });

                const add = document.createElement("button");
                add.textContent = "＋ 파라미터 추가";
                add.className = "secondary";
                add.addEventListener("click", () => { list.push({ paramName: "", paramType: "float" }); draw(); });
                pbody.appendChild(add);

                const ok = document.createElement("button");
                ok.textContent = "확인";
                ok.style.marginLeft = "8px";
                ok.addEventListener("click", () => {
                    const cleaned = list.filter((p) => p.paramName && p.paramName.trim());
                    if (cleaned.length) cfg.predefinedParams[key] = cleaned;
                    else delete cfg.predefinedParams[key];
                    onDone();
                });
                pbody.appendChild(ok);
            }
            draw();
        }

        render();
    }

    async function showAdminPanel() {
        const body = openModal("사용자 관리");
        body.textContent = "불러오는 중…";

        let users;
        try {
            const res = await api.listUsers();
            users = res.users;
        } catch (err) {
            body.textContent = "";
            showMsg(body, "error",
                err.status === 403 ? "관리자 권한이 필요합니다."
                                   : `사용자 목록을 불러오지 못했습니다: ${err.message}`);
            return;
        }

        body.innerHTML = "";

        const pending = users.filter((u) => u.status === "pending");
        if (pending.length > 0) {
            showMsg(body, "warn", `승인 대기 중인 신청이 ${pending.length}건 있습니다.`);
        }

        users.forEach((u) => {
            const row = document.createElement("div");
            row.className = "version-row";
            if (u.status === "pending") row.classList.add("is-head");

            const meta = document.createElement("div");
            meta.className = "version-meta";

            const title = document.createElement("div");
            const roleTag = u.role === "admin" ? ' <span style="color:#ffb74d;">[관리자]</span>' : "";
            title.innerHTML = `<span class="version-num">${esc(u.display_name)}</span>${roleTag}`;

            const sub = document.createElement("div");
            sub.className = "version-sub";
            const isSelf = currentUser && String(u.id) === String(currentUser.id);
            sub.textContent =
                `${u.email} · ${STATUS_LABEL[u.status] ?? u.status}` +
                ` · 차트 ${u.chart_count}개` +
                (u.must_change_password ? " · 비밀번호 변경 대기" : "") +
                (isSelf ? " · 나" : "");

            meta.appendChild(title);
            meta.appendChild(sub);
            row.appendChild(meta);

            const actions = document.createElement("div");
            actions.style.cssText = "display:flex;gap:4px;flex-shrink:0;";

            /** 액션 버튼 — 실행 후 목록을 다시 그린다. */
            const addAction = (label, cls, handler, confirmText) => {
                const b = document.createElement("button");
                b.textContent = label;
                if (cls) b.className = cls;
                b.style.cssText = "margin-top:0;padding:4px 8px;font-size:11px;";
                b.addEventListener("click", async () => {
                    if (confirmText && !confirm(confirmText)) return;
                    b.disabled = true;
                    try {
                        await handler();
                        showAdminPanel();
                    } catch (err) {
                        b.disabled = false;
                        showMsg(body, "error", err.message);
                    }
                });
                actions.appendChild(b);
            };

            if (u.status === "pending") {
                addAction("승인", null, () => api.setUserStatus(u.id, "active"));
            } else if (u.status === "active" && !isSelf) {
                addAction("비활성", "secondary", () => api.setUserStatus(u.id, "disabled"),
                    `${u.display_name} 계정을 비활성화합니다. 계속할까요?`);
            } else if (u.status === "disabled") {
                addAction("재활성", null, () => api.setUserStatus(u.id, "active"));
            }

            if (!isSelf) {
                if (u.role === "user") {
                    addAction("관리자 지정", "secondary", () => api.setUserRole(u.id, "admin"),
                        `${u.display_name}에게 관리자 권한을 부여합니다. 계속할까요?`);
                } else {
                    addAction("관리자 해제", "secondary", () => api.setUserRole(u.id, "user"),
                        `${u.display_name}의 관리자 권한을 해제합니다. 계속할까요?`);
                }
            }

            addAction("비번 초기화", "secondary", async () => {
                const res = await api.resetUserPassword(u.id);
                // 메일 발송이 없으므로 관리자가 직접 전달해야 한다.
                alert(
                    `${u.display_name}의 임시 비밀번호:\n\n${res.temporaryPassword}\n\n` +
                    `이 값을 본인에게 직접 전달하세요. 최초 로그인 시 변경이 강제됩니다.`
                );
            }, `${u.display_name}의 비밀번호를 임시 값으로 초기화합니다. 계속할까요?`);

            // 삭제 — 자기 자신·마지막 관리자는 서버가 막는다. 소유 차트·트랙은
            // 삭제되지 않고 관리자에게 이전된다(서버에서 처리).
            if (!isSelf) {
                addAction("삭제", "secondary", () => api.deleteUser(u.id),
                    `${u.display_name} 계정을 삭제합니다.\n` +
                    `이 사용자의 차트·트랙은 삭제되지 않고 관리자(나)에게 이전됩니다.\n계속할까요?`);
            }

            row.appendChild(actions);
            body.appendChild(row);
        });

        // 관리자가 직접 계정 생성 (승인 절차 없이 바로 활성)
        const create = document.createElement("div");
        create.style.cssText = "margin-top:14px;border-top:1px solid #555;padding-top:10px;";
        create.innerHTML = `
            <label>계정 직접 생성 — 이메일 또는 ID</label>
            <input type="text" id="adm-email">
            <label>표시 이름</label>
            <input type="text" id="adm-name">
        `;
        const createBtn = document.createElement("button");
        createBtn.textContent = "생성";
        createBtn.addEventListener("click", async () => {
            const email = create.querySelector("#adm-email").value.trim();
            const displayName = create.querySelector("#adm-name").value.trim();
            if (!email || !displayName) {
                showMsg(body, "warn", "이메일과 표시 이름을 입력하세요.");
                return;
            }
            createBtn.disabled = true;
            try {
                const res = await api.createUser(email, displayName);
                alert(
                    `${displayName} 계정 생성됨.\n\n임시 비밀번호: ${res.temporaryPassword}\n\n` +
                    `이 값을 본인에게 직접 전달하세요. 최초 로그인 시 변경이 강제됩니다.`
                );
                showAdminPanel();
            } catch (err) {
                createBtn.disabled = false;
                showMsg(body, "error",
                    err.status === 409 ? "이미 등록된 계정입니다." : `생성 실패: ${err.message}`);
            }
        });
        create.appendChild(createBtn);
        body.appendChild(create);

        // 내 비밀번호 변경
        const pw = document.createElement("button");
        pw.textContent = "내 비밀번호 변경";
        pw.className = "secondary";
        pw.style.marginLeft = "8px";
        pw.addEventListener("click", () => showPasswordChange());
        create.appendChild(pw);

        // 이벤트 정의 관리
        const evtCfg = document.createElement("div");
        evtCfg.style.cssText = "margin-top:14px;border-top:1px solid #555;padding-top:10px;";
        const evtBtn = document.createElement("button");
        evtBtn.textContent = "⚙ 이벤트 정의 관리";
        evtBtn.title = "이벤트 타입·ID·사전정의 파라미터 추가/수정/삭제";
        evtBtn.addEventListener("click", () => showEventConfigManager());
        evtCfg.appendChild(evtBtn);
        body.appendChild(evtCfg);
    }

    document.getElementById("server-admin").addEventListener("click", async () => {
        try {
            await ensureLoggedIn();
        } catch (err) {
            alert(`서버에 연결할 수 없습니다: ${err.message}`);
            return;
        }
        showAdminPanel();
    });

    document.getElementById("server-account").addEventListener("click", () => {
        showAccountModal();
    });

    // 페이지 입장에는 로그인이 필요하다. 로그인할 때까지 게이트가 막고,
    // 성공하면 관리자 버튼 노출을 맞추고 이벤트 정의·서버 연결을 복원한다.
    requireLogin()
        .then((ok) => {
            if (!ok) return; // 서버에 못 닿음 — 재시도 버튼이 이미 떠 있다.
            // 관리자가 저장한 이벤트 정의가 있으면 얹는다(없으면 기본값 유지).
            loadEventConfig();
            return restoreServerState();
        })
        .catch(() => { /* 게이트 내부에서 이미 처리됨 */ });

    updateStatus();

    inputAudio.addEventListener("change", (e) => {
        const file = e.target.files[0];
        const nameEl = document.getElementById("audio-file-name");
        if (nameEl) nameEl.textContent = file ? file.name : "";
        if (!file) {
            hasAudioFile = false;
            audioBuffer = null;
                        savedAudioFile = null;
            if (audioFileURL)
                URL.revokeObjectURL(audioFileURL);
            audioFileURL = null;
            demoAudio.src = '';
            
            const container = inputAudio.parentElement;
            const indicator = container.querySelector('.file-indicator');
            if (indicator)
                indicator.remove();

            saveToStorage();
            return;
        }

        if (audioFileURL)
            URL.revokeObjectURL(audioFileURL);
        audioFileURL = URL.createObjectURL(file);
        demoAudio.src = audioFileURL;
        demoAudio.volume = musicVolume;
        demoAudio.load();

        // 파일 이름 표시 제거
        // const container = inputAudio.parentElement;
        // let indicator = container.querySelector('.file-indicator');
        // if (!indicator) {
        //     indicator = document.createElement('div');
        //     indicator.className = 'file-indicator';
        //     indicator.style.cssText = 'margin-top: 5px; font-size: 12px; color: #4CAF50; font-weight: bold;';
        //     container.appendChild(indicator);
        // }
        // indicator.textContent = `선택된 파일: ${file.name}`;

        processAudioFile(file);
    });

    btnDemoPlay.addEventListener("click", () => {
        if (!demoAudio.src) {
            alert("오디오 파일을 먼저 선택해주세요.");
            return;
        }
        if (isPlaying) {
            resumeDemo();
        } else {
            startDemo();
        }
    });
    btnDemoPause.addEventListener("click", pauseDemo);
    btnDemoStop.addEventListener("click", stopDemo);

    seekbar.addEventListener("input", () => {
        if (!isPlaying)
            return;
        elapsedTime = seekbar.value / 1000;
        startTime = performance.now() - elapsedTime * 1000;

        // 현재 시간보다 이후의 노트와 이벤트 실행 상태를 리셋
        const notesToReset = [];
        notes.forEach((note, index) => {
            const noteId = `${note.type}-${note.beat}-${index}`;
            let finalTime;

            if (note.beat === 0 && note.type === "direction") {
                finalTime = getPreDelaySeconds();
            } else {
                const noteBpm = note.bpm || parseFloat(document.getElementById("bpm").value || 120);
                const noteSubdivisions = note.subdivisions || parseInt(document.getElementById("subdivisions").value || 16);
                const originalTime = beatToTime(note.beat, noteBpm, noteSubdivisions);
                finalTime = originalTime + getPreDelaySeconds();
            }

            if (finalTime > elapsedTime) {
                notesToReset.push(noteId);
            }
        });

        notesToReset.forEach(noteId => {
            playedNotes.delete(noteId);
        });

        // 현재 시간보다 이후의 이벤트 실행 상태를 리셋
        resetEventExecutionToTime(elapsedTime);

        const preDelaySeconds = getPreDelaySeconds();
        if (elapsedTime < MUSIC_START_TIME) {
            demoAudio.pause();
            demoAudio.currentTime = 0;
        } else {
            demoAudio.currentTime = Math.max(0, elapsedTime - MUSIC_START_TIME);
            if (!demoAudio.paused) {
                demoAudio.play();
            }
        }
        drawPath();
    });

    canvas.addEventListener("wheel", (e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 1.1 : 0.9;
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const worldX = (mouseX - viewOffset.x) / zoom;
        const worldY = (mouseY - viewOffset.y) / zoom;
        zoom *= delta;
        viewOffset.x = mouseX - worldX * zoom;
        viewOffset.y = mouseY - worldY * zoom;
        drawPath();
    }, {
        passive: false
    });

    canvas.addEventListener("mousedown", (e) => {
        // 좌클릭이 곡선 핸들 위에서 시작하면 패닝 대신 핸들 드래그.
        if (e.button === 0 && curveHandlesEnabled && !isPlaying) {
            const rect = canvas.getBoundingClientRect();
            const handle = findHandleAt(e.clientX - rect.left, e.clientY - rect.top);
            if (handle) {
                e.preventDefault();
                saveState();                 // 드래그 시작 시 1회만 히스토리 스냅샷
                draggingHandle = handle;
                handleInteractionActive = true;  // 이어질 click이 노트 포커스로 새지 않게
                canvas.style.cursor = "grabbing";
                return;
            }
        }
        if (e.button === 0 || e.button === 1) {
            isPanning = true;
            lastMousePos = {
                x: e.clientX,
                y: e.clientY
            };
        }
    });

    canvas.addEventListener("mousemove", (e) => {
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        if (draggingHandle) {
            const w = screenToWorld(mx, my);
            updateCurveFromHandleDrag(draggingHandle, w.x, w.y);
            invalidatePathCache();
            drawPath();                      // 실시간 프리뷰: 뒤 경로 전체가 따라 움직인다
            // 드래그 중 핸들 객체는 재계산으로 교체되므로 noteIndex로 다시 찾아 잡는다.
            const still = (pathCache.segmentTimes || []).find(s => s.handle && s.handle.noteIndex === draggingHandle.noteIndex);
            if (still) draggingHandle = still.handle;
            return;
        }

        if (isPanning) {
            const dx = e.clientX - lastMousePos.x;
            const dy = e.clientY - lastMousePos.y;
            viewOffset.x += dx;
            viewOffset.y += dy;
            lastMousePos = {
                x: e.clientX,
                y: e.clientY
            };
            drawPath();
            return;
        }

        // 유휴 상태: 핸들 위에 오면 커서를 grab으로 바꿔 잡을 수 있음을 알린다.
        if (curveHandlesEnabled && !isPlaying) {
            canvas.style.cursor = findHandleAt(mx, my) ? "grab" : "";
        }
    });

    canvas.addEventListener("mouseup", (e) => {
        if (draggingHandle) {
            const draggedIndex = draggingHandle.noteIndex;
            draggingHandle = null;
            canvas.style.cursor = "";
            saveToStorage();
            // 목록 전체를 다시 그리면 표에서 입력 중이던 셀이 파괴된다. 해당 행만 동기화.
            syncCurveCellInputs(draggedIndex);
            return;
        }
        if (e.button === 0 || e.button === 1) {
            isPanning = false;
        }
    });

    // 캔버스 우클릭: 곡선 핸들 위면 프리셋 메뉴.
    canvas.addEventListener("contextmenu", (e) => {
        if (!curveHandlesEnabled || isPlaying) return;
        const rect = canvas.getBoundingClientRect();
        const handle = findHandleAt(e.clientX - rect.left, e.clientY - rect.top);
        if (handle) {
            e.preventDefault();
            showCurvePresetMenu(e.clientX, e.clientY, handle.noteIndex);
        }
    });

    canvas.addEventListener("click", (e) => {
        // 곡선 핸들을 잡았던 상호작용의 후속 click은 노트 포커스로 소비하지 않는다.
        if (handleInteractionActive) {
            handleInteractionActive = false;
            return;
        }

        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        const clickBpm = parseFloat(document.getElementById("bpm").value || 120);
        const clickSubs = parseInt(document.getElementById("subdivisions").value || 16);
        const clickPreDelay = getPreDelaySeconds();
        const clickDirNotes = getDirectionNotesWithOffsets(clickBpm, clickSubs);
        const clickPathNotes = calculatePathDirectionNotes(clickDirNotes, clickBpm, clickSubs, clickPreDelay);
        const clickPathData = calculateNodePositions(clickPathNotes, clickBpm, clickSubs);
        const clickNodePositions = clickPathData.nodePositions;
        const clickOffsets = getOrComputeSectionOffsets();

        for (let i = 0; i < notes.length; i++) {
            const note = notes[i];
            const sectionOffset = clickOffsets[i] || 0;
            let noteFinalTime;
            if (note.beat === 0 && note.type === "direction" && sectionOffset === 0) {
                noteFinalTime = 0;
            } else if ((note.type === "tab" || note.type === "longtab") &&
                       note.hasOwnProperty('fadeDirectTime')) {
                noteFinalTime = note.fadeDirectTime;
            } else {
                const noteBpm = note.bpm || clickBpm;
                const noteSubs = note.subdivisions || clickSubs;
                noteFinalTime = sectionOffset + beatToTime(note.beat, noteBpm, noteSubs) + clickPreDelay;
            }
            const pos = getNotePositionFromPathData(noteFinalTime, clickPathNotes, clickNodePositions, clickPathData.segmentTimes);
            if (!pos)
                continue;
            const screenX = pos.x * zoom + viewOffset.x;
            const screenY = pos.y * zoom + viewOffset.y;
            const dist = Math.hypot(screenX - mx, screenY - my);
            if (dist < 10) {
                focusNoteAtIndex(i);
                break;
            }
        }
    });

    const batchEditCheckbox = document.getElementById('batch-edit-toggle');
    batchEditCheckbox.addEventListener('change', (e) => {
        isBatchEditEnabled = e.target.checked;
    });

    const cameraTrackingCheckbox = document.getElementById('camera-tracking');
    cameraTrackingCheckbox.addEventListener('change', (e) => {
        isCameraTracking = e.target.checked;
    });

    // 게임 카메라 영역 표시. 정지 중에도 즉시 반영되도록 다시 그린다.
    const cameraBoundsCheckbox = document.getElementById('camera-bounds');
    if (cameraBoundsCheckbox) {
        isCameraBoundsVisible = cameraBoundsCheckbox.checked;
        cameraBoundsCheckbox.addEventListener('change', (e) => {
            isCameraBoundsVisible = e.target.checked;
            cameraBoundsPrev.cam = null;
            cameraBoundsPrev.time = null;
            drawPath();
        });
    }

    const curveHandlesCheckbox = document.getElementById('curve-handles');
    if (curveHandlesCheckbox) {
        curveHandlesEnabled = curveHandlesCheckbox.checked;
        curveHandlesCheckbox.addEventListener('change', (e) => {
            curveHandlesEnabled = e.target.checked;
            if (!curveHandlesEnabled) canvas.style.cursor = "";
            drawPath();
        });
    }

    window.addEventListener('resize', () => {
        // Canvas resize handling if needed
    });

    setupVolumeControls();
    setupToggleFeatures();
    setupResizerFeatures();
    setupNoteButtons();

    ensureInitialDirectionNote(notes);
    loadFromStorage();
    renderSignatureList();   // 복원된 signature 구간을 화면에 반영

    // 기존 노트들에 개별 BPM/subdivision이 없으면 현재 설정값으로 초기화
    initializeNoteBpmSubdivisions();

    // BPM 제약 조건 초기화
    updateBpmConstraints();

    loadNoteSounds();

    drawPath();
    renderNoteList();
    renderEventList();
    updateCoordinateInfo();

    // 키보드 단축키
    document.addEventListener('keydown', (e) => {
        // Ctrl+Z (실행 취소) - 모든 상황에서 작동
        if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
            e.preventDefault();
            if (undo()) {
                console.log('Undo performed via Ctrl+Z');
            }
            return;
        }

        // Ctrl+Y 또는 Ctrl+Shift+Z (다시 실행) - 모든 상황에서 작동
        if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
            e.preventDefault();
            if (redo()) {
                console.log('Redo performed via Ctrl+Y or Ctrl+Shift+Z');
            }
            return;
        }

        // Ctrl+C (복사) - 선택된 이벤트/노트를 JSON 형태로 클립보드에 복사
        if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
            // 입력 필드에서는 기본 복사 기능 사용
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                return;
            }
            // 아무것도 선택되지 않았으면 기본 복사 기능 사용 (텍스트 선택 등)
            if (selectedEventIndices.size === 0 && selectedNoteIndices.size === 0) {
                return;
            }
            e.preventDefault();
            copySelectedItems();
            return;
        }

        // Ctrl+V (붙여넣기) - 클립보드의 JSON 데이터를 리스트에 추가
        if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
            // 입력 필드에서는 기본 붙여넣기 기능 사용
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                return;
            }
            e.preventDefault();
            pasteItems();
            return;
        }

        // 입력 필드나 버튼에 포커스가 있을 때는 노트 추가 단축키 작동 안 함
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') {
            return;
        }

        switch (e.key) {
            case ' ': // Space 키로 모든 선택 해제
                clearAllSelections();
                break;
            case 'Delete': // Del 키로 선택된 노트/이벤트 삭제
                deleteSelectedItems();
                break;
            case 'q':
                addNote({ type: "tab", isLong: false, longTime: 0 });
                break;
            case 'w':
                addNote({ type: "direction", isLong: false, longTime: 0 });
                break;
            case 'e':
                addNote({ type: "both", isLong: false, longTime: 0 });
                break;
            case 'r':
                addNote({ type: "node", isLong: false, longTime: 0 });
                break;
            case 'a':
                addNote({ type: "longtab", isLong: true });
                break;
            case 's':
                addNote({ type: "longdirection", isLong: true });
                break;
            case 'd':
                addNote({ type: "longboth", isLong: true });
                break;
        }
    });

    // 초기 버튼 상태 설정
    updateUndoRedoButtons();

    console.log('Initialization complete');
});

// 선택 관련 함수들

// 선택된 항목들 삭제 (Del 키)
function deleteSelectedItems() {
    // 현재 활성화된 탭 확인
    const notesTab = document.getElementById('notes-tab');
    const eventsTab = document.getElementById('events-tab');
    // 🔴 **타임라인 탭도 노트를 다룬다.** 이 분기가 없으면 타임라인에서 노트를 눌러
    // 선택해도 Del이 아무 일도 하지 않는다(사용자 보고) — 어느 탭도 active가 아니라
    // 두 분기 모두 건너뛰기 때문이다.
    const timelineTab = document.getElementById('timeline-tab');
    const isNotesTabActive = notesTab?.classList.contains('active')
        || timelineTab?.classList.contains('active');
    const isEventsTabActive = eventsTab?.classList.contains('active');

    if (isNotesTabActive) {
        // 노트 탭이 활성화된 경우 - 선택된 노트들 삭제
        deleteSelectedNotes();
    } else if (isEventsTabActive) {
        // 이벤트 탭이 활성화된 경우 - 선택된 이벤트들 삭제
        deleteSelectedEvents();
    }
}

// 선택된 노트들 삭제
function deleteSelectedNotes() {
    // 다중 선택이 없으면 단일 선택(포커스)된 노트를 대상으로 함
    if (selectedNoteIndices.size === 0 && selectedNoteIndex !== null) {
        selectedNoteIndices.add(selectedNoteIndex);
    }

    if (selectedNoteIndices.size === 0) {
        return; // 선택된 노트가 없으면 아무것도 하지 않음
    }

    // 변경 전 상태를 히스토리에 저장
    saveState();

    // 인덱스를 내림차순으로 정렬하여 뒤에서부터 삭제 (인덱스 변화 방지)
    const sortedIndices = Array.from(selectedNoteIndices).sort((a, b) => b - a);

    let deletedCount = 0;
    for (const index of sortedIndices) {
        const note = notes[index];
        // 차트 시작 노트는 삭제 금지 — 인덱스가 아니라 속성으로 판정한다(감사 B-7)
        if (isChartStartNote(note, index)) {
            continue; // 이 노트는 삭제하지 않음
        }

        if (index >= 0 && index < notes.length) {
            notes.splice(index, 1);
            deletedCount++;
        }
    }

    if (deletedCount > 0) {
        // 캐시 무효화
        invalidatePathCache();

        // 선택 상태 초기화
        selectedNoteIndices.clear();
        selectedNoteIndex = null;
        lastClickedNoteIndex = null;

        // 🔴 인스펙터는 **배열 인덱스**로 노트를 가리킨다. 삭제로 인덱스가 밀리면
        // 열려 있던 팝업이 엉뚱한 노트를 보여주거나(또는 없는 노트를) 가리키므로 닫는다.
        closeTimelineInspector();

        // UI 업데이트 및 저장
        saveToStorage();
        renderNoteList();
        drawPath();

        console.log(`${deletedCount}개의 노트가 삭제되었습니다.`);
    }
}

// 선택된 이벤트들 삭제
function deleteSelectedEvents() {
    if (selectedEventIndices.size === 0) {
        return; // 선택된 이벤트가 없으면 아무것도 하지 않음
    }

    // 변경 전 상태를 히스토리에 저장
    saveState();

    // 인덱스를 내림차순으로 정렬하여 뒤에서부터 삭제 (인덱스 변화 방지)
    const sortedIndices = Array.from(selectedEventIndices).sort((a, b) => b - a);

    let deletedCount = 0;
    for (const index of sortedIndices) {
        if (removeEvent(index)) {
            deletedCount++;
        }
    }

    if (deletedCount > 0) {
        // 선택 상태 초기화
        selectedEventIndices.clear();
        lastClickedEventIndex = null;

        // UI 업데이트 및 저장
        saveToStorage();
        renderEventList();

        console.log(`${deletedCount}개의 이벤트가 삭제되었습니다.`);
    }
}

// 차트 시작 노트인지 판정한다.
//
// 계약(`validateChart`, shared/notes.js)은 beat 0 direction 노트를 **필수**로 요구하고,
// `notes.find`로 **처음 만나는 하나**를 시작점으로 본다. 따라서 보호 대상은
// "인덱스 0"이 아니라 "그 조건을 처음 만족하는 노트"다.
//
// 예전에는 두 곳 모두 `index === 0`으로 판정해서, 정렬이나 undo로 이 노트가
// 인덱스 0을 벗어나면 보호가 풀려 삭제할 수 있었다(감사 B-7).
// 그러면 커밋 시점에야 422로 거부된다.
function isChartStartNote(note, index) {
    if (!note || note.beat !== 0 || note.type !== "direction") return false;
    return notes.findIndex(n => n.beat === 0 && n.type === "direction") === index;
}

// 선택 상태만 비운다(렌더 없음).
// 선택값은 전부 **배열 인덱스**라 notes/events를 통째로 갈아끼우면 의미를 잃는다.
// 남겨두면 undo 직후 Delete가 엉뚱한 노트를 지운다(감사 B-2).
// 배열 교체 지점은 대개 자기가 렌더를 부르므로, 여기서 렌더하면 이중 렌더가 된다.
function resetSelectionState() {
    selectedNoteIndices.clear();
    selectedEventIndices.clear();
    selectedNoteIndex = null;
    lastClickedNoteIndex = null;
    lastClickedEventIndex = null;
}

// 모든 선택 해제 (렌더 포함)
function clearAllSelections() {
    resetSelectionState();

    // UI 업데이트
    renderNoteList();
    renderEventList();

    console.log('모든 선택이 해제되었습니다.');
}

// 복사/붙여넣기 관련 함수들

// 선택된 이벤트/노트들을 JSON 형태로 클립보드에 복사
async function copySelectedItems() {
    try {
        // 아무것도 선택되지 않았으면 복사하지 않음
        if (selectedEventIndices.size === 0 && selectedNoteIndices.size === 0) {
            console.log('복사할 항목이 선택되지 않았습니다.');
            return;
        }

        const itemsToCopy = {
            type: 'WL_Editor_Data',
            events: [],
            notes: []
        };

        // 현재 설정값들 가져오기
        const currentBpm = parseFloat(document.getElementById("bpm").value || 120);
        const currentSubdivisions = parseInt(document.getElementById("subdivisions").value || 16);
        const currentPreDelaySeconds = getPreDelaySeconds();

        // 선택된 이벤트들 복사
        if (selectedEventIndices.size > 0) {
            const allEvents = getAllEvents();
            const eventIndices = Array.from(selectedEventIndices).sort((a, b) => a - b);
            for (const index of eventIndices) {
                if (index >= 0 && index < allEvents.length) {
                    const event = allEvents[index];
                    // 출력 형태의 JSON으로 변환
                    const jsonEvent = {
                        eventType: event.eventType,
                        eventId: event.eventId,
                        eventTime: event.eventTime,
                        eventParams: event.eventParams.map(param => ({
                            paramName: param.paramName,
                            paramValue: param.paramValue
                        }))
                    };
                    itemsToCopy.events.push(jsonEvent);
                }
            }
        }

        // 선택된 노트들 복사
        if (selectedNoteIndices.size > 0) {
            const noteIndices = Array.from(selectedNoteIndices).sort((a, b) => a - b);
            const copyOffsets = getOrComputeSectionOffsets();
            for (const index of noteIndices) {
                if (index >= 0 && index < notes.length) {
                    const note = notes[index];
                    // 출력 형태의 JSON으로 변환 (sectionOffset 포함)
                    const sectionOffset = copyOffsets[index] || 0;
                    const jsonNote = noteToJsonFormat(note, currentBpm, currentSubdivisions, currentPreDelaySeconds, sectionOffset);
                    itemsToCopy.notes.push(jsonNote);
                }
            }
        }

        if (itemsToCopy.events.length === 0 && itemsToCopy.notes.length === 0) {
            console.log('복사할 항목이 선택되지 않았습니다.');
            return;
        }

        // 클립보드에 복사
        const jsonString = JSON.stringify(itemsToCopy, null, 2);
        await navigator.clipboard.writeText(jsonString);

        console.log(`복사 완료: 이벤트 ${itemsToCopy.events.length}개, 노트 ${itemsToCopy.notes.length}개`);

        // 사용자에게 알림 (선택사항)
        showNotification(`복사 완료: 이벤트 ${itemsToCopy.events.length}개, 노트 ${itemsToCopy.notes.length}개`);

    } catch (error) {
        console.error('복사 중 오류:', error);
        showNotification('복사 중 오류가 발생했습니다.');
    }
}

// 클립보드의 JSON 데이터를 현재 선택된 항목과 같은 시간으로 붙여넣기
async function pasteItems() {
    try {
        const clipboardText = await navigator.clipboard.readText();
        let pasteData;

        try {
            pasteData = JSON.parse(clipboardText);
        } catch (parseError) {
            console.log('클립보드에 유효한 JSON 데이터가 없습니다.');
            return;
        }

        // WL_Editor_Data 형태인지 확인
        if (!pasteData || pasteData.type !== 'WL_Editor_Data') {
            console.log('WL Editor 데이터가 아닙니다.');
            return;
        }

        let pastedEventCount = 0;
        let pastedNoteCount = 0;

        // 현재 설정값들 가져오기
        const currentBpm = parseFloat(document.getElementById("bpm").value || 120);
        const currentSubdivisions = parseInt(document.getElementById("subdivisions").value || 16);

        // 현재 선택된 항목의 시간 가져오기
        let targetTime = null;
        let targetBeat = null;

        // 이벤트가 선택되어 있으면 이벤트 시간 사용
        if (selectedEventIndices.size > 0) {
            const allEvents = getAllEvents();
            const selectedIndex = Array.from(selectedEventIndices)[0];
            if (selectedIndex >= 0 && selectedIndex < allEvents.length) {
                targetTime = allEvents[selectedIndex].eventTime;
            }
        }

        // 노트가 선택되어 있으면 노트 비트 사용
        if (selectedNoteIndices.size > 0) {
            const selectedIndex = Array.from(selectedNoteIndices)[0];
            if (selectedIndex >= 0 && selectedIndex < notes.length) {
                targetBeat = notes[selectedIndex].beat;
            }
        }

        // 붙여넣기 전 현재 상태 저장 (Undo 지원)
        saveState();

        // 이벤트 붙여넣기
        if (pasteData.events && Array.isArray(pasteData.events)) {
            for (const eventData of pasteData.events) {
                const newEvent = {
                    eventType: eventData.eventType || 'camera',
                    eventId: eventData.eventId || '',
                    eventTime: targetTime !== null ? targetTime : (eventData.eventTime || 0),
                    eventParams: Array.isArray(eventData.eventParams) ?
                        eventData.eventParams.map(param => ({
                            paramName: param.paramName || '',
                            paramValue: param.paramValue || ''
                        })) : []
                };


                addEvent(newEvent);
                pastedEventCount++;
            }
        }

        // 노트 붙여넣기
        if (pasteData.notes && Array.isArray(pasteData.notes)) {
            // 프레이즈의 상대 간격을 보존한다(0-4). 예전에는 모든 노트에 targetBeat를
            // 그대로 넣어 4마디 프레이즈가 한 비트에 뭉쳤다.
            // 기준은 복사된 노트 중 가장 이른 beat이고, 각 노트는 그로부터의 차이를 유지한다.
            const sourceBeats = pasteData.notes.map(n => (typeof n.beat === 'number' ? n.beat : 0));
            const baseBeat = sourceBeats.length > 0 ? Math.min(...sourceBeats) : 0;

            pasteData.notes.forEach((noteData, i) => {
                // 계약 변환기를 쓴다 — 손으로 짠 목록이 angleOffset·bezier·fade를 빠뜨렸다(감사 B-1).
                const newNote = jsonToNoteFormat(noteData, currentBpm, currentSubdivisions);

                if (targetBeat !== null) {
                    newNote.beat = targetBeat + (sourceBeats[i] - baseBeat);
                }

                // beatReset은 node 외 타입에도 필요하다(계약 변환기는 node에만 붙인다).
                if (newNote.beatReset === undefined) {
                    newNote.beatReset = noteData.isBeatReset || false;
                }

                notes.push(newNote);
                pastedNoteCount++;
            });
        }

        if (pastedEventCount > 0 || pastedNoteCount > 0) {
            if (pastedEventCount > 0) {
                scheduleRender({ eventList: true });
            }
            if (pastedNoteCount > 0) {
                renderNoteList();
            }

            console.log(`붙여넣기 완료: 이벤트 ${pastedEventCount}개, 노트 ${pastedNoteCount}개`);
            showNotification(`붙여넣기 완료: 이벤트 ${pastedEventCount}개, 노트 ${pastedNoteCount}개`);
        } else {
            console.log('붙여넣을 데이터가 없습니다.');
        }

    } catch (error) {
        console.error('붙여넣기 중 오류:', error);
        showNotification('붙여넣기 중 오류가 발생했습니다.');
    }
}

// 알림 표시 함수 (간단한 구현)
function showNotification(message) {
    // 기존 알림이 있으면 제거
    const existingNotification = document.getElementById('copy-paste-notification');
    if (existingNotification) {
        existingNotification.remove();
    }

    // 새 알림 생성
    const notification = document.createElement('div');
    notification.id = 'copy-paste-notification';
    notification.textContent = message;
    notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #333;
        color: white;
        padding: 10px 15px;
        border-radius: 5px;
        z-index: 10000;
        font-size: 14px;
        transition: opacity 0.3s;
    `;

    document.body.appendChild(notification);

    // 3초 후 자동 제거
    setTimeout(() => {
        notification.style.opacity = '0';
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 300);
    }, 3000);
}

// Dialog 아이템 섹션 생성
function createDialogItemsSection(event, eventIndex) {
    const dialogContainer = document.createElement("div");
    dialogContainer.className = "dialog-container";

    // 기본 Parameters 섹션 (접혀있는 상태)
    const paramsSection = createCollapsibleParamsSection(event, eventIndex);
    dialogContainer.appendChild(paramsSection);

    // Dialog Items 섹션
    const dialogLabel = document.createElement("div");
    dialogLabel.className = "dialog-label";
    dialogLabel.innerHTML = '<span class="dialog-toggle">▼</span> Dialog Items';

    const dialogContent = document.createElement("div");
    dialogContent.className = "dialog-content";

    const dialogItemsList = document.createElement("div");
    dialogItemsList.className = "dialog-items-list";
    dialogItemsList.dataset.eventIndex = eventIndex;

    // dialogItems가 없으면 초기화
    if (!event.dialogItems) {
        event.dialogItems = [];
    }

    // Dialog 아이템들 렌더링
    event.dialogItems.forEach((item, itemIndex) => {
        const itemDiv = createDialogItemElement(event, eventIndex, item, itemIndex);
        dialogItemsList.appendChild(itemDiv);
    });

    // Add Dialog Item 버튼
    const addItemBtn = document.createElement("button");
    addItemBtn.textContent = "+ Dialog Item 추가";
    addItemBtn.className = "add-dialog-item-btn";
    addItemBtn.dataset.eventIndex = eventIndex;

    dialogContent.appendChild(dialogItemsList);
    dialogContent.appendChild(addItemBtn);
    dialogContainer.appendChild(dialogLabel);
    dialogContainer.appendChild(dialogContent);

    return dialogContainer;
}

// 접을 수 있는 기본 Parameters 섹션 생성
function createCollapsibleParamsSection(event, eventIndex) {
    const paramsContainer = document.createElement("div");
    paramsContainer.className = "params-container collapsed";

    const paramsLabel = document.createElement("div");
    paramsLabel.className = "params-label";
    paramsLabel.innerHTML = '<span class="params-toggle">▶</span> Parameters';

    const paramsContent = document.createElement("div");
    paramsContent.className = "params-content";
    paramsContent.style.display = "none";

    const paramsList = document.createElement("div");
    paramsList.className = "params-list";

    // 파라미터들 렌더링
    event.eventParams.forEach((param, paramIndex) => {
        const paramDiv = createEventParamElement(event, eventIndex, param, paramIndex);
        paramsList.appendChild(paramDiv);
    });

    const addParamBtn = document.createElement("button");
    addParamBtn.textContent = "파라미터 추가";
    addParamBtn.className = "add-param-btn";

    paramsContent.appendChild(paramsList);
    paramsContent.appendChild(addParamBtn);
    paramsContainer.appendChild(paramsLabel);
    paramsContainer.appendChild(paramsContent);

    return paramsContainer;
}

// Dialog 아이템 요소 생성
function createDialogItemElement(event, eventIndex, item, itemIndex) {
    const itemDiv = document.createElement("div");
    itemDiv.className = "dialog-item";
    itemDiv.dataset.itemIndex = itemIndex;
    // itemDiv.draggable = true; // 제거: 전체 아이템이 아닌 드래그 핸들만 드래그 가능하게

    // 드래그 핸들
    const dragHandle = document.createElement("div");
    dragHandle.className = "drag-handle";
    dragHandle.innerHTML = "⋮⋮";
    dragHandle.title = "드래그하여 순서 변경";
    dragHandle.draggable = true; // 드래그 핸들만 드래그 가능하게

    // 인덱스 표시
    const indexSpan = document.createElement("span");
    indexSpan.className = "item-index";
    indexSpan.textContent = `${itemIndex}:`;

    // 아이템 타입 선택
    const typeSelect = document.createElement("select");
    typeSelect.className = "dialog-item-type-select";
    typeSelect.dataset.eventIndex = eventIndex;
    typeSelect.dataset.itemIndex = itemIndex;

    const itemTypes = getDialogItemTypes();
    itemTypes.forEach(type => {
        const option = document.createElement("option");
        option.value = type;
        option.textContent = type;
        option.selected = type === item.type;
        typeSelect.appendChild(option);
    });

    // 아이템 필드들 생성
    const fieldsContainer = document.createElement("div");
    fieldsContainer.className = "dialog-item-fields";

    const fields = getDialogItemFields(item.type);
    fields.forEach(field => {
        const fieldDiv = document.createElement("div");
        fieldDiv.className = "dialog-item-field";

        const fieldLabel = document.createElement("label");
        fieldLabel.textContent = `${field.fieldName}: `;

        const fieldInput = document.createElement("input");
        fieldInput.type = field.fieldType === 'float' ? 'number' : 'text';
        if (field.fieldType === 'float') {
            fieldInput.step = "0.01";
        }
        fieldInput.value = item[field.fieldName] || '';
        fieldInput.placeholder = field.placeholder || '';
        fieldInput.className = "dialog-item-field-input";
        fieldInput.dataset.eventIndex = eventIndex;
        fieldInput.dataset.itemIndex = itemIndex;
        fieldInput.dataset.fieldName = field.fieldName;

        fieldLabel.appendChild(fieldInput);
        fieldDiv.appendChild(fieldLabel);
        fieldsContainer.appendChild(fieldDiv);
    });

    // 삭제 버튼
    const deleteBtn = document.createElement("button");
    deleteBtn.textContent = "×";
    deleteBtn.className = "delete-dialog-item-btn";
    deleteBtn.dataset.eventIndex = eventIndex;
    deleteBtn.dataset.itemIndex = itemIndex;
    deleteBtn.title = "아이템 삭제";

    // 요소들 조합
    const headerDiv = document.createElement("div");
    headerDiv.className = "dialog-item-header";
    headerDiv.appendChild(dragHandle);
    headerDiv.appendChild(indexSpan);
    headerDiv.appendChild(typeSelect);
    headerDiv.appendChild(deleteBtn);

    itemDiv.appendChild(headerDiv);
    itemDiv.appendChild(fieldsContainer);

    return itemDiv;
}

// 드래그 앤 드롭 관련 변수들
let draggedItem = null;
let draggedEventIndex = null;
let draggedItemIndex = null;

// Dialog 아이템 드래그 시작
function handleDialogItemDragStart(e) {

    // 드래그 핸들인지 확인
    if (!e.target.classList.contains('drag-handle')) {
        e.preventDefault();
        return;
    }

    const dialogItem = e.target.closest('.dialog-item');
    if (!dialogItem) {
        e.preventDefault();
        return;
    }

    draggedItem = dialogItem;
    draggedEventIndex = parseInt(dialogItem.closest('.event-item').dataset.eventIndex);
    draggedItemIndex = parseInt(dialogItem.dataset.itemIndex);


    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', dialogItem.outerHTML);

    // 드래그 중인 아이템 스타일 변경
    setTimeout(() => {
        dialogItem.style.opacity = '0.5';
    }, 0);
}

// Dialog 아이템 드래그 오버
function handleDialogItemDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const dialogItem = e.target.closest('.dialog-item');
    if (!dialogItem || !draggedItem || dialogItem === draggedItem) {
        return;
    }

    // 같은 이벤트 내의 아이템만 허용
    const targetEventIndex = parseInt(dialogItem.closest('.event-item').dataset.eventIndex);
    if (targetEventIndex !== draggedEventIndex) {
        return;
    }


    // 드롭 위치 표시를 위한 스타일 추가
    const rect = dialogItem.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;

    // 기존 drop-indicator 제거
    document.querySelectorAll('.drop-indicator').forEach(el => el.remove());

    if (e.clientY < midY) {
        // 위에 드롭
        dialogItem.style.borderTop = '2px solid #007cba';
    } else {
        // 아래에 드롭
        dialogItem.style.borderBottom = '2px solid #007cba';
    }
}

// Dialog 아이템 드롭
function handleDialogItemDrop(e) {
    e.preventDefault();

    if (!draggedItem) {
        return;
    }

    const targetItem = e.target.closest('.dialog-item');
    if (!targetItem || targetItem === draggedItem) {
        return;
    }

    // 같은 이벤트 내의 아이템만 허용
    const targetEventIndex = parseInt(targetItem.closest('.event-item').dataset.eventIndex);
    if (targetEventIndex !== draggedEventIndex) {
        return;
    }

    const targetItemIndex = parseInt(targetItem.dataset.itemIndex);

    // 드롭 위치 계산
    const rect = targetItem.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    let newIndex = targetItemIndex;

    if (e.clientY >= midY) {
        newIndex = targetItemIndex + 1;
    }

    // 드래그된 아이템이 뒤에 있을 때 인덱스 조정
    if (draggedItemIndex < newIndex) {
        newIndex--;
    }


    // 같은 위치로 드롭하면 무시
    if (newIndex === draggedItemIndex) {
        return;
    }

    // 아이템 이동
    if (moveDialogItem(draggedEventIndex, draggedItemIndex, newIndex)) {
        scheduleRender({ eventList: true });
        saveToStorage();
    }
}

// Dialog 아이템 드래그 종료
function handleDialogItemDragEnd(e) {
    // 스타일 초기화
    if (draggedItem) {
        draggedItem.style.opacity = '';
    }

    // border 스타일 제거
    document.querySelectorAll('.dialog-item').forEach(item => {
        item.style.borderTop = '';
        item.style.borderBottom = '';
    });

    // drop-indicator 제거
    document.querySelectorAll('.drop-indicator').forEach(el => el.remove());

    // 변수 초기화
    draggedItem = null;
    draggedEventIndex = null;
}

// 개발자 도구용: 가상 스크롤링 토글 함수
window.toggleEventListVirtualScrolling = function (enabled) {
    virtualScrollState.event.enabled = enabled;
    console.log(`가상 스크롤링: ${enabled ? '활성화' : '비활성화'}`);
    renderEventList();
};

// 개발자 도구용: 대량 테스트 이벤트 생성
window.generateTestEvents = function (count = 1000) {
    const startTime = performance.now();

    clearAllEvents();

    for (let i = 0; i < count; i++) {
        addEvent();
        const event = getEventAtIndex(i);
        if (event) {
            event.eventType = i % 2 === 0 ? 'player' : 'camera';
            event.eventId = 'test';
            event.eventTime = i * 0.1;
        }
    }

    const endTime = performance.now();
    console.log(`${count}개 테스트 이벤트 생성 완료 (${(endTime - startTime).toFixed(2)}ms)`);
    renderEventList();
};